/**
 * Reference renderer: IR → HTML.
 *
 * **This is not the renderer.** @Codex owns the React interpreter under
 * [0002](../../../Agents/Decisions/0002-division-of-labor.md). This exists so
 * the IR has a consumer that can be tested headlessly and so the loop can be
 * seen end to end — objects, spec, IR, pixels — without waiting on a framework.
 *
 * It is also the conformance reference: any other interpreter over the same IR
 * should produce the same accessible structure, which is the determinism
 * @Codex specified in 0004 — *equivalent accessible structure, not literal
 * pixel identity across fonts, browsers and viewports.*
 *
 * ---
 *
 * **On presentation.** Joaquín's read of the deployed page was that it looked
 * basic and hardcoded, and he was right: this file used to emit one shape for
 * every collection — a CSS grid of `minmax(9rem, 1fr)` — regardless of what the
 * collection contained. A spec describing a table and a spec describing a photo
 * wall rendered identically. That is not a styling shortfall, it is the
 * renderer declining to read its own input.
 *
 * So presentation is now **derived from the IR's contents**:
 *
 * | What the IR says | What it renders as |
 * | --- | --- |
 * | item has media, grid flow | a gallery, tiles honouring the media aspect |
 * | item has media, any other flow | a list, thumbnail beside text |
 * | no media, collection has a `groupBy` | a board, one column per group |
 * | no media, 2+ fields, a set rather than one thing | a table, one column per role |
 * | anything else | cards |
 *
 * Media outranks grouping, which was not the first ordering I wrote. Checking
 * `groupBy` first meant a poster wall grouped by country rendered as a kanban
 * board of text cards — the grouping was honoured and the posters, the highest
 * bandwidth thing on the screen, were thrown away. Grouping changes how a
 * collection is *arranged*; media changes what the items fundamentally are.
 *
 * No app is named anywhere in that table, which is the point. A Notion-shaped
 * database and an Obsidian-shaped note list are two outputs of the same rules,
 * reached by describing different content — not two code paths.
 */

import {
  ROLE_KIND, DERIVATION_LABELS, DERIVATION_YIELDS, APP_TEMPLATE_VERSIONS, DERIVATIVE_OF,
  type Derivation, type Role, type Source, type RegionPurpose, type AppTemplate,
} from '@morph/spec';
import type { IR, IRNode } from './ir.js';
import { toneOf } from './tone.js';
import {
  ANCHOR_ATTR, anchorAttrs, handleOf, relationTokenOf,
  type BoundValues, type GrantSet,
} from './edit.js';
import { surfaceOf, type Scheme, type Surface } from './palette.js';
import { VIRTUALIZE_ABOVE } from './compile.js';
import { fontStackFor } from './tokens.js';
import { isHostResolved } from './host-resolved.js';
import {
  capabilityInputFor, renderCapability, resetCapabilityBudget, unconsumedSlots,
  sectionAnchor, drawnNotes, inertItem,
  type CapabilityInput, type CapabilityInputs, type InertItem,
} from './capability.js';

/** Objects arrive already resolved to roles by the private binding manifest.
 *  The renderer never sees a property key, only a role — same boundary the
 *  generator works behind. */
export type RoleValues = Readonly<Record<string, unknown>>;

export interface RenderInput {
  ir: IR;
  objects: readonly BoundValues[];
  /**
   * The other named sets a collection may ask for.
   *
   * `objects` is the SUBJECT and stays the default, so every existing caller
   * keeps working untouched. A collection that declares `source: 'siblings'`
   * reads from here instead — which is how a panel can list the pool while the
   * content beside it shows one object, out of one spec.
   *
   * A host that does not supply a requested set gets the subject back rather
   * than an empty list: an outline that silently renders nothing looks exactly
   * like an outline of an empty pool, and the two are not the same fact.
   */
  sources?: Readonly<Partial<Record<Source, readonly BoundValues[]>>>;
  /**
   * ONE REGION'S rows, by the authored id of the collection that asked.
   *
   * `sources` above keys by `Source`, and `SOURCES` is
   * `['subject', 'siblings']` — so two subject collections in one lens both
   * read `objects`. A host that queried per region had nowhere to put the
   * results, which is what stood between the `viewState` mechanism and Coda's
   * *"a control whose value drives queries in several regions"*: the value
   * reached one region and no other.
   *
   * Consulted BEFORE `sources`, and optional, so every existing caller renders
   * exactly as before. A collection this does not name falls back to the rule
   * above it — and a host that names a collection the spec does not have simply
   * misses, which shows as a region drawing the subject rather than as a region
   * silently drawing another region's rows.
   *
   * The renderer still evaluates no predicates. It receives sets; the store
   * decides what is in them. @Codex's ruling on task 0081: filtering in two
   * places *"would create two query semantics and make store parity
   * unprovable."*
   */
  regionSets?: Readonly<Record<string, readonly BoundValues[]>>;
  /** The size of each region's set before the host windowed it, by the same
   *  key. Same contract as `totals`: missing means the supplied set is
   *  complete, and a total smaller than the set is clamped up to it. */
  regionTotals?: Readonly<Record<string, number>>;
  /**
   * The size of each named set before the host windowed it.
   *
   * A renderer handed 200 rows from a 100,000-object scope cannot recover the
   * other 99,800 from the values. Without this, its list tells assistive
   * technology it contains 200 things and its overflow status has no honest
   * number to report. The host already owns the query total, so it crosses the
   * same boundary as the corresponding set.
   *
   * Missing means the supplied set is complete, preserving every existing
   * caller. A total smaller than the supplied set is clamped up to the set:
   * rendering data is evidence, and metadata may not deny objects on screen.
   */
  totals?: Readonly<Partial<Record<Source, number>>>;
  /**
   * What the HOST calls each role, on screen.
   *
   * The renderer has always derived a role's caption from the role name
   * itself — `quantity` became "Quantity" — and that is right for a vocabulary
   * term and wrong for a property somebody declared. The Notes pool declares
   * `quantity` and calls it **Words**; every table header, reference row and
   * empty-field prompt said "Quantity", which on a note is not a thing a note
   * has. The name was in the pool the whole time and had no way to reach here.
   *
   * A RENDER INPUT rather than a field on the spec, and that distinction is the
   * point. A caption is content, the spec is the one place content must never
   * be, and a spec carrying "Words" could not be shared between pools or
   * translated. So the host passes the names it declared, alongside the values
   * it declared them for, and a host that passes nothing gets exactly what it
   * got before.
   */
  labels?: Readonly<Partial<Record<Role, string>>>;

  /**
   * The values an enum role may be chosen from.
   *
   * A RENDER INPUT for the same reason `labels` is: these are content, and the
   * spec is the one place content must never be. A pooled spec carrying
   * "In progress" could not be shared between pools, and a shape that carried
   * it would have stopped being a shape — `ObjectShape` is role kinds and
   * counts, never values.
   *
   * So the host supplies them, from what its collection actually holds, and a
   * host that passes nothing gets a chooser with only the current value in it
   * rather than no chooser: a `set-enum` grant the renderer declined to draw
   * would be the `node-slot` failure again — a path built end to end with
   * nothing at the end of it.
   */
  choices?: Readonly<Partial<Record<Role, readonly string[]>>>;
  /**
   * The host's declared value→TONE mapping per enum role — decision 0063.
   *
   * Which values exist is observed (`choices`, from the store); which
   * CATEGORY each one belongs to is authored, and the two come from different
   * places on purpose. A value absent here takes the tone `toneOf` derives
   * from its text: stable and order-independent, but not guaranteed distinct,
   * which is why an authored mapping exists at all.
   */
  tones?: Readonly<Partial<Record<Role, Readonly<Record<string, number>>>>>;
  /**
   * Folds computed over a WHOLE set, keyed by the source they describe.
   *
   * Keyed by source rather than by node, for the same reason `sources` is: the
   * rollups belong to the SET, not to a position in a tree. Two collections
   * over the same set share them, and each draws only the folds its own
   * `query.aggregate` names.
   *
   * The renderer computes nothing. It holds at most `VIRTUALIZE_ABOVE` objects,
   * so any arithmetic here would be a fold over a page printed as a total — a
   * wrong number presented with total confidence, and no way for a reader to
   * tell. Absent means **draw nothing**: never a partial sum, and never a zero.
   */
  rollups?: Readonly<Partial<Record<Source, Rollups>>>;
  /** Host-resolved inputs for registered capability nodes, keyed by IR index. */
  capabilities?: CapabilityInputs;
  /** Light or dark. Defaults to light; the demo follows the OS. */
  scheme?: Scheme;
  /**
   * Absent → a strictly read-only rendering that emits NO `contenteditable`.
   *
   * It used to be emitted unconditionally for a read-write binding, which is an
   * editing affordance with nothing behind it: a caret appeared, characters
   * were typed, and not one of them was ever written anywhere. An affordance
   * that does nothing is worse than a missing one, because the user believes
   * the edit happened.
   */
  edit?: { readonly epoch: number; readonly grants: GrantSet };
  /**
   * The WORKSPACE's own facts for a named app look — decision 0055: the
   * vault the explorer is a tree of. Host-resolved like a capability input,
   * because a spec names no pool; absent draws the pane without its folder
   * header rather than inventing a name.
   */
  workspace?: {
    readonly vault?: string;
    /** The app's chrome the host cannot back, declared here and drawn
     *  inert (0056): the explorer's vault strip — its switcher before the
     *  name (`place: 'start'`), help and settings after — and the pane
     *  heads' ×. Never a renderer constant: the strip drew "? ⚙" as text
     *  for a week, in the accessibility tree, declared by nobody. */
    readonly inert?: {
      readonly vault?: readonly InertItem[];
      readonly paneClose?: InertItem;
    };
    /** The backlinks traversal stopped early (the store's own word for
     *  why); the pane says so beside its count rather than presenting a
     *  partial list as complete (@Codex, P2 on d0c42da). */
    readonly backlinksNote?: string;
    /** How many OTHER notes mention the open note's title in their text
     *  without linking to it — computed by the host from store-backed
     *  content. Absent means the host did not compute it, and the pane
     *  draws no "Unlinked mentions" section rather than a made-up zero. */
    readonly unlinkedMentions?: number;
    /** Which pane each `sidebar` region is showing, keyed by the sidebar's
     *  spec id — host state, told to the renderer (0055: the chrome is in
     *  the spec, the CHOICE is not). A sidebar absent from the map, or
     *  naming a pane it does not hold, shows its first pane. */
    readonly sidePanes?: Readonly<Record<string, RegionPurpose>>;
    /**
     * A look's OPENING pane, named semantically rather than by spec id —
     * @Codex's P2 on `0d83f75`. Read as *"a sidebar that holds a pane of
     * purpose K opens on purpose V"*.
     *
     * `sidePanes` above is the right shape for a PERSON'S choice: they
     * clicked a tab in one specific region, and the id is what identifies
     * it. It is the wrong shape for a look's declaration, and the demo was
     * seeding it with `ws-sidebar` — an id the current builder happens to
     * mint. `templateShortfalls()` matches that region by purpose and
     * topology, not by id, so a valid `app.obsidian` presentation using any
     * other legal id passed the contract and then silently opened Backlinks
     * where the look declared Graph. A declaration keyed on a builder's
     * private naming is a declaration that only works by coincidence.
     *
     * Consulted AFTER `sidePanes`, because a person's click must beat a
     * look's opening once they have made one.
     */
    readonly opensPaneOver?: Readonly<Record<string, RegionPurpose>>;
    /** The search pane's query, echoed into its input so a repaint does not
     *  wipe what the person typed; the results are the host's, supplied as
     *  a subject collection under the pane's list id. */
    readonly searchQuery?: string;
    /** The explorer's folders a person has closed, by folder name — host
     *  state; a folder absent from the list is open. The folders themselves
     *  are the file list's `groupBy`, drawn by the renderer. */
    readonly folded?: readonly string[];
    /** Which of the main column's views shows — the note's editor or the
     *  vault's Graph view (a `graph` region beside the editor). Host state;
     *  absent means the note. */
    readonly mainView?: 'note' | 'graph';
  };
}

const esc = (s: unknown): string =>
  String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

/**
 * A human label for a role.
 *
 * Derived from the role token rather than tabulated, so adding a role to the
 * vocabulary cannot leave a column headed `media.poster-frame`. It is
 * English-only, which is a real gap: @Codex identified the need for closed
 * localized copy tokens in task 0001 and the vocabulary has none. This is a
 * placeholder standing exactly where that mechanism belongs.
 */
/** Vocabulary order, so a multi-effect button labels itself by the most
 *  consequential thing it can do rather than by whichever effect happened to be
 *  listed first. */
/**
 * A PRECEDENCE, not a list: the most specific effect names the button.
 *
 * `edit-layout` had NO entry in any of these three tables until the guard below
 * enumerated the vocabulary — so a button declaring it has always rendered as
 * *Run*, described to a screen reader with the raw token `edit-layout`. That
 * predates all of this. @Codex raised the tables about a different effect
 * entirely — `duplicate-object`, removed again with decision 0033 — and the
 * guard written for that one found this older gap on its first run.
 *
 * `EFFECT_ORDER` picks the label for an invocation declaring several effects,
 * so the order is a precedence: the most specific thing the button does should
 * be the thing it is called. A duplicate is a create, and calling it *New*
 * would name the general case and lose the whole of what it is.
 */
const EFFECT_ORDER = [
  'create-object', 'set-property', 'navigate', 'edit-layout',
] as const;

/** Logical edges as words a person can hear. Derived, never authored. */
const EDGE_PHRASE: Record<string, string> = {
  'block-start': 'at the top',
  'block-end': 'at the bottom',
  'inline-start': 'at the start',
  'inline-end': 'at the end',
  'center': 'centred',
};

const EFFECT_LABEL: Record<string, string> = {
  'create-object': 'New',
  'set-property': 'Update',
  'navigate': 'Open',
  'edit-layout': 'Arrange',
};

/** The same effects as a verb phrase, for the accessible description.
 *  A screen reader reads the description as a sentence, and "this button may
 *  new" is not one — the visible label and the spoken one need different
 *  grammar even though they name the same thing. */
/**
 * Third-person present, not "may" — task 0154 item 6. The blind drivers read
 * "this button may create an object" as the product hedging about itself,
 * and the amendment makes copy part of the look: controls say what they do.
 * The declaration IS what pressing does — the press either performs its
 * declared effects or refuses in words, so the definite form is honest.
 */
const EFFECT_PHRASE: Record<string, string> = {
  'create-object': 'creates an object',
  'set-property': 'changes a property',
  'navigate': 'opens a related object',
  'edit-layout': 'rearranges this layout',
};

export function labelForRole(role: Role): string {
  const leaf = role.includes('.') ? role.slice(role.indexOf('.') + 1) : role;
  const words = leaf.replace(/[-_]/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

type Presentation = 'board' | 'gallery' | 'table' | 'list' | 'cards';

/** Every node in a template subtree, in order. */
function subtree(ir: IR, root: number): IRNode[] {
  const out: IRNode[] = [];
  const walk = (i: number) => {
    const n = ir.nodes[i];
    if (!n) return;
    out.push(n);
    for (const c of n.children) walk(c);
  };
  walk(root);
  return out;
}

/** Is this collection the body of a document?
 *
 *  `slot` is set on the direct children of a capability node, so the question
 *  is answered from the IR rather than from a name the host passed in. The
 *  parent lookup is a scan; a lens has tens of nodes, not thousands. */
function isDocumentBody(ir: IR, collection: IRNode): boolean {
  if (collection.slot !== 'body') return false;
  const parent = ir.nodes.find((n) => n.children.includes(collection.i));
  return parent?.kind === 'capability' && parent.capability === 'document-flow';
}

/**
 * A DERIVED value — Coda's formula column, with no formula in it.
 *
 * Every one of these is a function of a single stored value, which is what
 * keeps them honest: nothing here can read another column, so no derived cell
 * can depend on layout, on ordering, or on what else the lens is showing.
 *
 * Returns `undefined` when the value cannot answer the question, and the caller
 * draws an empty cell. A derivation that guessed — zero words for a value it
 * could not read, year 1970 for a date it could not parse — would put a
 * plausible number in a column, which is worse than a blank.
 */
export function derived(op: Derivation, value: unknown): string | number | undefined {
  switch (op) {
    case 'year':
    case 'month': {
      // Read from the STRING, never through `new Date`. `new Date('1960-03-10')`
      // is UTC midnight, so a runtime west of Greenwich reports the previous
      // day — and in January, the previous YEAR. The same trap the calendar
      // documents, one module over, and it is worth the duplication of four
      // lines to not import a date parser into the renderer.
      const iso = value instanceof Date
        ? (Number.isNaN(value.getTime()) ? undefined : value.toISOString().slice(0, 10))
        : typeof value === 'string' ? /^\d{4}-\d{2}-\d{2}/.exec(value.trim())?.[0] : undefined;
      if (!iso) return undefined;
      const month = Number(iso.slice(5, 7));
      if (month < 1 || month > 12) return undefined;
      return op === 'year' ? Number(iso.slice(0, 4)) : iso.slice(0, 7);
    }
    case 'words': {
      // ABSENT is zero, not unknown. An object with no body has no words in
      // it, and that is a fact rather than a gap — a blank cell there says
      // "we could not tell", which is a different and less true sentence. The
      // same call `count` makes below, for the same reason.
      if (value === undefined || value === null || value === '') return 0;
      if (typeof value !== 'string') return undefined;
      // Split on any whitespace run, and count nothing for a blank string —
      // `''.split(/\s+/)` is `['']`, which is one word that is not there.
      const trimmed = value.trim();
      return trimmed === '' ? 0 : trimmed.split(/\s+/).length;
    }
    case 'characters':
      // `Array.from`, not `.length`: a string's length counts UTF-16 code
      // units, so an emoji or any astral character counts as two. A column
      // headed "Characters" should say what a person would count.
      if (value === undefined || value === null || value === '') return 0;
      return typeof value === 'string' ? Array.from(value).length : undefined;
    case 'count':
      // A ref role holds several targets or one. One is a count of one, which
      // is why this is not `Array.isArray(value) ? … : undefined`.
      return Array.isArray(value) ? value.length
        : value === undefined || value === null || value === '' ? 0 : 1;
  }
}

/**
 * WHICH item template an object is drawn by.
 *
 * The mechanism behind the one line `ACCEPTANCE.md` calls "the part that
 * matters more than any of them": a collection may hold genuinely different
 * kinds of thing and render each as what it is. A Notion database has one
 * schema, so a row missing a property shows an empty cell; a guarded
 * collection gives that object a different card.
 *
 * It was accepted, validated, compiled — and then discarded. Every read of
 * `itemTemplates` in this file took `[0]`, so N branches were drawn as one for
 * every object. The schema allowed eight and the renderer honoured the first.
 *
 * A guard NAMES a predicate rather than carrying one, so this is a lookup and
 * not an evaluator: does the object have a value for that role. The LAST
 * template is the default — the validator requires a `when` on every item but
 * the last — so this always answers.
 */
function templateFor(
  collection: IRNode, obj: BoundValues | undefined,
): { when?: { has: Role }; root: number } | undefined {
  const templates = collection.itemTemplates;
  if (!templates?.length) return undefined;
  for (const t of templates) {
    if (!t.when) return t;
    const v = obj?.[t.when.has];
    // Present means a value that is actually there. An empty string and an
    // empty array are what a store hands back for a property somebody cleared,
    // and branching on those would put an object in the branch built for the
    // thing it no longer has.
    const present = v !== undefined && v !== null && v !== ''
      && !(Array.isArray(v) && v.length === 0);
    if (present) return t;
  }
  // Every branch was guarded and none matched. The validator forbids that
  // shape, so reaching here means an unvalidated spec — draw it with the last
  // template rather than nothing, because a collection that renders empty
  // looks like an empty pool.
  return templates[templates.length - 1];
}

/** The derivation table in this file's header, as code. */
export function presentationOf(
  ir: IR, collection: IRNode, opts: { single?: boolean } = {},
): Presentation {
  const tpl = collection.itemTemplates?.[0];
  if (!tpl) return 'cards';
  const kids = subtree(ir, tpl.root);
  const media = kids.filter((n) => n.kind === 'media');
  const fields = kids.filter((n) => n.kind === 'field');
  const single = opts.single ?? false;

  if (media.length > 0) return collection.flow === 'grid' ? 'gallery' : 'list';
  // A WORKSPACE PANE LISTS, and it lists FIRST — before `groupBy` can make a
  // board of it. The explorer's `groupBy` is its folders (0055, the ninth
  // verdict's explorer hierarchy), drawn as a tree by `collection()`; a
  // board of kanban columns inside a 240px pane is the generic presentation
  // wearing the app's chrome, which control 6 rejects by name.
  const pane = ir.nodes[collection.parent];
  if (pane?.kind === 'region'
      && (pane.purpose === 'backlinks' || pane.purpose === 'explorer' || pane.purpose === 'search')) return 'cards';
  if (collection.query?.groupBy) return 'board';
  // A DOCUMENT BODY IS A SEQUENCE OF SECTIONS, whatever its fields look like.
  //
  // Found by building folding: the demo's document view binds a title and a
  // body per section, two textual fields, and fell straight through the
  // threshold below into a table — so the body rendered as rows, no card was
  // emitted, and every `expand` affordance in it had nothing to attach to.
  // Measured in a browser, not reasoned about: `.m-foldable` and
  // `details.m-fold` were both zero. The table of contents made it worse by
  // listing eleven cards that were not sections.
  //
  // The first fix was broader — any collection carrying a prose role becomes
  // cards — and it was wrong. It removed the only table in the product, took
  // the rollup footer with it, and Notion, which is the bar `ACCEPTANCE.md`
  // names, does show a long-text property in a table: truncated to a cell,
  // never as a reason to abandon the table. So the rule is about what this
  // collection IS and not about what its fields happen to be.
  // MORE THAN ONE BRANCH is a declaration that the items are not uniform, and
  // a table needs uniform items — its columns come from one template, so a
  // heterogeneous collection drawn as a table would head every row with the
  // first branch's columns and leave the rest of the objects with blanks under
  // headings that do not describe them. Which is exactly the Notion behaviour
  // this mechanism exists to beat.
  if ((collection.itemTemplates?.length ?? 0) > 1) return 'cards';
  if (isDocumentBody(ir, collection)) return 'cards';
  // A WORKSPACE PANE LISTS — decision 0055. The explorer is a file tree and
  // the backlinks pane is a stack of mentions; a table with TITLE and BODY
  // headings inside either is Morph's generic table wearing the app's
  // chrome, which 0055 control 6 rejects by name. Measured before this line
  // existed: three backlinks with two bindings crossed the threshold below
  // and the pane grew column headers. Decided by the pane's declared purpose,
  // never inferred from the fields — the test itself sits above the
  // `groupBy` line, so the explorer's folders never become a board.
  // Two columns is still a table. The threshold was three, which made tables
  // unreachable at low density: the metadata budget shrinks with the appetite,
  // so an airy text collection never had enough fields to qualify and fell all
  // the way back to cards. A table is the right answer whenever the items are
  // uniform, textual, and there is more than one of them.
  if (fields.length >= 2 && !single) return 'table';
  return 'cards';
}

/**
 * The field NODES a table renders as columns, in template order.
 *
 * It used to return roles, and dropping the nodes was what made table cells
 * unanchorable: a cell knew which role it showed and not which binding it came
 * from, so there was no node index to check a grant against and no way to edit
 * in a table at all — the first line of task 0026's "done when".
 */
function columnsOf(ir: IR, collection: IRNode): IRNode[] {
  const tpl = collection.itemTemplates?.[0];
  if (!tpl) return [];
  return subtree(ir, tpl.root).filter((n) => n.kind === 'field' && n.role);
}

/** Does anything in this subtree carry a write grant? */
function hasGrantIn(
  ir: IR, root: number, grants: GrantSet | undefined, obj?: BoundValues,
): boolean {
  const object = handleOf(obj);
  return !!grants && subtree(ir, root).some((n) => grants.forNode(n.i, object));
}

/** The canonical display text for a value.
 *
 *  Exported because the edit host writes it too — on revert and on commit — and
 *  a second formatter would make a reverted cell differ from the same cell
 *  after a repaint, which reads as the edit half-landing. */
/** Whether the field being rendered sits inside a card's own hit target
 *  (a `<button>`): set by the card branch around its children, read by the
 *  fields that would otherwise draw a control of their own. */
let insideHit = false;

export const formatValue = (v: unknown): string => {
  if (v === undefined || v === null) return '';
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(1);
  return String(v);
};

const fmt = formatValue;


/**
 * What a fold produced, as the renderer needs it.
 *
 * Declared here rather than imported from the store, because the runtime does
 * not depend on the store and must not start: a host that keeps its objects
 * anywhere at all can supply these.
 *
 * `values` is keyed `"<role>:<fn>"`. `n` is the size of the set the fold saw,
 * which is what lets a group header say how many things are in the group rather
 * than how many are currently painted.
 */
export interface Rollups {
  readonly n: number;
  readonly values: ReadonlyMap<string, number>;
  /**
   * Grouped folds, INSEPARABLE from the role they group by.
   *
   * These two were independently optional, which made the store's
   * `AggregateResult` — `{ n, values, groups }` — structurally assignable to
   * `Rollups`. A host could hand `store.aggregate(scope, specs, { groupBy })`
   * straight through, get no type error, and silently lose every group count
   * and total, because the renderer ignores a `groups` map that does not say
   * what it groups by. A contract that can be satisfied wrongly without
   * complaint is not a contract. Found by @Codex.
   */
  readonly grouped?: {
    readonly by: string;
    readonly groups: ReadonlyMap<string, {
      readonly n: number;
      readonly values: ReadonlyMap<string, number>;
    }>;
  };

}

/** The key `Rollups.values` uses. One function, so a reader and a writer cannot
 *  disagree about the separator — the store exports the same one. */
export const foldKey = (role: string, fn: string): string => `${role}:${fn}`;

/**
 * How a fold reads.
 *
 * A total is not the same shape as the values it folded: `sum` of a duration is
 * a span of time, `count` is a plain integer, and `avg` of a rating wants a
 * decimal where the ratings themselves are shown whole. Formatting by the
 * FUNCTION as well as the role is what keeps "19h 12m total" from reading as
 * "1152".
 */
const formatFold = (role: string, fn: string, v: number): string => {
  if (fn === 'count') return String(v);
  if (role === 'duration') {
    // Minutes, which is how a runtime is stored. Hours and minutes is how a
    // person reads six thousand of them.
    const mins = Math.round(v);
    const h = Math.floor(mins / 60);
    return h > 0 ? `${h}h ${mins % 60}m` : `${mins}m`;
  }
  if (ROLE_KIND[role as Role] === 'date') {
    // A `span` is a DURATION; `min` and `max` are INSTANTS. Formatting both as
    // instants printed a one-year span as a date in 1970, which is not a wrong
    // format so much as a wrong answer. Found by @Codex.
    if (fn === 'span') {
      const days = Math.round(v / 86_400_000);
      if (days >= 730) return `${Math.round(days / 365.25)} years`;
      if (days >= 60) return `${Math.round(days / 30.44)} months`;
      return `${days} days`;
    }
    return new Date(v).toISOString().slice(0, 10);
  }
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
};

/**
 * Is this region the Connections stack?
 *
 * ONE definition, called by the region that draws the chrome and by the slot
 * that draws a chip only inside it. They were written as two predicates for one
 * question, and they disagreed on the deployed page — @Codex: *"the repair
 * checks only `parent.purpose === 'relations'`, but the renderer's actual
 * `relationStack` predicate also requires `children.length > 1` (and non-empty
 * rendered children). Those predicates can and do disagree."* Notes declares a
 * single relation, so it got no Connections chrome and a chip anyway.
 *
 * `kidsDrawn` is the render-time half: a reader whose every row is empty must
 * get no chrome at all, and that is a fact about this render rather than about
 * the spec. The `purpose` half is DECLARED — see `IRNode.purpose`.
 */
const connectionsOf = (nodes: IR['nodes'], i: number, kidsDrawn: boolean): boolean => {
  const r = i >= 0 ? nodes[i] : undefined;
  return kidsDrawn && r?.kind === 'region' && r.purpose === 'relations' && r.children.length > 1;
};

/** Per-render counter for ids that must be unique across REPEATED renders of
 *  one template node — see `headId` in the region case. Reset per call, so two
 *  documents rendered separately do not drift apart. */
let connSeq = 0;

export function renderHTML(
  { ir, objects, sources, regionSets, regionTotals, totals, rollups, capabilities,
    scheme = 'light', edit, labels, choices, tones, workspace }: RenderInput,
): string {
  // A NAMED APP LOOK brings its own surfaces — decision 0055: Obsidian's
  // near-black layered greys and violet accent are part of the template's
  // declared visual contract, not a palette a person picked, so the derived
  // ramp yields to them. The scheme is still honoured where the template
  // declares it variable; today `app.obsidian` declares dark only.
  /**
   * A KEPT REPRODUCTION reopens as what it was, or says why it cannot —
   * 0055 control 3, and @Codex's P2 on ebf7b8c: the version the spec carries
   * was displayed in an attribute and decided nothing, so an old accepted
   * `app.obsidian` would have been silently repainted by whatever this
   * runtime draws today. The table is the vocabulary's, shared with the
   * generator; a version this runtime does not draw is a refusal in words,
   * where the lens would have been.
   */
  if (ir.template && APP_TEMPLATE_VERSIONS[ir.template.id] !== ir.template.version) {
    return `<div class="m-root m-refused" role="note" data-template="${esc(ir.template.id)}" `
      + `data-template-version="${esc(ir.template.version)}">This layout was kept as `
      + `${esc(ir.template.id)} ${esc(ir.template.version)}, and this runtime draws `
      + `${esc(APP_TEMPLATE_VERSIONS[ir.template.id])}. It has not been migrated, so it is `
      + `not repainted.</div>`;
  }
  const s = ir.template ? TEMPLATE_SURFACES[ir.template.id] : surfaceOf(ir.tokens.palette, scheme);
  /** What to call a role on screen: the host's name for it, or the one derived
   *  from the vocabulary term when the host did not supply one. Every caption
   *  in this file goes through here, so a host that names one role does not get
   *  a page where that role is named and its neighbours are not. */
  const nameOf = (role: Role): string => labels?.[role] ?? labelForRole(role);
  // One screen, one budget. A capability's ceiling is measured for one instance
  // of it, and a collection template can materialise sixty — so the budget has
  // to be per render pass or the measured number describes something nobody
  // ever sees. Found by @Codex.
  resetCapabilityBudget();
  /** The roles this layout actually DRAWS as media — every `kind: 'media'`
   *  node in the IR, which is the only kind `media()` is reached through.
   *
   *  Derived from the producer rather than from the pool or the spec's
   *  bindings: a role bound for a caption or a query is not a picture on
   *  screen, and a derivative may only stand down for a source somebody can
   *  see. Computed once per render pass — the walk is over `ir.nodes`, which
   *  does not change while one page is drawn. */
  /** The same question asked of ONE ITEM TEMPLATE — @Codex's P2 on `d881f5b`,
   *  and the render-global set above is what they found wrong.
   *
   *  A polymorphic collection carries a branch per kind of thing and draws ONE
   *  of them per object (`templateFor`). A branch binding `media.thumb` beside
   *  another branch binding `media.hero` therefore made the global set say the
   *  hero is drawn — so the selected thumbnail stood down for a picture the
   *  unselected branch would have drawn, and the object rendered with NO
   *  image. Reproduced here before it was fixed: both media nodes in the IR,
   *  neither URL in the output.
   *
   *  It is the same defect the comment at the suppression already describes
   *  one scope out. That comment says the set must be "the roles this IR
   *  actually hands to `media()`"; for a guarded collection the IR hands over
   *  a different set per object, and a render-global answer cannot say which.
   *
   *  STATIC, not per object, and that is what makes it cheap: a media node
   *  lives in exactly one branch whichever object is drawn, so the scope is a
   *  property of the NODE. Only the innermost enclosing template counts — a
   *  collection nested inside a card must not inherit its host's siblings —
   *  so a smaller subtree always replaces a larger one. */
  /*  AND A NESTED TEMPLATE IS NOT PART OF ITS HOST'S SET — @Codex's P2 on
   *  `2ef9cfb`. The first version read a template's roles straight off
   *  `subtree(ir, t.root)`, and a subtree contains any nested item template's
   *  nodes: a card binding `media.thumb` around a collection whose own cards
   *  bind `media.hero` got a set of BOTH, and the host's thumbnail stood down
   *  for a picture only the inner OBJECT draws.
   *
   *  The per-node half was already right — `scopeSize` gives a node inside the
   *  nested template the smaller scope. What was wrong is that the SET is a
   *  property of the TEMPLATE and was computed from nodes the template does
   *  not own. So the innermost-template map is built first and the sets are
   *  built from it, which makes the two halves answer from one fact instead of
   *  from two walks that can disagree.
   *
   *  WHAT THIS IS AND IS NOT: `validateSpec` REFUSES a collection inside an
   *  item template — `collection-in-item-template`, and deliberately on the
   *  SUBTREE rather than the slot, so no depth escapes it — and `compile.ts`
   *  mints `itemTemplates` only for a collection. A nested item template
   *  therefore cannot occur in a spec the product accepts, and the clauses for
   *  this build the IR directly and assert that refusal as their own term.
   *  Fixed regardless: this function is a library that renders whatever IR it
   *  is handed, and a scope computed from nodes it does not own is wrong on
   *  its own terms rather than by permission of a guard in another package. */
  const innermost = new Map<number, number>();
  const scopeSize = new Map<number, number>();
  for (const n of ir.nodes) {
    for (const t of n.itemTemplates ?? []) {
      const kids = subtree(ir, t.root);
      for (const k of kids) {
        const held = scopeSize.get(k.i);
        if (held !== undefined && held <= kids.length) continue;
        scopeSize.set(k.i, kids.length);
        innermost.set(k.i, t.root);
      }
    }
  }
  const rolesOwnedBy = new Map<number, Set<Role>>();
  for (const k of ir.nodes) {
    const owner = innermost.get(k.i);
    if (owner === undefined || k.kind !== 'media' || k.role === undefined) continue;
    const held = rolesOwnedBy.get(owner) ?? new Set<Role>();
    held.add(k.role);
    rolesOwnedBy.set(owner, held);
  }
  const templateScope = new Map<number, ReadonlySet<Role>>();
  for (const [i, owner] of innermost) {
    templateScope.set(i, rolesOwnedBy.get(owner) ?? new Set<Role>());
  }
  /** A node outside every item template — a single-object page, a region — has
   *  the whole render as its scope, which is what this always did. */
  /** And the SUBJECT's own scope, for every node outside an item template —
   *  @Codex's P2 on `5fe2d35`, which is the fallback the fix above left.
   *
   *  That fallback was the render-global set, and a single-object page is
   *  exactly where it is wrong: a subject-bound `media.thumb` beside a
   *  `source: 'siblings'` collection whose cards draw `media.hero` saw the
   *  hero "drawn" and stood down — for a node that renders a DIFFERENT
   *  OBJECT. Reproduced before the fix, one spec `validateSpec` accepts:
   *
   *      subject thumb drawn: false
   *      subject hero  drawn: false
   *      sibling hero  drawn: true
   *
   *  The subject showed no image at all, which is the same loss the item
   *  template fix ended, reached through the other door.
   *
   *  A node outside every item template renders the SUBJECT, so its
   *  neighbours are the other such nodes and nothing inside a collection's
   *  cards. Still static: which template a node lives in does not change with
   *  the object. `drawsMedia` — the render-global set both fixes replaced — is
   *  gone rather than left as an unused fallback. */
  const subjectScope = new Set<Role>(ir.nodes.flatMap((n) =>
    (n.kind === 'media' && n.role !== undefined && !templateScope.has(n.i) ? [n.role] : [])));
  const mediaDrawnBeside = (n: IRNode): ReadonlySet<Role> => templateScope.get(n.i) ?? subjectScope;

  /** The object-level half of an anchor: which object, and which render pass.
   *  Emitted once per card or row rather than per cell — a cell reaches it by
   *  walking up, and repeating a handle on every cell is markup for nothing.
   *
   *  The HANDLE is emitted unconditionally; only the EPOCH waits on a write
   *  grant. This used to return '' outright without `edit`, which meant a
   *  reader got no object identity anywhere and so could follow nothing: a
   *  search hit, a relation chip, a card hit and a page-tree row all resolve
   *  through `data-morph-o`, and every one of them silently did nothing.
   *  Task 0179 measured it on the deployed preview; decision 0062 rules that
   *  an object handle is not permission and the epoch is the half that is. */
  const objectAttrs = (obj: BoundValues | undefined, node: number): string => {
    const handle = handleOf(obj);
    const base = anchorAttrs(handle, node, edit?.epoch);
    /* A REAL OBJECT WITH NO HANDLE IS A BOUNDARY, not a gap — @Codex's P2 on
     * 16c7bcc. `BoundValues` makes the handle optional, so a mixed feed can
     * put a card here that stands for an object nobody can name. Emitting the
     * node index and no object let the walk sail past to the enclosing
     * single-object surface, and the card resolved to the OUTER object:
     * clicking a nameless item opened the page it sat on. The table path had
     * been guarded for this since `ed4f7a7`; the card path had not.
     *
     * An empty attribute stops the walk (see `walk()` in edit.ts). It is
     * emitted only when there IS an object and it has no handle — an absent
     * `obj` still means "ask my ancestors", which is what a single-object lens
     * relies on, since its root is a region and its fields carry only the node
     * half. */
    return obj !== undefined && handle === undefined
      ? ` ${ANCHOR_ATTR.object}=""${base}` : base;
  };

  /** The node-level half: which binding this element came from. Role, direction
   *  and mutation are recovered from the IR, never from the DOM. */
  const nodeAttr = (i: number): string => ` ${ANCHOR_ATTR.node}="${i}"`;
  /**
   * A HUE for a value — one of twelve, from a hash of its text, so the same
   * value wears the same colour on every card and in every cell and two
   * values rarely share one. Presentation of the value, never data (0055):
   * the var is written on enum and reference values in every look, and a
   * look decides whether to read it (Fibery's does — the thirty-second
   * verdict's next item, the chips' per-value colour). An EMOJI per value,
   * which the reference's chips also carry, is data a person chose in the
   * real app and the shape does not hold; none is drawn.
   */
  const hueOf = (text: string): number => {
    let h = 0x811c9dc5;
    for (let i = 0; i < text.length; i += 1) { h ^= text.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    return (h % 12) * 30;
  };
  const hueStyle = (text: string): string => ` style="--m-hue:${hueOf(text)}"`;
  /**
   * WHICH CATEGORICAL SLOT an enum value occupies — decision 0063, and the
   * same `data-m-tone` vocabulary the workspace rail already uses.
   *
   * This replaces nothing yet: `--m-hue` rides alongside it, because two
   * looks still key their enum pills on the hue and moving them is a separate
   * step. What the tone adds is the half a hash cannot give — a DECLARED
   * category. Measured on the Tasks pool before this existed: all four
   * statuses painted one background, and even had the look read the hue,
   * `Open` and `In progress` both land on slot 270 of twelve.
   */
  const toneAttr = (role: Role, text: string): string =>
    ` data-m-tone="${toneOf(text, tones?.[role]?.[text])}"`;
  /** The first character of a name, for an avatar drawn from it. */
  const initialOf = (text: string): string => esc([...text.trim()][0]?.toUpperCase() ?? '');

  /** This render's nodes, bound to the one Connections classification. */
  const isConnections = (i: number, kidsDrawn: boolean): boolean =>
    connectionsOf(ir.nodes, i, kidsDrawn);

  /**
   * The `document-flow` capability a node is a section OF, if any.
   *
   * Stops at the FIRST capability above it rather than searching for a document
   * anywhere up the chain: a card inside a calendar cell inside a document is a
   * day's entry, not a section of the document, and giving it a footnote marker
   * would put an apparatus reference on something the apparatus does not
   * describe.
   */
  const documentAbove = (i: number): number | undefined => {
    for (let at = ir.nodes[i]?.parent ?? -1; at >= 0; at = ir.nodes[at]?.parent ?? -1) {
      const up = ir.nodes[at];
      if (up?.kind === 'capability') {
        return up.capability === 'document-flow' ? at : undefined;
      }
    }
    return undefined;
  };

  /**
   * A section's footnote marker — the superscript that sends a reader to the
   * note, and the half of the apparatus the document module cannot draw.
   *
   * The module places `body`, `header`, `footer` and `footnotes` and renders
   * none of them; by the time it runs, the sections are HTML. A marker has to
   * be INSIDE its section, so it is emitted here, where the section is. What
   * this decides is nothing: whether a section has a note, what the note says
   * and where it points are all the host's, arriving as `DocumentFlowInput`.
   * This turns one of them into a link.
   *
   * The number is the note's index in `drawnNotes`, and the module lays the
   * same list out in the same order — which is why both sides call that one
   * function rather than each filtering the host's array their own way.
   *
   * Silent when the spec did not ask for footnotes. A marker pointing at a
   * `#hash` that was never rendered is a link to the top of the page, and a
   * reader who follows one has been told a note exists that does not.
   */
  const noteMarker = (
    n: IRNode, ordinal: number | undefined, obj: BoundValues | undefined,
  ): string => {
    if (ordinal === undefined) return '';
    const cap = documentAbove(n.i);
    if (cap === undefined) return '';
    if ((ir.nodes[cap]?.config ?? {})['footnotes'] !== true) return '';
    const input = capabilityInputFor(capabilities, cap, handleOf(obj));
    if (input?.capability !== 'document-flow') return '';
    const notes = drawnNotes(input.document);
    const at = notes.findIndex((x) => x.anchor === sectionAnchor(n.i, ordinal));
    const note = notes[at];
    if (!note) return '';
    // `doc-noteref` and `doc-endnote` are the DPUB-ARIA pair for exactly this:
    // a reference in the text and the entry it points at, inside a
    // `doc-endnotes` section. A superscript link with a good label is already
    // operable; these are what let assistive technology say "note reference"
    // rather than "link, 1", and move between the two ends as an apparatus.
    return `<sup class="m-doc-marker"><a href="#${esc(note.note)}" role="doc-noteref"`
         + ` aria-label="Note ${at + 1}, ${esc(note.text)}">${at + 1}</a></sup>`;
  };

  /**
   * An element's attributes: always its node index, plus the editable set when
   * the binding granted a write.
   *
   * The node index used to be emitted **only** for editable elements, so a
   * read-only element had no join key back to the IR at all — and the shipped
   * product is mostly read-only elements. The visible cost was that clicking a
   * reference did nothing: resolution needs object and node, both sat on the
   * root, and the node was simply missing, so every click on a non-editable
   * element resolved to nothing. (Following a reference is navigation, so it
   * resolves through `readNavigationAnchor` and needs no epoch — decision
   * 0062. The epoch is on the root only under a write grant.)
   *
   * The index is safe to emit unconditionally. It is a position in the IR, not
   * an object id — ids are deliberately kept out of markup, which is why
   * following a reference asks the store to traverse rather than reading a
   * target id off the DOM.
   *
   * `aria-label` is not optional on the editable set, and the floor now depends
   * on it: compile.ts marks an editable binding `edit-inline`, so `checkFloor`
   * counts it as interactive, and `hasAccessibleName` treats it as naming itself
   * from its role — which is only true because this is where that name is
   * emitted. Drop it and every editable cell is announced as "edit text, blank"
   * with nothing to catch it.
   */
  const editAttrs = (n: IRNode, obj?: BoundValues): string => {
    const base = nodeAttr(n.i);
    if (!edit?.grants.forNode(n.i, handleOf(obj))) return base;
    const label = n.role ? nameOf(n.role) : 'value';
    return `${base} contenteditable="true" role="textbox" aria-multiline="false"`
         + ` tabindex="0" aria-label="${esc(label)}"`
         + ` data-morph-empty="${esc(emptyPrompt(n.role))}"`;
  };

  /**
   * What an empty editable cell SHOWS, which is not always what it is called.
   *
   * For every role but one they are the same string: an empty `author` cell
   * reads `Author`, which tells you what to type there. A `title` is different
   * because a title cell in a collection is not read as a field, it is read as
   * the row's NAME — and a list of twelve films with a thirteenth row reading
   * `Title` looks like a form that leaked into the table.
   *
   * Decision 0024, which stopped `Untitled` being written into the slot on
   * creation: *"Until the person names it, `Untitled` is presentation fallback
   * copy for an empty title slot, not authored data."* This is where that copy
   * lives, so it stays out of the store — CSS draws it from the attribute and
   * the element is genuinely empty, which is also why typing into it replaces
   * it rather than appending to it.
   *
   * `aria-label` deliberately does NOT change. A screen reader announcing this
   * cell wants the field's name, and "edit text, Title" is right where "edit
   * text, Untitled" would name the value it is about to be given.
   */
  const emptyPrompt = (role: Role | undefined): string =>
    role === 'title' ? 'Untitled' : role ? nameOf(role) : 'value';

  /** Whether this node actually granted a write, for the places that used to
   *  read `editAttrs() === ''` as the answer to that question. */
  const isEditable = (n: IRNode, obj?: BoundValues): boolean =>
    !!edit?.grants.forNode(n.i, handleOf(obj));

  /**
   * A media role resolves to either a bare URL or a media descriptor.
   *
   * The store holds media as `{ blob, mime }` rather than a string, because it
   * has to answer "is this populated" and "what kind is this" without opening
   * the value. Projection hands that descriptor straight through, so a renderer
   * that only understood strings drew grey boxes for every stored object while
   * every test passed — the same failure this file already carries a comment
   * about, one layer further out.
   *
   * Still refuses anything that is not an http(s) URL. A media value is data,
   * and data is exactly where an untrusted URL would arrive from.
   */
  const mediaURL = (v: unknown): string => {
    const raw = typeof v === 'string'
      ? v
      : v && typeof v === 'object' && typeof (v as { blob?: unknown }).blob === 'string'
        ? (v as { blob: string }).blob
        : '';
    // A URL the HOST minted, and not one that arrived in data. Decision 0017
    // has the host read a person's own file out of its own store and mint an
    // object URL for it — and then that URL hit this guard, because `blob:` is
    // not `http(s)`. The decision anticipated the temptation and rejected it:
    // widening the test to accept `blob:` would be trusting a STRING in the
    // data, and a `blob:` URL looks same-origin precisely when it is forged.
    //
    // A SYMBOL cannot be forged by data. Nothing that arrives through
    // `projection` — JSON, IndexedDB, a network response — can carry one, so
    // `HOST_RESOLVED` is a mark only code holding the symbol can make. The
    // guard below is untouched for every other input, which is why
    // `media.test.ts` still refuses all six schemes.
    if (isHostResolved(v)) return raw;
    return /^https?:\/\//.test(raw) ? raw : '';
  };

  /**
   * Media, branching on the CONTRACT rather than on the file.
   *
   * Decision 0014, extension 1: a video has a duration and a poster frame,
   * audio has a waveform and no aspect ratio at all, a still has an aspect and
   * nothing else. One renderer for all three drew a grey rectangle where a
   * trailer should be — which is what the demo did for every recording until
   * `av` existed.
   *
   * The medium comes from the ROLE, resolved at compile time. Nothing here
   * sniffs the value: the renderer never opens a blob, and deciding what
   * something is by looking inside it would put content inspection in the
   * presentation layer.
   *
   * A recording is NOT decorative, so unlike a still it carries a real label. A
   * `<video>` a screen reader announces as nothing is a control the user cannot
   * find, and `controls` without a name is exactly that.
   */
  /**
   * THE ROLE A MEDIA NODE DRAWS, ON THE ELEMENT — task `0225`.
   *
   * `mountManualHideControls` discovers the roles it offers by walking the
   * stage for `m-role-` classes, and this function emitted one on NONE of its
   * branches — so Hide was offered for every text field and for no attachment,
   * in any look, while the generator has honoured `taste.suppressedRoles` for
   * attachments all along (`isAttachment` in `app-template.ts`). A capability
   * the substrate had and nothing could ask for.
   *
   * AN ATTRIBUTE, NOT AN `m-role-` CLASS, and this file says why twice already
   * about table cells: those classes carry the generic role STYLING, so a
   * `<video>` wearing `m-role-title` would take a heading's size. `data-role`
   * is the presentation-neutral marker invented for exactly this.
   *
   * MEASURED BEFORE IT WAS ADDED: the whole stylesheet selects `data-role` in
   * two rules, both `.m-app-coda .m-as-table td[data-role="author"]`, and no
   * branch below emits a `td`. So nothing begins matching.
   *
   * ONE WRAPPER RATHER THAN NINE EDITS. The branches have nine markup returns
   * and a tenth would be added without this; putting the attribute on each is
   * the list `0064` says not to write. Every non-empty return begins with an
   * opening tag, which is a property of code in this function rather than of
   * anything outside it, so injecting into that tag reaches all of them —
   * including the empty-state notes, because "No Video" is the video role's
   * occurrence and a person must be able to hide it too.
   *
   * AT THE END OF THE OPENING TAG rather than straight after the tag NAME,
   * which is where the first version put it. Two reasons, and the second is
   * the one that caught it: every attribute in this file is written after the
   * class, so leading with `data-role` would make media the one exception; and
   * `app-fabric-render.test.ts` asserts the literal `<video class="m-media
   * m-av m-video"`, so the first version broke an unrelated clause about
   * Fabric's surfaces. A change that forces an unrelated assertion to be
   * rewritten is usually the change being wrong, not the assertion.
   *
   * `[^>]*` cannot run past the tag because every attribute VALUE here goes
   * through `esc`, so no raw `>` survives inside one.
   */
  const media = (n: IRNode, obj: BoundValues | undefined, cls = 'm-media'): string => {
    const drawn = mediaMarkup(n, obj, cls);
    if (drawn === '' || n.role === undefined) return drawn;
    return drawn.replace(/^(<[a-z]+[^>]*)>/, `$1 data-role="${esc(n.role)}">`);
  };
  const mediaMarkup = (n: IRNode, obj: BoundValues | undefined, cls = 'm-media'): string => {
    const url = mediaURL(obj?.[n.role ?? '']);
    /**
     * A DERIVATIVE STANDS DOWN FOR ITS SOURCE, per OBJECT — @Codex's P2 on
     * `4a34aef`.
     *
     * `2e06944` drew this line in the GENERATOR, from the aggregate shape:
     * if `media.hero` appeared anywhere in the pool, `media.thumb` was not an
     * attachment. Over a heterogeneous pool the two roles can sit on
     * DISJOINT records — the repository's own fixture demonstrates that role
     * presence and equal coverage prove nothing about overlap — so opening a
     * thumb-only record emitted the hero node, which has no value there, and
     * the record's only image disappeared.
     *
     * Whether a derivative is redundant is a fact about ONE OBJECT AND ONE
     * LAYOUT, and this is the only place that knows both. The source's own
     * value decides: a record carrying both draws the picture (which is what
     * `2e06944` was about, and what `baseline.ts` means by "a SINGLE object
     * keeps the source"), and a record carrying only the derivative draws it.
     *
     * AND ONLY WHERE THE LAYOUT DRAWS THE SOURCE — @Codex's P2 on `98376fb`,
     * and the sentence above said ONE OBJECT while the code read one object
     * and assumed the other half. A spec binding only `media.thumb` over a
     * pool whose records also carry `media.hero` has no hero node to draw:
     * standing the thumbnail down for a source the layout never renders
     * leaves the object with NO image, which is the same loss `2e06944`
     * caused from the shape and this fix was written to end. `drawsMedia` is
     * the set of roles this IR actually hands to `media()`, so the
     * suppression asks about the picture a person is looking at rather than
     * about a value in the record.
     *
     * ONLY WITHIN ONE KIND, which the first version of this got wrong and a
     * clause caught. A thumbnail is another rendering of the same still, so
     * drawing both is drawing one thing twice. A POSTER FRAME is not another
     * rendering of a recording — the player already shows it — so a poster
     * node declared beside a video is a deliberate still and stands. The
     * `DERIVATIVE_OF` table spans both cases; `ROLE_KIND` tells them apart:
     * hero and thumb are `media`, a video is `av`.
     */
    const source = n.role ? DERIVATIVE_OF[n.role] : undefined;
    if (source !== undefined && n.role !== undefined
      && ROLE_KIND[source] === ROLE_KIND[n.role]
      && mediaDrawnBeside(n).has(source) && mediaURL(obj?.[source])) return '';
    // The ROLE, and the object it belongs to.
    //
    // Four players on one page all announced as "Film" tells a screen-reader
    // user nothing about which one they are about to start. The object's own
    // name is right there in its title binding. Found by @Codex.
    const roleLabel = n.role ? nameOf(n.role) : 'media';
    const named = obj?.['title'] ?? obj?.['subtitle'];
    const label = named ? `${roleLabel} — ${String(named)}` : roleLabel;

    // An OPTIONAL slot draws nothing when empty. In a single-object lens
    // "No Video" answers a question the reader asked by opening that object; in
    // a shared card over a ragged pool it is every object advertising what it
    // does not have.
    if (n.optional && !url) return '';

    if (n.medium === 'video') {
      if (!url) return `<div class="${cls} m-av-empty" role="note">No ${esc(roleLabel)}</div>`;
      const poster = mediaURL(obj?.['media.poster-frame']);
      // A spec that declares `aspect: '9/16'` on a video meant it. The first
      // version accepted the aspect, compiled it into the IR and then let the
      // stylesheet force 16/9 over the top — a portrait video rendered
      // letterboxed and nothing said so. Accepting a value and discarding it is
      // worse than refusing it. Found by @Codex.
      const ratio = n.aspect ? ` style="aspect-ratio:${esc(n.aspect)}"` : '';
      // `preload="none"`, matching audio.
      //
      // @Codex raised this as a P1 and the argument holds: `preload="metadata"`
      // fetches from a data-supplied third-party URL BEFORE the user acts,
      // which tells that URL's operator that this person opened this object —
      // IP, timing, and browser characteristics, for a lens they merely looked
      // at. A painted `background-image` already does that for stills, so the
      // egress is not new; what IS new is that it hands attacker-controlled
      // bytes to the browser's media container and codec parsers rather than
      // its image decoder, which is a different native attack surface, reached
      // without a click.
      //
      // Nothing visible is lost: the poster frame is a separate still and still
      // paints. The cost is that duration is unknown until play, which is a
      // fair price for not announcing what someone is reading.
      // The node index, so the playback host can join this element back to the
      // IR and recover its role. Emitted on the element itself rather than
      // inherited, because a card in a gallery paints several recordings and
      // the nearest annotated ancestor would name whichever one came last.
      return `<video class="${cls} m-av m-video"${nodeAttr(n.i)} controls preload="none"${ratio}`
           + `${poster ? ` poster="${esc(poster)}"` : ''}`
           + ` aria-label="${esc(label)}" src="${esc(url)}"></video>`;
    }

    /**
     * A paged document — a PDF, a scan, a contract.
     *
     * `<object>` rather than `<iframe>` or a bundled viewer, because the
     * browser already has one and the standing rule is to buy the design layer
     * rather than rebuild it. `<object>` is also the only one of the three that
     * has FALLBACK CONTENT built into the element: a browser with no PDF
     * viewer renders the children instead, and that is not a hypothetical — the
     * Chromium this repo's harnesses drive reports `navigator.pdfViewerEnabled`
     * as `false`, has no plugins, and treats a PDF navigation as a download.
     * So the fallback is the path our own gate exercises, and a viewer-less
     * browser is a configuration a real person has.
     *
     * **Only a document the HOST resolved is embedded.** This is stricter than
     * the `av` path and deliberately so. A video answers the privacy half of
     * decision 0017 with `preload="none"` — the element exists, the bytes are
     * not fetched until the person presses play. `<object>` has no such hint:
     * it loads on paint. So rather than accept a weaker guarantee, a document
     * that is not host-resolved is not embedded at all — it is offered as a
     * link, which fetches nothing, tells no third party who is reading, and
     * hands no attacker-controlled bytes to a PDF parser before a click. A
     * person's own file out of their own store embeds; a URL that arrived in
     * data does not.
     */
    if (n.medium === 'document') {
      if (!url) return `<div class="${cls} m-doc-empty" role="note">No ${esc(roleLabel)}</div>`;
      const name = named ? String(named) : roleLabel;
      // The link is the fallback AND the whole rendering for a URL that came
      // from data. One element, so the two paths cannot drift.
      const open = `<a class="m-doc-open" href="${esc(url)}" target="_blank" rel="noopener noreferrer">`
                 + `Open ${esc(name)}</a>`;
      if (!isHostResolved(obj?.[n.role ?? ''])) {
        return `<div class="${cls} m-doc m-doc-linked"><span class="m-doc-what">`
             + `${esc(roleLabel)}</span>${open}</div>`;
      }
      return `<object class="${cls} m-doc" type="application/pdf" data="${esc(url)}"`
           + ` aria-label="${esc(label)}">`
           + `<div class="m-doc-fallback"><span class="m-doc-what">`
           + `${esc(roleLabel)} — this browser has no document viewer</span>${open}</div>`
           + `</object>`;
    }

    if (n.medium === 'audio') {
      if (!url) return `<div class="${cls} m-av-empty" role="note">No ${esc(roleLabel)}</div>`;
      // No aspect ratio, and none is applied: audio has no picture, so a box
      // shaped like one is a lie about what is in it.
      return `<figure class="m-audio"><figcaption>${esc(roleLabel)}</figcaption>`
           + `<audio${nodeAttr(n.i)} controls preload="none" aria-label="${esc(label)}"`
           + ` src="${esc(url)}"></audio></figure>`;
    }

    // A STILL THAT ISN'T THERE, said rather than drawn as nothing.
    //
    // Measured: `<div class="m-media m-fit-cover" style="aspect-ratio:2/3"
    // role="img" aria-hidden="true"></div>` — **0×0 pixels**, no text, hidden
    // from assistive tech, and claiming `role="img"` while having no image.
    // The same defect `field()` carries a comment about — *"an empty editable
    // div is zero-height and invisible, so the cell that exists precisely so
    // somebody can fill it was the one thing they could not see or aim at"* —
    // at its third leaf.
    //
    // The rule it now follows is the one already written above for `av`, and
    // `document` implements it too: an OPTIONAL slot draws nothing when empty,
    // because on a shared card over a ragged pool it is every object
    // advertising what it does not have. A single-object lens is the opposite
    // — "No Poster" answers a question the reader asked by opening that
    // object. `n.optional` is checked at the top of this function, so a
    // collection card is untouched and only the lens that asked gets an
    // answer.
    if (!url) return `<div class="${cls} m-still-empty" role="note">No ${esc(roleLabel)}</div>`;

    const style = [
      n.aspect ? `aspect-ratio:${esc(n.aspect)}` : '',
      `background-image:url('${esc(url)}')`,
    ].filter(Boolean).join(';');
    // Decorative by construction: the card names itself from its label
    // binding, so an empty alt is correct rather than lazy. Announcing the
    // same name twice is worse than announcing it once.
    return `<div class="${cls} m-fit-${n.fit}"${style ? ` style="${style}"` : ''} `
         + `role="img" aria-hidden="true"></div>`;
  };

  /**
   * A referenced object, rendered as a preview.
   *
   * This is the Notion page popup and the Obsidian hover preview, and it is one
   * mechanism rather than two features: a relation target is just another
   * object, so it gets rendered the way an object gets rendered.
   *
   * **It is a preview lens, not the target's own lens.** Rendering the target
   * properly means compiling a second spec mid-render, which turns layout into
   * a graph walk and lands directly on the fixed-point question in task 0016.
   * That is the right end state and it is not this function. What this does is
   * pick the target's highest-bandwidth roles — media, then name, then up to
   * three others — which is the same ranking the generator uses and is honest
   * about being an approximation of it.
   *
   * No JavaScript. The reveal is `:hover` and `:focus-within`, so it works
   * under keyboard and needs no hydration.
   *
   * **Two stop conditions, not one.** @Codex's review of decision 0014 caught
   * that this function shipped with only the depth bound: *"maxDepth alone does
   * not prevent a cycle from repeating work up to the limit, so the runtime
   * must track the active object/lens path and emit the bounded reference
   * fallback when a key reappears."* Exactly right, and the visible symptom is
   * worse than the wasted work — with A → B → A and a depth of four, a reader
   * is shown an object nested inside itself, which is nonsense that happens to
   * terminate. `path` is the objects on the current branch, so a repeat is
   * caught immediately while two siblings referencing the same object are not
   * confused for a cycle.
   */
  const preview = (
    target: RoleValues, depth: number, maxDepth: number,
    path: ReadonlySet<RoleValues> = new Set(),
  ): string => {
    if (path.has(target)) {
      return '<div class="m-ref-deeper" role="note">already shown further up this chain</div>';
    }
    if (depth > maxDepth) {
      // The bound, reached. Say so rather than rendering nothing: a preview
      // that silently stops is indistinguishable from an empty relation.
      return '<div class="m-ref-deeper" role="note">deeper relations not followed</div>';
    }
    const branch = new Set(path).add(target);
    const roles = Object.keys(target) as Role[];
    // A still for the thumbnail. `av` is deliberately NOT eligible: a preview
    // thumbnail is a picture, and a recording's locator is not one.
    const mediaRole = roles.find((r) => ROLE_KIND[r] === 'media');
    // A transcript is text, and a preview looking for "some text to name this
    // by" happily picked it — putting ten thousand characters of prose into
    // `.m-ref-name` and expanding the popup around the whole thing. It is long
    // prose that BELONGS to an object, never the object's identity. Found by
    // @Codex.
    const nameRole = roles.find((r) => r === 'title' || r === 'subtitle')
                  ?? roles.find((r) => ROLE_KIND[r] === 'text' && r !== 'transcript');
    const rest = roles
      .filter((r) => r !== mediaRole && r !== nameRole && ROLE_KIND[r] !== 'ref'
        // A recording's value is a blob descriptor. Rendered as a metadata row
        // it reads as a raw URL, or as `[object Object]` for a stored
        // descriptor — storage data presented as prose. A preview says the
        // recording is THERE and leaves playing it to the object's own lens.
        // Same for a document, and for the same reason: `[object Object]` in a
        // metadata row is storage data presented as prose.
        && ROLE_KIND[r] !== 'av' && ROLE_KIND[r] !== 'doc' && r !== 'transcript')
      .slice(0, 3);

    // Named, not shown: "this object has a trailer" is the useful fact at
    // preview size, and it costs one row rather than a player. A document is
    // the same fact — "there is a contract on this" — and embedding a PDF
    // viewer inside a hover preview would be worse than for a player.
    const attached = roles.filter(
      (r) => (ROLE_KIND[r] === 'av' || ROLE_KIND[r] === 'doc') && mediaURL(target[r]));

    const url = mediaRole ? mediaURL(target[mediaRole]) : '';
    const thumb = mediaRole
      ? `<div class="m-ref-media"${url ? ` style="background-image:url('${esc(url)}')"` : ''}` +
        ` role="img" aria-hidden="true"></div>`
      : '';
    const body = [
      nameRole ? `<div class="m-ref-name">${esc(fmt(target[nameRole]))}</div>` : '',
      ...rest.map((r) =>
        `<div class="m-ref-row"><span class="m-ref-key">${esc(nameOf(r))}</span>` +
        `<span class="m-ref-val">${esc(fmt(target[r]))}</span></div>`),
      ...attached.map((r) =>
        `<div class="m-ref-row"><span class="m-ref-key">${esc(nameOf(r))}</span>` +
        `<span class="m-ref-val">available</span></div>`),
      // One level of nesting inside a preview, so a relation-of-a-relation is
      // visible as existing without being followed.
      ...roles.filter((r) => ROLE_KIND[r] === 'ref')
        .flatMap((r) => asObjects(target[r]).slice(0, 1)
          .map((t) => preview(t, depth + 1, maxDepth, branch))),
    ].join('');
    return `<div class="m-ref-card">${thumb}<div class="m-ref-body">${body}</div></div>`;
  };

  /** A ref role's value is the referenced object, or several of them. */
  const asObjects = (v: unknown): RoleValues[] =>
    Array.isArray(v) ? (v as RoleValues[])
      : v && typeof v === 'object' ? [v as RoleValues] : [];

  /**
   * What a LOOKUP reads: follow this node's reference role, take `lookup` from
   * each object on the far side.
   *
   * `expandRefs` is what makes this possible without a second store call — the
   * projection the renderer is handed already carries ref roles as nested
   * RoleValues, which is what `node-slot` and `refField` render. So a lookup is
   * a read of data already in hand, not a traversal the renderer performs.
   *
   * ALL of them, joined, when the reference holds several. Taking the first
   * would silently drop values, and a column that shows one of three answers
   * with no sign that two are missing is the failure this repo keeps finding in
   * its own checks. Empty values are dropped rather than rendered as gaps in a
   * list, because "a, , c" reads as a value that is blank rather than one that
   * is absent.
   */
  const lookedUp = (n: IRNode, obj: BoundValues | undefined): string[] => {
    if (!n.lookup) return [];
    return asObjects(obj?.[n.role ?? ''])
      .map((t) => t[n.lookup!])
      .filter((v) => v !== undefined && v !== null && v !== '')
      .map((v) => fmt(v));
  };

  const refField = (n: IRNode, obj: BoundValues | undefined): string => {
    const targets = asObjects(obj?.[n.role ?? '']);
    if (!targets.length) return '';
    const max = n.maxDepth ?? 1;
    const chips = targets.map((t) => {
      const nameRole = (Object.keys(t) as Role[])
        .find((r) => r === 'title' || ROLE_KIND[r] === 'text');
      const name = nameRole ? fmt(t[nameRole]) : 'linked object';
      // The subject seeds the path. Without it a back-reference renders the
      // subject once inside its own preview before the cycle is caught — the
      // object appears inside itself, one level later than it should.
      const seed: ReadonlySet<RoleValues> = obj ? new Set([obj]) : new Set();
      // INSIDE A CARD'S HIT TARGET the chip is a span, not a button: a
      // button in a button is invalid markup, and the parser recovers by
      // closing the card's button early — the card split in two, its
      // fields after the relation outside the hit. The span keeps the
      // class the host routes by, so a click on the chip still opens what
      // it points at (Fibery's parent chip on a card) while the rest of
      // the card opens the card; the keyboard reaches the relation from
      // the opened object, as before.
      const hit = insideHit
        ? `<span class="m-ref-hit m-ref-in-hit"${hueStyle(name)} data-m-initial="${initialOf(name)}">${esc(name)}</span>`
        : `<button class="m-ref-hit" type="button"${hueStyle(name)} data-m-initial="${initialOf(name)}" aria-label="${esc(name)}, linked object">${esc(name)}</button>`;
      return `<span class="m-ref">${hit}`
           + `<span class="m-ref-pop" role="note">${preview(t, 1, max, seed)}</span></span>`;
    }).join('');
    // Carries the node anchor like every other field. Without it, clicking a
    // reference resolved to nothing — `readAnchor` walks up for object, node
    // and epoch, and this was the one leaf that never supplied a node.
    return `<div class="m-field m-kind-ref m-role-${esc(n.role)}"${nodeAttr(n.i)}>`
         + `${chips}</div>`;
  };

  /**
   * An enum, chosen rather than typed.
   *
   * The producer for a path that was already built end to end and had nothing
   * at the end of it: `EditGesture` has carried `{kind: 'choose', option}`
   * since decision 0007, `resolveEdit` normalises it, the store performs
   * `set-enum`, and the demo's bridge even refuses a typed value with *"this
   * field is chosen from, not typed into"*. Nothing emitted the thing you
   * choose with, so the generator marked every enum `read` and said why.
   *
   * A `<select>`, not a text box. Typing free text into an enum cell resolves
   * as `set-value`, which the binding does not declare, so every keystroke
   * would be refused — that was the original argument for read-only and it is
   * still correct. Inventing a NEW value is a different act and keeps its
   * existing route through the property form; this chooses among what the
   * collection holds.
   *
   * **The current value is always an option, even when the host did not offer
   * it.** Otherwise a `<select>` silently reports the first option as selected
   * and the next change writes a value nobody picked — an edit nobody made,
   * which is the worst failure available to an editable cell.
   *
   * The empty state is a placeholder option that is `selected` and `disabled`:
   * an absent enum must be visibly absent and must not be writable back as ''.
   *
   * **A slot that HOLDS a value gets a selectable empty option instead**, named
   * for its result rather than left blank. The two cases are different acts:
   * choosing '' on a cell that is already empty writes a no-op, which is what
   * the disabled placeholder prevents; choosing it on a cell that holds a value
   * CLEARS it, which is an edit the store has always supported through `clear`.
   *
   * Until task 0096 this file emitted only the first, so every enum in the
   * product was a one-way door — settable, never unsettable. The reason took
   * two readings to find: `edit-bridge.ts` refused an empty enum citing a rule
   * about making slots ABSENT, which clearing does not do, and `Instruction`'s
   * scalar payloads had no inhabitant meaning "no value" for it to send even if
   * it had wanted to. @Codex's ruling: *"empty is a property state, not a
   * schema mutation."*
   *
   * The affordance and the path behind it landed together, deliberately. A
   * renderer-only version of this was built first and reverted: it offered a
   * clear the bridge then refused, which is an affordance with nothing behind
   * it — the failure this repo holds to be worse than a missing one.
   */
  const chooser = (n: IRNode, value: unknown): string => {
    const role = n.role as Role;
    const current = value === undefined || value === null || value === ''
      ? undefined : String(value);
    const offered = choices?.[role] ?? [];
    const options = current !== undefined && !offered.includes(current)
      ? [current, ...offered] : offered;
    const opts = options.map((o) =>
      `<option value="${esc(o)}"${o === current ? ' selected' : ''}>${esc(o)}</option>`).join('');
    const placeholder = current === undefined
      ? `<option value="" selected disabled>${esc(nameOf(role))}</option>`
      : `<option value="" class="m-clear">${esc(`No ${nameOf(role).toLowerCase()}`)}</option>`;
    // `nodeAttr`, not `editAttrs`. A `<select>` is already focusable, already
    // has a role, and `contenteditable="true" role="textbox"` on one is both
    // nonsense and a lie to a screen reader. What it needs from the anchor
    // machinery is the node index, which is how the host resolves the edit.
    return `<select class="m-field m-editable m-choose m-kind-enum m-role-${esc(role)}"`
         + `${nodeAttr(n.i)}${current === undefined ? '' : hueStyle(current) + toneAttr(role, current)} aria-label="${esc(nameOf(role))}">${placeholder}${opts}</select>`;
  };

  const field = (n: IRNode, obj: BoundValues | undefined): string => {
    const stored = obj?.[n.role ?? ''];
    // A DERIVED column shows a function of the value, aligned by what the
    // function YIELDS rather than by the role it read — a Year computed from a
    // date is a number, and left-aligning it with the dates would put a column
    // of integers under a heading that sorts like text.
    if (n.derive) {
      const value = derived(n.derive, stored);
      const kind = DERIVATION_YIELDS[n.derive];
      if (value === undefined) {
        return `<div class="m-field m-derived m-kind-${kind} m-derive-${esc(n.derive)}"></div>`;
      }
      return `<div class="m-field m-derived m-kind-${kind} m-derive-${esc(n.derive)}">`
           + `${esc(String(value))}</div>`;
    }
    // BEFORE the ref routing below. A lookup is declared ON a reference role,
    // so without this it would fall into `refField` and draw the related
    // object's name chip — the reference itself, which is the one thing a
    // lookup column exists NOT to show.
    if (n.lookup) {
      const vals = lookedUp(n, obj);
      const lk = ROLE_KIND[n.lookup];
      const cls = `m-field m-lookup m-kind-${lk} m-lookup-${esc(n.lookup)}`;
      return vals.length === 0
        ? `<div class="${cls}"></div>`
        : `<div class="${cls}">${esc(vals.join(', '))}</div>`;
    }
    const value = stored;
    const kind = n.role ? ROLE_KIND[n.role] : 'text';
    if (kind === 'ref') return refField(n, obj);
    if (kind === 'enum' && isEditable(n, obj)) return chooser(n, value);
    const attrs = editAttrs(n, obj);
    // An absent value under a read-write binding must still render a cell.
    // Returning '' for empty meant the one cell a user needs to click to FILL a
    // property was the one cell that was not there — per-object property sets
    // make that the ordinary case, not a corner of it.
    if (value === undefined || value === null || value === '') {
      if (!isEditable(n, obj)) return '';
      // Carries its own prompt. An empty editable div is zero-height and
      // invisible, so the cell that exists precisely so somebody can fill it
      // was the one thing they could not see or aim at.
      return `<div class="m-field m-editable m-kind-${kind} m-role-${esc(n.role)}"${attrs}`
           + ` data-morph-empty="${esc(emptyPrompt(n.role))}"></div>`;
    }
    // A SEARCH HIT shows why it matched — the tenth verdict: *"the matching
    // term is not highlighted in the excerpt, so the eye must rediscover why
    // the result matched."* Inside a search pane, with a query in hand, the
    // text is windowed to its first match and every match is a <mark>. A
    // read-only field only: an editable one is a person's text, not a hit.
    const q = workspace?.searchQuery;
    if (q && !isEditable(n, obj) && inPane(n, 'search')) {
      return `<div class="m-field m-kind-${kind} m-role-${esc(n.role)}"${attrs}>`
           + `${marked(fmt(value), q)}</div>`;
    }
    return `<div class="m-field${isEditable(n, obj) ? ' m-editable' : ''} m-kind-${kind} `
         + `m-role-${esc(n.role)}"${attrs}${kind === 'enum' ? hueStyle(fmt(value)) + toneAttr(n.role as Role, fmt(value)) : ''}>${esc(fmt(value))}</div>`;
  };

  /**
   * Whether an editor's note is BLANK: no title, no prose, every property
   * field empty, every relation slot empty — the state a New note opens
   * in. Read from the fields and slots the editor actually holds, never
   * from a fixed role list, so a template that binds `transcript` as its
   * prose is judged by that. A single value anywhere makes it not blank.
   */
  const isBlankNote = (editor: IRNode, o: BoundValues | undefined): boolean => {
    if (!o) return false;
    const empty = (v: unknown): boolean =>
      v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
    const walk = (i: number): boolean => {
      const k = ir.nodes[i];
      if (!k) return true;
      // A field's role, or a node-slot's (the compiler files `via` as
      // `role`): any bound value present makes the note not blank.
      if ((k.kind === 'field' || k.kind === 'node-slot') && k.role !== undefined && !empty(o[k.role])) return false;
      return k.children.every(walk);
    };
    return walk(editor.i);
  };

  /** Whether a node sits under a region of the given purpose. */
  const inPane = (node: IRNode, purpose: RegionPurpose): boolean => {
    let p: number | undefined = node.parent;
    while (p !== undefined && p >= 0) {
      const k: IRNode | undefined = ir.nodes[p];
      if (!k) return false;
      if (k.kind === 'region' && k.purpose === purpose) return true;
      p = k.parent;
    }
    return false;
  };

  /** The text with every case-folded occurrence of `q` wrapped in a mark,
   *  windowed so the FIRST match is in view: a hit whose match sits past the
   *  excerpt's clamp is a hit that cannot say why it matched. Escaped piece
   *  by piece, so the mark is the only markup. */
  const marked = (text: string, q: string): string => {
    const lower = text.toLowerCase();
    const needle = q.toLowerCase();
    const first = lower.indexOf(needle);
    if (first < 0) return esc(text);
    const LEAD = 48;
    const from = first > LEAD ? lower.lastIndexOf(' ', first - LEAD) + 1 : 0;
    const shown = text.slice(from);
    const shownLower = lower.slice(from);
    const out: string[] = from > 0 ? ['… '] : [];
    let i = 0;
    for (;;) {
      const j = shownLower.indexOf(needle, i);
      if (j < 0) break;
      out.push(esc(shown.slice(i, j)), `<mark class="m-ws-hl">${esc(shown.slice(j, j + q.length))}</mark>`);
      i = j + q.length;
    }
    out.push(esc(shown.slice(i)));
    return out.join('');
  };

  /**
   * A placed child, wrapped.
   *
   * The wrapper exists so that placement costs nothing in the node kinds
   * themselves — a card does not need to know it is a sidebar. It also keeps
   * the placed panel in DOM ORDER, which is the whole accessibility story for
   * decision 0014's floating panel: it is reachable by Tab because it was never
   * moved out of the sequence, only painted elsewhere. Nothing here emits
   * `tabindex="-1"`, and nothing reorders — so "stays keyboard reachable" is a
   * property of the construction rather than a promise.
   */
  const placeWrap = (i: number, html: string): string => {
    const p = ir.nodes[i]?.placement;
    if (!p) return html;
    // ESCAPED, every one of them. These were the only spec-derived strings in
    // this file written into markup raw, and an adversarial pass confirmed the
    // consequence: an `edge` of `">
    //   <script>…</script><b x="` closed the attribute and the tag and ran.
    //
    // `compile.ts` now also checks each against its closed set, so this is the
    // second of two defences rather than the only one — but the escape is the
    // one that holds when a spec reaches a renderer without the compiler's
    // check having been the thing that ran, which `compile.ts` says in its own
    // comment can happen.
    const extent = p.extent ? ` data-morph-extent="${esc(p.extent)}"` : '';
    const collapsible = p.collapsible ? ' data-morph-collapsible="true"' : '';
    const where = p.mode === 'docked'
      ? ` data-morph-edge="${esc(p.edge)}"`
      : p.mode === 'floating'
        ? ` data-morph-rest="${esc(p.rest)}"`
        : ` data-morph-attach="${esc(p.attachment)}"`
          + ` data-morph-anchor="${esc(String(p.anchor))}"`;
    // A panel painted over the content is announced as nothing without this.
    // The label is DERIVED — mode plus edge, both from closed sets — so it says
    // where the panel is without an author having written a word, which is the
    // same rule the invoke button's label follows.
    const label = p.mode === 'docked'
      ? `Panel, ${EDGE_PHRASE[p.edge] ?? p.edge}`
      : p.mode === 'floating'
        ? `Floating panel, ${EDGE_PHRASE[p.rest] ?? p.rest}`
        : `Attached panel, ${EDGE_PHRASE[p.attachment] ?? p.attachment}`;

    // `position-anchor` was never emitted anywhere, so every `anchor()` in the
    // stylesheet was invalid at computed-value time and an anchored panel
    // silently fell back to the surface corner — in a browser with full anchor
    // support. The test that guarded it asserted the `anchor-name` SUBSTRING,
    // which is exactly the attribute-not-behaviour failure this file
    // congratulates itself on avoiding for tab order.
    const style = p.mode === 'anchored' ? ` style="position-anchor:--n${p.anchor}"` : '';

    // A collapse control, because `collapsible` had no consumer anywhere: the
    // attribute was emitted, the CSS hid the panel, and there was nothing to
    // expand it again — reachability lost with no route back. The toggle stays
    // visible when collapsed, which is what makes the state recoverable.
    const toggle = p.collapsible
      ? `<button class="m-place-toggle" type="button" aria-expanded="true"`
        + ` data-morph-collapse="${i}" aria-label="Collapse ${esc(label)}">−</button>`
      : '';

    // The id is what view state keys on when it restores a dragged position.
    // It is a node INDEX, not an object id — the same reason `nodeAttr` is safe
    // to emit on everything.
    return `<div class="m-place" role="group" aria-label="${esc(label)}"`
         + ` data-morph-place="${p.mode}" data-morph-node="${i}"`
         + `${where}${extent}${collapsible}${style}>${toggle}${html}</div>`;
  };

  /**
   * The button behind an `invoke` affordance.
   *
   * Its LABEL is derived from the declared effects, not authored. That is not
   * only consistency with "presentation is derived from IR contents" — an
   * author-supplied caption would be free text in the spec, which is a content
   * channel, and the spec is the one place content must never be. So the button
   * says what it *does*, from the closed vocabulary, and a user who wants
   * different words is asking for a different action.
   *
   * The action name goes in a data attribute rather than being resolved here.
   * A renderer emits a string; it holds no registry and executes nothing, which
   * is what keeps the permission boundary in one place instead of one per
   * renderer.
   */
  const invokeButton = (
    n: IRNode,
    /**
     * Which ROW this button belongs to, when the button is one of many.
     *
     * A card's button is inside the card, so its accessible name is read in the
     * card's context and "Update — this button may change a property" is
     * unambiguous. A table's is not: twelve rows produce twelve buttons whose
     * names are identical, and a screen reader user tabbing through hears the
     * same sentence twelve times with nothing distinguishing them. `<td>` gives
     * a column and row NUMBER, which is a position, not a subject.
     *
     * So the row's own name goes into the label — the same string already
     * printed in the title cell beside it, not a new disclosure. Absent when
     * the template names nothing, in which case the position is all there is
     * and the label stays as it was.
     */
    within?: string,
    /**
     * Which of the repeated ones this is, 1-based.
     *
     * A subject name is not a unique name. Two objects may share a title —
     * `Untitled` is one press of New away, twice — and then two row buttons
     * carry the same accessible name again, which is the invariant this was
     * written to hold. @Codex found it on the very fix for it.
     *
     * UNCONDITIONAL rather than appended only when names collide, which is what
     * the review asked for. A name whose SHAPE depends on whether some other
     * row happens to share a title is a name that changes when unrelated data
     * changes: the same button reads *"Update Untitled"* today and *"Update
     * Untitled, 3 of 12"* after someone edits a different object. A person
     * learning the interface cannot rely on that, and a screen-reader user
     * least of all. The position is always true, always stable, and it is what
     * the platform already announces for a list item — a table cell button has
     * no such announcement, which is why it is said here.
     */
    at?: { readonly position: number; readonly size: number },
  ): string => {
    const inv = n.invocation;
    if (!inv) return '';
    const primary = EFFECT_ORDER.find((e) => inv.effects.includes(e));
    const label = primary ? EFFECT_LABEL[primary] : 'Run';
    const phrases = inv.effects.map((e) => EFFECT_PHRASE[e] ?? e);
    const does = phrases.length > 1
      ? `${phrases.slice(0, -1).join(', ')} and ${phrases[phrases.length - 1]}`
      : phrases[0] ?? 'does nothing';
    const place = at ? `${at.position} of ${at.size}` : '';
    const subject = within
      ? `${label} ${within}${place ? `, ${place}` : ''}`
      : place ? `${label}, ${place}` : label;
    // `m-invoke-row` is @Codex's, and it is better than keying the compact
    // treatment off `.m-collection .m-invoke`: the class follows the same
    // question the accessible name does — was this action named by a subject,
    // i.e. is it one of many — rather than depending on which ancestor happens
    // to carry which class.
    return `<button class="m-invoke${within ? ' m-invoke-row' : ''}" type="button"`
         + ` ${ANCHOR_ATTR.node}="${n.i}" data-morph-action="${esc(inv.action)}"`
         + ` aria-label="${esc(subject)} — ${esc(does)}">${esc(label)}</button>`;
  };

  /** `anchor-name` for a node something is anchored TO. It used to be emitted
   *  for regions only, so anchoring to a card or a collection produced no
   *  anchor at all and the panel quietly landed in the surface corner. */
  const anchorName = (i: number): string =>
    ir.nodes.some((m) => m.placement?.mode === 'anchored' && m.placement.anchor === i)
      ? ` style="anchor-name:--n${i}"` : '';

  /**
   * `inList` says whether this node's markup will land inside a `<ul>`.
   *
   * A card used to be an `<li>` unconditionally, and a card is not always in a
   * list: a docked panel puts one directly inside a region, which is a `<div>`.
   * An `<li>` with no list around it is invalid markup, and Chromium does the
   * documented thing with it — `display:list-item`, `list-style-type:disc` — so
   * three bullets sat beside the New / Update / Open buttons on every panel.
   * `.m-collection{list-style:none}` never reached them, because that selector
   * is on the `<ul>` those cards were never inside.
   *
   * This is threaded rather than inferred from `n.parent`, because the question
   * is about the MARKUP being produced and not about the IR: a collection
   * renders as a table too, and those rows are `<tr>`. The two call sites that
   * pass `true` are the two that open a `<ul>`.
   *
   * An orphan `<li>` is not only ugly. It is announced as a list item by a
   * screen reader with no list to be an item of, which is the same defect
   * wearing the other modality.
   */
  /** The panes a sidebar region holds, in spec order — regions with a purpose
   *  the labels know. Hoisted beside `paneShowing` because the window band's
   *  tool strip needs the same answer the pane tabs do: @Codex's ruling on
   *  `2e06944` makes the band the authoritative pane selector, and two places
   *  computing "which pane is showing" is two places to disagree. */
  const panesOf = (side: IRNode): { purpose: Exclude<RegionPurpose, 'relations'> }[] =>
    side.children.flatMap((c) => {
      const k = ir.nodes[c];
      const p = k?.kind === 'region' ? k.purpose : undefined;
      return p !== undefined && p !== 'relations' ? [{ purpose: p }] : [];
    });
  /** Which pane shows: the person's choice if it names one of them, then the
   *  look's opening declaration matched on what the sidebar HOLDS, else the
   *  first — a stale choice (a pane that left the spec) shows the first
   *  rather than nothing. */
  const paneShowing = (side: IRNode): RegionPurpose | undefined => {
    const panes = panesOf(side);
    const chose = side.specId !== undefined ? workspace?.sidePanes?.[side.specId] : undefined;
    if (panes.some((p) => p.purpose === chose)) return chose;
    // The look's opening, resolved by CONTENT: whichever of this sidebar's own
    // panes the declaration keys on. No id anywhere, so a presentation that
    // names its regions differently still opens where the look said.
    const opens = workspace?.opensPaneOver;
    if (opens) {
      for (const held of panes) {
        const want = opens[held.purpose];
        if (want !== undefined && panes.some((p) => p.purpose === want)) return want;
      }
    }
    return panes[0]?.purpose;
  };

  const node = (
    i: number, obj: BoundValues | undefined, inList = false, ordinal?: number,
    listItem?: { readonly position: number; readonly size: number },
    /**
     * Position used only to distinguish repeated action names. Boards restart
     * list positions per group, so their action ordinal has to be separate.
     */
    actionItem: { readonly position: number; readonly size: number } | undefined = listItem,
  ): string => {
    const n = ir.nodes[i];
    if (!n) return '';

    switch (n.kind) {
      case 'region': {
        /**
         * THE PANES OF A NAMED APP LOOK draw their own chrome — decision
         * 0055, and the first deployed verdict's list: a properties block
         * writes the NAME beside each value ("Claude    2026-08-14" was read
         * as nobody's); the explorer opens with its tool row and the vault's
         * folder and closes with the vault strip; the backlinks pane names
         * the note it is about and counts what it lists. Every word here is
         * the host's (the vault name, the role labels) or the object's (the
         * title) — the pane invents nothing, and draws nothing it was not
         * given.
         */
        // The pane heads' × — the app's chrome, from the host, inert (0056).
        const paneX = workspace?.inert?.paneClose ? inertItem(workspace.inert.paneClose, 'm-ws-tab-x') : '';
        const paneChrome = (purpose: RegionPurpose | undefined) => {
          if (purpose === 'explorer') {
            const vault = workspace?.vault;
            const vaultInert = workspace?.inert?.vault ?? [];
            const vaultStart = vaultInert.filter((i) => i.place === 'start').map((i) => inertItem(i)).join('');
            const vaultEnd = vaultInert.filter((i) => i.place !== 'start').map((i) => inertItem(i)).join('');
            const glyph = (d: string, l: string) =>
              `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" `
              + `stroke-width="2" stroke-linecap="round" role="img" aria-label="${esc(l)}"><path d="${d}"/></svg>`;
            // ONE tool, and it acts — the same rule as the ribbon (@Codex,
            // P1 on b0af641: no dead controls in an imitation). Obsidian's
            // strip also has New folder and Sort; they return when the host
            // has an action for them, not before.
            return {
              before: `<div class="m-ws-tools">`
                + `<button type="button" class="m-ws-tool" data-ws-action="new" aria-label="New note">${glyph('M12 5v14M5 12h14', 'New note')}</button>`
                + `</div>`
                + (vault !== undefined
                  ? `<div class="m-ws-folder" aria-hidden="true"><span class="m-ws-chev">⌄</span>${esc(vault)}</div>`
                  : ''),
              // The strip: the vault's name, the app's switcher before it
              // and its help and settings after — each an inert item the
              // host declared, none a constant, none in the accessibility
              // tree (0056).
              after: vault !== undefined
                ? `<div class="m-ws-vault"><span class="m-ws-vault-name">${vaultStart}${esc(vault)}</span>`
                  + (vaultEnd ? `<span class="m-ws-vault-tools" aria-hidden="true">${vaultEnd}</span>` : '') + `</div>`
                : '',
            };
          }
          if (purpose === 'sidebar') {
            // A strip of pane tabs drawn FROM THE PANES the sidebar holds —
            // the chrome is in the spec (0055 control 2); which pane shows
            // is the host's `workspace.sidePane`, defaulting to the first.
            // Each tab names ITS sidebar, so the host can hold two of them
            // (Obsidian's left and right) to different choices.
            //
            // ICONS, not words — the tenth verdict's ruling 2: the app's
            // sidebar header is *"a compact row of toolbar icons above the
            // vault tree"*, and a labelled Files | Search strip *"reads more
            // like a conventional two-tab pane switcher"*. The label stays
            // as the tab's accessible name and its tooltip, never as text.
            const tabs = panesOf(n).map((k) =>
              `<button type="button" class="m-ws-side-tab${k.purpose === paneShowing(n) ? ' m-ws-side-tab-on' : ''}"`
              + ` role="tab" aria-selected="${k.purpose === paneShowing(n)}" data-ws-pane="${k.purpose}"`
              + `${n.specId !== undefined ? ` data-ws-sidebar="${esc(n.specId)}"` : ''}`
              + ` aria-label="${PANE_LABELS[k.purpose]}" title="${PANE_LABELS[k.purpose]}">`
              + `<svg class="m-ws-glyph" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"`
              + ` stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">`
              + `<path d="${PANE_GLYPHS[k.purpose]}"/></svg></button>`).join('');
            return {
              before: `<div class="m-ws-side-tabs" role="tablist" aria-label="Sidebar panes">${tabs}</div>`,
              after: '',
            };
          }
          // A SEARCH pane's query box and count are the `workspace-search`
          // module's — a registered component, not a branch here (@Codex,
          // P1 on 93d57d7). The pane itself carries no chrome.
          if (purpose === 'graph') {
            // The VAULT graph is the main column's view: its name is the
            // Graph view tab in the strip above it, so no pane head.
            const vault = n.children.some((c) => ir.nodes[c]?.kind === 'capability'
              && ir.nodes[c]?.config?.['vault'] === true);
            if (vault) return { before: '', after: '' };
            const title = typeof obj?.['title'] === 'string' ? obj['title'] : undefined;
            return {
              before: title !== undefined
                ? `<div class="m-ws-pane-head">Graph of ${esc(title)}${paneX}</div>`
                : '',
              after: '',
            };
          }
          if (purpose === 'backlinks') {
            const title = typeof obj?.['title'] === 'string' ? obj['title'] : undefined;
            const list = n.children.map((c) => ir.nodes[c]).find((k) => k?.kind === 'collection');
            const key = list?.specId;
            const count = key !== undefined && regionTotals
              && Object.prototype.hasOwnProperty.call(regionTotals, key)
              ? regionTotals[key]
              : key !== undefined && regionSets
                && Object.prototype.hasOwnProperty.call(regionSets, key)
                ? regionSets[key]!.length : undefined;
            const note = workspace?.backlinksNote;
            const unlinked = workspace?.unlinkedMentions;
            // QUIET when there is nothing — the twelfth verdict: two empty
            // section heads and their zeros had more weight than their
            // content. With no backlinks and no unlinked mentions the pane
            // keeps its head and the list's own empty line, nothing else;
            // the sections return the moment either count is above zero.
            const quiet = count === 0 && !note && (unlinked === undefined || unlinked === 0);
            if (quiet) {
              return {
                before: title !== undefined
                  ? `<div class="m-ws-pane-head">Backlinks for ${esc(title)}${paneX}</div>`
                  : '',
                after: '',
              };
            }
            return {
              before: (title !== undefined
                ? `<div class="m-ws-pane-head">Backlinks for ${esc(title)}${paneX}</div>`
                : '')
                + `<div class="m-ws-section">Linked mentions`
                + (count !== undefined
                  ? `<span class="m-ws-count">${count}${note ? `+ · ${esc(note)}` : ''}</span>` : '')
                + `</div>`,
              // Said only when the host counted it — a zero nobody computed
              // is invented content (0055).
              after: unlinked !== undefined
                ? `<div class="m-ws-section m-ws-section-unlinked">Unlinked mentions`
                  + `<span class="m-ws-count">${unlinked}</span></div>`
                : '',
            };
          }
          return { before: '', after: '' };
        };
        const named = (c: number, html: string): string => {
          if (n.purpose !== 'properties') return html;
          const role = ir.nodes[c]?.role;
          if (!role) return html;
          return `<div class="m-ws-prop"><span class="m-ws-prop-name">${esc(nameOf(role))}</span>${html}</div>`;
        };
        // A sidebar shows ONE of its panes; the others are rendered and
        // hidden, so switching is a host state change and not a regeneration
        // (0023: a gesture that changes nothing about the pool regenerates
        // nothing). `hidden` on a wrapper, so the pane's own markup — its
        // landmark, its rows — is untouched.
        const shown = (c: number, html: string): string => {
          // THE MAIN COLUMN — the region holding the editor — shows the
          // editor or, when the host says so, the vault's Graph view (a
          // `graph` region beside it; the tenth verdict's list). Same
          // mechanism as a sidebar: rendered and hidden, never dropped.
          if (n.purpose === undefined && n.children.some((k) => ir.nodes[k]?.purpose === 'editor')) {
            const purpose = ir.nodes[c]?.purpose;
            const graphOn = workspace?.mainView === 'graph'
              && n.children.some((k) => ir.nodes[k]?.purpose === 'graph');
            if (purpose === 'editor' && graphOn) return `<div class="m-ws-main-off" hidden>${html}</div>`;
            if (purpose === 'graph' && !graphOn) return `<div class="m-ws-main-off" hidden>${html}</div>`;
            return html;
          }
          if (n.purpose !== 'sidebar') return html;
          const purpose = ir.nodes[c]?.purpose;
          if (purpose === undefined || purpose === paneShowing(n)) return html;
          return `<div class="m-ws-side-off" hidden>${html}</div>`;
        };
        const chrome = paneChrome(n.purpose);
        /**
         * A BLANK NOTE is writing-first — the twelfth verdict: *"the empty
         * note opens with a large grey Untitled, then six empty property
         * rows and two dividers before the dim Start writing… prompt"*. In
         * the editor of a note with no title, no prose, nothing in any
         * property and no relation, the properties and relations fold into
         * one closed disclosure — still there, still editable a click away
         * — so the body prompt sits under the title, as the app's blank
         * note does. Anything typed anywhere unfolds it on the next paint.
         */
        const blank = n.purpose === 'editor' && isBlankNote(n, obj);
        const foldable = (c: number): boolean =>
          blank && (ir.nodes[c]?.purpose === 'properties' || ir.nodes[c]?.purpose === 'relations');
        const drawn = n.children.map((c) => [c, placeWrap(c, shown(c, named(c, node(c, obj))))] as const);
        const foldedIn = drawn.filter(([c]) => foldable(c)).map(([, h]) => h).join('');
        let foldEmitted = false;
        const body = drawn.map(([c, h]) => {
          if (!foldable(c)) return h;
          if (foldEmitted) return '';
          foldEmitted = true;
          return `<details class="m-ws-blank-fold"><summary class="m-ws-blank-fold-head">Properties</summary>${foldedIn}</details>`;
        }).join('');
        const kids = chrome.before + body + chrome.after;
        // A region only becomes a placement surface if something is actually
        // placed in it. Otherwise it stays the flex container it has always
        // been — switching every region to grid would have quietly restyled the
        // entire existing corpus to buy nothing.
        const hosts = n.children.some((c) => ir.nodes[c]?.placement);
        // `kids` must be NON-EMPTY, and that clause is the whole of @Codex's
        // finding: *"every empty `node-slot` returns an empty string because it
        // has neither targets nor an attach grant, but this branch still
        // unconditionally emits the heading and bordered Connections region. A
        // task with neither Background nor Parent therefore shows a
        // screen-reader landmark and visible panel containing no rows."*
        //
        // The single-relation case has obeyed this rule since decision 0016 —
        // `if (!targets.length && !canAttach) return ''` in the slot — and
        // `relation-affordances.test.ts` asserts it as *"an EMPTY slot draws
        // nothing at all for a reader."* Wrapping several slots in a region
        // re-introduced at the REGION level exactly what the slot level was
        // careful to avoid: chrome around nothing, offering a reader a landmark
        // that leads to no rows. An affordance with nothing behind it is worse
        // than a missing one, and so is a heading.
        // See `connectionsOf`. One classification, two call sites.
        const relationStack = isConnections(n.i, kids !== '');
        // A WORKSPACE PANE carries its purpose as a class and a landmark name
        // (decision 0055): the explorer, editor and backlinks regions are
        // what the template's stylesheet lays out, and what a reader jumps
        // to. `relations` keeps its own treatment below.
        const pane = n.purpose !== undefined && n.purpose !== 'relations'
          ? n.purpose : undefined;
        const cls = `m-region m-${n.flow}${hosts ? ' m-surface' : ''}`
          + `${relationStack ? ' m-connections' : ''}`
          + `${pane ? ` m-ws-pane m-ws-${pane}` : ''}`;
        // A HEADING inside a LABELLED GROUP, not a styled div.
        //
        // @Codex, on the deployed markup: *"it is exposed as a generic node,
        // with no heading role, level, or labelled enclosing region. The two
        // rows are named groups, but their shared visual heading is not
        // represented to assistive technology."*
        //
        // Both halves are needed and only one is obvious. The heading gives a
        // landmark to jump to; the `role="group"` with `aria-labelledby` is what
        // makes the rows BELONG to it — without that, a reader who lands on
        // "Parent task" has no way to learn it is one of this object's
        // connections rather than a section of the page. Sighted readers get
        // that from the box; the group is how everyone else does.
        //
        // Unique per RENDERED INSTANCE, not per template node.
        //
        // `m-conn-${n.i}` is the node's index in the IR, which is a TEMPLATE
        // position: a collection renders the same node once per object, so
        // every card emitted the identical id and every `aria-labelledby`
        // pointed at the first one. @Codex: *"assistive technology [may]
        // associate rows with the first item's heading rather than their own."*
        //
        // A duplicate id is not a rendering bug you can see — the page looks
        // right and only the accessibility tree is wrong, which is the same
        // class of defect as the generic-node heading this replaced.
        const headId = `m-conn-${n.i}-${connSeq += 1}`;
        const heading = relationStack
          ? `<h2 class="m-connections-head" id="${headId}">Connections</h2>` : '';
        const grouping = relationStack
          ? ` role="group" aria-labelledby="${headId}"` : '';
        /**
         * A REGION CARRIES ITS NODE INDEX, and until this it was the only kind
         * that did not.
         *
         * `drop-host.ts` builds its element index from `[data-morph-n]` and
         * skips any target whose host is missing from it — `if (!host)
         * continue`. A region was always missing, so the overlay silently
         * discarded EVERY target hosted on one: `into` a region, `before` and
         * `after` between a region's children, and all of `beside`, which is
         * region-hosted by construction.
         *
         * Measured on the deployed build before this line existed: the drag
         * offered `{ into: 5, before: 5, after: 5 }` and **not one `beside`**,
         * while the spec on screen had six nodes whose parent was a region. The
         * engine enumerated them; the overlay dropped them on the floor.
         *
         * That is why `ACCEPTANCE.md`'s Notion row scored *columns* as half —
         * *"proved at the drop engine and not on a page"*. The mechanism was
         * complete and unreachable, which is the same shape as task 0086 one
         * layer over.
         *
         * `paint()` already has a comment worrying about a host with no
         * element, added when an index desync was traced to one. It was read as
         * an edge case. It was every region.
         *
         * This does not change what `readAnchor` resolves. It walks ancestors
         * for the NEAREST node attribute, and every element it is used from —
         * a field, a card, an editable — carries its own, so a region can only
         * ever be found by something that had no answer before.
         */
        const paneName = pane !== undefined && !grouping
          ? ` role="region" aria-label="${PANE_LABELS[pane]}"` : '';
        return `<div class="${cls}"${nodeAttr(n.i)}${grouping}${paneName}`
             + `${anchorName(n.i)}${label(n, obj)}>${heading}${kids}</div>`;
      }

      case 'collection': {
        // A collection has several return shapes — table, board, list — so the
        // anchor name goes on a wrapper, and only when something is actually
        // anchored to it. No anchor, no wrapper, no change to any existing lens.
        const inner = collection(n);
        const an = anchorName(n.i);
        return an ? `<div class="m-anchor"${an}>${inner}</div>` : inner;
      }

      case 'card': {
        // Placed children wrapped here too. The schema grants `placement` to
        // card and capability, and only the region case ever called
        // `placeWrap` — so a docked panel inside a card was accepted by the
        // schema, the validator and the compiler and then vanished with no
        // diagnostic anywhere.
        // A caret cannot be placed inside a <button>, so a card that offers
        // inline editing cannot also be one big button. The affordance that
        // loses is the card's, because a select is recoverable by clicking
        // something else and a field you cannot type into is not editable at
        // all.
        const editableInside = hasGrantIn(ir, n.i, edit?.grants, obj);
        // A transport is interactive, and a `<video controls>` inside a
        // `<button>` is both invalid markup and a real conflict: every click on
        // play, scrub or volume also activates the card. Exactly the argument
        // the caret already won here — the affordance that loses is the card's,
        // because a select is recoverable by clicking something else and a
        // transport you cannot press is not a player. Found by @Codex.
        const transportInside = subtree(ir, n.i)
          .some((m) => m.kind === 'media' && (m.medium === 'video' || m.medium === 'audio'));
        // A grouped collection draws board columns, and a column's head is a
        // fold toggle (a button; so is the column's create door). Inside one
        // big card button that is a button in a button — invalid, and a
        // click meant to fold the column would select the card. The same
        // argument as the transport's, one row up. Found by @Codex.
        const boardInside = subtree(ir, n.i)
          .some((m) => m.i !== n.i && m.kind === 'collection' && m.query?.groupBy !== undefined);
        // `invoke` is deliberately not counted here. A card whose only
        // affordance is a button must not itself become one big button — that
        // would nest a button inside a button, which is invalid, and would make
        // the whole card fire the action.
        const others = (n.affordances ?? []).filter((a) => a !== 'invoke');
        /**
         * `expand` is a DISCLOSURE, and it was being drawn as a select.
         *
         * The affordance has been in the vocabulary since the start and nothing
         * ever read it. A card carrying it fell into the branch below with
         * every other affordance and became one big `<button>` — so the whole
         * card was a click target that selected, nothing folded, and a screen
         * reader was told "button, <name>" with no `aria-expanded` at all.
         * Accepted by the schema, compiled into the IR, and rendered as a
         * different affordance: the same shape as the slot content that was
         * accepted four times and drawn zero, one row over.
         *
         * `<details>` rather than a button and a hidden region, because the
         * browser already has a disclosure — keyboard, `aria-expanded`, and the
         * open state come with it, and the standing rule is to buy that rather
         * than rebuild it.
         *
         * The card element itself stays: it carries the section anchor a table
         * of contents points at, and moving that onto the `<details>` would
         * quietly change what a contents link targets.
         */
        const foldable = others.includes('expand');
        // An unnamed object is still a named CARD — `Untitled`, the same
        // fallback copy the tab strip and the editor use (0024: presentation,
        // never data), so its row reads and its button has an accessible
        // name. The fourth deployed verdict saw a blank selected bar.
        const rawName = n.nameFrom ? obj?.[n.nameFrom] : undefined;
        const unnamed = n.nameFrom !== undefined && (rawName === undefined || rawName === '');
        const name = n.nameFrom ? (unnamed ? 'Untitled' : esc(rawName)) : '';
        // No `&& !foldable` here. It was in a first version and a mutation
        // test proved it dead: the `if (foldable)` branch below returns before
        // this value is ever read, so removing the clause changed no output at
        // all. A guard that cannot fail is not a guard, and one that reads
        // like the load-bearing part of a fix is worse than nothing — the
        // branch order is what keeps a disclosure from also being a button,
        // and it is asserted in `e2e/document-flow.mjs`.
        /* AND the card must be able to RESOLVE — the table's own rule since
         * `ed4f7a7`, arriving one surface over. @Codex's P2 on `16c7bcc`
         * offered two remedies and I took only the boundary, on the argument
         * that three clauses in `av-review`, `fold` and `runtime` render a
         * handle-less card and assert it is interactive, so gating here would
         * cost a real capability.
         *
         * Their P2 on `660c7bd` answered that, and it is the better reading:
         * those three clauses *"only inspect unrelated markup using
         * handle-less fixtures and never activate the card"*. They pinned an
         * accident of the fixtures, not a capability — so the fixtures gained
         * handles and the gate is here. What the boundary alone left behind
         * was a native, focusable button promising an action that silently
         * does nothing, which is worse for a keyboard or screen-reader user
         * than for the mouse I was picturing.
         *
         * `obj === undefined` stays interactive and is a different case: there
         * is no object here, so the enclosing surface's handle is the honest
         * answer, and a single-object lens depends on exactly that. */
        const interactive = others.length > 0
          && !editableInside && !transportInside && !boardInside
          && !(obj !== undefined && handleOf(obj) === undefined);
        // The children are rendered AFTER the decision, under it: a field
        // that would be a button of its own (a relation chip) draws as a
        // span inside a hit target. Decided here, from the affordances, not
        // read back from the markup.
        const wasInsideHit = insideHit;
        insideHit = interactive && !foldable;
        const kids = n.children.map((c) => placeWrap(c, node(c, obj))).join('');
        insideHit = wasInsideHit;
        const anchor = objectAttrs(obj, n.i);
        // NAMED BY THE CARD IT IS ON, exactly as a table row's is.
        //
        // The first version passed the row name only from `table()`, on the
        // reasoning that a card's button is read in the card's context. @Codex
        // drove the deployed board and measured what that was worth: *"13 row
        // buttons, all named `Update — this button may change a property`"*.
        // A board card is as repeated as a table row, and a screen-reader user
        // tabbing a board hears the same sentence thirteen times.
        //
        // `name` is the same string the card's own heading uses, so this
        // discloses nothing the card does not already say out loud.
        const button = invokeButton(
          n,
          n.nameFrom ? fmt(obj?.[n.nameFrom]) : undefined,
          // Only when this card IS one of many. A single-object lens draws one
          // card and "1 of 1" is noise.
          actionItem,
        );
        const an = anchorName(n.i);
        const t = inList ? 'li' : 'div';
        // A windowed list item says where it sits in the COMPLETE set, not only
        // in the DOM page. Both attributes belong on the item; putting
        // aria-setsize on the <ul> is ignored by the listitem contract.
        const set = inList && listItem
          ? ` aria-setsize="${listItem.size}" aria-posinset="${listItem.position}"`
          : '';
        // A section anchor, minted by the same function the host uses to build
        // a table of contents — so the link and the target cannot drift. Only
        // when the card is an item of a collection, because only then does an
        // ordinal mean anything.
        // `tabindex="-1"` because an id is only half of a destination. A
        // fragment jump focuses its target only if the target is focusable;
        // otherwise the viewport moves and `activeElement` stays on `<body>`,
        // so a keyboard or screen-reader user is left exactly where they were.
        // Measured in both directions on the note apparatus by @Codex — and it
        // was already true of every table-of-contents entry, which has been
        // shipping since the contents list landed.
        const sec = ordinal === undefined ? ''
          : ` id="${sectionAnchor(n.i, ordinal)}" tabindex="-1"`;
        // The other end of the note apparatus. `noteMarker` explains why the
        // renderer draws this and not the document module; `m-card-noted` is
        // what lets the stylesheet put it on the heading's line, and it is a
        // class the renderer sets rather than a `:has()` the browser computes,
        // because the renderer already knows the answer.
        const mark = noteMarker(n, ordinal, obj);
        const noted = mark ? ' m-card-noted' : '';
        /**
         * TASK 0088 — the action shares the card's first line instead of
         * owning one.
         *
         * Measured before the fix, Tasks pool at 1500x1000, `regular` /
         * `balanced`: a board card carrying an action was **88.7px** and the
         * same card without one **44px**. The button is 44px — `--target`,
         * which `tokens.ts` holds at every density — and it was emitted as a
         * normal-flow sibling *after* the contents, so it inflated the line it
         * landed on by the full target. Twelve cards on that board, so the
         * action cost 536px of a 1000px viewport.
         *
         * The class is set only when there IS an action, so a card without one
         * renders byte-identically to before and nothing about the corpus
         * moves. That matters more than brevity here: this is the layout of
         * every card in the product.
         *
         * `m-card-body` exists for the SAME reason `.m-card-hit` does, and only
         * in the branch that lacks it. A grid places children in cells, so the
         * contents have to be one child to stay in one cell — the interactive
         * form already wraps them in `.m-card-hit` and the foldable form in
         * `<details>`, and only the plain form leaves them as bare siblings.
         * Wrapping unconditionally would have put a div around every card in
         * the corpus to buy nothing.
         */
        // `kids` as well as `button`: a PANEL card's only child is its own
        // action — `<div class="m-card"><button class="m-invoke">New</button>`
        // — and a card whose sole content is the action has nothing to share a
        // line with. Gridding it would put an empty first column beside the
        // button and shove it to the far edge of whatever the card stretches
        // to, which is a visible change to the shell for no height saved.
        const acting = button !== '' && kids !== '' ? ' m-card-acting' : '';
        const body = acting ? `<div class="m-card-body">${kids}</div>` : kids;
        /**
         * THE OPEN NOTE is the explorer's current row — decision 0055's
         * declared active-file state, and @Codex's P2 on ebf7b8c: the
         * stylesheet had an `aria-current` rule nothing ever set. Decided
         * here from the objects the renderer holds: a card in an explorer
         * pane whose object IS the subject (the first object of a
         * single-object lens) is current. Never from the DOM.
         */
        const inExplorer = (() => {
          let p = n.parent;
          while (p >= 0) {
            const a = ir.nodes[p];
            if (a?.kind === 'region' && a.purpose === 'explorer') return true;
            p = a?.parent ?? -1;
          }
          return false;
        })();
        const cur = inExplorer && objects[0] !== undefined && obj !== undefined
          && ir.nodes[ir.root]?.kind !== 'collection'
          && handleOf(obj) === handleOf(objects[0])
          ? ' aria-current="true"' : '';
        if (foldable) {
          // The naming field goes in the SUMMARY and not also in the body.
          // Rendering every binding as a child and repeating the title above it
          // would print the section's name twice, which is what a first version
          // did — the summary is the heading, so the heading leaves the flow.
          // Guarded on `nameFrom` being set. Without the guard the comparison
          // is `role === undefined`, which matches the first child that has no
          // role at all — a nested collection, say — and hoists it out of
          // `rest` into a summary that then renders it as a label. Content
          // accepted and silently discarded, which is the thing this file
          // keeps having to be talked out of.
          const head = n.nameFrom === undefined ? undefined
            : n.children.find((c) => ir.nodes[c]?.role === n.nameFrom);
          const rest = n.children
            .filter((c) => c !== head)
            .map((c) => placeWrap(c, node(c, obj))).join('');
          const label = name || 'Section';
          return `<${t} class="m-card m-foldable${noted}${acting}"${set}${sec}${anchor}${an}${cur}>`
               + `<details class="m-fold" open><summary class="m-fold-summary">${label}</summary>`
               + `<div class="m-fold-body">${rest}</div></details>${mark}${button}</${t}>`;
        }
        // OUTSIDE the card's own hit target in the interactive form. A link
        // nested inside a button is not operable as a link — the browser gives
        // the click to the button — so a marker drawn there would select the
        // card instead of going to its note.
        const unnamedCls = unnamed ? ' m-card-unnamed' : '';
        return interactive
          ? `<${t} class="m-card${noted}${acting}${unnamedCls}"${set}${sec}${anchor}${an}${cur}><button class="m-card-hit" type="button" aria-label="${name}">${kids}</button>${mark}${button}</${t}>`
          : `<${t} class="m-card${noted}${acting}${unnamedCls}"${set}${sec}${anchor}${an}${cur}>${body}${mark}${button}</${t}>`;
      }

      case 'field': {
        const drawn = field(n, obj);
        /* A SINGLE-OBJECT PAGE'S TITLE IS THE PAGE'S HEADING — @Codex's P2 on
         * 27a1a65, and it is an accessibility regression this task itself
         * introduced.
         *
         * Silencing the page head was right: it removed a large dead copy of
         * the pool's name from a page about one object. But it removed the
         * page's only `h1` with it, and the object's own title is a `div`
         * carrying `role="textbox"`. A role is singular, so the edit surface
         * cannot also announce itself as a heading — and somebody navigating
         * by headings then has no way to find what the page is ABOUT. The
         * clause I wrote made it worse by requiring the heading count to be
         * zero.
         *
         * The ARIA pattern for an editable heading is a heading ELEMENT
         * containing the editable region, so the two roles sit on two
         * elements: `h1` for navigation, `textbox` for typing. That is also
         * exactly what @Codex's original P1 asked for — *"exactly one visible
         * page title, the editable object title"* — now true rather than
         * approximated by two absences.
         *
         * Gated on the IR's CARDINALITY, never on the tree: at a set the same
         * role in the same position is a card's title, and wrapping that in an
         * `h1` would put one heading per row. `purpose: 'editor'` is the
         * DECLARED half, the same pair `workspacePagehead` reads one layer up.
         */
        const parent = n.parent === undefined ? undefined : ir.nodes[n.parent];
        /* AND THE EDITOR MUST BE THE PAGE'S OWN, not one repeated per row —
           @Codex's P2 on 0d83f75. `ir.cardinality` is a fact about the whole
           spec and `parent.purpose` is one hop; between them sits the case
           neither sees, and it is a legal spec: a single-object page holding
           a nested COLLECTION whose item template is itself an editor region.
           Every repeated title then satisfied both terms and was wrapped as
           another page-level h1, so related items got page-title styling and
           the page announced several primary headings — the exact defect the
           wrapper was written to end, arriving one level in.

           Walked to the root rather than tested one hop, because the nesting
           can be deeper than a collection's immediate child. A collection
           ANYWHERE above means this title belongs to one of many. */
        const belongsToTheSubject = (() => {
          let at = n.parent === undefined ? -1 : n.parent;
          while (at >= 0) {
            const up = ir.nodes[at];
            if (up === undefined) return false;
            if (up.kind === 'collection' || up.kind === 'card') return false;
            at = up.parent;
          }
          return true;
        })();
        const pageTitle = ir.cardinality === 'single' && n.role === 'title'
          && parent?.kind === 'region' && parent.purpose === 'editor'
          && belongsToTheSubject;
        if (!pageTitle) return drawn;
        /**
         * AN UNTITLED, READ-ONLY PAGE STILL NEEDS A HEADING — @Codex's P2 on
         * 49cc0e3, and it is the grantless half of the fix above.
         *
         * `field()` returns '' for an empty value with no write grant: the
         * editable path has its `data-morph-empty` prompt, the read-only one
         * has nothing. So the wrapper emitted an EMPTY h1 — and since the
         * page head is now deliberately silent, a reader got neither a
         * visible title nor a named heading. An empty heading is worse than
         * none: a screen reader announces it and says nothing.
         *
         * The fallback is PRESENTATION ONLY and is never stored — the
         * object has no title, and drawing a word is not the same as
         * giving it one.
         *
         * AND IT WEARS THE TITLE'S TYPOGRAPHY — @Codex's P2 on `ed4f7a7`,
         * which is this fix one dimension on. `.m-ws-title` deliberately
         * inherits size, weight and leading so the wrapper carries no box of
         * its own, and every rule that makes a page title look like one —
         * the generic `--type-page` rule and each look's own — keys on
         * `.m-role-title`. A bare span matched none of them, so the heading
         * went from empty to present-but-drawn-as-body-text: a page whose
         * title is the same size as its properties.
         *
         * So the fallback carries the title component's class as well as its
         * own. It is not editable and carries no anchor, so it gains the
         * presentation and none of the behaviour — and a look's own
         * three-class title rule reaches it exactly as it reaches a real one.
         */
        /**
         * AND THE WRITE GRANT IS THE HALF THAT FIX MISSED — task 0185 finding
         * 3, and the heading's name was MEASURED rather than reasoned about.
         *
         * `drawn === ''` is the read-only test. Under a grant, `field()`
         * returns a role=textbox div carrying data-morph-empty, so `drawn` is
         * not empty, the fallback below is never emitted, and the visible word
         * comes from .m-editable:empty::before. Generated content does not
         * reach an accessible name, and Chromium says so:
         *
         *   empty, write grant   heading name = ""        <- the defect
         *   named, write grant   heading name = "Roadmap"
         *   empty, read-only     heading name = "Untitled"
         *
         * A writer presses New, lands on the new page, and somebody navigating
         * by headings hears "heading level one" and nothing after it — the
         * state the comment above calls worse than none, on the grant state
         * that is the product's default.
         *
         * NOT an aria-label on the h1, which is what this reached for first
         * and is wrong in a way only a live measurement shows: a label is
         * fixed at render, so the heading goes on announcing "Untitled" while
         * the person types their title into it. Measured, same run:
         *
         *   aria-label       empty "Untitled"   after typing "Untitled"  <- stale
         *   hidden label     empty "Untitled"   after typing "Roadmap"
         *
         * So the fallback is a real node the accessible name is computed FROM,
         * removed from the tree by display:none the moment the editable is not
         * empty. It keys on :empty, the same condition the visible prompt keys
         * on, so the announced fallback and the drawn one appear and disappear
         * together rather than being two conditions that can disagree.
         *
         * AND IT IS EMITTED ONLY FOR A TITLE THAT IS BLANK AT RENDER — which
         * the first version was not, and the cost was measured rather than
         * argued. `textContent` includes display:none text, and FOUR consumers
         * read this h1's text as the page's title: coda-look and notion-look
         * both collect `#stage h1` textContent, notion-look again for its
         * editable-heading count, and cross-view walks every heading. With the
         * node always present, Coda's row page read
         *
         *     headings: ["Land the object storeUntitled"]
         *
         * and `coda-look` went 15/15 to 14/15. That is the `Plein SoleilHide`
         * defect exactly — a control appended inside the thing it describes —
         * and it was found by asking 0064's one question of this diff: what
         * did it add, and who assumes that set is closed.
         *
         * What the gate gives up is the case where somebody DELETES a title
         * that was rendered with one: the heading then has no fallback until
         * the next repaint. That state is already the visible prompt's, since
         * a contenteditable emptied by hand keeps a <br> and stops matching
         * :empty — so drawn and announced still agree, which is the property
         * this pair is for.
         */
        const raw = obj?.[n.role ?? ''];
        const blankTitle = raw === undefined || raw === null || raw === '';
        const untitled = drawn === '';
        // `tabindex="-1"` so a NAVIGATION can put focus here — task 0185
        // finding 2. Out of the tab order and into the focus one: pressing a
        // row's Open replaces the whole stage, so the pressed button is gone
        // and focus falls to <body>; the destination has to be somewhere the
        // host can send it, and the page's own heading is what a person
        // arriving wants announced.
        return `<h1 class="m-ws-title" tabindex="-1">`
          + (untitled ? `<span class="m-role-title m-untitled">Untitled</span>` : drawn)
          + (blankTitle && isEditable(n, obj)
            ? `<span class="m-hidden-label">${esc(emptyPrompt(n.role))}</span>` : '')
          + `</h1>`;
      }
      case 'media':  return media(n, obj);

      case 'node-slot': {
        // This used to render an empty dashed box, which meant every relation
        // in every spec drew nothing at all — the single most valuable
        // unimplemented thing in the runtime.
        const targets = asObjects(obj?.[n.role ?? '']);
        // The two relation gestures, if the spec declared them AND a grant was
        // minted for them. Decision 0016 gives a node slot `mutations` rather
        // than a boolean precisely so these can differ: a slot may offer attach
        // and not detach.
        const canAttach = !!edit?.grants.forRelation(n.i, 'link', handleOf(obj));
        const canDetach = !!edit?.grants.forRelation(n.i, 'unlink', handleOf(obj));
        // *"A node slot that grants `link` must render an anchored,
        // keyboard-operable, accessibly named attach affordance EVEN WHEN IT
        // CURRENTLY HAS ZERO TARGETS; read-only empty slots may remain visually
        // empty."* An empty relation with no way to fill it is the one case
        // where drawing nothing makes the feature unreachable.
        if (!targets.length && !canAttach) return '';
        const max = n.maxDepth ?? 1;
        /**
         * The spec the referenced object is drawn with, when the spec says.
         *
         * Each child is rendered against the TARGET rather than the subject,
         * which is the whole of what "a region hosts a complete spec" means at
         * this layer — the same `node()` every other tree goes through, with a
         * different object under it.
         *
         * `preview()` remains the fallback, and it is worth being plain about
         * what it is: a thumbnail, a name, and three metadata rows the RENDERER
         * picks with `.slice(0, 3)`. Presentation decided in the one place this
         * product says presentation is never decided. A spec that says nothing
         * still gets it; a spec that says, does not.
         *
         * Depth is not re-entered here. `preview` counts its own recursion
         * because it follows refs itself; a hosted spec follows exactly the
         * relations it names, and the validator's `max-depth` already bounds
         * how deep those nest.
         */
        const hosted = n.children.length > 0
          ? (t: RoleValues) => n.children.map((c) => node(c, t as BoundValues)).join('')
          : (t: RoleValues) => preview(t, 1, max, obj ? new Set([obj]) : new Set());
        const name = nameOf(n.role!);
        /** The target's own name, for an accessible label that says WHICH
         *  object a control opens — "Open" alone repeated three times down a
         *  Connections stack names nothing. */
        const nameIn = (t: RoleValues): string => {
          const rs = Object.keys(t) as Role[];
          // The SAME rule `preview` uses, not a second one. A first version
          // took any text role, which meant `transcript` — and
          // `av-review.test.ts` caught it instantly, because that is the exact
          // defect it exists for: *"prose that BELONGS to an object, never the
          // object's identity."* @Codex found it there, twice, and I wrote it
          // again into an aria-label where no eye would have seen it.
          const r = rs.find((x) => x === 'title' || x === 'subtitle')
                 ?? rs.find((x) => ROLE_KIND[x] === 'text' && x !== 'transcript');
          return r ? fmt(t[r]) : 'linked object';
        };
        /**
         * One detach control per rendered OCCURRENCE, bound to that
         * occurrence's own token.
         *
         * The token comes from the store's projection, which mints one for
         * every relation it expands — so the control names the occurrence a
         * person actually clicked rather than "the first target that matches".
         * A target with no token cannot be detached and gets no control: that
         * is a reference the reading manifest does not address, and a button
         * for it would be an affordance over a write the store refuses.
         */
        /**
         * An occurrence, with a way to OPEN it as well as remove it.
         *
         * @Codex, on the chip treatment: *"the chip is neither clickable nor
         * keyboard-focusable, so the compact row has no way to inspect the
         * object it names despite the implementation comment promising 'a click
         * to go and see the rest'."*
         *
         * That comment was mine, in the paragraph justifying hiding the detail
         * rows — I argued for a chip on the grounds that you could click
         * through, and then shipped one you could not. The same claims-not-code
         * failure this branch keeps finding, in the sentence defending the
         * design.
         *
         * A SIBLING control rather than wrapping the drawn card: a button may
         * only contain phrasing content, and `hosted()` renders a card of divs
         * — nesting it would be invalid markup and, with Remove already inside,
         * a nested interactive control. Codex allowed either: *"render the
         * target through the reference-hit affordance or wire an equivalent
         * accessible activation."*
         *
         * Carries the occurrence INDEX, because a relation may hold several
         * targets and "the object this role points at" does not identify one of
         * them. The host resolves it by traversing the relation and taking that
         * position, which is the same order the projection drew.
         */
        // ONLY inside a Connections stack. @Codex: *"when a lens has only one
        // node slot — the case used by the existing one-relation pools —
        // `relationStack` is false, so the full `.m-ref-card` remains visible,
        // but this unconditional button adds a second visible copy of the
        // target name beside it. That changes the exact single-role surface
        // that `ACCEPTANCE.md` requires to keep its existing presentation."*
        //
        // Right, and it is the failure mode of every fix that reaches one level
        // too wide: the chip exists because the stack HIDES the card, so a slot
        // that still draws its card needs no second name.
        //
        // A first repair read `parent.purpose === 'relations'` here and called
        // it *"the same marker the stack itself is drawn from, so the two can
        // never disagree."* They disagreed on the deployed page within the day:
        // the region's predicate also requires more than one child, so Notes —
        // which declares ONE relation — kept its full card and gained the chip
        // beside it, the very surface the repair existed to protect. @Codex:
        // *"Reuse the exact stack classification … do not independently
        // approximate it."*
        //
        // Two predicates that must agree are ONE predicate, and writing the
        // second is how they stop agreeing. `true` for the rendered-children
        // half because a slot only reaches here while drawing one, so the
        // parent's `kids` cannot be empty.
        const inStack = isConnections(n.parent, true);
        const withDetach = (t: RoleValues): string => {
          const drawn = hosted(t);
          const token = relationTokenOf(t);
          // The control's VISIBLE text is the target's name, so in a
          // Connections row the chip and the control are one thing rather than
          // a chip beside a button. @Codex asked for the target rendered
          // *through* an activation affordance; this is that, and it also fixes
          // what putting a button next to the card did to the row — it grew to
          // 116px against 63 for its neighbours, which is the compactness the
          // ruling decides on.
          //
          // THE OCCURRENCE'S OWN TOKEN, not its position — task 0078, @Codex.
          // `traverse` deduplicates edges by `from>to`, so a slot-backed
          // reference and an edge to the same object are two rendered
          // occurrences and ONE traversal edge: a host counting positions in
          // that list opened the following target. The projection that drew
          // this row already minted an opaque identity per occurrence for
          // detach, and the read path now uses the same one.
          //
          // No token, no control. A read affordance whose identity cannot be
          // resolved is an affordance with nothing behind it, which this file
          // opens by refusing.
          const open = inStack && token
            ? `<button class="m-slot-open" type="button"`
              + ` ${ANCHOR_ATTR.node}="${n.i}" data-morph-t="${esc(token)}"`
              + ` aria-label="Open ${esc(nameIn(t))}">${esc(nameIn(t))}</button>`
            : '';
          if (!canDetach || !token) return `<div class="m-slot-item">${drawn}${open}</div>`;
          return `<div class="m-slot-item">${drawn}${open}`
               + `<button class="m-detach" type="button"`
               + ` ${ANCHOR_ATTR.node}="${n.i}" data-morph-t="${esc(token)}"`
               + ` aria-label="Remove from ${esc(name)}">Remove</button></div>`;
        };
        const attach = canAttach
          ? `<button class="m-attach" type="button"`
            + ` ${ANCHOR_ATTR.node}="${n.i}"`
            + ` aria-label="Add to ${esc(name)}">Add</button>`
          : '';
        return `<div class="m-slot" role="group" `
             + `aria-label="${esc(name)}">`
             + `<div class="m-slot-head">${esc(name)}</div>`
             + targets.map(withDetach).join('')
             + attach
             + `</div>`;
      }

      case 'capability': {
        // Slot children, rendered by the HOST and grouped by slot name. The
        // module places them; it never draws them. A field inside a document's
        // header is an ordinary field and must look like one.
        const slotHTML: Record<string, string[]> = {};
        for (const c of n.children) {
          const slot = ir.nodes[c]?.slot;
          if (!slot) continue;
          (slotHTML[slot] ??= []).push(node(c, obj));
        }
        /* THE BAND'S TOOL STRIP IS THE PANE SELECTOR — @Codex's ruling on
           `2e06944`. Suppressing the duplicate row is half of it; the half
           that matters is that the surviving control must not be
           semantically weaker than the one that went. The strip's `selects`
           declaration comes from the host (which action means which pane is a
           fact about the app being reproduced); WHICH pane is showing is the
           renderer's, resolved against what the sidebar HOLDS — the same
           `paneShowing` the tabs use, so the two cannot disagree.

           The sidebar is chosen by CONTENT: whichever one holds a pane the
           declaration keys on. No id, and no assumption that the left one
           comes first among the children. */
        const capInput = capabilityInputFor(capabilities, n.i, handleOf(obj));
        const ribbonInput = ((): CapabilityInput | undefined => {
          if (n.capability !== 'workspace-ribbon') return capInput;
          // The whole input, not one field of it — see the spread below.
          const ribbon = capInput?.capability === 'workspace-ribbon' ? capInput.ribbon : undefined;
          const selects = ribbon?.selects;
          if (!ribbon || !selects) return capInput;
          const wanted = new Set(Object.values(selects));
          for (const m of ir.nodes) {
            if (m.kind !== 'region' || m.purpose !== 'sidebar') continue;
            const held = panesOf(m).map((x) => x.purpose);
            if (!held.some((h) => wanted.has(h))) continue;
            /* SPREAD, then override — @Codex's P2 on `ee1dd16`. This rebuilt
               the input from two named fields, so `tools` (new in the previous
               commit) never reached `workspaceRibbon` on the one path that
               matters: a look with an explorer sidebar. Its custom inventory
               was silently replaced by the four defaults AND its mapped action
               then reported as undeclared — the refusal built to catch a
               look's mistake, firing on the renderer's own. A rebuild that
               names fields goes stale the next time one is added; a spread
               cannot. */
            return { capability: 'workspace-ribbon',
              ribbon: { ...ribbon, ...(paneShowing(m) !== undefined
                ? { active: paneShowing(m)! } : {}) } };
          }
          return capInput;
        })();
        const out = renderCapability(n, ribbonInput, slotHTML);
        // Slot content nothing will draw is SAID, not swallowed.
        //
        // This case renders the module and never its children, which is correct
        // — a module owns its own interior. What was not correct was that a
        // spec could fill a declared slot, pass the validator, compile into the
        // IR, and produce nothing at all with no diagnostic. Accepting a value
        // and discarding it is worse than refusing it; this repo has the same
        // sentence written beside a video aspect ratio that was accepted and
        // overridden.
        const dropped = unconsumedSlots(n, n.children.map((c) => ir.nodes[c]?.slot));
        const note = dropped.length
          ? `<div class="m-capability m-capability-refused" role="note">`
            + `${esc(n.capability)} does not draw ${dropped.length > 1 ? 'slots' : 'slot'} `
            + `${dropped.map((d: string) => esc(d)).join(', ')} — that content is not on screen`
            + `</div>`
          : '';
        const withNote = note ? `${out}${note}` : out;
        const an = anchorName(n.i);
        // CSS anchor positioning requires a generated box. The generic
        // m-anchor wrapper is display:contents for structural nodes, so a
        // capability gets a dedicated block wrapper.
        return an ? `<div class="m-anchor m-capability-anchor"${an}>${withNote}</div>` : withNote;
      }
    }
  };

  /* ------------------------------------------------------ the five shapes */

  const collection = (n: IRNode): string => {
    const tpl = n.itemTemplates?.[0];
    if (!tpl) return '<div class="m-collection"></div>';

    // WHICH set this collection shows. Everything below reads `set`, not
    // `objects` — including `presentationOf`'s `single`, which decides table
    // versus cards. A panel listing twelve siblings beside a single-object lens
    // must be laid out as twelve things, and reading `objects.length` there
    // would have laid it out as one.
    //
    // An explicit `subject` resolves to `objects`, exactly as absence does. The
    // schema defines absence AS `subject`, so two specs that mean the same
    // thing must render the same thing — and the earlier version consulted
    // `sources.subject` when a host happened to supply it, which made two
    // equivalent layouts diverge. Found by @Codex.
    const source: Source = n.source !== undefined && n.source !== 'subject'
      ? n.source : 'subject';
    // THIS REGION'S own rows, and the three cases are deliberately different.
    //
    // 1. no `regionSets` at all        -> the legacy path, byte-identical
    // 2. `regionSets` naming this one  -> that region's set
    // 3. `regionSets` NOT naming it    -> refuse, loudly
    //
    // The third is @Codex's ruling on task 0081 and it corrects what I shipped
    // an hour earlier. I fell back to `objects`, and wrote in the comment that
    // a missing key would be *"visibly wrong rather than silently paired with
    // another region's rows"* — which is not what falling back does. It draws
    // the whole subject, and a region drawing everything looks exactly like a
    // region whose filter matched everything. *"Applying that fallback to a
    // missing keyed region would conceal an addressing defect as an apparently
    // unfiltered region."*
    //
    // A host that opts into region addressing has taken responsibility for
    // naming every subject region; failing to is a host defect, not user data.
    // The a11y floor already establishes that the runtime refuses a layout it
    // cannot render correctly rather than rendering it wrongly.
    let own: readonly BoundValues[] | undefined;
    if (regionSets !== undefined && source === 'subject') {
      // OWN PROPERTY, not a lookup. `id` is any string the schema accepts, so a
      // collection authored as `constructor` or `toString` reads an INHERITED
      // property off an ordinary object literal — the refusal below is skipped
      // and the renderer then calls `.slice()` on a function. Found by @Codex,
      // and it is the same defect the collection `source` guard already carries
      // a comment about: `source: "constructor"` resolved a function off the
      // prototype, which is why membership there is a `Set` test.
      own = n.specId !== undefined
        && Object.prototype.hasOwnProperty.call(regionSets, n.specId)
        ? regionSets[n.specId] : undefined;
      if (own === undefined) {
        throw new Error(
          `region addressing is active and no rows were supplied for subject `
          + `collection "${n.specId ?? '(no id)'}" — supplied: `
          + `${JSON.stringify(Object.keys(regionSets))}`);
      }
    }
    const supplied = own ?? (source === 'subject' ? objects : sources?.[source]);
    const set = supplied ?? objects;
    // When a named source is absent, the rows fall back to the subject, so the
    // total must follow the rows too. A sibling total attached to subject rows
    // would be a confident lie about both the overflow and each item position.
    const reported = (own !== undefined && n.specId !== undefined
      ? (regionTotals !== undefined
        && Object.prototype.hasOwnProperty.call(regionTotals, n.specId)
        ? regionTotals[n.specId] : undefined)
      : supplied === undefined ? totals?.subject : totals?.[source])
      ?? set.length;
    const total = Math.max(set.length, Number.isFinite(reported) ? Math.floor(reported) : set.length);

    // Virtualization: the compile-time flag OR the set actually in hand.
    //
    // `virtualize` is decided from `CompileContext.itemCount`, which describes
    // the SUBJECT — so a single-object lens compiled with `itemCount: 1` and
    // handed two hundred siblings sailed past the cap and materialized every
    // one of them. A renderer that materializes 500k rows is a failed renderer
    // regardless of how correct the markup is, and the count the compiler was
    // given is not the count this collection has. Found by @Codex.
    // The folds for THIS collection's set, if the host computed any.
    const folds = rollups?.[source];

    // The folds this collection asked for, DEDUPLICATED ONCE.
    //
    // The schema puts no uniqueness constraint on its eight aggregate entries,
    // so a spec repeating `{ duration, sum }` printed it twice. I fixed that in
    // the table footer only, and left `board()` mapping the raw array — the
    // same defect, in the other presentation, in the commit that claimed to
    // repair it. Deduplicating where both paths read it is the fix that cannot
    // drift. Found by @Codex.
    const seenFold = new Set<string>();
    const askedOnce = (n.query?.aggregate ?? [])
      .filter((a) => !seenFold.has(foldKey(a.role, a.fn)) && seenFold.add(foldKey(a.role, a.fn)));

    const capped = n.virtualize || total > VIRTUALIZE_ABOVE || set.length > VIRTUALIZE_ABOVE;
    const shown = capped ? set.slice(0, VIRTUALIZE_ABOVE) : set;
    const mode = presentationOf(ir, n, { single: total <= 1 });
    const more = capped && total > shown.length
      ? `<div class="m-more" role="status">${total - shown.length} more</div>` : '';

    /** Keep the virtualization status in the SAME flow as the specialized
     * collection it describes. Returning board/table + status as siblings made
     * both direct children of a placed surface; `.m-surface > *` then assigned
     * both to the `main` grid area and stretched the status over the board,
     * painting "140 more" across its first heading. Found by @Codex driving the
     * deployed 100k route. */
    const withMore = (content: string): string => more
      ? `<div class="m-virtualized">${content}${more}</div>`
      : content;

    if (mode === 'table') {
      /**
       * A TABLE SCROLLS INSIDE ITS OWN BOX rather than pushing the page.
       *
       * `.m-surface > *` carries `min-width: 0` so a grid child may shrink —
       * but a `<table>` cannot render narrower than its min-content width, and
       * `width: 100%` is a hint it overrides. So a table whose columns need
       * more room than the `main` grid area has does not shrink and does not
       * clip: it OVERFLOWS, straight across whatever is docked beside it.
       *
       * Measured, on the notes pool at 1280px, when the action column was
       * added: the table went from 456px wide — ending 41px clear of the
       * docked panel — to 567px, running 70px UNDER the panel's rows and
       * intercepting every click meant for them. The panel was still painted;
       * it was simply no longer pressable. CI caught it; the same run passed
       * locally, because whether the overlap swallows a click depends on where
       * a row's centre falls, which is a font-metric away from either answer.
       *
       * The wrapper is what `min-width: 0` needs to bite: the DIV shrinks to
       * its grid area, the table keeps its intrinsic width inside it, and the
       * overflow becomes a scrollbar instead of a collision. This is not
       * specific to the action column — any table wide enough for its column
       * already did this, and at narrow viewports several do.
       */
      return withMore(
        `<div class="m-table-scroll">${table(n, tpl.root, shown, folds, askedOnce, total)}</div>`,
      );
    }
    if (mode === 'board') return withMore(board(
      n, tpl.root, shown, folds, capped, askedOnce, total,
    ));

    // The ORDINAL travels with the item, so a card can carry a section anchor a
    // table of contents can point at. It is a position in this render and
    // nothing else — no object id reaches the markup.
    const item = (o: BoundValues, k: number): string => {
      const root = templateFor(n, o)?.root ?? tpl.root;
      const listItem = { position: k + 1, size: total };
      const rendered = node(root, o, true, k, listItem);
      // Cards already own their <li>. Every other legal item root still needs
      // one: a field, media, region, capability or slot directly under <ul>
      // is invalid list markup and has nowhere to carry set metadata.
      return ir.nodes[root]?.kind === 'card'
        ? rendered
        : `<li class="m-collection-item"${objectAttrs(o, root)}`
          + ` aria-setsize="${total}" aria-posinset="${k + 1}">${rendered}</li>`;
    };
    /**
     * THE EXPLORER'S FOLDERS — decision 0055, the ninth verdict's *"explorer
     * hierarchy with nested folders."* A `groupBy` on the file list is the
     * vault's partition, and inside an explorer pane it is drawn as a TREE:
     * one folder per group under the vault's own row, each a disclosure
     * button the host toggles (`data-ws-fold`, its state in
     * `workspace.folded`) over the notes that belong to it. Folder names
     * sort as the app sorts them — A to Z — because that is the app's rule
     * for a file tree, not a taste the spec left open; the notes inside keep
     * the set's order. The ordinal and the set size stay collection-wide, so
     * a screen reader still counts one list of files.
     */
    const pane = ir.nodes[n.parent];
    const folderBy = pane?.kind === 'region' && pane.purpose === 'explorer'
      ? n.query?.groupBy : undefined;
    if (folderBy !== undefined) {
      /**
       * ONE LEVEL DEEPER — the twelfth verdict's nested folder. A
       * `subGroupBy` on the file list groups each folder's notes again;
       * a DATE role groups by its month (`2026-08`), which is how a vault
       * of dated notes is kept, rather than by the day. A folder's fold
       * key is its PATH (`Claude/2026-08`), so two months under two
       * authors fold apart. Inside a folder, sub-folders list first and
       * the notes with no sub value after them — the same rule as the
       * root's loose files.
       */
      const subBy = n.query?.subGroupBy;
      const keyOf = (o: BoundValues, by: Role): string => {
        const text = fmt(o[by]);
        return ROLE_KIND[by] === 'date' && /^\d{4}-\d{2}/.test(text) ? text.slice(0, 7) : text;
      };
      type Row = { o: BoundValues; k: number };
      const folded = new Set(workspace?.folded ?? []);
      const folder = (name: string, path: string, body: string): string => {
        const open = !folded.has(path);
        return `<li class="m-ws-tree-folder">`
          + `<button type="button" class="m-ws-folder-row" data-ws-fold="${esc(path)}" aria-expanded="${open}">`
          + `<span class="m-ws-chev" aria-hidden="true">${open ? '⌄' : '›'}</span>${esc(name)}</button>`
          + `<ul class="m-collection m-ws-folder-items m-${n.flow}" role="list" aria-label="${esc(path)}"${open ? '' : ' hidden'}>`
          + `${body}</ul></li>`;
      };
      // Rows by key, in first-seen order; the empty key is "loose".
      const partition = (rows: readonly Row[], by: Role): { groups: Map<string, Row[]>; loose: Row[] } => {
        const groups = new Map<string, Row[]>();
        const loose: Row[] = [];
        for (const r of rows) {
          const key = keyOf(r.o, by);
          if (!key) { loose.push(r); continue; }
          (groups.get(key) ?? groups.set(key, []).get(key)!).push(r);
        }
        return { groups, loose };
      };
      const leaves = (rows: readonly Row[]): string => rows.map((r) => item(r.o, r.k)).join('');
      const byName = ([a]: [string, Row[]], [b]: [string, Row[]]) => a.localeCompare(b);
      // A note with NO value for the folder role is a loose file at the
      // vault's root, not a member of a folder called "—": it lists after
      // the folders, as the app lists loose files (seen on a new note,
      // which has no author yet, filed under a dash).
      const top = partition(shown.map((o, k) => ({ o, k })), folderBy);
      const folders = [...top.groups].sort(byName).map(([name, rows]) => {
        if (subBy === undefined) return folder(name, name, leaves(rows));
        const inner = partition(rows, subBy);
        const subs = [...inner.groups].sort(byName)
          .map(([sub, subRows]) => folder(sub, `${name}/${sub}`, leaves(subRows))).join('');
        return folder(name, name, subs + leaves(inner.loose));
      }).join('');
      return `<ul class="m-collection m-as-${mode} m-${n.flow} m-ws-tree" role="list">${folders}${leaves(top.loose)}${more}</ul>`;
    }
    const items = shown.map(item).join('');
    return `<ul class="m-collection m-as-${mode} m-${n.flow}" role="list">${items}${more}</ul>`;
  };

  const table = (
    n: IRNode, tplRoot: number, rows: readonly BoundValues[], folds?: Rollups,
    asked: readonly { role: Role; fn: string }[] = [], total = rows.length,
  ): string => {
    const cols = columnsOf(ir, n);
    /**
     * CODA'S ROW BUTTON — task 0086.
     *
     * `table()` never called `invokeButton`, so a card declaring an
     * `invocation` drew a button when its collection presented as CARDS and
     * **drew nothing at all when it presented as a table** — silently, with no
     * refusal and no error. That is the presentation a person would put a row
     * button in, and *"buttons that run actions"* is in `ACCEPTANCE.md`'s Coda
     * row.
     *
     * It is a COLUMN rather than something tucked into the title cell, because
     * a table's contract is that a cell belongs to a column and a column has a
     * heading. A button living inside the Title cell would be announced as part
     * of the title, and the row would have a control no column accounts for —
     * `aria-rowcount` and the header row would both be describing a table with
     * one fewer thing in it than there is.
     *
     * From the TEMPLATE root, which is the same node `columnsOf` reads its
     * columns out of, so the button and the cells beside it come from one
     * declaration.
     */
    const rowAction = ir.nodes[tplRoot];
    const actionCol = rowAction?.invocation ? rowAction : undefined;
    /**
     * THE ROW'S OPEN CONTROL — @Codex's P1 on `27a1a65`, and task 0176's
     * missing half.
     *
     * A collection that declares `navigate` gets a hit target per item — as a
     * CARD. `.m-card-hit` is emitted on the card path only, and a table
     * renders `tr`/`td`, so the branch was never taken and the declared
     * affordance had no presentation at all. Measured on Coda's table: 0 hit
     * targets, 0 links, 0 buttons in a row, every cell an editing control
     * since 0176's first pass made the table typeable. A real click therefore
     * types or opens a chooser, and the row page the generator can now emit
     * was reachable by nobody.
     *
     * @Codex's ruling, which is also the answer to the question I put to
     * them: *"add a generic focusable per-row Open control outside the
     * editable cells."*
     *
     * LEADING, not trailing. Every cell in the row is already tabbable, so a
     * control appended at the end would put Open after six editing stops in
     * four looks at once; at the front it is the first thing a keyboard
     * reaches on the row, which is also where Coda and Notion draw theirs.
     *
     * It needs no click handler of its own: the `tr` already carries
     * `objectAttrs`, so the host's existing anchor walk resolves a press to
     * THIS object, exactly as it does from a card's hit. And it is a real
     * `button`, so it is not in the host's `NOT_THE_CARD` list — the elements
     * that must never resolve to the card — because unlike a chooser or a
     * transport, opening the row IS the card's own gesture.
     */
    /**
     * The control stands only where it can RESOLVE — @Codex's P2 on 49cc0e3.
     *
     * That principle is unchanged; what "can resolve" MEANS has changed. The
     * condition used to be `edit !== undefined`, because a press is resolved
     * by walking up to the row's `data-morph-o` and `objectAttrs` emitted it
     * only under a write grant — so under `?viewer=reader` the anchor was
     * absent, a press reached nothing, and withholding the button was right.
     *
     * Decision 0062 removed that coupling: the handle is emitted
     * unconditionally and only the epoch waits on a grant, because navigation
     * is not a write. A reader's row therefore CAN resolve, and withholding
     * the control now would leave the table and the card hits following two
     * rules that disagree — a reader who can open an object from a search hit
     * and not from the table it lives in.
     *
     * What survives is the per-OBJECT half below: `rowOpens` still requires a
     * handle, which is the fact that can differ row by row.
     */
    /* FROM THE TEMPLATE TOO — @Codex's P2 on `d881f5b`. This read the
       COLLECTION alone, and the card path reads the CARD: `interactive` is
       `others.length > 0` over the item root's own affordances, so a spec
       declaring `navigate` only on its item card drew a working `.m-card-hit`
       as cards and, switched to a table, lost navigation entirely — no Open
       column and no hit target, because a table renders `tr`/`td` and never
       takes the card branch.

       Measured before the fix, one spec rendered twice:

           navigate on the collection   3 m-kind-open   the column stands
           navigate on the card         0 m-kind-open   nothing at all

       `rowAction` is the same node `columnsOf` reads its columns out of, and
       the comment above it already says the button and the cells beside it
       must come from one declaration. This is that sentence applied to the
       button's own existence rather than only to its label. */
    const opens = n.affordances?.includes('navigate') === true
      || rowAction?.affordances?.includes('navigate') === true;
    const OPEN_LABEL = EFFECT_LABEL['navigate'] ?? 'Open';
    /**
     * What NAMES a row, and it is not always the title column — @Codex's P2.
     *
     * A card may be named by a `subtitle` binding: compilation sets
     * `nameFrom` and the row is perfectly nameable, but a lookup for
     * `role === 'title'` finds nothing and every control in the table gets
     * the identical name "Open". A dozen buttons a screen-reader user cannot
     * tell apart is the defect this label exists to avoid.
     *
     * So: the card's declared `nameFrom` first — the same value the row
     * button uses — then the title column, then the row's ordinal, which is
     * never nothing.
     */
    const nameFrom = rowAction?.nameFrom;
    const titleCol = cols.find((c) => c.role === 'title' && !c.derive && !c.lookup);
    /** The column that carries the row's NAME, resolved once for the whole
     *  table: the card's declared `nameFrom` where a column draws that role,
     *  and the title column otherwise. Marked on the cell as `data-m-name`
     *  so a look can emphasise it — or count from it — without the renderer
     *  knowing the look and without the look guessing the role. */
    const nameCol = (nameFrom !== undefined && cols.some((c) => c.role === nameFrom && !c.derive && !c.lookup))
      ? nameFrom : titleCol?.role;
    const openHead = opens
      // Named from the same closed effect vocabulary the action column uses.
      // A blank `th` leaves the column unannounced.
      // The name is in a VISUALLY HIDDEN span, not hidden by a rule on the
      // `th`. Measured: `font-size:0` on `.m-as-table th.m-kind-open` lost to
      // `.m-app-coda .m-as-table th` — equal specificity, later in the sheet —
      // so the word "Open" stood in the header row where the reference has no
      // header at all. A look's own `th` rule will always be able to win that
      // fight; markup cannot be overridden by one.
      ? `<th scope="col" class="m-kind-open">`
        + `<span class="m-hidden-label">${esc(EFFECT_LABEL['navigate'] ?? 'Open')}</span></th>`
      : '';
    const actionHead = actionCol
      // Named by what the button DOES, from the closed effect vocabulary — the
      // same string the buttons under it carry. A blank `<th>` would leave the
      // column unannounced, and "Actions" is a word nobody declared.
      ? `<th scope="col" class="m-kind-action">`
        + `${esc(EFFECT_LABEL[EFFECT_ORDER.find((e) =>
            actionCol.invocation!.effects.includes(e)) ?? ''] ?? 'Run')}</th>`
      : '';
    // A DERIVED column is headed by what it computes and aligned by what that
    // yields — "Words", right-aligned with the integers — not by the role it
    // read from. A Words column headed "Body" would be a table saying one thing
    // and showing another, which is the defect this file keeps recording.
    // A LOOKUP column is aligned by what it READS on the far side — a related
    // row's number is a number — and headed by that role QUALIFIED BY THE
    // RELATION it came through.
    //
    // Qualified unconditionally, never only when it collides. A table showing
    // its own title beside a related object's title would otherwise put two
    // different columns under one heading with only their order telling them
    // apart — the exact defect `DERIVATION_LABELS` records having been found by
    // a gate assertion that looked a column up by its header and got the wrong
    // one. And conditional disambiguation was refused in #138 for a reason that
    // holds here too: a name whose shape depends on what some other column
    // happens to be is a name that changes when unrelated data changes.
    const colKind = (c: IRNode) =>
      c.lookup ? ROLE_KIND[c.lookup]
        : c.derive ? DERIVATION_YIELDS[c.derive] : ROLE_KIND[c.role!];
    const colName = (c: IRNode) =>
      c.lookup ? `${nameOf(c.lookup)} (${nameOf(c.role!)})`
        : c.derive ? DERIVATION_LABELS[c.derive] : nameOf(c.role!);
    const head = openHead + cols.map((c) =>
      `<th scope="col" class="m-kind-${colKind(c)}">`
      + `${esc(colName(c))}</th>`).join('') + actionHead;
    const body = rows.map((o, k) => {
      const cells = cols.map((c) => {
        const kind = colKind(c);
        // A derived cell is never editable — the spec layer refuses a binding
        // that both derives and writes, so `isEditable` is already false here.
        // Computed rather than read, and stated explicitly because a table cell
        // is the one place a value with no inverse could quietly get a caret.
        if (c.derive) {
          const v = derived(c.derive, o[c.role!]);
          return `<td class="m-kind-${kind}"><div class="m-cell m-derived">`
               + `${v === undefined ? '' : esc(String(v))}</div></td>`;
        }
        // A lookup cell is never editable either, and for a stronger reason
        // than a derived one: the value belongs to another object entirely, so
        // a caret here would be a write aimed at a row that is not on screen.
        // The spec layer refuses `read-write` beside a lookup, so `isEditable`
        // is already false — said out loud because a table cell is the one
        // place a value with no inverse could quietly get one.
        if (c.lookup) {
          const vals = lookedUp(c, o);
          return `<td class="m-kind-${kind}"><div class="m-cell m-lookup">`
               + `${esc(vals.join(', '))}</div></td>`;
        }
        const attrs = editAttrs(c, o);
        // A table cell is chosen from too. Notion's table view has select
        // cells, and routing only the card path through `chooser` would have
        // meant the board could set a status and the table of the same objects
        // could not — one collection, two answers to whether a property is
        // writable, decided by which presentation the shape happened to derive.
        // The cell carries its ROLE as a data attribute beside its kind, so
        // a look can style an author cell (an avatar before the name)
        // without the renderer knowing the look. An attribute, not an
        // `m-role-` class: those carry the generic role styling (a title's
        // size) and a cell wearing one would grow into a heading.
        const roleAttr = c.role ? ` data-role="${esc(c.role)}"` : '';
        /**
         * WHICH COLUMN IS THE ROW'S NAME — @Codex's P2 on `ed4f7a7`.
         *
         * Three looks emphasise the name column, and Fibery counts its rows
         * from it, by matching `data-role="title"`. A card named by a
         * `subtitle` binding sets `nameFrom` to that role instead: the row is
         * perfectly nameable, the Open control resolves it correctly since
         * the previous round, and the COLUMN presentation goes on hard-coding
         * a different role — so the wrong column is bolded and Fibery's
         * counter never increments, taking its row numbers with it.
         *
         * The renderer knows which column names the row and the stylesheet
         * cannot, so the renderer says so. A marker rather than a class, for
         * the same reason `data-role` is one: an `m-role-` class carries the
         * generic role styling and a cell wearing one grows into a heading.
         */
        const nameAttr = c.role !== undefined && c.role === nameCol ? ' data-m-name=""' : '';
        // A RELATION cell is its chips — the same `refField` a card draws,
        // one button per target opening what it points at, operable here
        // because a row is not a button. Before this branch a ref column
        // printed `String([{…}])`, which is "[object Object]": no harness
        // had ever read one. Fibery's table is the first look whose point
        // is that an assignee is an entity, so it is the first to need it.
        if (kind === 'ref') {
          return `<td class="m-kind-ref"${roleAttr}${nameAttr}>${refField(c, o)}</td>`;
        }
        if (kind === 'enum' && isEditable(c, o)) {
          return `<td class="m-kind-enum"${roleAttr}${nameAttr}>${chooser(c, o[c.role!])}</td>`;
        }
        // The editable element is INSIDE the cell, not the cell itself: a <td>
        // carrying role="textbox" stops being a table cell, and the row/column
        // relationship is most of what makes a table readable without sight.
        const hue = kind === 'enum' && fmt(o[c.role!]) !== ''
        ? hueStyle(fmt(o[c.role!])) + toneAttr(c.role as Role, fmt(o[c.role!])) : '';
        const inner = isEditable(c, o)
          ? `<div class="m-cell m-editable"${attrs}${hue}>${esc(fmt(o[c.role!]))}</div>`
          : `<div class="m-cell"${attrs}${hue}>${esc(fmt(o[c.role!]))}</div>`;
        return `<td class="m-kind-${kind}"${roleAttr}${nameAttr}>${inner}</td>`;
      }).join('');
      // The row's button, named by the row. `objectAttrs` on the `<tr>` is what
      // makes it land on THIS object: `readAnchor` walks up from the pressed
      // element to the nearest `data-morph-o`, which is the row, exactly as it
      // walks up to the card on the other path.
      const action = actionCol
        ? `<td class="m-kind-action">`
          + `${invokeButton(
              actionCol,
              actionCol.nameFrom ? fmt(o[actionCol.nameFrom]) : undefined,
              // The absolute row number, not the index in this window: the
              // table's own `aria-rowindex` counts the whole set, and a button
              // naming a different number from the row it sits in would be two
              // answers to "which row is this".
              { position: k + 1, size: total },
            )}`
          + `</td>`
        : '';
      // Named by the ROW, not by the column: "Open" six times over is six
      // controls a screen-reader user cannot tell apart. The title column's
      // value is the row's name where there is one.
      //
      // AND THE POSITION IS SAID EVERY TIME — @Codex's P2 on `03c446c`,
      // superseding the narrower fix task 0185 finding 5 first got. That one
      // gave the NAMELESS row a coordinate and left named rows alone, which
      // still leaves two rows that share a name announcing the identical
      // control: `Untitled` is one press of New away, twice. The Update
      // button three lines up already appends the position unconditionally,
      // and its comment says why in full -- a name whose SHAPE depends on
      // whether some other row happens to share a title is a name that
      // changes when unrelated data changes.
      //
      // So the vocabulary is the invoke button's, exactly: a subject and a
      // place when there is a subject, the place alone when there is not.
      const rowName = (nameFrom ? fmt(o[nameFrom]) : '')
        || (titleCol ? fmt(o[titleCol.role!]) : '');
      const rowPlace = `${k + 1} of ${total}`;
      /**
       * AND THE ANCHOR IS PER ROW, not per table — @Codex's P2 on `ed4f7a7`,
       * which is the reader-mode finding one dimension in.
       *
       * `opens` reads the node's affordances, a fact about the render.
       * `objectAttrs` emits `data-morph-o` only when `handleOf(obj)` returns
       * one, a fact about THIS OBJECT — and `BoundValues` makes the handle
       * optional, so a mixed feed with an unhandled row got a button whose
       * press resolves nothing. A dead control in one row of a table whose
       * other rows work is worse than the reader's, because nothing about it
       * looks different.
       *
       * This half is the one decision 0062 did NOT retire. The render-level
       * condition went with the coupling; the object-level one is about
       * whether this row has an identity at all, which no grant can supply.
       *
       * The CELL still stands. A row that drops the gutter is a row one cell
       * short of the header, which is the alignment defect the foot already
       * taught: the column is the table's, the control is the row's.
       */
      const rowOpens = opens && handleOf(o) !== undefined;
      const open = opens
        ? `<td class="m-kind-open">`
          + `<button type="button" class="m-row-open"`
          + ` aria-label="${esc(rowName
            ? `${OPEN_LABEL} ${rowName}, ${rowPlace}`
            : `${OPEN_LABEL}, ${rowPlace}`)}">`
          // The glyph is drawn here rather than through `ntGlyph`, which lives
          // in `capability.ts` and is not exported: a chevron, aria-hidden,
          // because the button's name is on the button.
          + `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"`
          + ` stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">`
          + `<path d="M9 6l6 6-6 6"></path></svg></button></td>`
        : '';
      const openCell = rowOpens ? open : (opens ? '<td class="m-kind-open"></td>' : '');
      // Row 1 is the header. Data rows retain their absolute positions even
      // when only the first window is in the DOM.
      return `<tr aria-rowindex="${k + 2}"${objectAttrs(o, tplRoot)}>${openCell}${cells}${action}</tr>`;
    }).join('');
    // The foot. One cell per column, and a column shows a total only if the
    // SPEC asked for that fold AND the host computed it. Anything else is
    // blank — never a partial sum computed from `rows`, which is a page.
    const footCells = cols.map((c) => {
      // EVERY fold for this column. The schema allows eight aggregate entries
      // and does not require one per role, so `find` silently dropped a second
      // fold on the same column — a spec asking for sum AND avg got sum, with
      // nothing saying the other was discarded. Found by @Codex.
      // Deduplicated by role AND function. The schema puts no uniqueness
      // constraint on its eight aggregate entries, so a spec repeating
      // `{ duration, sum }` printed the same total twice — a regression I
      // introduced replacing `find()` with `filter()` to fix a different bug.
      // Distinct folds on one column (sum AND avg) still both render. Found by
      // @Codex.
      // Never on a DERIVED column. A fold is computed by the store over stored
      // values, and a derived column shares its role with the stored one it
      // reads — so without this, a `sum` asked for `duration` would print that
      // sum under a "Year" column computed from a different role's date. The
      // store cannot fold what it has not stored, and pretending otherwise
      // would put a real number under the wrong heading.
      const parts = c.derive ? '' : asked
        .filter((a) => a.role === c.role)
        .map((a) => ({ a, v: folds?.values.get(foldKey(a.role, a.fn)) }))
        .filter((x): x is { a: typeof asked[number]; v: number } => x.v !== undefined)
        .map(({ a, v }) =>
          `<span class="m-foot-v">${esc(formatFold(a.role, a.fn, v))}</span>`
          + `<span class="m-foot-fn">${esc(a.fn)}</span>`)
        .join('');
      if (!parts) return '<td></td>';
      return `<td class="m-foot-cell m-kind-${ROLE_KIND[c.role!]}">${parts}</td>`;
    });
    // The foot spans the ACTION COLUMN too. A `<tfoot>` row one cell short of
    // its header is a ragged table: the browser leaves a gap under the last
    // column, and the `colspan` on the "over N objects" row stops reaching the
    // end of the table it is a caption for.
    //
    // The action column itself never carries a fold. A fold is the store
    // aggregating stored values, and a button is not a value.
    const width = cols.length + (actionCol ? 1 : 0) + (opens ? 1 : 0);
    // AND THE FOOT NEEDS THE GUTTER'S CELL — @Codex's P2 on 49cc0e3. The
    // header and every body row gained a leading cell and `footCells` still
    // began at the first data column, so a rollup landed under the gutter,
    // every later value shifted left, and the row was one cell short of the
    // header. Bumping `width` alone fixed the colspan and not the alignment.
    const footOpen = opens ? '<td class="m-kind-open"></td>' : '';
    if (actionCol) footCells.push('<td></td>');
    const foot = footCells.some((c) => c !== '<td></td>')
      // `n` from the fold, not `rows.length`: the number of things folded is
      // what makes a total legible, and it is not the number on screen.
      ? `<tfoot><tr class="m-foot" aria-rowindex="${total + 2}">${footOpen}${footCells.join('')}</tr>`
        + (folds ? `<tr class="m-foot-n" aria-rowindex="${total + 3}"><td colspan="${width}">`
          + `over ${folds.n} objects</td></tr>` : '')
        + `</tfoot>`
      : '';

    // aria-rowcount includes the header and any footer rows as well as every
    // data row, including rows outside this DOM window.
    const structuralRows = 1 + total + (foot ? (folds ? 2 : 1) : 0);
    return `<table class="m-collection m-as-table" aria-rowcount="${structuralRows}">`
         + `<thead><tr aria-rowindex="1">${head}</tr></thead>`
         + `<tbody>${body}</tbody>${foot}</table>`;
  };

  const board = (
    n: IRNode, tplRoot: number, items: readonly BoundValues[], folds?: Rollups,
    capped = false, asked: readonly { role: Role; fn: string }[] = [],
    total = items.length,
  ): string => {
    const by = n.query!.groupBy!;
    // Group order is the order groups are first seen, which is stable for a
    // sorted query and arbitrary otherwise. Sorting group names alphabetically
    // would be a taste decision the spec did not ask for.
    const groups = new Map<string, BoundValues[]>();
    for (const o of items) {
      const key = fmt(o[by]) || '—';
      (groups.get(key) ?? groups.set(key, []).get(key)!).push(o);
    }
    /**
     * The SECOND grouping, inside each column — Notion's and Coda's board and
     * table both do this, and the field has been in the schema, validated and
     * pooled, drawing nothing.
     *
     * The cards are wrapped in a sub-section per distinct value rather than
     * listed flat, so the column reads as *"Argentina / 1975, 1978"* instead of
     * six cards in one pile. Same first-seen order as the outer grouping and
     * for the same reason: sorting the names would be a taste decision the spec
     * did not ask for.
     *
     * NO COUNT on a sub-heading, ever. `folds.grouped` is computed for the
     * OUTER role only, so nothing folded describes a sub-group — and the
     * painted count is a page under virtualization, which is the
     * wrong-number-with-confidence this file has now been corrected for twice.
     * A number that is only sometimes right is worse here than no number: the
     * outer heading beside it is real, so a reader has no way to tell them
     * apart.
     */
    const sub = n.query?.subGroupBy;
    // LANES — the second grouping drawn as ROWS across the columns, a matrix
    // of lane × state, which is Fibery's swimlane board (the twenty-second
    // and twenty-seventh verdicts' largest structural gap). Asked for by
    // `flow: 'grid'` on a board that carries a `subGroupBy`; a `stack` board
    // keeps the sub-sections inside each column below. A lane's key is the
    // relation's TITLE when the role is a reference (a sprint is an object,
    // and `String(object)` is nobody's lane), the formatted value otherwise.
    // Fewer than two lanes is not a matrix: the board draws plain.
    const laneKey = (o: BoundValues): string => {
      const v = o[sub!];
      if (v && typeof v === 'object') {
        const titles = asObjects(v).map((x) => fmt(x['title'])).filter(Boolean);
        if (titles.length) return titles.join(', ');
      }
      return fmt(v) || '—';
    };
    const laneOrder: string[] = [];
    if (sub && n.flow === 'grid') for (const o of items) { const k = laneKey(o); if (!laneOrder.includes(k)) laneOrder.push(k); }
    // Lanes in their names' order, the unnamed lane last: a board's columns
    // take the enum's declared order, but a relation has none, and the order
    // the cards happened to arrive in is nobody's.
    laneOrder.sort((a, b) => (a === '—' ? 1 : b === '—' ? -1 : a.localeCompare(b)));
    const lanes = laneOrder.length >= 2;
    const laneLabel = (k: string) => k === '—' ? `No ${nameOf(sub!).toLowerCase()}` : k;
    // List positions correctly restart inside each group. Action names cannot:
    // a screen-reader user tabs across the whole board, so those positions are
    // collection-wide even when virtualization makes a group's total unknown.
    let actionPosition = 0;
    const cardsOf = (set: readonly BoundValues[], size?: number) =>
      set.map((o, k) => node(
        tplRoot,
        o,
        true,
        undefined,
        size === undefined ? undefined : { position: k + 1, size },
        { position: actionPosition += 1, size: total },
      )).join('');
    const stack = (members: readonly BoundValues[], setSize?: number): string => {
      const cards = (set: readonly BoundValues[], size = setSize) => cardsOf(set, size);
      if (!sub || lanes) return `<ul class="m-group-items" role="list">${cards(members)}</ul>`;
      const inner = new Map<string, BoundValues[]>();
      for (const o of members) {
        // The same key as a lane's: a reference names its sub-group by its
        // target's title here too, now that the validator admits one.
        const k = laneKey(o);
        (inner.get(k) ?? inner.set(k, []).get(k)!).push(o);
      }
      // ONE sub-group is not a sub-grouping. Drawing a lone heading over every
      // card in a column adds a row of chrome that divides nothing, which is
      // the same "reaching one level too wide" this branch keeps producing.
      if (inner.size < 2) return `<ul class="m-group-items" role="list">${cards(members)}</ul>`;
      return [...inner].map(([k, set]) =>
        `<div class="m-subgroup" role="group" aria-label="${esc(laneLabel(k))}">`
        + `<h4 class="m-subgroup-head">${esc(laneLabel(k))}</h4>`
        + `<ul class="m-group-items" role="list">${cards(
          set, capped ? undefined : set.length,
        )}</ul></div>`).join('');
    };

    const cols = [...groups].map(([key, members]) => {
      // Only when the fold describes THIS grouping. A groups map computed for
      // a different role matched by label alone, which is how a board gets a
      // confidently wrong total.
      const g = folds?.grouped?.by === by ? folds.grouped.groups.get(key) : undefined;
      // The GROUP's size, not the number painted. `members.length` was the
      // count here, which under virtualization is a page presented as a group
      // size — the same wrong-number-with-confidence this whole feature is
      // built to avoid, already shipped and unnoticed.
      // The group's real size, or NOTHING.
      //
      // `members.length` is the painted count, which under virtualization is a
      // page presented as a group total — the defect this whole feature exists
      // to prevent. Falling back to it when the fold describes a different
      // grouping reopened exactly that hole. It is honest only when nothing was
      // capped, because then painted and actual are the same number. Found by
      // @Codex, twice.
      const count = g?.n ?? (capped ? undefined : members.length);
      // LABELLED. A header showing `21h 6m` and `4.5` cannot say which was
      // summed and which averaged, while the table foot beside it does — the
      // same number deserves the same explanation in both places. Found by
      // @Codex.
      const rolled = asked
        .map((a) => ({ a, v: g?.values.get(foldKey(a.role, a.fn)) }))
        .filter((x): x is { a: typeof asked[number]; v: number } => x.v !== undefined)
        .map(({ a, v }) =>
          `<span class="m-group-roll">`
          + `<span class="m-roll-k">${esc(nameOf(a.role))} ${esc(a.fn)}</span> `
          + `${esc(formatFold(a.role, a.fn, v))}</span>`)
        .join('');
      // A column FOLDS to a rail — Fibery's collapsed "Resolved" column, the
      // twenty-second verdict's structural gap beside the swimlanes. Whether
      // it is folded right now is VIEW STATE (decision 0014): the host reads
      // the key off the section and sets `data-morph-collapsed`, exactly as
      // for a panel, and the toggle stays visible when folded so the state
      // is recoverable. The key names THIS board — the look and the board's
      // spec id (its node index when the spec gave it none) — then the
      // grouping role and the value: the first cut was role and value
      // alone, and folding Shipped on Morph's own Features board folded
      // Fibery's Shipped after a reload, because the store persists across
      // looks and both boards group by the same role. Found by @Codex.
      const fold = `board:${ir.template?.id ?? 'morph'}:${n.specId ?? `n${n.i}`}:${by}:${key}`;
      // WHICH SLOT this lane's value occupies -- task 0178, decision 0063.
      // A board column is the clearest case the tone exists for: Notion's
      // lanes took their tint, pill and dot from nth-child, so the colour was
      // a fact about WHERE the column sat. Group by another role, hide a lane,
      // reorder the options, or compose two looks over one pool and the same
      // status changes colour. The value is right here and always was.
      /* A CAPTION IS NOT A COLUMN, and until now both were `.m-group` —
         decision 0073's naming debt, and the one row the specificity sweep
         reports as such. With lanes this section holds no cards at all (see
         the `lanes ?` below): it is the caption of a column in a lane-by-column
         matrix, while without lanes the same element IS the column and holds
         its stack. One class for two components is what 0073 forbids
         inventorying, and it had already cost four CSS rules — a generic and a
         look each declaring a column and each retracting it for the thing that
         is not one.

         A MODIFIER rather than a rename, because the two really do share a
         supertype: both are a foldable section with a head, and the collapse
         rules and the host's fold key are written about exactly that. It is
         the same shape as `.m-region.m-row` against `.m-region.m-stack`. What
         changes is that a rule meaning COLUMN now says so. */
      return `<section class="m-group${lanes ? ' m-lane-caption' : ''}" aria-label="${esc(key)}"${toneAttr(by, key)} data-morph-collapse-key="${esc(fold)}">`
           + `<h3 class="m-group-head"><span class="m-group-name">${esc(key)}</span>`
           + `${count === undefined ? '' : `<span class="m-count">${count}</span>`}`
           + `${rolled}`
           + `<button type="button" class="m-group-toggle" data-morph-collapse="${esc(fold)}"`
           + ` aria-expanded="true" aria-label="Collapse ${esc(key)}">\u00ab</button></h3>`
           + (lanes ? '' : stack(members, g?.n ?? (capped ? undefined : members.length)))
           // "+ New page" at the foot of the column — the `create` affordance,
           // drawn only when the person may write: the host's create door
           // with this column's value preset, so a page made here lands here.
           // A look may omit this door where its reference does (0057): Fibery's
           // column heads carry the state's name alone, and its toolbar draws
           // the `New` the reference itself uses, so the capability moves
           // rather than going. `TEMPLATE_BOARD_HEAD` says which.
           + (n.affordances?.includes('create') && edit !== undefined
             && (ir.template === undefined || TEMPLATE_BOARD_HEAD[ir.template.id].create)
             ? `<button type="button" class="m-group-add" data-ws-action="new"`
               + ` data-ws-preset-role="${esc(by)}" data-ws-preset-value="${esc(key)}">`
               + `<span class="m-group-add-plus" aria-hidden="true">+</span> New page</button>`
             : '')
           + `</section>`;
    }).join('');
    if (lanes) {
      // The head row: the lane label's empty corner, then each column's head.
      // Then a row per lane: its label at the left, a cell per column holding
      // the cards in that lane AND that state — empty cells stay, as the
      // matrix's own shape (a lane with nothing in Review is a fact).
      const head = `<div class="m-lane m-lane-head"><div class="m-lane-label" aria-hidden="true"></div>${cols}</div>`;
      const rows = laneOrder.map((k) =>
        `<div class="m-lane" role="group" aria-label="${esc(laneLabel(k))}"><h4 class="m-lane-label">${esc(laneLabel(k))}</h4>`
        + [...groups].map(([key, members]) => {
          const cell = members.filter((o) => laneKey(o) === k);
          return `<div class="m-lane-cell" data-group="${esc(key)}" data-morph-collapse-key="${esc(`board:${ir.template?.id ?? 'morph'}:${n.specId ?? `n${n.i}`}:${by}:${key}`)}">`
               + `<ul class="m-group-items" role="list">${cardsOf(cell, capped ? undefined : cell.length)}</ul></div>`;
        }).join('') + `</div>`).join('');
      return `<div class="m-collection m-as-board m-as-lanes" role="group" style="--cols:${groups.size}" `
           + `aria-label="grouped by ${esc(nameOf(by))}, lanes by ${esc(nameOf(sub!))}, showing ${items.length} of ${total} objects">`
           + `${head}${rows}</div>`;
    }
    return `<div class="m-collection m-as-board" role="group" `
         + `aria-label="grouped by ${esc(nameOf(by))}, showing ${items.length} of ${total} objects">`
         + `${cols}</div>`;
  };

  const label = (n: IRNode, obj: BoundValues | undefined): string =>
    n.nameFrom && obj?.[n.nameFrom] ? ` aria-label="${esc(obj[n.nameFrom])}"` : '';

  const t = ir.tokens;
  const vars = [
    `--gap:${t.gapPx}px`,
    `--measure:${t.measureCh}ch`,
    `--leading:${t.leading}`,
    `--target:${t.minTargetPx}px`,
    `--edit-target:${t.editTargetPx}px`,
    `--edit-pad:${t.editPadPx}px`,
    ...t.scale.map((v, i) => `--s${i}:${v}px`),
    `--font:${fontStackFor(t.typography)}`,
    `--page:${s.page}`, `--raised:${s.raised}`, `--surface:${s.surface}`,
    `--surface-hover:${s.surfaceHover}`, `--border-subtle:${s.borderSubtle}`,
    `--border:${s.border}`, `--border-strong:${s.borderStrong}`,
    `--solid:${s.solid}`, `--text-muted:${s.textMuted}`, `--text:${s.text}`,
    `--accent:${s.accent}`, `--focus-ring:${s.focusRing}`,
    `--selection-accent:${s.selectionAccent}`,
  ].join(';');

  /**
   * THE VIEW-STATE CONTROLS — Coda's sliders and selects.
   *
   * Drawn ABOVE the lens rather than inside a region, because a slot drives
   * queries in SEVERAL regions: `Predicate.valueFrom` is an index into the
   * lens's slot list, so every region whose filter names slot 2 narrows when
   * slot 2 moves. A control living inside one region would say it belongs to
   * that region, which is the thing that makes this Coda's rather than a filter
   * box on one table.
   *
   * The value is not here and never will be: it is per-person, per-device view
   * state under decision 0014, so the host holds it and passes it as
   * `Scope.viewState`. This draws the control and names which slot it is; what
   * it currently reads is the host's business.
   *
   * `data-morph-vs` is the INDEX, because that is what `valueFrom` is. A name
   * would be a second identity for the same thing and the first free-string
   * channel back into a query.
   *
   * **The CAPTION comes from `labels` first, and that is not a nicety.** The
   * generator has to invent `slot.name` — it is required by the schema and it
   * derives from the vocabulary term, because a pooled spec must carry nothing
   * about the pool's contents. So the Films control read *"Date"* while every
   * other field in that pool read *"Released"*, and Notes and Tasks lost their
   * declared date labels the same way: one surface in the page ignoring the
   * host's own names for its roles. Measured by @Codex; task 0087.
   *
   * The authored name stays the fallback rather than `labelForRole`, because a
   * spec that arrived from somewhere else may have a name worth more than one
   * re-derived from the role here.
   */
  const slots = ir.viewState ?? [];
  const controls = slots.length
    ? `<div class="m-viewstate" role="group" aria-label="Filters">`
      + slots.map((slot, i) => {
        const kind = ROLE_KIND[slot.role];
        const caption = labels?.[slot.role] ?? slot.name;
        // A reference filter needs a scoped object picker which can return an
        // opaque ObjectId. A text box would look functional while comparing a
        // label to an id, so refuse that affordance explicitly until the host
        // supplies the picker contract.
        if (kind === 'ref') {
          return `<p class="m-vs-unavailable" role="note">${esc(caption)}: `
            + `reference filter unavailable — choose an object picker</p>`;
        }
        const type = kind === 'number' || kind === 'score' ? 'number'
          : kind === 'date' ? 'date' : 'text';
        // The input is nested in its label, so no document-global id is needed.
        // This remains valid when the same IR is rendered twice in one page.
        if (slot.op === 'between') {
          return `<fieldset class="m-vs-slot"><legend class="m-vs-name">${esc(caption)}</legend>`
            + `<input class="m-vs-input" type="${type}" data-morph-vs="${i}" `
            + `data-morph-vs-bound="0" aria-label="${esc(caption)} from">`
            + `<input class="m-vs-input" type="${type}" data-morph-vs="${i}" `
            + `data-morph-vs-bound="1" aria-label="${esc(caption)} to"></fieldset>`;
        }
        return `<label class="m-vs-slot">`
             + `<span class="m-vs-name">${esc(caption)}</span>`
             + `<input class="m-vs-input" type="${type}" data-morph-vs="${i}" `
             + `aria-label="${esc(caption)}"></label>`;
      }).join('')
      + `</div>`
    : '';

  const single = ir.nodes[ir.root]?.kind !== 'collection' ? objects[0] : undefined;
  // The template's id becomes a class the stylesheet lays the workspace out
  // by, and an attribute a person or a harness can read the provenance from.
  const app = ir.template
    ? ` m-app m-app-${ir.template.id.replace(/^app\./, '')}` : '';
  /**
   * A SHELL WITH A WINDOW BAND, keyed on what the spec CONTAINS rather than on
   * which app it is.
   *
   * The band's rules were written under `.m-app-obsidian` first and 0173's
   * ratchet refused them — correctly. Eight app-scoped selectors for a
   * component that belongs to every look is the exact shape the 2026-09-03
   * amendment forbids: a seventh app whose reference is a desktop window would
   * have needed its own eight. Keyed here instead, it declares the capability
   * and gets the arrangement.
   */
  /* THE SHELL THAT OWNS THE BAND, not any shell containing one anywhere —
     @Codex's P2 on 0d83f75. This scanned the WHOLE IR, so a workspace-window
     nested in a child region, or sitting in a polymorphic item template that
     was not the one selected, still marked the root. The generic rules below
     are written as m-shell-windowed > .m-region.m-row, so the marker would
     then turn a root row with no band of its own into a full-height grid
     whose first track is 36px, rearranging content that has no window
     controls in it at all.

     The immediate topology is the whole test, and it is what the CSS already
     says: the band must be a DIRECT child of the root region, which is the
     one element that selector can reach. Node 0 is the root. */
  const windowed = (ir.nodes[0]?.children ?? []).some((c) => {
    const n = ir.nodes[c];
    return n?.kind === 'capability' && n.capability === 'workspace-window';
  }) ? ' m-shell-windowed' : '';
  /* AND WHETHER THE BAND TOOK THE EXPLORER'S OWN PANE — @Codex's P2 on
     3b84c58, narrowed by their P2 on 4a34aef.

     A windowed shell is not the same claim as a shell whose tool strip has
     taken the sidebar's pane tabs: a look that declares the window and leaves
     its ribbon as ordinary commands, or has no ribbon at all, still needs the
     sidebar's own tabs and its New note.

     TWO THINGS WERE STILL TOO WIDE. The scan walked EVERY node, so a ribbon
     nested inside a child region — or one in a polymorphic branch that was
     not selected — marked the root, which is the same defect the `windowed`
     marker above was narrowed for and I did not carry across. And any
     non-empty `selects` counted, so a band mapping only `{ graph: 'graph' }`
     to a RIGHT-side pane suppressed the LEFT sidebar's controls that Graph
     had not replaced.

     The rules below reach the sidebar that HOLDS THE EXPLORER, so the marker
     says exactly that: the root band's ribbon selects the explorer's own
     purpose. A band that selects something else takes nothing from it. */
  const panesInBand = windowed !== '' && (ir.nodes[0]?.children ?? []).some((c) => {
    const n = ir.nodes[c];
    if (n?.kind !== 'capability' || n.capability !== 'workspace-ribbon') return false;
    const input = capabilityInputFor(capabilities, n.i, undefined);
    if (input?.capability !== 'workspace-ribbon') return false;
    return Object.values(input.ribbon.selects ?? {}).includes('explorer');
  }) ? ' m-shell-panes-in-band' : '';
  const appAttr = ir.template
    ? ` data-template="${esc(ir.template.id)}" data-template-version="${esc(ir.template.version)}"` : '';
  return `<div class="m-root${app}${windowed}${panesInBand}" style="${vars}" data-density="${ir.tokens.density}" `
       + `data-typography="${ir.tokens.typography}" data-palette="${ir.tokens.palette}" `
       + `data-scheme="${ir.template ? 'dark' : scheme}" data-viewport="${ir.viewportClass}"${appAttr}>`
       + `${controls}${node(ir.root, single)}</div>`;
}

/**
 * Baseline stylesheet.
 *
 * Reads only custom properties the IR resolved — it invents no colour, size or
 * spacing of its own, which is the same rule the generator works under. The one
 * thing it does own is *relationships*: that a table's header sits above its
 * rows, that a gallery tile's caption sits under its image. Those are facts
 * about the shape, not numbers, and they are the renderer's job.
 */
/** What a workspace pane is called to a reader — the landmark name. */
const PANE_LABELS: Readonly<Record<Exclude<RegionPurpose, 'relations'>, string>> = {
  explorer: 'Files',
  editor: 'Editor',
  backlinks: 'Backlinks',
  properties: 'Properties',
  sidebar: 'Sidebar',
  graph: 'Graph',
  search: 'Search',
};

/** The sidebar tab glyphs — one 24-box stroke path per pane, the app's
 *  own vocabulary (a folder, a magnifier, a link, a small graph). A pane
 *  without a glyph here draws a generic box, which a viewed shot catches. */
const PANE_GLYPHS: Readonly<Record<Exclude<RegionPurpose, 'relations'>, string>> = {
  explorer: 'M3 7h6l2 2h10v10H3z',
  editor: 'M4 4h16v16H4zM8 9h8M8 13h8M8 17h5',
  backlinks: 'M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1',
  properties: 'M4 6h16M4 12h16M4 18h10',
  sidebar: 'M4 4h16v16H4zM9 4v16',
  graph: 'M6 17a2 2 0 1 0 .01 0M18 7a2 2 0 1 0 .01 0M18 17a2 2 0 1 0 .01 0M7.5 15.5l9-7M8 17h8',
  search: 'M11 4a7 7 0 1 0 0 14a7 7 0 0 0 0-14zM20 20l-4-4',
};

/**
 * The surfaces a NAMED APP LOOK declares — decision 0055's "versioned visual
 * tokens". Read from the reference (`docs/references/canon-looks.md`,
 * Obsidian: near-black `#1e1e1e` page, panes a shade apart, 1px borders a
 * shade lighter still, text `#dcddde`, violet `#a882ff` for links, tags and
 * the active state). These are the template's, not a palette's: the derived
 * ramp cannot produce Obsidian's violet from a neutral hue, and it should not
 * try — the look is a fact about the app being reproduced.
 */
/**
 * What a look's BOARD COLUMN HEAD carries — decision 0057, applied to task
 * 0172.
 *
 * The generic head draws the state's name, its count, the fold toggle, and a
 * create door with that column's value preset. Fibery's reference draws the
 * name and nothing else: no count, no per-column plus. 0057 rules that a look
 * *"may select, arrange, or omit truthful host-backed presentation"* provided
 * *"a capability omitted at one reference location must remain reachable
 * elsewhere when the reference supplies an equivalent backed location"* — and
 * Fibery's toolbar draws the `New` the reference itself uses, so the create
 * door is relocated rather than removed.
 *
 * A record rather than a branch, for task 0173's reason: a seventh app whose
 * heads are bare adds a row here.
 *
 * The COUNT is a stylesheet rule rather than an emit-time decision, and that
 * is forced rather than chosen: 0057 keeps the count on a FOLDED rail, which
 * is the one place the reference draws one — and whether a column is folded
 * right now is view state the host applies after paint (0014), so the renderer
 * cannot know it here. `create` has no such dependency and is decided here.
 */
const TEMPLATE_BOARD_HEAD: Readonly<Record<AppTemplate, { readonly create: boolean }>> = {
  'app.obsidian': { create: true },
  'app.notion': { create: true },
  'app.coda': { create: true },
  'app.word': { create: true },
  'app.fibery': { create: false },
  'app.fabric': { create: true },
};

const TEMPLATE_SURFACES: Readonly<Record<AppTemplate, Surface>> = {
  /*
   * `raised` was #202020 until 2026-09-04 — @Codex's finding 4 in the
   * Obsidian viewed comparison, and the sharpest token result of the six
   * passes. Our editor ground is EXACTLY the reference's and the
   * construction is right (separate fills, not border-only separation);
   * the raised panes were simply a quarter of the reference's lift:
   *
   *   reference   #1e1e1e -> #262626   +8
   *   ours        #1e1e1e -> #202020   +2
   *
   * Re-measured here as the MODE over a patch rather than a single pixel,
   * because one sample can land on text, a border or an antialiased edge
   * and report a colour nothing is painted: #262626 on 1623 of 1800 pixels
   * of the explorer pane, and again on the tab bar's ground.
   *
   * A TOKEN, which is why this is one line rather than a sweep: every
   * `var(--raised)` consumer in the look takes it, and the 2026-09-03
   * amendment asks for exactly this shape.
   */
  'app.obsidian': {
    page: '#1e1e1e', raised: '#262626', surface: '#2a2a2a', surfaceHover: '#303030',
    borderSubtle: '#2c2c2c', border: '#363636', borderStrong: '#4a4a4a',
    solid: '#a882ff', textMuted: '#9a9a9a', text: '#dcddde',
    accent: '#a882ff', focusRing: '#a882ff', selectionAccent: '#a882ff',
  },
  // Notion is LIGHT, and that is a fact about the app: white page, a warm
  // near-white sidebar, near-black text, hairline warm-grey borders, one
  // blue for the New button. Read from the pinned reference.
  'app.notion': {
    page: '#ffffff', raised: '#f7f7f5', surface: '#f1f1ef', surfaceHover: '#ebebea',
    borderSubtle: '#efefed', border: '#e9e9e7', borderStrong: '#d3d3d1',
    solid: '#2383e2', textMuted: '#787774', text: '#37352f',
    accent: '#2383e2', focusRing: '#2383e2', selectionAccent: '#2383e2',
  },
  // CODA, decision 0055's third look: white doc, a light grey page tree,
  // near-black text, hairline borders, Coda's red for its buttons. Read
  // from the pinned reference.
  'app.coda': {
    page: '#ffffff', raised: '#f7f7f8', surface: '#f1f1f3', surfaceHover: '#e9e9ec',
    borderSubtle: '#ededef', border: '#e3e3e6', borderStrong: '#cfcfd4',
    solid: '#e4543f', textMuted: '#6f7075', text: '#1f1f22',
    // The one look measured using two accents at once. Its buttons are
    // red and its lit page-tree row is blue, which is why selection is a
    // role of its own rather than a synonym for accent.
    accent: '#e4543f', focusRing: '#e4543f', selectionAccent: '#2f6ee0',
  },
  // WORD, decision 0055's fourth look: a white sheet on a pale blue-grey
  // desk, light chrome, Word's blue for the active tab. Read from the
  // pinned reference.
  'app.word': {
    page: '#ffffff', raised: '#f3f3f3', surface: '#e6e9ee', surfaceHover: '#dde2e9',
    borderSubtle: '#e1e1e1', border: '#d0d0d0', borderStrong: '#b8bcc4',
    solid: '#185abd', textMuted: '#616161', text: '#1f1f1f',
    accent: '#185abd', focusRing: '#185abd', selectionAccent: '#185abd',
  },
  // FIBERY, decision 0055's fifth look: white everywhere — the sidebar is
  // white too — near-black dense text, cool hairline borders, Fibery's
  // orange for the lit controls. Read from the pinned references.
  'app.fibery': {
    page: '#ffffff', raised: '#fafafa', surface: '#f3f4f6', surfaceHover: '#ececef',
    borderSubtle: '#ececef', border: '#e2e3e7', borderStrong: '#cfd1d6',
    solid: '#f2762b', textMuted: '#6b6f76', text: '#1c1c1e',
    accent: '#f2762b', focusRing: '#f2762b', selectionAccent: '#f2762b',
  },
  // FABRIC, decision 0055's sixth look: a white window, near-black 16px
  // prose, the title in muted grey, the panel's greys, Fabric's lilac for
  // the one lit control. Read from the pinned reference.
  'app.fabric': {
    page: '#ffffff', raised: '#f7f7f8', surface: '#f2f2f4', surfaceHover: '#e9e9ec',
    borderSubtle: '#efefef', border: '#e6e6e8', borderStrong: '#d2d2d6',
    solid: '#6b5cff', textMuted: '#8a8a8f', text: '#1a1a1a',
    accent: '#6b5cff', focusRing: '#6b5cff', selectionAccent: '#6b5cff',
  },
};

export const BASE_CSS = `
.m-viewstate{display:flex;flex-flow:row wrap;gap:.6rem;align-items:flex-end;
  margin-block-end:.9rem;padding-block-end:.6rem;
  border-block-end:1px solid var(--border-subtle)}
.m-vs-slot{display:flex;flex-direction:column;gap:.2rem}
.m-vs-name{font-size:calc(var(--s0) * .78);color:var(--text-muted);
  letter-spacing:.02em}
.m-vs-input{font:inherit;font-size:calc(var(--s0) * .9);color:var(--text);
  background:var(--page);border:1px solid var(--border);border-radius:.35rem;
  padding:.3rem .45rem;min-height:var(--edit-target);min-width:9rem}
.m-vs-input:focus-visible{outline:2px solid var(--focus-ring);outline-offset:1px}
/* THE CATEGORICAL TONE SLOTS — decision 0063, and generic on purpose.
   A look must be able to become a seventh app by repointing tokens rather
   than by gaining rules, which is what 0173 measures; six per-look rules
   times six looks is thirty-six, and the ratchet caught exactly that when
   these were written under .m-app-coda. So the MAPPING from a slot to a pair
   of tokens is generic and the VALUES are a look's own.

   Defaults here so no look reads a property only another look defines: a
   generic default a look re-points is the shape 0173 asks for.

   ALL TWELVE SLOTS ARE THEIR OWN — @Codex, P2 on 57138aa, and it is this
   file's own argument turned on itself. The mapping used to pair 6 with 0,
   7 with 1 and so on IN THE SELECTOR, so a seventh or composed look declaring
   twelve distinct categorical colours could not express half of them however
   many tokens it supplied: it would have needed six new rules, which is the
   thing 0173 measures and this comment claims to prevent.

   So the fold happens in the VALUES instead. Slots 6 to 11 default to their
   0-to-5 counterparts, which is what a six-colour look wants and is exactly
   the behaviour the paired selectors gave; a look with twelve says twelve and
   is heard. A default is a value a look re-points. A selector is not.

   THE NAV ROW IS A SUBJECT TOO — @Codex's P2 on 2e06944. The mapping reached
   cells and choosers only, so Fibery's rail carried six app-scoped rules of
   its own mapping the same twelve slots to a private property and six
   literals: a composed or seventh look using the same navigation-row
   component got no categorical paint at all unless it wrote another six.
   One mapping, three subjects, and a look supplies only the values. */
/* :where() SO A LOOK WINS ON SPECIFICITY, NOT ON SOURCE ORDER — task 0203,
   decision 0058, found by the sweep 0069 required and committed the same day
   these defaults were. Both this block and the typography one below sit on
   the m-root class, and the root element carries m-root, m-app and its own
   m-app-name class together, so a look's
   own m-app-name block is (0,1,0) against (0,1,0) on THE SAME
   ELEMENT. The look wins today only because its block is later in the sheet,
   which is exactly what 0058 forbids: nothing renders wrong and nothing would,
   until somebody moves a block.

   The where pseudo-class contributes zero, so these become (0,0,0) and every look beats
   them by being a look. That is what a DEFAULT should have been all along —
   the same argument the tone mapping already makes for folding slots 6-11 into
   values rather than selectors. */
:where(.m-root){--tone-0-bg:var(--surface);--tone-0-fg:var(--text);--tone-0-soft:var(--tone-0-bg);--tone-0-ink:var(--tone-0-fg);
  --tone-1-bg:var(--surface);--tone-1-fg:var(--text);--tone-1-soft:var(--tone-1-bg);--tone-1-ink:var(--tone-1-fg);
  --tone-2-bg:var(--surface);--tone-2-fg:var(--text);--tone-2-soft:var(--tone-2-bg);--tone-2-ink:var(--tone-2-fg);
  --tone-3-bg:var(--surface);--tone-3-fg:var(--text);--tone-3-soft:var(--tone-3-bg);--tone-3-ink:var(--tone-3-fg);
  --tone-4-bg:var(--surface);--tone-4-fg:var(--text);--tone-4-soft:var(--tone-4-bg);--tone-4-ink:var(--tone-4-fg);
  --tone-5-bg:var(--surface);--tone-5-fg:var(--text);--tone-5-soft:var(--tone-5-bg);--tone-5-ink:var(--tone-5-fg);
  --tone-6-bg:var(--tone-0-bg);--tone-6-fg:var(--tone-0-fg);--tone-6-soft:var(--tone-0-soft);--tone-6-ink:var(--tone-0-ink);
  --tone-7-bg:var(--tone-1-bg);--tone-7-fg:var(--tone-1-fg);--tone-7-soft:var(--tone-1-soft);--tone-7-ink:var(--tone-1-ink);
  --tone-8-bg:var(--tone-2-bg);--tone-8-fg:var(--tone-2-fg);--tone-8-soft:var(--tone-2-soft);--tone-8-ink:var(--tone-2-ink);
  --tone-9-bg:var(--tone-3-bg);--tone-9-fg:var(--tone-3-fg);--tone-9-soft:var(--tone-3-soft);--tone-9-ink:var(--tone-3-ink);
  --tone-10-bg:var(--tone-4-bg);--tone-10-fg:var(--tone-4-fg);--tone-10-soft:var(--tone-4-soft);--tone-10-ink:var(--tone-4-ink);
  --tone-11-bg:var(--tone-5-bg);--tone-11-fg:var(--tone-5-fg);--tone-11-soft:var(--tone-5-soft);--tone-11-ink:var(--tone-5-ink)}
:is(.m-cell,select.m-choose,.m-nav-row,.m-group)[data-m-tone="0"]{--tone-bg:var(--tone-0-bg);--tone-fg:var(--tone-0-fg);--tone-soft:var(--tone-0-soft);--tone-ink:var(--tone-0-ink)}
:is(.m-cell,select.m-choose,.m-nav-row,.m-group)[data-m-tone="1"]{--tone-bg:var(--tone-1-bg);--tone-fg:var(--tone-1-fg);--tone-soft:var(--tone-1-soft);--tone-ink:var(--tone-1-ink)}
:is(.m-cell,select.m-choose,.m-nav-row,.m-group)[data-m-tone="2"]{--tone-bg:var(--tone-2-bg);--tone-fg:var(--tone-2-fg);--tone-soft:var(--tone-2-soft);--tone-ink:var(--tone-2-ink)}
:is(.m-cell,select.m-choose,.m-nav-row,.m-group)[data-m-tone="3"]{--tone-bg:var(--tone-3-bg);--tone-fg:var(--tone-3-fg);--tone-soft:var(--tone-3-soft);--tone-ink:var(--tone-3-ink)}
:is(.m-cell,select.m-choose,.m-nav-row,.m-group)[data-m-tone="4"]{--tone-bg:var(--tone-4-bg);--tone-fg:var(--tone-4-fg);--tone-soft:var(--tone-4-soft);--tone-ink:var(--tone-4-ink)}
:is(.m-cell,select.m-choose,.m-nav-row,.m-group)[data-m-tone="5"]{--tone-bg:var(--tone-5-bg);--tone-fg:var(--tone-5-fg);--tone-soft:var(--tone-5-soft);--tone-ink:var(--tone-5-ink)}
:is(.m-cell,select.m-choose,.m-nav-row,.m-group)[data-m-tone="6"]{--tone-bg:var(--tone-6-bg);--tone-fg:var(--tone-6-fg);--tone-soft:var(--tone-6-soft);--tone-ink:var(--tone-6-ink)}
:is(.m-cell,select.m-choose,.m-nav-row,.m-group)[data-m-tone="7"]{--tone-bg:var(--tone-7-bg);--tone-fg:var(--tone-7-fg);--tone-soft:var(--tone-7-soft);--tone-ink:var(--tone-7-ink)}
:is(.m-cell,select.m-choose,.m-nav-row,.m-group)[data-m-tone="8"]{--tone-bg:var(--tone-8-bg);--tone-fg:var(--tone-8-fg);--tone-soft:var(--tone-8-soft);--tone-ink:var(--tone-8-ink)}
:is(.m-cell,select.m-choose,.m-nav-row,.m-group)[data-m-tone="9"]{--tone-bg:var(--tone-9-bg);--tone-fg:var(--tone-9-fg);--tone-soft:var(--tone-9-soft);--tone-ink:var(--tone-9-ink)}
:is(.m-cell,select.m-choose,.m-nav-row,.m-group)[data-m-tone="10"]{--tone-bg:var(--tone-10-bg);--tone-fg:var(--tone-10-fg);--tone-soft:var(--tone-10-soft);--tone-ink:var(--tone-10-ink)}
:is(.m-cell,select.m-choose,.m-nav-row,.m-group)[data-m-tone="11"]{--tone-bg:var(--tone-11-bg);--tone-fg:var(--tone-11-fg);--tone-soft:var(--tone-11-soft);--tone-ink:var(--tone-11-ink)}
:where(.m-root){font-family:var(--font);font-size:var(--s0);line-height:var(--leading);
  color:var(--text);background:var(--page);
  -webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility}
.m-region{display:flex;gap:var(--gap)}

/* THE APP SHELL'S LAYOUT HYGIENE, and it was five looks writing one rule --
   task 0173. A flex or grid child defaults to min-width and min-height auto,
   which refuses to shrink below its content, so a shell column with a long
   table in it pushes the window wider instead of scrolling inside itself.
   Every look that opens a full-window shell wants that off; none of them was
   expressing a taste by saying so.

   Scoped to m-app rather than m-root because it is about a look's shell and
   not about a card on the hub, and this is the first generic rule to use that
   class -- it means A LOOK IS OPEN, which is exactly the condition the five
   app-scoped copies were each restating under their own name.

   WHERE, not a bare class, so it lands at 0-1-0 -- 0058's direction, and
   0073's requirement. Any look rule that can reach these elements has to name
   the region as well as the app, so it is 0-2-0 or more and wins; a look rule
   that somehow reached them at 0-1-0 would TIE, and the tie is what
   e2e/specificity-sweep.mjs fails on at a ceiling of zero rather than
   something this comment can promise. The first version wrote
   .m-app and tied at 0-2-0 with two rules the resting capture cannot see:
   Fabric's own answer below, and Obsidian's sidebar. A tie is settled by
   position in the sheet, which nobody chose. Fabric is the look that did not
   stretch, and it says so below rather than being carried by the absence of a
   declaration. */
:where(.m-app)>.m-region>*{min-width:0;min-height:0;align-self:stretch}

/* THE SHELL'S INNER COLUMN, and the same five looks wrote this one too. A
   column inside the app's band carries the page's ground and no gap of its
   own: the panes inside it draw their own separation. Both values are
   already tokens, which is what makes this a rule rather than five.

   WHERE around the SUBJECT as well as around the app, so the whole selector
   is 0-1-0 rather than 0-3-0. That matters here and did not for the rule
   above: Word's editor is a .m-region.m-stack grandchild too, and it takes
   its ground from .m-app-word .m-ws-editor at 0-2-0. A generic rule at
   0-3-0 would repaint it. At 0-1-0 every look's own answer wins, which is
   what a promotion is supposed to leave intact. */
:where(.m-app)>.m-region>:where(.m-region.m-stack){gap:0;background:var(--page)}

/* THE APP'S BAND IS A GRID WITH NO GAP, and all five shell looks said so
   under their own names. WHICH grid is the taste -- the tracks differ in
   every one of them -- but that it IS a grid, and that the columns meet with
   no gutter, is the arrangement itself. A look's own rule is 0-3-0 and this
   is 0-2-0, so every look still owns its tracks.

   Obsidian's band rule is 0-2-0 as well, being written with WHERE around the
   app class, so it would have TIED this one on both properties. It no longer
   declares either: the shared half is here and its rule keeps only what is
   its own. A tie decided by position in the sheet is what 0073 puts at a
   ceiling of zero, and two rules agreeing on a value does not make the tie
   any less undeclared. */
/* AND THE BAND FILLS THE VIEWPORT. Four looks declared height:100vh
   privately; Obsidian did not, because the windowed-shell rule further down
   already carries it for that look -- which is why this is stated here as
   well rather than instead.

   The two rules are the same declaration for DIFFERENT POPULATIONS, both at
   0-2-0, and they overlap on Obsidian. That is deliberate and it is not the
   tie 0073 forbids, which is a generic beating a LOOK by source order.
   m-shell-windowed keys on the spec containing a workspace-window and m-app
   on ir.template, independently, so neither rule covers the other's set: a
   windowed spec with no template gets its height only from that rule, and
   Coda, Fabric, Fibery and Notion get theirs only from this one. Measured on
   the shipped product, m-shell-windowed appears on Obsidian alone and always
   beside m-app, so today the sets intersect and nothing tells them apart —
   which is a fact about today's specs and not about what the producer
   permits.

   AND IT STRETCHES ITS COLUMNS. Notion, Fibery, Fabric and Obsidian each said
   align-items:stretch here, all four overriding the generic row default --
   :where(.m-region.m-row){align-items:flex-start}, written for rows in
   general and at 0-0-0. Coda is the fifth shell band and never said it: it
   was taking that default, so promoting the other four changed a look that
   had not been asked. It declares flex-start for itself now, below. */
/* THE SHELL'S TRACKS ARE A TOKEN, and the property is declared here once --
   0173's track slice. Five looks declared grid-template-columns on their own
   band and five more rules folded it at a viewport, so a seventh app could not
   have a shell of its own without writing the property again. Two tokens:
   --shell-tracks is what the look's shell IS, and --shell-tracks-folded is
   what it becomes when it folds. FOLDED rather than narrow, because a media
   query is what TRIGGERS that state and not what the state is -- app.fibery
   folds on a collapsed nav, with no viewport in it at all.

   The last fallback is none, which is this property's own INITIAL value, so a
   look declaring neither token gets exactly what it got before this rule
   existed. A default is not nothing.

   AND THE FOLD IS NOT GENERIC. The specification this was built from proposed
   one generic rule setting the folded token at 48rem, on the strength of three
   looks sharing minmax(0,1fr) there. Measured at 600px before a line was
   written: app.fabric does not fold at all, it computes 180px 300px, and a
   generic fold would have folded it. The breakpoint stays with the look --
   CSS cannot put a token in a media condition, so a seventh app folding at
   55rem writes one rule of its own and nothing here moves. */
:where(.m-app)>.m-region.m-row{display:grid;gap:0;height:100vh;align-items:stretch;
  grid-template-columns:var(--shell-tracks-folded,var(--shell-tracks,none))}
/* AND THE NAV FOLDS WITH IT — task 0232, promoted from three looks that wrote
   this rule identically but for their own name. app.notion, app.coda and
   app.fibery are also the only three that draw a nav in a shell band at all,
   measured across six doors, so this changes those three and reaches a seventh
   that has one.

   It is a CONSISTENCY rule and not a design one, which is what makes it
   promotable: 0218 found the fold DEAD in Notion and in Fibery — each read
   .m-app-<look> .m-nav at (0,2,0) against the nav's own base rule at (0,2,0),
   and a media query adds no specificity, so neither had ever applied — and
   repaired both without asking which other look has a nav. Coda's was never
   written. A list of the instances somebody found is not the set, and one
   generic rule is the only shape that cannot have that defect again.

   Named as a CHILD OF THE ROW, because the grid ITEM is the nav: hiding the
   nav inside its item leaves an empty track exactly where it was, which is
   0195's finding. The :is() carries the anchored spelling because a composed
   layout wraps a capability in m-capability-anchor, so a child selector alone
   is a fold that misses the wrapped case.

   (0,4,0), which is what the three it replaces were minus their app name.
   Above the nav's own (0,1,0) display:flex, and above app.fibery's
   collapsed-nav rule at (0,3,0) — both of which say display:none too, so the
   only thing that resolution decides is which rule the cascade names. */
/* A COMPOSED PART IS LAYOUT-TRANSPARENT — task 0237. A part borrowed from
   another look is wrapped so that look's DESCENDANT rules reach it; the
   wrapper must contribute no box, or it becomes the shell's grid item in the
   borrowed part's place. */
.m-part{display:contents}
@media (max-width: 48rem){
  :where(.m-app)>.m-region.m-row>:is(.m-nav,.m-capability-anchor:has(.m-nav),.m-part:has(.m-nav)){display:none}}

/* A TOOL IS A COMPONENT, and this rule was never Obsidian's: a 30px square
   with a 6px radius drawing its own glyph is what any look's tool strip
   wants, and keeping it app-scoped meant the SELECTED state below could not
   reach it without escalating specificity, which 0058 forbids as the answer.
   Generic now, so the state rule is generic-against-generic and three
   app-scoped selectors leave 0173's count. */
.m-ws-tool{display:grid;place-items:center;width:30px;height:30px;border:0;
  border-radius:6px;background:transparent;color:inherit;cursor:pointer;padding:0}
.m-ws-tool:hover{background:var(--surface);color:var(--text)}
/* A SELECTED tool looks selected. The row it replaced drew its active tab at
   the surface with the text's colour, and @Codex's ruling on 2e06944 is
   explicit that the band must not be semantically OR visibly weaker than the
   control it supersedes. Keyed on the STATE rather than on the app. */
.m-ws-tool-on{background:var(--surface);color:var(--text)}
.m-ws-tool:focus-visible{outline:2px solid var(--focus-ring);outline-offset:1px}

/* A TAB'S CHIP, and the close's native chrome — @Codex's two P2s on c8b317d.
   The slot is what a look paints; the tab and its close sit in it as
   siblings, because a control nested inside a role=tab can be flattened out
   of the accessibility tree.

   And the close is a NATIVE BUTTON, so it arrives with the user agent's
   border, background and padding unless they are taken off. The look set
   only its colour, so the multi-tab state — the one 0198 exists to reach —
   drew a bordered box around the mark and widened every tab, while a DOM
   harness that reads the element stayed green. Obsidian's own block already
   carries a comment recording the same defect one element out, on the
   inactive tab's button face; this reset is generic, because nothing about a
   button having no chrome is Obsidian's. A look supplies only the four
   values: the chip's radius, its floor width, the mark's padding and the gap
   the label gives up to it. */
/* THE DOCUMENT CANVAS'S OWN BLOCK RHYTHM — @Codex's deployed viewed verdict
   on ae46bbb, which shipped 0199 and named two token differences.

   The canvas had NO rules of its own. Its blocks inherited whatever surrounded
   them plus the user agent's defaults, so a heading took the body's 1.6
   leading and a list took the UA's 40px indent. Measured on the deployed
   build, beside the pinned reference:

     heading   24px / 38.4px = 1.6   harmless on one line, and substantially
                                     airier than the reference once it wraps
     list      padding-left 40px     against the reference's ~20px, so a
                                     top-level bullet reads as a sublist

   Their words on the fix, and the reason it is two values and not a
   component: "Preserve the native marker and hanging construction, but reduce
   the outer indentation rather than replacing the component."

   Tokens rather than constants, because the canvas belongs to every look that
   mounts it — Word's sheet and Obsidian's note today, a seventh app's editor
   tomorrow — and a look that wants a different rhythm sets a value rather than
   writing a rule. The quote is untouched: they measured its rule, inset and
   colour as the closest of the three and said to keep it. */
.m-document-canvas :is(h1,h2,h3,h4,h5,h6){line-height:var(--m-doc-head-leading,1.3)}
.m-document-canvas :is(ul,ol){padding-inline-start:var(--m-doc-list-indent,20px)}
/* A FOCUSED DOCUMENT BLOCK SHOWS ITS CARET, NOT A FIELD OUTLINE — @Codex's
   deployed vault verdict, 2026-09-05, and it is the only thing that verdict
   refused to ship. Their sentence: the focused lead paragraph "looks like a
   bordered multiline input placed above the document", and it is decisive
   because it surrounds the first thing the reader meets. Measured here on the
   same door: the block is focused ON ARRIVAL, matches focus-visible, and wears
   outline 1px auto rgb(16,16,16) -- Chromium's OWN ring, not ours. Nothing in
   this sheet painted it, which is why no look had ever overruled it and why
   grepping our focus rules found nothing.

   A contenteditable block's focus affordance is the caret, which is what every
   editor this repo reproduces shows and what the reference shows. So the rule
   suppresses the UA ring for document blocks ONLY.

   Deliberately NOT a weakening of the global focus ring, which their verdict
   also asks for in as many words: buttons, tabs, row openers and every other
   discrete control keep theirs, because the attribute selector reaches nothing
   but a canvas block.

   The default is CSS's own initial value spelled out rather than 0, because
   border-top:0 style shorthands resolve their colour to transparent instead of
   currentcolor -- the as-table slice paid for that one. And it is a token, so
   a look that wants an edit affordance opts in: Word already declares a dotted
   1px at higher specificity and is untouched. */
[data-morph-document-block]:focus,
[data-morph-document-block]:focus-visible{
  outline:var(--doc-block-focus-outline,medium none currentcolor);
  outline-offset:var(--doc-block-focus-offset,0);
  background:var(--doc-block-focus-bg,transparent)}
/* THE ACTIVE TAB'S ACTION SLOT — decision 0072. It replaces the
   role=presentation chip that used to wrap each tab and its close, because a
   presentational wrapper does not un-role its DESCENDANTS: the button stayed
   an accessibility-owned child of the tablist. The slot is a SIBLING of the
   tablist and carries the same chip paint, so the pair still reads as one
   control the way the collapsed search does -- two painted halves with
   nothing drawn on the junction between them.

   0072 also permits an overlay, and the frame would need position:relative to
   bound one. That is NOT written here: nothing overlays, and an opening left
   for a placement no look uses is the vocabulary-nobody-reads defect 0193 was
   filed for. One line brings it back the day a look wants one. */
.m-ws-tab-action{display:flex;align-items:center;flex:0 0 auto;
  border-radius:var(--m-tab-radius,0);
  border-start-start-radius:0;border-end-start-radius:0;
  margin-inline-start:var(--m-tab-action-pull,0)}
/* The tab carries the chip's shape now that the chip is not an element. A
   look that draws a gap between tabs cancels it across THIS junction with
   --m-tab-action-pull, or the strip's own ground shows through the middle of
   what a person reads as one tab.

   --m-tab-min WAS DEAD HERE for one commit, and the specificity lesson 0203
   is about caught it: this rule is (0,1,0) and the look's own .m-ws-tab is
   (0,2,0) declaring min-width:0, so the floor never applied and the token
   read like a working one. The look's zero was there to let a flex item
   shrink below its content -- which an explicit floor does just as well --
   so the zero goes and the token means something again. */
.m-ws-tab{min-width:var(--m-tab-min,0);border-radius:var(--m-tab-radius,0)}
.m-ws-tab-close{appearance:none;-webkit-appearance:none;border:0;background:none;
  margin:0;font:inherit;line-height:0;cursor:pointer;color:inherit;
  display:grid;place-items:center;border-radius:4px;padding:var(--m-tab-close-pad,0)}
/* With a close beside it the label gives up its trailing padding to the mark,
   so the chip's two controls cover the whole chip and none of it is dead --
   and the trailing corners belong to the slot, not to the label. Keyed on the
   FRAME holding a close rather than on a wrapper, since there is no longer a
   wrapper to ask. */
.m-ws-tabs:has(.m-ws-tab-close) .m-ws-tab-on{padding-inline-end:var(--m-tab-gap,0);
  border-start-end-radius:0;border-end-end-radius:0}
.m-ws-tab-close:focus-visible{outline:2px solid var(--focus-ring);outline-offset:-2px}
/* THE COLLAPSED SEARCH — task 0187, and it is a COMPONENT rather than a skin.
   A look whose row has no space for a box declares a trigger; the module draws
   a real button and the box waits behind the hidden attribute. Everything here
   is a token, so the second look that wants a collapsed search sets values
   rather than growing a block of its own.

   The tool box lays the trigger and the box out and carries the placement, so
   both land in the same place — the collapsed row and the open row differ by
   what is in that place, not by where it is. Looks that want the module to lay
   out as nothing keep their own display:contents. */
/* THE RIBBON IS A COMPONENT, and it had none — decisions 0070 and 0071.
   Every declaration lived in .m-app-obsidian .m-ws-ribbon, so a seventh look
   mounting a ribbon inherited nothing and would have had to write its own
   block. It owns its box here and reads one token per property; the windowed
   arrangement selects the in-band outlets at :where() specificity, and the
   look supplies both sets of values. align-items is not a token: the rail
   centres its tools and so does the band, and 0070 asks for the minimum. */
.m-ws-ribbon{display:flex;align-items:center;
  flex-direction:var(--ribbon-flow,column);gap:var(--ribbon-gap,0);
  padding:var(--ribbon-padding,0);background:var(--ribbon-ground,transparent);
  border-right:var(--ribbon-edge,medium none currentcolor);
  color:var(--ribbon-ink,inherit)}
/* THE TOOL'S BOX IS A TOKEN, and its default is the majority's -- task 0173.
   Four doors draw this tool: Notion wants a real flex box for it, and
   Obsidian, Fibery and Fabric all want NO box at all, each saying
   display:contents in a rule of its own. Read out of the DOM, not off the
   selectors. So the generic defaulted to the one look that wanted it and the
   other three each paid a selector to undo it.

   Every sibling declaration here is already a token. This one is now too,
   which is what makes a seventh app's answer a value rather than a rule: it
   gets contents free and sets the token if it wants a box. Notion sets it in
   the token block it already has, so the three rules come out and nothing
   goes back in. */
.m-ws-search-tool{display:var(--m-ws-search-display,contents);flex-wrap:wrap;align-items:center;
  justify-content:var(--m-ws-search-align,normal);gap:var(--m-ws-search-gap,0);
  min-height:var(--m-ws-search-row,auto);margin:var(--m-ws-search-place,0);
  /* The tool is LAYOUT, not surface. A look that pulls it onto another row
     (Notion's, so the search rides in the view row) would otherwise put an
     invisible sheet over that row: measured, and it swallowed the clicks on
     Filter, Sort and the view pills. Its children take the pointer back. */
  pointer-events:none}
.m-ws-search-tool>*{pointer-events:auto}
.m-ws-search-tool>.m-ws-section{flex-basis:100%}
.m-ws-search-box[hidden]{display:none}
.m-ws-search-open{appearance:none;-webkit-appearance:none;border:0;background:none;
  font:inherit;line-height:0;cursor:pointer;color:var(--text-muted);
  display:grid;place-items:center;padding:0;
  width:var(--m-ws-search-btn,28px);height:var(--m-ws-search-btn,28px);
  border-radius:var(--m-ws-search-btn-radius,4px)}
.m-ws-search-open:hover{background:var(--surface);color:var(--text)}
/* OPEN, the glyph reads as the field's leading icon rather than as a button
   floating over the endcap the field covers: it takes the field's ground and
   its height, so the pair is one opaque strip with nothing showing through
   between them. It STAYS pressable — it is the way back for a person with no
   keyboard.

   AND THE OUTLINE IS ONE OUTLINE — @Codex's deployed viewed verdict on
   main.a976fb0f1746.js, and it is the half a functional clause cannot reach.
   The geometry was already right: 0px between the two, heights within 0.19px,
   a 212px field on a 34px row, all of which they measured and told me not to
   touch. What they saw was the PAINT: the border began at the input's left
   edge, so the eye read a button beside a box however tightly the boxes met.

   The trigger takes the field's edge on three sides and gives up the fourth;
   the input gives up its left. The outline crosses the junction with nothing
   drawn on it, so the pair is one control 240px wide. Both halves are tokens,
   because a second look that collapses its search wants its own edge and not
   Notion's. */
.m-ws-search-tool:has(>.m-ws-search-box:not([hidden]))>.m-ws-search-open{
  background:var(--m-ws-search-open-bg,transparent);
  height:var(--m-ws-search-row,var(--m-ws-search-btn,28px));
  border:var(--m-ws-search-edge,0);border-right:0;
  border-radius:var(--m-ws-search-radius,0) 0 0 var(--m-ws-search-radius,0)}
/* The focus ring is the CONTROL's, not the input's — a half-lit outline is
   the same defect one state along. */
.m-ws-search-tool:has(>.m-ws-search-box:not([hidden]) .m-ws-search-input:focus)
  >.m-ws-search-open{border-color:var(--m-ws-search-edge-on,var(--focus-ring))}
/* Only where a trigger stands beside it: a look with no collapsed search
   keeps the box it always had, border and all.

   THE SEAM IS A TOKEN, and it became one because 0203 wrote this rule's
   structural-lock record. 0069 term 3 requires a token outlet, and control 2
   asks whether THIS rule reads what the record offers -- it read nothing, the
   only tokens in sight belonging to the sibling trigger rule above. An outlet
   on a neighbour is not an outlet: a seventh look wanting a hairline seam had
   no way to ask for one. The default is unchanged and still nothing drawn,
   measured at 0 of 1819 position-keyed capture rows. */
.m-ws-search-tool:has(>.m-ws-search-open)>.m-ws-search-box>.m-ws-search-input{
  border-left:var(--m-ws-search-seam,0);
  border-start-start-radius:var(--m-ws-search-seam-radius,0);
  border-end-start-radius:var(--m-ws-search-seam-radius,0)}
.m-ws-search-open:focus-visible{outline:2px solid var(--focus-ring);outline-offset:-2px}
/* The New button's mark — Notion's dropdown chevron, inside the real button
   (0056). A span rather than a change to the button's display, so a look that
   draws its own glyph before the label keeps the layout it had. */
.m-viewswitch-new-mark{margin-inline-start:var(--m-new-mark-gap,.4em);opacity:.85;font-size:.95em}

/* A named app look — decision 0055. The template's stylesheet owns the
   RELATIONSHIPS of its workspace (which pane sits where, at what width, with
   which border), exactly as the base stylesheet owns a table's header sitting
   above its rows. Every colour is a token the template declared above;
   nothing here invents one. Written from the obsidian.md reference and judged
   beside it at 1440×900 — see docs/references/targets/obsidian-note.html. */
.m-app-obsidian{--ws-rail:#181818;--text-faint:#6f6f6f;
  --m-tab-radius:6px 6px 0 0;--m-tab-min:52px;--m-tab-close-pad:7px 12px 7px 0;--m-tab-gap:6px;
  --m-tab-action-pull:-2px;
  min-height:100vh;height:100%;font-size:15px;line-height:1.55;
  -webkit-font-smoothing:antialiased}
/* THE RIGHT PANE IS A SHARE OF THE WINDOW, not a fixed strip — decision 0068,
   @Codex's finding 3. Measured off the pinned capture by scanning the row
   above the phone mockup, with the tab row confirming every boundary:

     reference window   62 - 1000   939 wide
       explorer         62 -  260   199    21.2%
       editor          262 -  642   381    40.6%
       graph           644 - 1000   357    38.0%

   The reference's editor and graph pane are NEARLY EQUAL. Ours was 300px
   against an 826px editor -- 21.5% to 59.2% of the same window -- so the miss
   was the pane, not the editor: the editor is wide because the thing beside
   it is narrow.

   Of the WHOLE window, now that finding 1 has landed (task 0194): the wide
   layout has no ribbon column, so the three shares are the three columns and
   the subtraction this comment used to describe is gone with the track. The
   two narrow breakpoints below still carry a 44px column, deliberately --
   0195's containment -- and they are the only 44px left here.

   A FLOOR and no ceiling, for the reason the Fabric panel records: a ceiling
   is an invention about window widths the reference says nothing on, and the
   one written there put the defect straight back at 1920 and 2560. 300px is
   the width this pane already shipped at.

   IT SHIPS WITH THE PANE'S CONTENT, and that is the whole of 0068's ruling:
   the reference's pane is 38% because it holds a GRAPH. Widening it while it
   still held an empty backlinks list would have improved a measured ratio and
   made the product worse to look at. */
/* THE WINDOW IS THE VIEWPORT, and until now the vault only had a FLOOR.
   min-height on the shell and minmax(100vh,1fr) on this track both say "at
   least the window" and neither says "at most", so the editor -- which has
   carried overflow-y:auto all along -- was never height-constrained and its
   scrollHeight equalled its clientHeight at all three widths measured. On
   main.eeacb23d3755.js with a seeded note of 965 characters: the shell was
   974px at 1440, 1057px at 600 and 1457px at 375, all in a 900px window, so
   the vault's chrome scrolled away under the note instead of the note
   scrolling inside the vault.

   0195 fixed the COLUMNS -- a folded sidebar had pushed the editor into a
   44px track and the shell to 5,671px -- and the height came down with them
   far enough to sit under that clause's ceiling. It was never bound.

   Bound here rather than on the shell because the shell is a block box whose
   child's height would stay auto. This is the shape app.word already uses,
   and app.word's own comment cites this rule as the precedent for it.

   TASK 0217 is the rest of it, and it is larger than this fix. Measured the
   same hour: app.notion is 1682px at 600, app.coda 1177, and app.fibery is
   1791px at 1440x900 -- twice the window at the very viewport every look
   harness drives. All three carry the same floor-only shape. And the binding
   is spelled privately three times now, here and in app.word and app.fabric,
   which is one structural fact -- a shell is the viewport -- written once per
   look. It belongs to the arrangement; m-shell-windowed cannot hold it today
   because every one of its rules but one -- the line hiding the band below
   the breakpoint -- sits inside a min-width media query, and this defect is
   worst below that breakpoint. */
:where(.m-app-obsidian)>.m-region.m-row{position:relative;--m-winrow-end:2;
  --shell-tracks:minmax(240px, 21.2%) minmax(0,1fr) minmax(300px, 38%);
  grid-template-rows:minmax(0,1fr)}
/* THE WINDOW'S BAND. Every column spans both rows by default, so the editor's
   tab strip keeps its place at the very top and the layout is what it was —
   except over the ribbon and the explorer, where the window's own controls
   take the first row and those two begin beneath it.

   That is the arrangement the reference has: one band across the window's top
   carrying the controls at its left and the panes' tabs to their right. It is
   also the placement @Codex ruled for after arguing both sides — a row of its
   own is neither the tab strip (which begins 314px in, so controls there read
   as decoration on the active tab) nor the 44px ribbon (too narrow for a group
   that spans 50px, so they would overlay the boundary beside it).

   The explorer's column is told from the right pane's by what it HOLDS rather
   than by its position among the children: both are .m-ws-sidebar, and a rule
   keyed on child order is a rule the next inserted node breaks — which is
   exactly what inserting this row would have done to the two rules below. */
.m-app-obsidian>.m-region>.m-ws-status{grid-column:1/-1}
/* THE WINDOW BAND — GENERIC, keyed on the shell that declares the capability
   rather than on the app that first wanted it. Written under
   the app-scoped Obsidian block first and refused by 0173's ratchet, correctly: eight
   app-scoped selectors for a component that belongs to every look is the
   shape the 2026-09-03 amendment forbids, and a seventh app whose reference
   is a desktop window would have needed its own eight. It declares
   the workspace-window capability and gets the arrangement.

   Every column spans both rows by default, so the editor's tab strip keeps
   its place at the very top; the LEADING column gives up its first row to the
   window's controls and to the look's own tool strip, and its pane begins
   beneath them. That is the reference's band, scanned across one row: the
   controls at the window's left, the sidebar's tools beside them, then the
   panes' tabs — one unbroken strip from edge to edge.

   A WIDE-LAYOUT arrangement, declared where the full column set is. Below the
   breakpoint the layout still carries a 44px ribbon column — narrower than
   the 66px a correctly spaced group needs — and every row assignment here
   would push a pane into a track it was not auto-placed into. Measured while
   writing it: the explorer went from 556px to 16px at 600px wide. So the
   whole arrangement is scoped and the narrow layout keeps what it had, which
   is 0195's containment and not a second composition anybody chose.

   The leading column's pane is told from the trailing one by what it HOLDS,
   never by its position among the children: both are .m-ws-sidebar, and a
   rule keyed on child order is a rule the next inserted node breaks — which
   is what inserting this row did to the two breakpoint rules below. */
/* AND THE WINDOWED SHELL IS THE VIEWPORT -- @Codex, P2 on eb3633b, and they
   are right that this is the arrangement's job and not a look's. Their
   sentence is the finding: a new or composed look that declares
   workspace-window and has content taller than the viewport gets a floor from
   min-height and no ceiling, so the band, the sidebars and the status chrome
   scroll away with the document -- and app.obsidian escaped that only through
   a private height:100vh, which means the supposedly reusable arrangement
   reproduced the defect for the next look unless it added another private fix.

   Declared OUTSIDE the min-width query the rest of this arrangement lives in,
   because the marker is emitted from the IR whenever the root region holds a
   workspace-window and is not viewport-dependent; a binding that only applied
   above 64rem would leave the narrow window exactly where 0195 found it.

   The children rule is the other half, and 0217 paid for learning that it is
   not optional: capping app.coda's row left its grid item at 930.92 in a 900px
   track with the DOCUMENT still scrolling, so a shell bound with items that
   cannot shrink and stretch is a shell bound and a page that still scrolls.
   The panes own their overflow from there.

   app.obsidian's private height:100vh is REMOVED with this, so the arrangement
   is proved to carry it rather than merely declared to. */
.m-shell-windowed>.m-region.m-row{height:100vh}
.m-shell-windowed>.m-region.m-row>*{min-width:0;min-height:0;align-self:stretch}
.m-shell-windowed>.m-region.m-row>.m-ws-winrow{display:none}
/* THE SHELL ESTABLISHES ITS OWN FORMATTING CONTEXT. @Codex P2 on bcbe1fc:
   every track and placement below was written here, generically, while
   display:grid came only from the app-scoped Obsidian rule above — so a
   seventh look declaring the capability on an ordinary flow:'row' root kept a
   flex container and every grid-row and grid-column here was INERT. The
   commit that introduced this called it generic; it was generic for one app,
   which is a comment asserting a property the code lacks. */
/* AND IT ESTABLISHES IT ONLY WHERE THE TRACKS ARE — @Codex's P2 on
   ee1dd16, which is the other half of the finding above. Making
   display:grid generic was right and it was written OUTSIDE the wide query,
   while every track and placement is inside it. So a generic windowed shell
   at 64rem or below became a grid with no template: an implicit single
   column, with the panes stacked vertically. Obsidian never showed it,
   because its own block declares display:grid unconditionally and its narrow
   rules give it columns — the app masking a generic defect, which is the
   shape this whole section is about.

   Below the breakpoint the window band is hidden (the rule above), so the
   shell is an ordinary row there and wants its ordinary flow. */
@media (min-width: 64.01rem) {
  /* MINIMALLY STRONGER THAN A LOOK, and the condition is the reason -- decision
     0074 row 1, resolved by 0073's method 3. This tied a look at (0,3,0) and
     source order settled a TOPOLOGY question, which nobody declared. The
     has() states what the marker already means on the DOM side: this row
     holds a window band, so its three tracks are the band, the content and
     the status line. (0,4,0) is the least that exceeds a look, and it selects
     the same elements the marker does -- the band is what the marker is
     emitted for.

     A DESCENDANT has(), not a child one -- @Codex, P2 on e5ffb92. Written
     with a child combinator it stopped matching the moment anything was
     anchored TO the band: anchorName wraps a capability in
     m-capability-anchor to give the anchor a generated box, so the band
     becomes a grandchild and the whole three-track grid vanished. The
     immediate-topology test that a child combinator was doing is not this
     rule's job and never was -- the MARKER does it, and does it on the IR,
     where a wrapper cannot hide anything. So the marker decides WHETHER the
     band is this row's own, and the has() only states that it is there. */
  .m-shell-windowed>.m-region.m-row{display:grid;align-items:stretch;
    grid-template-rows:36px minmax(0,1fr) auto;
    min-height:100vh}
  .m-shell-windowed>.m-region.m-row>*{grid-row:1/3}
  /* THE BAND SPANS THE WHOLE TOP by default, which is what a window band is.
     A look whose own tabs live in the band's right half narrows it with one
     DECLARATION rather than a rule of its own — Obsidian sets
     --m-winrow-end:3 inside the grid rule it already has, so the arrangement
     stays generic and 0173's selector count does not move. */
  .m-shell-windowed>.m-region.m-row>.m-ws-winrow{display:flex;grid-row:1;
    grid-column:1/var(--m-winrow-end,-1);
    padding:var(--winrow-padding,0 0 0 14px);
    background:var(--raised);border-bottom:1px solid var(--border)}
  /* THE LOOK'S TOOL STRIP IS THE BAND'S, not a column of its own — task 0194,
     which is finding 1 of 0170 stated properly. The reference scan across
     row y=555 runs one unbroken strip from the window's edge: the controls
     at x62-137, then the SIDEBAR'S OWN TOOLS at x138-260, then the editor's
     tabs. Ours put the tools in a full-height 44px column down the left edge,
     which is a composition the reference does not have at all.

     Same cell as the band, end-aligned, so the tools finish where the sidebar
     column finishes exactly as the reference's do. The band paints the
     ground and is listed first, so the strip draws over it and needs no
     ground of its own — two items in one cell rather than a second track,
     because a track would be a number to keep in step with the column it
     sits above. */
  /* THE IN-BAND PAINT LIVES WITH THE ARRANGEMENT THAT CAUSES IT — @Codex's P2
     on 74e732b, and it was mine from an hour earlier. This outlet sat at the
     top level while the placement above it is gated by this query, so below
     64rem the ribbon was still a vertical rail and was already wearing the
     band's paint: no ground, no separator, right-only padding. Decision 0070
     says the arrangement CHOOSES the outlet; an arrangement that is not in
     effect chooses nothing. :where() keeps it at (0,0,0) so a look still wins.
     The 1440x900 capture could not see this — e2e/obsidian-look drives 900px
     for it. */
  :where(.m-shell-windowed) .m-ws-ribbon{
    padding:var(--ribbon-padding-in-band,var(--ribbon-padding,0));
    background:var(--ribbon-ground-in-band,var(--ribbon-ground,transparent));
    border-right:var(--ribbon-edge-in-band,var(--ribbon-edge,medium none currentcolor))}
  .m-shell-windowed>.m-region.m-row>.m-ws-ribbon{grid-row:1;grid-column:1;
    justify-self:end;flex-direction:row}
  /* SHORTENED to the minimum that names the relationship -- 0070's other half,
     applied in 0203. This was .m-shell-windowed>.m-region.m-row>.m-ws-ribbon>
     .m-ws-ribbon-space (0,5,0) against a look's (0,2,0): .m-region.m-row says
     where the ribbon sits, not what it is. The .m-ws-ribbon parent STAYS or the
     rule ties the look and source order settles a topology question. The
     neighbours here keep their long chains because no look declares the
     properties they set, so the sweep does not contest them and there is
     nothing to minimise against. */
  .m-shell-windowed .m-ws-ribbon>.m-ws-ribbon-space{flex:0 0 4px}
  .m-shell-windowed>.m-region.m-row>.m-ws-ribbon .m-ws-tool-tabs{display:flex;
    align-items:center;gap:inherit}
  /* AND THE DUPLICATE ROWS GO — @Codex's ruling on 2e06944, judged by looking
     at our capture beside the reference: theirs runs band-then-tree, ours put
     a second Files/Search tablist and a third row holding a lone New between
     them. Two controls for one selection in two rows, which is the defect
     finding 1 was about arriving one layer in.

     The band's own tools carry the tab semantics now, so this hides a
     DUPLICATE rather than a capability -- the condition the ruling puts on
     it. Told by CONTENT, not by child order: the sidebar the band selects
     for is the one holding an explorer, and the RIGHT sidebar's own tabs
     stay, because Backlinks and Graph are a different pane the band does not
     reach.

     KEYED ON THE BAND ACTUALLY OWNING THEM -- @Codex's P2 on 3b84c58, and it
     is the generic-for-one-app defect committed inside the fix for it. These
     read m-shell-windowed, so a windowed composition whose ribbon is ordinary
     commands (no selects declaration) or which has no ribbon at all lost its
     pane switching and its New note by declaring the reusable window shell and
     nothing else. m-shell-panes-in-band is stamped only when the strip really
     carries the pane selectors; without it the sidebar keeps its own.

     Both shortened the same way and for the same reason as the ribbon spacer
     above (0203). :has(.m-ws-explorer) is load-bearing -- the RIGHT sidebar has
     no explorer and keeps its own tabs -- and .m-ws-sidebar stays with it,
     because :has() on a bare descendant matches every ancestor of an explorer,
     the shell included. */
  .m-shell-panes-in-band .m-ws-sidebar:has(.m-ws-explorer) .m-ws-side-tabs{display:none}
  .m-shell-panes-in-band .m-ws-explorer>.m-ws-tools{display:none}
  .m-shell-windowed>.m-region.m-row>.m-ws-sidebar:has(.m-ws-explorer){grid-row:2}
  .m-shell-windowed>.m-region.m-row>.m-ws-status{grid-row:3}
}
/* Where the panes do not fit, the EDITOR keeps the width — @Codex, P2 on
   ebf7b8c: below 614px the three fixed columns consumed the grid and the
   editor collapsed to zero. Obsidian itself folds its side panes away on a
   narrow window; the note is what a person came for. */
@media (max-width: 64rem) {
  .m-app-obsidian>.m-region.m-row{--shell-tracks-folded:44px 270px minmax(0,1fr)}
  /* The RIGHT pane, told by what it does not hold. This read
     :nth-last-child(2) until the window row was inserted ahead of it — a rule
     keyed on child order, broken by adding a child, which is the reason the
     band rules above name their targets by content instead. */
  .m-app-obsidian>.m-region.m-row>.m-ws-sidebar:not(:has(.m-ws-explorer)){display:none}
}
@media (max-width: 40rem) {
  .m-app-obsidian>.m-region.m-row{--shell-tracks-folded:44px minmax(0,1fr)}
  /* THE LEFT SIDEBAR folds away, and naming the SIDEBAR rather than the pane
     inside it is the whole fix — task 0195.
     
     This read .m-app-obsidian .m-ws-explorer{display:none} and was wrong
     twice over. It lost the cascade: two classes against the two of
     .m-app-obsidian .m-ws-pane and of the explorer's own rule, both later in
     this sheet, and a media query adds no specificity — so it had presumably
     never applied since it was written. And hiding it would not have been
     enough anyway: the GRID ITEM is the sidebar, so an empty 556px column
     would have stayed exactly where it was.
     
     What it cost, measured at 600px on main.a976fb0f1746.js: the sidebar took
     the flexible column, so the tab strip and the editor auto-placed into new
     rows of the 44px ribbon column — 44px wide, starting at y=900, below the
     fold, the editor 4,735px tall because its text had nowhere to go, and the
     shell 5,671px tall in a 900px window. A person on a narrow window met a
     44px column of wrapped text under a full-height ribbon.
     
     Told by CONTENT, as the 64rem rule beside it is and for the reason its
     comment gives: a rule keyed on child order broke the moment a child was
     added. The two now read as a pair — that one hides the sidebar WITHOUT
     the explorer, this one the sidebar WITH it. */
  .m-app-obsidian>.m-region.m-row>.m-ws-sidebar:has(.m-ws-explorer){display:none}
}
/* The right SIDEBAR: a strip of pane tabs over one visible pane — Obsidian's
   backlinks / graph switcher. The strip sits where the pane head used to
   start; the active tab is the brighter one with the strip's bottom rule
   broken under it. */
.m-app-obsidian .m-ws-sidebar{display:flex;min-height:0;
  padding:0;gap:0}
/* Restated AFTER the base rule — @Codex, P2 on 2c1ead8: the breakpoint
   above set display:none and this base rule, later in the sheet at the
   same specificity, set it back to flex, so the sidebar stayed visible
   after the grid dropped its column. Order is the cascade's only tool
   here, and the clause that pins it reads the sheet's order. */
/* The RIGHT sidebar only — the one before the status bar. @Codex, P2 on
   93d57d7: the left explorer/search column is a sidebar too now, and a
   rule on the class hid both while the grid kept the 270px column, so the
   editor slid into the explorer's slot and the flexible column went
   unused. */
@media (max-width: 64rem) { .m-app-obsidian>.m-region.m-row>.m-ws-sidebar:nth-last-child(2){display:none} }
/* The showing pane FILLS its sidebar, as the app's do: the explorer runs to
   the bottom of the window with the vault switcher pinned there, and the
   backlinks pane is a column, not a card. Seen in the tree's first shot —
   both side panes ended where their content did, a Morph panel sitting in
   an Obsidian column. */
.m-app-obsidian .m-ws-sidebar>.m-ws-pane{padding:0 .5rem .5rem;border:0;min-height:0;
  flex:1 1 auto}
/* A compact row of ICON tabs at the top of the sidebar — the tenth
   verdict's ruling 2. Each is a small square with the glyph; the showing
   pane's is the lit one on a rounded surface, and the label is a tooltip
   and the accessible name, never text in the strip. */
.m-app-obsidian .m-ws-side-tabs{display:flex;gap:.15rem;padding:.3rem .5rem;height:36px;
  box-sizing:border-box;align-items:center;border-bottom:1px solid var(--border);
  background:var(--raised);flex:none}
.m-app-obsidian .m-ws-side-tab{appearance:none;border:0;background:transparent;
  color:var(--text-muted);font:inherit;width:28px;height:26px;padding:0;
  display:inline-flex;align-items:center;justify-content:center;
  border-radius:5px;cursor:pointer}
.m-app-obsidian .m-ws-side-tab .m-ws-glyph{display:block}
.m-app-obsidian .m-ws-side-tab:hover{color:var(--text);background:var(--surface-hover)}
.m-app-obsidian .m-ws-side-tab-on{color:var(--text);background:var(--surface)}
.m-app-obsidian .m-ws-side-tab:focus-visible{outline:2px solid var(--focus-ring);outline-offset:-2px}
/* The local graph in its pane: dots on the pane's ground, faint edges,
   small labels under the dots, the open note lifted. The reference's
   graph is exactly this at rest — dots, not labelled boxes. */
.m-app-obsidian .m-ws-graph .m-force-graph{overflow:hidden;margin:0}
.m-app-obsidian .m-ws-graph .m-force-graph svg{width:100%;height:auto;max-width:100%;
  display:block;background:transparent}
.m-app-obsidian .m-ws-graph .m-graph-edges line{stroke:#5c5c5c;stroke-width:1.25}
.m-app-obsidian .m-ws-graph .m-graph-dot{appearance:none;border:0;background:transparent;
  color:#b4b4b4;font:inherit;font-size:10.5px;line-height:1.1;padding:0;
  width:100%;display:flex;flex-direction:column;align-items:center;gap:3px;
  cursor:pointer;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.m-app-obsidian .m-ws-graph .m-graph-dot-mark{width:9px;height:9px;border-radius:50%;
  background:#9a9a9a;flex:none}
.m-app-obsidian .m-ws-graph .m-graph-dot[aria-current="true"] .m-graph-dot-mark{
  width:12px;height:12px;background:var(--solid)}
.m-app-obsidian .m-ws-graph .m-graph-dot[aria-current="true"]{color:var(--text)}
/* HOVER emphasis — the fourth verdict: "no visible interaction affordance
   beyond the dots themselves". The dot grows and brightens and its label
   comes forward; the current note keeps its violet. */
.m-app-obsidian .m-ws-graph .m-graph-dot:hover .m-graph-dot-mark,
.m-app-obsidian .m-ws-graph .m-graph-dot:focus-visible .m-graph-dot-mark{
  background:var(--text);transform:scale(1.35);transition:transform .12s ease}
.m-app-obsidian .m-ws-graph .m-graph-dot:hover,
.m-app-obsidian .m-ws-graph .m-graph-dot:focus-visible{color:var(--text)}
.m-app-obsidian .m-ws-graph .m-graph-dot[aria-current="true"]:hover .m-graph-dot-mark{background:var(--solid)}
.m-app-obsidian .m-ws-graph .m-graph-dot:focus-visible{outline:2px solid var(--focus-ring);
  outline-offset:-2px;border-radius:4px}
.m-app-obsidian .m-ws-graph .m-graph-dot-label{max-width:138px;overflow:hidden;text-overflow:ellipsis}
.m-app-obsidian .m-ws-graph .m-graph-relations{position:absolute;width:1px;height:1px;
  overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
/* THE GRAPH — the vault as a canvas in the main column, and the local graph
   in the side pane; one rule paints both. Faint edges, dots sized by degree,
   labels only on hover or focus; topology is the image. The figure fills its
   column and the SVG scales to it.

   THE GROUND IS THE PAGE'S, NOT BLACK. It was a literal #000 under a
   comment that said "Black ground" -- and the reference's graph area is
   #1e1e1e right across the pane, scanned rather than sampled:

     ours   #000000  x910-1439    the whole pane
     ref    #1e1e1e  x644-1000    the whole pane

   Found by LOOKING after the pane was made to open on the graph, and it
   could not have been found before: while the pane held backlinks the graph
   was never on screen at the door, so neither deployed drive could see it.
   Fixing one finding is what made the next one visible.

   The token rather than the number, so it is the editor's own ground by
   construction -- which is what the reference shows and is one literal fewer.

   THIS DOES NOT ANSWER TASK 0192. That asks what the pane BEHIND the canvas
   is painted, and an opaque canvas hides it either way. This is the canvas's
   own visible fill, which is the one fact decision 0068 says the capture
   supports. */
.m-app-obsidian>.m-region>.m-region.m-stack>.m-ws-graph{background:var(--page);padding:0;
  flex:1 1 auto;min-height:0;display:flex}
.m-app-obsidian .m-ws-graph .m-force-graph-vault{flex:1 1 auto;display:flex;
  flex-direction:column;position:relative}
.m-app-obsidian .m-ws-graph .m-force-graph-vault svg{flex:1 1 auto;height:100%;width:100%;
  min-height:0}
.m-app-obsidian .m-ws-graph .m-force-graph-vault .m-graph-edges line{stroke:#3a3a3a;stroke-width:1.1}
.m-app-obsidian .m-ws-graph .m-force-graph-vault .m-graph-dot-mark{
  width:calc(7px + var(--deg,0) * 2.5px);height:calc(7px + var(--deg,0) * 2.5px);background:#8a8a8a}
.m-app-obsidian .m-ws-graph .m-force-graph-vault .m-graph-dot[aria-current="true"] .m-graph-dot-mark{
  width:calc(10px + var(--deg,0) * 2.5px);height:calc(10px + var(--deg,0) * 2.5px);background:var(--solid)}
.m-app-obsidian .m-ws-graph .m-force-graph-vault .m-graph-dot-label{opacity:0;transition:opacity .12s ease;
  font-size:11px;color:var(--text)}
.m-app-obsidian .m-ws-graph .m-force-graph-vault .m-graph-dot:hover .m-graph-dot-label,
.m-app-obsidian .m-ws-graph .m-force-graph-vault .m-graph-dot:focus-visible .m-graph-dot-label,
.m-app-obsidian .m-ws-graph .m-force-graph-vault .m-graph-dot[aria-current="true"] .m-graph-dot-label{opacity:1}
.m-app-obsidian .m-ws-graph .m-force-graph-vault figcaption{position:absolute;right:.9rem;top:.7rem;
  font-size:12px;color:var(--text-faint);margin:0}
.m-app-obsidian .m-ws-graph figcaption{font-size:11.5px;color:var(--text-muted);
  padding:.35rem .25rem 0}
/* THE EDITOR IS A WRITING SURFACE, not a form — @Codex's fourth deployed
   verdict, item 1, judged from the unnamed-note state: a violet-outlined
   title box, "Author Author" (the empty cell's placeholder repeating the
   name beside it), "Body" as a label, a rounded relation box. Obsidian's
   title is a bare line with a caret; an empty property reads "Empty"; an
   empty body is blank space that takes a caret; the links are rows. */
.m-app-obsidian .m-ws-editor .m-editable:focus,
.m-app-obsidian .m-ws-editor .m-editable:focus-visible{outline:none;box-shadow:none;background:transparent}
.m-app-obsidian .m-ws-editor .m-role-title.m-editable:empty::before{content:"Untitled"}
.m-app-obsidian .m-ws-editor .m-role-title.m-editable:empty:focus::before{opacity:.6}

.m-app-obsidian .m-ws-editor .m-role-body.m-editable:empty{min-height:10em}
/* The blank canvas says where writing starts — @Codex's eighth verdict:
   "it does not yet say start writing here". A quiet first line, no box;
   the generic rule fades it to .3 on focus and the first keystroke ends
   it. Obsidian's own canvas is bare, but its caret is already there; ours
   sits in the title on creation (0024), so the body needs a word. */
.m-app-obsidian .m-ws-editor .m-role-body.m-editable:empty::before{content:"Start writing…";
  color:#5a5a5a;font-style:normal}
.m-app-obsidian .m-ws-editor .m-connections{border-radius:0}
/* An unnamed note's explorer row says so, as fallback copy (0024), not a
   blank selected bar. */
.m-app-obsidian .m-ws-explorer .m-card-unnamed .m-card-hit:empty::before{content:"Untitled";color:var(--text-muted);font-style:italic}
.m-app-obsidian .m-ws-tools{display:flex;gap:6px;padding:.3rem .5rem .4rem;
  color:var(--text-muted);border-bottom:1px solid var(--border);margin:0 -.5rem .3rem}
.m-app-obsidian .m-ws-tools .m-ws-tool{width:26px;height:26px}
.m-app-obsidian .m-ws-folder{padding:.2rem .5rem;color:var(--text-muted);
  font-size:13.5px;white-space:nowrap}
.m-app-obsidian .m-ws-chev{display:inline-block;width:14px;color:var(--text-faint)}
/* THE FOLDER TREE — the ninth verdict's explorer hierarchy, one level
   deeper for the twelfth. A folder row is a disclosure the host toggles;
   its notes sit one step further in than a note under the vault, and a
   folder inside a folder one step again. The rules name the tree's own
   classes at ANY depth: the first draft named the top level only
   (the tree, then its direct li, then its list), so a nested list kept the generic block
   display over its hidden attribute (a folded month hid nothing) and
   inherited the row indent as a container (its notes sat two indents in
   and wrapped). The pane's generic row rules (li > *) are undone on every
   list, which is a container and not a row. */
.m-app-obsidian .m-ws-explorer .m-ws-tree li.m-ws-tree-folder{display:block;padding:0;margin:0}
.m-app-obsidian .m-ws-explorer .m-ws-tree .m-ws-folder-row{display:flex;align-items:center;
  gap:.15rem;width:100%;padding:.2rem .5rem .2rem .85rem;color:var(--text-muted);font:inherit;
  font-size:13px;font-weight:500;min-height:1.6rem;height:auto;border:0;border-radius:5px;
  background:transparent;box-shadow:none;cursor:pointer;text-align:start;white-space:nowrap;
  overflow:hidden;text-overflow:ellipsis}
.m-app-obsidian .m-ws-explorer .m-ws-tree .m-ws-folder-row:hover{color:var(--text);
  background:transparent}
.m-app-obsidian .m-ws-explorer .m-ws-tree .m-ws-folder-row[aria-expanded="false"] .m-ws-chev{
  transform:translateY(-1px)}
.m-app-obsidian .m-ws-explorer .m-ws-tree .m-ws-folder-items,
.m-app-obsidian .m-ws-explorer .m-ws-tree .m-ws-folder-items:hover{display:block;width:auto;
  padding:0;margin:0;min-height:0;height:auto;overflow:visible;white-space:normal;
  border-radius:0;background:transparent;cursor:default}
.m-app-obsidian .m-ws-explorer .m-ws-tree .m-ws-folder-items[hidden]{display:none}
/* Indents by depth: a note under a folder, a folder inside a folder, a
   note under that. */
.m-app-obsidian .m-ws-explorer .m-ws-tree .m-ws-folder-items>li>.m-card-hit{padding-left:2.15rem}
.m-app-obsidian .m-ws-explorer .m-ws-tree .m-ws-folder-items>li>.m-ws-folder-row{padding-left:1.95rem}
.m-app-obsidian .m-ws-explorer .m-ws-tree .m-ws-folder-items .m-ws-folder-items>li>.m-card-hit{
  padding-left:3.05rem}
.m-app-obsidian .m-ws-vault{margin:auto -.5rem 0;padding:.5rem .75rem 0;
  border-top:1px solid var(--border);display:flex;justify-content:space-between;
  color:var(--text-muted);font-size:13px}
.m-app-obsidian .m-ws-vault-name{display:inline-flex;align-items:center;gap:.4rem}
.m-app-obsidian .m-ws-vault-tools{display:inline-flex;align-items:center;gap:.7rem}
.m-app-obsidian .m-ws-vault .m-inert-glyph{width:15px;height:15px}
.m-app-obsidian .m-ws-tab-x .m-inert-glyph{width:11px;height:11px}

.m-app-obsidian .m-ws-pane.m-ws-properties{display:grid;
  grid-template-columns:max-content 1fr;gap:.35rem .9rem;align-items:baseline;
  padding:.5rem 0;margin:0 auto 1.4rem;border-top:1px solid #303030;
  border-bottom:1px solid #303030;font-size:14px;color:var(--text-muted);
  background:transparent}

.m-app-obsidian .m-ws-properties .m-field{margin:0}
.m-app-obsidian .m-ws-pane-head{display:flex;align-items:center;gap:10px;
  height:36px;margin:-.75rem -.9rem .6rem;padding:0 14px;background:var(--raised);
  border-bottom:1px solid var(--border);font-size:13px;color:var(--text)}
.m-app-obsidian .m-ws-section{display:flex;justify-content:space-between;
  text-transform:uppercase;letter-spacing:.04em;
  font-weight:600;margin:0 0 .5rem}
.m-app-obsidian .m-ws-section-unlinked{margin:.9rem -.9rem 0;padding:.75rem .9rem 0;
  border-top:1px solid var(--border)}
.m-app-obsidian .m-ws-count{color:var(--text-faint);font-weight:400;text-transform:none;
  letter-spacing:0}
/* OBSIDIAN'S SHELL COLUMNS TAKE THE PAGE GROUND, and this is not the shared
   answer the generic rule above carries -- it is this look OVERRIDING ITS
   OWN sidebar rule. .m-app-obsidian .m-ws-sidebar paints 262626 at 0-2-0,
   and the two shell-level sidebars must be 1e1e1e like the reference's
   explorer, so this rule at 0-4-0 puts them back. Found by promoting the
   shared half and watching exactly these two elements move; the gap half
   went generic and only the ground stayed. */
.m-app-obsidian>.m-region>.m-region.m-stack{background:var(--page)}
/* The chrome modules are the app's own components, and Morph's dashed
   capability box is no longer something a look has to reset: decision 0071
   moved it onto the REFUSAL note, which is the actually-unstyled case, so a
   component simply draws its own box. All six looks carried the identical
   reset before that, which is what made it the defect rather than the fix. */
.m-app-obsidian{--ribbon-gap:.4rem;--ribbon-padding:.55rem 0;
  --ribbon-ground:var(--ws-rail);--ribbon-edge:1px solid #111;
  --ribbon-ink:var(--text-muted);
  --ribbon-padding-in-band:0 8px 0 0;--ribbon-ground-in-band:transparent;
  --ribbon-edge-in-band:medium none currentcolor}
.m-app-obsidian .m-ws-ribbon-space{flex:1}
.m-app-obsidian .m-ws-pane{background:var(--raised);display:flex;
  gap:0;overflow:auto}
.m-app-obsidian .m-ws-explorer{border-right:1px solid var(--border);padding:.5rem .5rem;
  font-size:13.5px}

.m-app-obsidian .m-ws-explorer .m-collection,.m-app-obsidian .m-ws-backlinks .m-collection{display:block;
  gap:0;margin:0;padding:0}
.m-app-obsidian .m-ws-explorer .m-collection>li{display:block;padding:0;margin:0;border:0;
  background:transparent;box-shadow:none}
.m-app-obsidian .m-ws-explorer .m-collection>li>*{display:block;width:100%;
  min-height:1.75rem;height:auto;padding:.2rem .5rem .2rem 1.35rem;
  border-radius:5px;color:#c8c8c8;white-space:nowrap;overflow:hidden;
  text-overflow:ellipsis;font:inherit;font-size:13.5px;line-height:1.5;border:0;
  background:transparent;box-shadow:none;cursor:pointer;text-align:start}
.m-app-obsidian .m-ws-explorer .m-collection>li>*:hover{background:var(--surface)}
.m-app-obsidian .m-ws-explorer .m-collection>li[aria-current="true"]>*,
.m-app-obsidian .m-ws-explorer .m-collection>li.m-current>*{background:#3a3a3a;color:#fff}
.m-app-obsidian .m-ws-explorer .m-role-title,.m-app-obsidian .m-ws-backlinks .m-role-title{font-size:inherit;
  font-weight:inherit;margin:0;line-height:inherit}
/* THE STRIP IS BOUNDED — @Codex's P2 on c8b317d. 0198 made the strip
   accumulate without limit while every tab was white-space:nowrap at the
   default flex minimum, so past a handful of notes the tabs stopped fitting
   the editor column and ran into the pane beside it. Obsidian's own answer is
   the one taken here: the tabs SHRINK and their labels ellipsize, so every
   open note stays pointable-at. An overflow of hidden is the floor under it —
   if even the minimum widths do not fit, the strip clips rather than
   escaping, which is a bounded presentation rather than a broken one. */
.m-app-obsidian .m-ws-tabs{display:flex;align-items:flex-end;gap:2px;height:36px;
  padding:0 .4rem;background:var(--raised);border-bottom:1px solid var(--border);
  font-size:13px;color:var(--text-muted);overflow:hidden}
.m-app-obsidian .m-ws-tablist{display:contents}
.m-app-obsidian .m-ws-tab{padding:7px 14px;white-space:nowrap;border:0;background:transparent;
  color:inherit;font:inherit;cursor:default;overflow:hidden;text-overflow:ellipsis;
  display:block;text-align:left}
/* An inactive tab is a BUTTON — the user agent's white button face showed
   through on the first screenshot of it; it wears the strip's own look. */
.m-app-obsidian button.m-ws-tab{cursor:pointer}
/* Only the ACTIVE tab carries a close, so an inactive tab fills its slot
   exactly and the hover paint has no reason to climb to the chip. */
.m-app-obsidian button.m-ws-tab:hover{background:var(--surface);color:var(--text)}
.m-app-obsidian button.m-ws-tab:focus-visible{outline:2px solid var(--focus-ring);outline-offset:-2px}
/* The chip is TWO ELEMENTS now (0072): the active tab and the action slot
   beside it. Both take the chip's ground, and --m-tab-action-pull cancels the
   strip's 2px gap across the junction between them, so what a person reads as
   one tab has nothing drawn down its middle. */
.m-app-obsidian .m-ws-tab-on,
.m-app-obsidian .m-ws-tab-action{background:var(--page);color:var(--text)}
.m-app-obsidian .m-ws-tab-x,.m-app-obsidian .m-ws-tab-plus{color:var(--text-faint)}
.m-app-obsidian .m-ws-tab-plus{padding:7px 10px;background:none;border:0;color:var(--text-muted);
  font:inherit;font-size:16px;line-height:1;cursor:pointer;border-radius:4px}
.m-app-obsidian .m-ws-tab-plus:hover{color:var(--text)}
.m-app-obsidian .m-ws-editor{background:var(--page);padding:2.2rem 0 4rem;align-items:center;position:relative}
.m-app-obsidian .m-ws-editor>*{width:min(700px,calc(100% - 4rem));margin:0 auto}
/* The title is the TEXT colour, not a brighter one — @Codex's finding 7 in
   the Obsidian viewed comparison: "the title is also pure white while the
   reference title is visibly softer".

   Measured off the pinned capture as the BRIGHTEST pixel in a patch of each
   run of text, because antialiasing makes every glyph a gradient from the
   ground to the ink: a mode returns the GROUND and a mean returns a value
   nothing is painted. The extreme is the ink.

     note title   #dadada     h2 heading  #dadada     active tree row  #dadada
     body prose   #b3b3b3     explorer row #b3b3b3    inactive tab     #666666

   So the reference paints its title, its headings and its selected rows all
   at ONE value, and that value is its body text's -- Obsidian does not make
   a title brighter than what it sits above. Ours was #ffffff against a
   --text of #dcddde.

   The RELATION is what is asserted, not the number. Every absolute above is
   a lower bound: the capture is a downscale, so no pixel need reach full
   coverage and the true ink may be brighter than the brightest sample. What
   the samples do establish is that the title and the text are the SAME, and
   that is a relation a downscale cannot invent.

   And it takes a literal out of an app-scoped rule, which the 2026-09-03
   amendment asks for independently of the finding. */
.m-app-obsidian .m-ws-editor .m-role-title{font-size:30px;font-weight:700;color:var(--text);
  letter-spacing:-.01em;line-height:1.25;margin:0 0 .6rem}
.m-app-obsidian .m-ws-editor .m-region.m-row{display:flex;flex-wrap:wrap;gap:.5rem 1.6rem;
  color:var(--text-muted);font-size:14px;margin:0 0 1.4rem}
.m-app-obsidian .m-ws-editor .m-region.m-row .m-role-label,.m-app-obsidian .m-ws-editor .m-region.m-row dt{
  color:var(--text-muted)}
.m-app-obsidian .m-ws-editor .m-role-body{font-size:16px;line-height:1.6;color:var(--text)}
.m-app-obsidian .m-ws-editor a,.m-app-obsidian .m-ws-editor .m-ref-hit{color:var(--accent);
  text-decoration:underline;text-decoration-color:rgb(168 130 255/.5);
  text-underline-offset:3px}
.m-app-obsidian .m-ws-editor .m-connections{border:0;
  background:transparent}
.m-app-obsidian .m-ws-editor .m-connections-head{font-size:12px;text-transform:uppercase;
  letter-spacing:.04em;color:var(--text-muted);font-weight:600;margin:0 0 .4rem}


/* The visible chip is the button that OPENS the target (.m-slot-open); the
   .m-ref-card beside it is the hover preview and keeps the base styling.
   No backticks in this block: it lives inside a template literal, and one
   ended the stylesheet early — "slot is not defined" at page load.
   A link reads as a wikilink: violet, regular weight, underlined. Attach and
   detach stay quiet — they are Obsidian's "+" affordance, not a call to
   action. */
.m-app-obsidian .m-ws-editor .m-slot button{border:0;background:transparent;
  color:var(--text-faint);font-size:13px;font-weight:400;padding:.1rem .35rem;min-height:0}
.m-app-obsidian .m-ws-editor .m-slot button:hover{color:var(--text);background:var(--surface)}
.m-app-obsidian .m-ws-editor .m-slot .m-slot-open{
  text-decoration-color:rgb(168 130 255/.5);text-underline-offset:3px}

/* Obsidian's status bar is a BOX in the window's bottom-right corner, not
   a bar across the foot — the reference render: "1 backlink 🕮 206 words"
   in a small muted box over the editor's corner. Absolute, so the panes
   run under it, as the app's do. */
.m-app-obsidian .m-ws-status{position:absolute;right:0;bottom:0;gap:14px;
  height:auto;padding:5px 12px 6px;
  border:1px solid var(--border);border-right:0;border-bottom:0;border-top-left-radius:6px;
  color:#8a8a8a}
.m-app-obsidian .m-ws-status-vault{color:#4caf50}
/* The relations under the note read as PROPERTIES, not as a database form —
   the first deployed verdict: "relations trapped in the Morph-like
   CONNECTIONS table … grey Add controls read as database fields". The
   heading stays for a reader (a landmark) and leaves the pixels; each
   relation is a name and its violet links; attach is a quiet "+". */
.m-app-obsidian .m-ws-editor .m-connections-head{position:absolute;width:1px;
  height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
.m-app-obsidian .m-ws-editor .m-connections{margin-top:.2rem;display:flex;
  flex-direction:column}
.m-app-obsidian .m-ws-editor .m-slot{display:flex;flex-wrap:wrap;border:0;background:transparent}
.m-app-obsidian .m-ws-editor .m-slot-head{
  text-transform:none;letter-spacing:0;font-weight:400;min-width:0}
/* NO display HERE — decision 0074 row 3, and this one was dead twice over: it
   is the comma-separated TEXT treatment that the pill below superseded, its
   companion comma is cancelled by content:none further down, and both lost to
   the generic anyway. */
.m-app-obsidian .m-ws-editor .m-slot-item+.m-slot-item::before{color:var(--text-muted)}
.m-app-obsidian .m-ws-editor .m-slot .m-attach{font-size:0;padding:0 .3rem;line-height:1}
.m-app-obsidian .m-ws-editor .m-slot .m-attach::before{content:"+";font-size:14px}
.m-app-obsidian .m-ws-editor .m-slot .m-attach:hover::before{color:var(--text)}
/* ONE PROPERTIES PANEL — the sixth deployed verdict: the three-column
   strip "feels generated and database-like", and the relation rows with
   trailing + are "the strongest remaining form-editor residue". Obsidian's
   properties are a vertical list, name left in a fixed column, value
   right, list values as link pills; the relations are more rows of the
   same list. The two blocks share the column, the rule and the rhythm,
   so the eye reads one panel. Later in the sheet than the rules above at
   equal specificity, on purpose. */
.m-app-obsidian .m-ws-editor .m-region.m-row.m-ws-properties{display:grid;
  grid-template-columns:132px minmax(0,1fr);gap:.1rem .8rem;align-items:center;
  margin:.5rem 0 0;padding:.4rem 0 .3rem;border-top:1px solid #303030;border-bottom:0}
.m-app-obsidian .m-ws-prop{display:contents}
.m-app-obsidian .m-ws-prop-name{color:#8a8a8a;font-size:13.5px;padding:.15rem 0}
.m-app-obsidian .m-ws-properties .m-field{color:var(--text);font-size:13.5px;padding:.15rem .3rem;
  border-radius:4px}
.m-app-obsidian .m-ws-properties .m-field:hover{background:var(--surface)}
.m-app-obsidian .m-ws-prop .m-editable:empty::before{content:"Empty";color:#7a7a7a}
.m-app-obsidian .m-ws-editor .m-connections{margin:0 0 1rem;padding:.1rem 0 .4rem;
  border-top:0;border-bottom:1px solid #303030;gap:.1rem}
.m-app-obsidian .m-ws-editor .m-slot{align-items:center;gap:.25rem .35rem;padding:.05rem 0}
.m-app-obsidian .m-ws-editor .m-slot-head{flex:0 0 132px;margin-right:.45rem;font-size:13.5px;color:#8a8a8a}
.m-app-obsidian .m-ws-editor .m-slot-item+.m-slot-item::before{content:none}
.m-app-obsidian .m-ws-editor .m-slot .m-slot-open{display:inline-block;font-size:13.5px;
  padding:.1rem .5rem;border-radius:4px;background:#2c2c2c;color:var(--accent);
  text-decoration:none}
.m-app-obsidian .m-ws-editor .m-slot .m-slot-open:hover{background:#363636;text-decoration:underline;
  text-decoration-color:var(--accent);text-underline-offset:3px}
.m-app-obsidian .m-ws-editor .m-slot .m-attach::before{color:#7a7a7a}
/* The + is CONTEXTUAL — the seventh deployed verdict (three sandboxes): a
   trio of resting + controls was the strongest surviving form-editor
   signal. Obsidian adds a list value by hovering or focusing the row; the
   control is still in the tab order and appears the moment the row is
   hovered or anything in it has focus. */
.m-app-obsidian .m-ws-editor .m-slot .m-attach{opacity:0;transition:opacity .1s}
.m-app-obsidian .m-ws-editor .m-slot:hover .m-attach,
.m-app-obsidian .m-ws-editor .m-slot:focus-within .m-attach{opacity:1}
/* A pill and its remove control are one unit; the control shows on hover
   or focus as Obsidian's × does, and stays reachable by keyboard. */
/* NOR here — 0074 row 3 again. The look restated the Connections component's
   own inline-item decision and tied it at (0,3,0); the alignment and gap stay,
   because those are Obsidian's. */
.m-app-obsidian .m-ws-editor .m-slot-item{align-items:center;gap:.1rem}
.m-app-obsidian .m-ws-editor .m-slot-item .m-detach{font-size:0;padding:0;width:0;min-width:0;
  margin:0;border:0;overflow:hidden;color:#7a7a7a;border-radius:3px}
.m-app-obsidian .m-ws-editor .m-slot,.m-app-obsidian .m-ws-editor .m-slot+.m-slot{border:0}
.m-app-obsidian .m-ws-editor .m-slot-item .m-detach::before{content:"×";font-size:13px;line-height:1}
.m-app-obsidian .m-ws-editor .m-slot-item:hover .m-detach,
.m-app-obsidian .m-ws-editor .m-slot-item .m-detach:focus-visible{width:auto;padding:0 .3rem}
/* THE BLANK NOTE'S FOLD — the twelfth verdict. One quiet disclosure
   where the properties panel was, closed, so the body prompt sits under
   the title; open, the panel and the relations draw exactly as before. */
.m-app-obsidian .m-ws-editor .m-ws-blank-fold{margin:.2rem 0 .6rem;border:0;background:transparent;padding:0;border-radius:0;box-shadow:none}
.m-app-obsidian .m-ws-editor .m-ws-blank-fold-head{font-size:12.5px;color:var(--text-faint);cursor:pointer;
  list-style:none;padding:.15rem 0;user-select:none}
.m-app-obsidian .m-ws-editor .m-ws-blank-fold-head::-webkit-details-marker{display:none}
.m-app-obsidian .m-ws-editor .m-ws-blank-fold-head::before{content:"›";display:inline-block;width:14px;
  color:var(--text-faint);transition:transform .12s ease}
.m-app-obsidian .m-ws-editor .m-ws-blank-fold[open]>.m-ws-blank-fold-head::before{transform:rotate(90deg)}
.m-app-obsidian .m-ws-editor .m-ws-blank-fold-head:hover{color:var(--text)}
/* The prose starts under the panel with a breath, as under Obsidian's. */
.m-app-obsidian .m-ws-editor .m-connections+.m-role-body{margin-top:1.1rem}
/* The title placeholder reads as a title, not as a hint (the sixth verdict:
   "too muted"). */
.m-app-obsidian .m-ws-editor .m-role-title.m-editable:empty::before{color:#a3a3a3}
/* The SEARCH pane — Obsidian's, in the left sidebar: a query field at the
   top, a quiet count, then the hits as rows of title and the prose they
   matched in. The left sidebar's panes keep the explorer's ground. */
.m-app-obsidian .m-ws-left>.m-ws-pane{padding:.5rem .5rem .5rem;border:0}
.m-app-obsidian .m-ws-left .m-ws-side-tabs{margin:0}
.m-app-obsidian .m-ws-search-box{padding:.2rem .25rem .45rem}
.m-app-obsidian .m-ws-search-input{background:#1a1a1a;border:1px solid #363636;
  border-radius:6px;font-size:13.5px;padding:.4rem .6rem}
/* A quiet focus — the tenth verdict: the ring was "heavier than the rest
   of the sidebar chrome". The app lights the border and nothing else. */
.m-app-obsidian .m-ws-search-input:focus{border-color:rgb(168 130 255/.6);box-shadow:none}
.m-app-obsidian .m-ws-search-input::placeholder{color:var(--text-faint)}
.m-app-obsidian .m-ws-search .m-ws-section{padding:.3rem .25rem 0}
.m-app-obsidian .m-ws-pane.m-ws-search .m-collection>li{display:block;padding:.4rem .5rem;margin:0 0 .2rem;
  border:0;border-radius:5px;background:transparent;box-shadow:none}
.m-app-obsidian .m-ws-pane.m-ws-search .m-collection>li>*{border:0;background:transparent;padding:0;box-shadow:none}
.m-app-obsidian .m-ws-pane.m-ws-search .m-role-title{font-weight:600;color:#e0e0e0;font-size:13px;margin:0 0 .15rem}
.m-app-obsidian .m-ws-pane.m-ws-search .m-role-body{color:var(--text-muted);font-size:12.5px;line-height:1.4;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.m-app-obsidian .m-ws-pane.m-ws-search .m-card-hit:hover{background:var(--surface)}
/* The matched term in a hit — the app's own highlight, a translucent
   amber over the text, no colour change. */
.m-app-obsidian .m-ws-pane.m-ws-search .m-ws-hl{background:rgb(255 208 0/.35);color:inherit;
  border-radius:2px;padding:0 1px}
.m-app-obsidian .m-ws-backlinks{border-left:1px solid var(--border);padding:.75rem .9rem;
  font-size:13.5px}
.m-app-obsidian .m-ws-backlinks .m-collection>li{display:block;padding:.4rem .5rem;margin:0 0 .25rem;
  border:0;border-radius:5px;background:transparent;box-shadow:none}
.m-app-obsidian .m-ws-backlinks .m-collection>li>*{border:0;background:transparent;padding:0;
  box-shadow:none}
.m-app-obsidian .m-ws-backlinks .m-role-title{font-weight:600;color:#e0e0e0;font-size:13px;
  display:block}
.m-app-obsidian .m-ws-backlinks .m-role-body{color:var(--text-muted);font-size:12.5px;
  line-height:1.4;margin-top:2px;display:-webkit-box;-webkit-line-clamp:3;
  -webkit-box-orient:vertical;overflow:hidden}
.m-app-obsidian .m-ws-backlinks .m-collection:empty::after{content:"No backlinks found.";
  color:var(--text-faint);font-size:12.5px}

/* Placement — decision 0014.
   Grid areas, because a grid's columns run along the INLINE axis: the first
   column is inline-start in English and inline-start in Arabic, and it moves on
   its own when 'dir' changes. That is the entire reason the vocabulary refuses
   'left' — a physical edge would have needed a second stylesheet to be correct
   in half the world, and would silently have been wrong instead. */
.m-surface{position:relative;display:grid;gap:var(--gap);
  grid-template-columns:auto minmax(0,1fr) auto;
  grid-template-rows:auto minmax(0,1fr) auto;
  grid-template-areas:"bs bs bs" "is main ie" "be be be"}
.m-surface > *{grid-area:main;min-width:0}
.m-place[data-morph-edge="block-start"]{grid-area:bs}
.m-place[data-morph-edge="block-end"]{grid-area:be}
.m-place[data-morph-edge="inline-start"]{grid-area:is}
.m-place[data-morph-edge="inline-end"]{grid-area:ie}

/* A docked panel reads as a panel, not as loose content sitting at the edge. */
.m-place[data-morph-place="docked"]{background:var(--raised);
  border:1px solid var(--border-subtle);border-radius:12px;padding:var(--gap)}
.m-place[data-morph-edge="inline-start"],.m-place[data-morph-edge="inline-end"]{
  inline-size:var(--panel,16rem)}
/* Where there is no room beside the content, a panel goes ABOVE it rather than
   squeezing it. Measured in Chromium at 390px before this existed: the panel
   took 382px and the content column resolved to width ZERO — the text was in
   the DOM, off-screen, and nothing reported it, because 'minmax(0,1fr)' lets
   the main column collapse instead of overflowing.

   This is the renderer owning a relationship, which is its job: at a width
   where "beside" does not exist, "beside" is not a layout the spec can have.
   The spec is unchanged and still says inline-end — the same lens on a wide
   screen still docks. Notion and Obsidian both do exactly this, and neither
   asks the document to say so. */
@media (max-width: 34rem) {
  /* grid-template-areas is what DEFINES the three columns, so resetting only
     grid-template-columns left the grid three wide and the panel still beside
     the content at 58px. Measured, not assumed. */
  .m-surface{grid-template-areas:none;grid-template-columns:minmax(0,1fr);
    grid-template-rows:auto}
  .m-surface > *{grid-area:auto}
  .m-place[data-morph-edge="inline-start"],
  .m-place[data-morph-edge="inline-end"]{grid-area:auto;inline-size:auto}
}

.m-place[data-morph-extent="hug"]{--panel:max-content}
.m-place[data-morph-extent="compact"]{--panel:12rem}
.m-place[data-morph-extent="roomy"]{--panel:22rem}

/* Floating. 'rest' is where it sits before anyone has dragged it; once view
   state holds a position the host writes inset directly and these lose. Logical
   insets throughout, for the same reason the edges are logical. */
.m-place[data-morph-place="floating"]{position:absolute;z-index:30;
  inline-size:var(--panel,15rem);background:var(--page);
  border:1px solid var(--border);border-radius:12px;padding:var(--gap);
  box-shadow:0 14px 40px -16px rgb(0 0 0/.45)}
.m-place[data-morph-rest="block-start"]{inset-block-start:var(--gap);
  inset-inline-start:50%;translate:-50% 0}
.m-place[data-morph-rest="block-end"]{inset-block-end:var(--gap);
  inset-inline-start:50%;translate:-50% 0}
.m-place[data-morph-rest="inline-start"]{inset-inline-start:var(--gap);
  inset-block-start:50%;translate:0 -50%}
.m-place[data-morph-rest="inline-end"]{inset-inline-end:var(--gap);
  inset-block-start:50%;translate:0 -50%}
.m-place[data-morph-rest="center"]{inset-block-start:50%;inset-inline-start:50%;
  translate:-50% -50%}

/* Anchored — attached to another node rather than to the surface.
   CSS anchor positioning does this natively where it exists; where it does not,
   the 'position-anchor' and 'anchor()' declarations are simply dropped by the
   parser and the panel keeps the absolute position the floating rules gave it.
   That degradation is stated rather than hidden: it lands at the surface edge
   instead of beside its anchor, which is wrong but never invisible. */
.m-place[data-morph-place="anchored"]{position:absolute;z-index:30;
  inline-size:var(--panel,15rem);background:var(--page);
  border:1px solid var(--border);border-radius:12px;padding:var(--gap);
  box-shadow:0 14px 40px -16px rgb(0 0 0/.45);
  inset-block-start:var(--gap);inset-inline-end:var(--gap)}
@supports (anchor-name: --a) {
  .m-place[data-morph-attach="inline-end"]{
    inset-inline-start:anchor(inline-end);inset-block-start:anchor(block-start)}
  .m-place[data-morph-attach="inline-start"]{
    inset-inline-end:anchor(inline-start);inset-block-start:anchor(block-start)}
  .m-place[data-morph-attach="block-start"]{
    inset-block-end:anchor(block-start);inset-inline-start:anchor(inline-start)}
  .m-place[data-morph-attach="block-end"]{
    inset-block-start:anchor(block-end);inset-inline-start:anchor(inline-start)}
  .m-place[data-morph-attach="center"]{
    inset-block-start:anchor(center);inset-inline-start:anchor(center)}
}

/* A placed panel is a compact surface: it sits BESIDE the document rather than
   being it, so a title inside one is a list label and not a headline. This is a
   relationship about placement — it holds for every placed node, not for the
   demo's sidebar — which is the renderer's own job in the way the header/rows
   relationship is. The sizes still come from the resolved scale; nothing here
   invents a number. */
/* Measured before this changed, on the Notes pool: a title in the TABLE beside
   it renders at var(--s0) weight 400, and the panel rendered the same names at
   var(--s0) weight 600. The size was already subordinate; the weight was
   inverted — the list pointing AT the document was heavier than the document.
   Body weight, so a panel item reads as a name in a list, which is what it is. */
.m-place .m-role-title{font-size:var(--s0);font-weight:400;letter-spacing:0;line-height:1.35}
.m-place .m-region,.m-place .m-collection{gap:calc(var(--gap) * .5)}
.m-place .m-media{display:none}

/* A collapsed panel is VIEW STATE, so the host sets this attribute from
   view-state.ts — the spec cannot say it and never should. */
.m-place[data-morph-collapsed="true"] > *:not(.m-place-toggle){display:none}
.m-place[data-morph-collapsed="true"]{inline-size:auto;min-inline-size:0;padding:.25rem}
.m-place-toggle{float:inline-end;font:inherit;line-height:1;min-width:var(--target);
  min-height:var(--target);border-radius:7px;border:1px solid var(--border-subtle);
  background:none;color:var(--text-muted);cursor:pointer}
.m-place-toggle:hover{color:var(--text);border-color:var(--border-strong)}
.m-place-toggle:focus-visible{outline:2px solid var(--focus-ring);outline-offset:2px}
.m-anchor{display:contents}
.m-capability-anchor{display:block;min-width:0}

/* A button. It says what it does, because its label is derived from the
   declared effects rather than authored — see invokeButton. */
.m-invoke{font:inherit;font-size:calc(var(--s0) * .86);font-weight:600;
  min-height:var(--target);padding:.3em .9em;border-radius:8px;
  border:1px solid var(--border);background:var(--raised);color:var(--text);
  cursor:pointer;align-self:flex-start;
  transition:border-color .14s ease,background .14s ease}
.m-invoke:hover{border-color:var(--border-strong);background:var(--surface-hover)}
.m-invoke:focus-visible{outline:2px solid var(--focus-ring);outline-offset:2px}
.m-invoke[disabled]{opacity:.5;cursor:not-allowed}
/* A REPEATED row action is quieter than a panel one.

   @Codex drove the deployed board: "one full-size Update block per card
   materially increases every row's height and makes the board much noisier than
   the rest of Morph." Correct. A panel button is one control a person goes to;
   a row button appears once per object, and at thirteen the control stops being
   an affordance and becomes the texture of the page.

   IT KEEPS min-height:var(--target), and that is the one line of @Codex's own
   patch not taken. Its version set min-height:2rem — 32px, which clears the
   24px WCAG 2.5.8 floor a11y.ts checks and undercuts the 44px tokens.ts holds
   at EVERY density, with the comment "target never shrinks. See a11y.ts."
   checkFloor reads ir.tokens.minTargetPx, which would still report 44, so the
   gate could not have caught a stylesheet lowering it. A floor asserted at one
   layer and quietly undercut at another is the defect this repo keeps finding
   in itself.

   So the weight comes off the BOX instead: no fill, no border until hover,
   muted until hover, tighter type.

   AND THE HEIGHT DOES NOT COME BACK, which the first version of this comment
   claimed. @Codex measured it: the button keeps min-height:var(--target) and is
   still emitted after the card's contents as a separate normal-flow control, so
   every board card is still a full 44px line taller than one without an action.
   Only the TABLE gets the height for free, because there the button is a cell
   in a row that already exists.

   That is a real limit and it is not fixed here. Making a card's action share a
   line with its contents is a change to how a card lays out — every card in the
   product, not only the ones with actions — and it belongs in its own task
   with its own measurements rather than at the end of this one. Task 0088.

   NO BACKTICKS IN HERE — BASE_CSS is a template literal. */
.m-invoke-row{font-weight:500;font-size:calc(var(--s0) * .78);
  padding:.15em .55em;background:transparent;border-color:transparent;
  color:var(--text-muted)}
.m-invoke-row:hover{border-color:var(--border);background:var(--raised);
  color:var(--text)}
/* NOT LOWERED, and task 0213 records why. This ties .m-app-fabric .m-ws-editor
   at (0,2,0) on flex-direction, and :where(.m-stack) would resolve it safely —
   measured: nothing generic declares flex-direction at (0,1,0) on a stack
   region, and a six-door capture moved 0 of 1818 values. It is refused for a
   different reason. region-flow.test.ts DERIVES this selector from the FLOWS
   vocabulary, asking for a rule at .m-region.m- plus each of the four flow names,
   and that derivation is the guard's whole purpose: its own comment records
   that a hand-listed version could not notice the vocabulary growing. Any
   spelling :where() produces is one the derivation cannot reach, so resolving
   this tie here buys one row and costs a guard that cannot go stale. */
.m-region.m-stack{flex-direction:column}
:where(.m-region.m-row){flex-direction:row;align-items:flex-start}
.m-region.m-flow{flex-wrap:wrap}
/* "grid" is in FLOWS and had no rule at all, so a region that asked for a grid
   got the bare .m-region flex row — the vocabulary said one thing and the
   stylesheet did another. Nothing in the generator emits it today (every
   grid flow in the corpus is on a COLLECTION, whose layout comes from
   .m-as-gallery and friends), so this was latent rather than visibly broken.

   :not(.m-surface) is the load-bearing part. A region hosting a placed child
   also carries .m-surface, which is its own grid with named template areas —
   and .m-region.m-grid outranks .m-surface on specificity, so an unguarded
   rule would have silently replaced the placement grid on every surface that
   happened to declare a grid flow. Fixing a latent bug by introducing a live
   one is a bad trade. */
.m-region.m-grid:not(.m-surface){display:grid;
  grid-template-columns:repeat(auto-fill,minmax(clamp(8rem,14vw,13rem),1fr))}

.m-collection{list-style:none;margin:0;padding:0}

/* gallery — tiles sized by the media aspect the spec asked for */
.m-as-gallery{display:grid;gap:var(--gap);
  grid-template-columns:repeat(auto-fill,minmax(clamp(8rem,14vw,13rem),1fr))}
.m-as-gallery .m-card{min-width:0}
.m-as-gallery .m-media{width:100%;border-radius:8px;
  background-color:var(--surface);background-size:cover;background-position:center;
  box-shadow:0 1px 2px rgb(0 0 0/.06),0 8px 24px -12px rgb(0 0 0/.25);
  transition:transform .18s cubic-bezier(.2,.7,.3,1),box-shadow .18s ease}
.m-as-gallery .m-card-hit:hover .m-media{transform:translateY(-3px) scale(1.012);
  box-shadow:0 2px 4px rgb(0 0 0/.08),0 18px 36px -16px rgb(0 0 0/.35)}
.m-as-gallery .m-field{margin-top:.5em}

/* list — thumbnail beside text */
.m-as-list{display:flex;flex-direction:column;gap:calc(var(--gap) * .5)}
.m-as-list .m-card-hit{display:grid;grid-template-columns:auto 1fr;gap:var(--gap);
  align-items:center;padding:calc(var(--gap) * .5);border-radius:10px;
  transition:background .14s ease}
.m-as-list .m-card-hit:hover{background:var(--surface)}
/* A STILL in a list is a thumbnail. */
.m-as-list .m-media{width:3.25rem;border-radius:6px;background-color:var(--surface);
  background-size:cover;background-position:center}

/* A RECORDING is not. The thumbnail rule applied to every .m-media caught video and
   audio too, collapsing a 16:9 player and its transport into a 3.25rem black
   square — present, correct, and impossible to operate. A player needs a
   content width; a poster does not. Found by @Codex driving the preview. */
.m-as-list .m-av,
.m-as-list .m-audio{grid-column:1 / -1;width:100%;max-width:min(46rem, 100%)}
.m-as-list .m-video{aspect-ratio:16/9;height:auto}
.m-as-list .m-audio audio{width:100%}

/* table — one column per bound role, and since 0173's as-table slice a
   TOKEN SET rather than three near-identical app blocks.

   Notion, Coda and Fibery each carried their own .m-as-table, th, td and
   td[data-m-name] rules, and Notion and Fibery a :last-child edge rule as
   well. Read side by side they were the same declarations with different
   numbers in them: sixteen rules, eighteen selectors, one shape. A seventh
   app wanting a table needed a seventeenth.

   Every default below is what the generic rule gave before the slice, spelled
   as CSS's own initial value where the property had no declaration at all —
   medium none currentcolor for a border, 0 for a margin — so a look that
   supplies no token renders exactly what it rendered. Proved rather than
   asserted: the computed style of every header, every first-row cell and
   every table across all six doors, before and after, is byte-identical.

   Two tokens are read and set by nobody: --table-head-rule and
   --table-row-rule, where all three looks happened to want the default.
   They are the seam a seventh look needs, not decoration — and the day one
   is set, nothing here changes.

   WHAT IS *NOT* A TOKEN is as much the point. box-sizing is not, because
   index.html carries *{box-sizing:border-box} and declaring it here made
   the default content-box and took 5.5px off every Coda row. A cell's
   COLOUR is not either: .m-kind-date,.m-kind-number,.m-kind-enum up the
   sheet mutes those three, and any declaration here — even inherit — beats
   it, so --table-muted-color sits on that set instead. NAMED for the set and
   not for the cell: it reaches those three kinds and no others, and a token
   called --table-cell-color would be a comment claiming a reach the rule
   does not have. A look that sets it to --text is saying its table does not
   draw those kinds quietly. Both were caught by the before/after capture and
   neither by a test. */
.m-as-table{width:var(--table-width,100%);margin:var(--table-margin,0);
  border-collapse:var(--table-collapse,separate);border-spacing:0;
  font-size:var(--table-font,var(--s0));
  border-top:var(--table-top-rule,medium none currentcolor)}
.m-as-table th{text-align:var(--table-head-align,left);
  font-weight:var(--table-head-weight,550);
  font-size:var(--table-head-font,calc(var(--s0) * .82));
  letter-spacing:var(--table-head-tracking,.04em);
  text-transform:var(--table-head-transform,uppercase);
  color:var(--table-head-color,var(--text-muted));
  background:var(--table-head-bg,transparent);
  padding:var(--table-head-pad,0 var(--gap) calc(var(--gap) * .5));
  border-bottom:var(--table-head-rule,1px solid var(--border));
  border-right:var(--table-col-rule,medium none currentcolor)}
.m-as-table td{padding:var(--table-cell-pad,calc(var(--gap) * .55) var(--gap));
  height:var(--table-row-height,auto);
  border-bottom:var(--table-row-rule,1px solid var(--border-subtle));
  border-right:var(--table-col-rule,medium none currentcolor);
  vertical-align:var(--table-cell-align,top)}
/* The row's NAME by its marker, never by its ordinal (0178). The three looks
   that emphasise it all said 500; a look that says nothing inherits the
   weight of the cell it is in, which is what no rule at all gives today. */
.m-as-table td[data-m-name]{font-weight:var(--table-name-weight,inherit)}
/* An EDGE, in 0178's sense: the rightmost cell has no rule to its right
   whatever it holds. Generic because it is true of every look that draws a
   column rule at all, and a no-op for one that draws none. */
.m-as-table th:last-child,.m-as-table td:last-child{border-right:0}
.m-as-table tbody tr{transition:background .12s ease}
/* THE ROW'S OPEN CONTROL (@Codex's P1 on 27a1a65). Generic, and tokenised
   for the same reason the property row is: every table app has row detail —
   Coda, Notion, Fibery, Airtable — so a seventh look re-points --row-open-*
   rather than writing its own selector.

   It is always in the accessibility tree and always focusable; only its
   OPACITY responds to hover and focus, the way Coda's and Notion's do. A
   control that is display:none until hover is a control a keyboard user does
   not have, and a table whose rows can only be opened with a mouse is the
   defect this fixes wearing a different hat.

   :focus AS WELL AS :focus-visible, and the difference was measured rather
   than assumed. Focusing the control reported opacity 0 under
   :focus-visible alone -- correct behaviour, since that pseudo-class is
   about the browser's own heuristic for whether focus should be SHOWN, and
   it declines for a programmatic focus. But a control a script can focus and
   nobody can see is the same defect one layer in, and the cost of the
   broader rule is a visible chevron in a case where a mouse user would not
   have seen one. That trade goes to the keyboard. */
/* THREE classes, deliberately. A look's own .m-app-x .m-as-table th is two
   classes and an element, ties a generic .m-as-table th.m-kind-open, and --
   being later -- resets padding-inline through its padding shorthand, so
   the 2rem gutter grew to 48-56px of content plus padding. .m-collection is
   on the same table element, so naming it costs nothing and wins.
   @Codex's P2 on 49cc0e3. */
.m-collection.m-as-table th.m-kind-open,.m-collection.m-as-table td.m-kind-open{
  box-sizing:border-box;width:var(--row-open-width,2rem);
  min-width:var(--row-open-width,2rem);max-width:var(--row-open-width,2rem);
  padding:0;text-align:center;position:relative}
/* One generic visually-hidden label. The stylesheet already carries this
   exact rule four times under four private names — .m-tl-hidden,
   .m-graph-relations and two more — which is 0173's duplication in the
   accessibility layer rather than the colour one. Not renamed here: a
   harness pins .m-tl-hidden by name, and a rename is its own change with
   its own evidence. Named generically so those four can collapse into it. */
.m-hidden-label{position:absolute;inline-size:1px;block-size:1px;padding:0;
  margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
.m-row-open{display:inline-flex;align-items:center;justify-content:center;
  width:1.5rem;height:1.5rem;padding:0;border:0;border-radius:var(--radius-sm,4px);
  background:transparent;color:var(--row-open-color,var(--text-muted));
  cursor:pointer;opacity:var(--row-open-rest,0)}
/* THE HIT AREA IS THE TOKEN'S, and the painted box is the look's -- @Codex's
   P2 on ed4f7a7, and this file has refused the other resolution twice.
   The button paints 24x24, which clears the 24px WCAG 2.5.8 floor a11y.ts
   checks and undercuts the 44px tokens.ts holds at every density. checkFloor
   reads ir.tokens.minTargetPx, so it goes on reporting 44 while the control
   a finger aims at is 24 -- a floor asserted at one layer and quietly
   undercut at another, which is the exact shape refused in #138 and again on
   the card action.
   The precedent says the target does not move and the height comes off the
   LAYOUT. A table has no layout to give: the gutter is 2rem because the
   reference's is, and forcing the row to 44px would make four looks taller
   than the apps they reproduce. So the hit area leaves the painted box
   instead -- a pseudo-element var(--target) WIDE, over a gutter narrower
   than it. Nothing moves, nothing repaints, and a tap 6px to either side of
   the glyph reaches the button. That overlap of the neighbouring cells is
   the point rather than a cost: the gutter is where a person aims, and the
   title cell's own edit surface begins after its padding.
   The height was var(--target) too, and the note below is why it is not. */
/* AND IT STOPS AT ITS OWN ROW -- task 0185 finding 4, measured rather than
   argued, because the comment above argues the wrong collision. It says the
   hover path enables one row's area at a time so two areas cannot meet, and
   that is true of pseudo against pseudo. The one that exists is an enabled
   area against a NEIGHBOUR'S CELL: with row 4 hovered, at 1440x900 on Coda,
   the row is 39.09px and the centred 44px box runs 3.08px above it and
   1.83px below. Chromium at a point 1px inside row 5, in the gutter column:

     elementFromPoint   BUTTON .m-row-open, in row 4
     the click opened   "Fixed-point layout" -- row 4, aiming at row 5

   That is the opening-the-wrong-object failure the media query below already
   fixed for touch, surviving in the hover path in a two-pixel band.

   The containing block moves to the CELL rather than the height shrinking:
   the horizontal reach is the half of this that was wanted -- the gutter is
   where a person aims -- and a cap on the height alone would have kept the
   overlap wherever a look makes its rows shorter than 44px, which is every
   look that reproduces a table. So the box is the row's height by
   construction and cannot depend on a number nobody re-derives. The button
   gives up its own position:relative for it and needs none of its own; a
   pseudo-element's hits still go to the element it belongs to. */
.m-row-open::after{content:"";position:absolute;left:50%;top:0;
  width:var(--target);height:100%;transform:translateX(-50%);
  pointer-events:none}
/* AND IT ONLY EXTENDS WHERE THE CONTROL IS AVAILABLE, which the first
   version of this rule got wrong and would have shipped.
   The button rests at opacity 0 and an invisible element is still
   hit-testable, so an always-live 44px box sits over the leading edge of
   the title cell and about 10px into the rows above and below it -- a
   person clicking near the top of a title would have opened the row above
   instead of typing. That is a worse defect than the small target it was
   fixing, on a gesture people make constantly.
   The enlarged area matters exactly when the control is reachable, so it
   turns on with the same three conditions that reveal it. The painted 24px
   box stays clickable at rest, as it always was. */
.m-as-table tbody tr:hover .m-row-open::after,
.m-row-open:focus::after,.m-row-open:focus-visible::after{pointer-events:auto}
.m-as-table tbody tr:hover .m-row-open,.m-row-open:focus,.m-row-open:focus-visible{opacity:1}
/* A TOUCH DEVICE has no hover and cannot focus the control before tapping
   it, and every other cell in the row edits a value or opens a chooser — so
   the invisible gutter would be the only route to the object page and a
   finger could not find it. Always visible there, as .m-detach already
   does. @Codex's P2 on 49cc0e3. */
/* TOUCH GETS THE CELL, NOT THE PSEUDO-ELEMENT -- @Codex's P2 on 4317f20,
   and it is the SAME defect I thought I had just fixed, re-created one
   media query over.
   The hover path enables one row's 44px area at a time, which this comment
   read as meaning it could not collide with a neighbour's -- true of pseudo
   against pseudo, and wrong about the collision that existed, which task
   0185 measured and the note above records. Under (hover:none) there is no
   hover to gate on, so the rule enabled EVERY row's at once, and a tap in
   the shared band could be claimed by the next row's later-painted pseudo
   and open the wrong object. (Coda's rows measure 39.09px at 1440x900, not
   the 34px this comment first said; the overlap was about 5px, not 10.) Opening the wrong object is a worse failure than a
   small target, which is what decides the trade below.
   So on touch the BUTTON fills its own gutter cell and the pseudo-element
   stands down. The target is then the cell -- 2rem wide by the row's own
   height -- which cannot overlap anything, because a cell is exactly the
   space that belongs to this control and to nothing else.
   That is 32x39 at Coda's measured row height: above the 24px WCAG 2.5.8 floor
   a11y.ts checks and below the 44px tokens.ts holds. Named rather than
   hidden: the honest reading of the target contract is that it wants a
   taller row on touch, which is a look decision this rule cannot make. */
@media (hover:none){.m-row-open{opacity:1;width:100%;height:100%}
  .m-row-open::after{display:none}}
.m-row-open:focus-visible{outline:2px solid var(--focus-ring);outline-offset:1px}
.m-row-open:hover{background:var(--surface);color:var(--text)}
.m-as-table tbody tr:hover{background:var(--table-row-hover,var(--surface))}
/* A derived value is as real as a stored one, so it is not tinted or badged.
   What it does get is tabular figures: four of the five derivations yield a
   number, and a column of counts that does not line up is harder to read than
   one that does.
   (No backticks in here. A backtick in a comment inside BASE_CSS ends the
   template literal and the build stops parsing. Five times now.) */
.m-cell.m-derived,.m-field.m-derived{font-variant-numeric:tabular-nums}
/* A LOOKUP reads a value that belongs to ANOTHER OBJECT, and says so quietly.

   Tabular figures for the same reason a derived column has them — a lookup of a
   number is a column of numbers. Muted because the value is borrowed: the row
   it sits in does not own it, and a person scanning a table should be able to
   tell the columns that describe this object from the one describing its
   neighbour without reading the header again.

   Not italic, not bracketed, no marker glyph. The heading already says
   "Words (Links to)", and a second signal in every cell would be the fold's
   own mistake — telling the reader the same thing twice and spending a column
   of visual weight to do it.

   NO BACKTICKS IN HERE — BASE_CSS is a template literal. */
.m-cell.m-lookup,.m-field.m-lookup{font-variant-numeric:tabular-nums;
  color:var(--text-muted)}
.m-as-table td.m-kind-number,.m-as-table td.m-kind-score{text-align:right;
  font-variant-numeric:tabular-nums}
.m-as-table td.m-kind-date{font-variant-numeric:tabular-nums}
.m-as-table td:is(.m-kind-date,.m-kind-number,.m-kind-enum){
  color:var(--table-muted-color,var(--text-muted))}
/* A date that wraps onto two lines turns a tidy column into a ragged one, and
   it is never the column worth spending width on. */
.m-as-table td.m-kind-date,.m-as-table td.m-kind-number,
.m-as-table td.m-kind-score{white-space:nowrap}
/* The action column takes the width its button needs and no more, and never
   wraps: a row button that reflows to two lines makes every row in the table
   taller than the values it is beside. Right-aligned because it is the last
   column and a button hard against the values reads as belonging to one. */
.m-as-table th.m-kind-action,.m-as-table td.m-kind-action{width:1%;
  white-space:nowrap;text-align:right}
/* And the table it is in scrolls rather than overflowing — see the comment in
   collection(). A table cannot render below its min-content width, so without
   this the action column pushed the whole table out of its grid area and
   across the docked panel beside it.

   NO BACKTICKS IN HERE. BASE_CSS is a template literal, so a backtick ends it
   and the file stops parsing — the fourth time this has been done in this
   repo, and the second in this file. Code voice in these comments, not prose
   voice. */
.m-table-scroll{overflow-x:auto;max-inline-size:100%}

/* board — a column per group */
.m-as-board{display:flex;gap:var(--gap);align-items:flex-start;overflow-x:auto;
  padding-bottom:calc(var(--gap) * .5)}
/* THE COLUMN, and only the column. A :where() inside :not() contributes
   nothing, so this stays (0,1,0) — a bare :not with the class in it would
   raise it to (0,2,0) and tie every look rule that names the class. */
.m-group:not(:where(.m-lane-caption)){flex:0 0 clamp(13rem,20vw,17rem);background:var(--raised);
  border:1px solid var(--border-subtle);border-radius:12px;padding:var(--gap)}
/* THE CAPTION'S BOX IS THE COMPONENT'S -- task 0173's caption slice.
   Notion and Fibery each RESTATED display:flex and align-items:center, which
   this rule already gives them: duplication removable with no token at all,
   and invisible to a count of selectors because it removes no rule.

   THE COUNT'S PILL STAYS, and the slice's own map said to remove it. That map
   read "both consumers turn all of it off" off a rule inventory and concluded
   a default facing the wrong way. There is a THIRD consumer and it was not in
   the inventory, because it overrides nothing: Morph's own board. Measured,
   the same capture, column 0 of each surface --

     generic tasks board   bg rgb(238,239,240)  radius 999px  pad 1.06 5.29
     Notion                bg transparent       radius 999px  pad 0
     Fibery                bg transparent       radius 0      pad 0

   -- so flipping the default would have stripped the product's own stage of a
   pill in the name of removing one nobody uses. An inventory pairs rules by
   name and cannot see the consumer that writes none. */
.m-group-head{display:flex;align-items:center;justify-content:space-between;
  margin:0 0 var(--gap);font-size:calc(var(--s0) * .84);font-weight:600;
  letter-spacing:.03em;text-transform:uppercase;color:var(--text-muted)}
/* A GROUP NAME IS A NAME, AND A NAME IS NOT SHOUTED -- task 0205.
   The head is a LABEL row and reads as one: uppercase, tracked, muted. The
   name inside it is a value the pool supplies, and every reference draws it
   as ordinary text. It had no rule of its own, so it inherited the label's
   treatment -- and BOTH looks that draw it cancelled that inheritance with
   the same two declarations. Two looks writing one identical cancellation is
   the component asking to exist: the shared vocabulary here is not a size or
   a colour, it is the statement that this text is a name.
   A seventh app that groups a collection now gets a readable name rather
   than a shouted one, which is the standing question answered the right way
   -- and it costs no look a rule, because both of them delete two. */
.m-group-name{text-transform:none;letter-spacing:0}
.m-count{font-variant-numeric:tabular-nums;background:var(--surface);
  border-radius:999px;padding:.1em .5em;font-size:.9em}
.m-subgroup{margin-block-start:.35rem}
.m-subgroup-head{margin:0 0 .25rem;font-size:calc(var(--s0) * .78);
  font-weight:600;letter-spacing:.02em;text-transform:uppercase;
  color:var(--text-dim);border-block-end:1px solid var(--border-subtle);
  padding-block-end:.15rem}
/* LANES: a board whose second grouping is drawn as rows across the columns —
   a head row of the columns' heads over the lane label's corner, then a row
   per lane, its label at the left and a cell per column. */
.m-as-lanes{flex-direction:column;align-items:stretch;gap:0;--lane-track:minmax(13rem,1fr);--lane-rail:2.5rem}
/* The tracks are the host's to override: a FOLDED column's track is the rail's
   width, and only the host knows which columns are folded (view state). The
   fallback is every column at its full track. */
.m-lane{display:grid;grid-template-columns:8rem var(--lane-cols,repeat(var(--cols,1),var(--lane-track)));gap:var(--gap);align-items:start}
/* LOWERED WITH :where() — decision 0073's first permitted resolution, task
   0213. This tied .m-app-fibery .m-lane at (0,2,0) on padding-top, and a tie
   is settled by position in the stylesheet: the look happens to come later,
   which nobody declared. The FIRST .m-lane is what the rule is about — a lane
   after another one — so zeroing it costs nothing the selector was saying and
   leaves (0,1,0), under every look. Pixel-neutral by construction because the
   look already won; proven by capture rather than by that argument. */
:where(.m-lane)+.m-lane{border-top:1px solid var(--border-subtle);padding-top:var(--gap)}
.m-lane-label{margin:0;padding:.4rem .5rem;font-size:calc(var(--s0) * .85);font-weight:600;color:var(--text-muted)}
/* THE CAPTION, saying what it IS rather than what it is not. This replaces a
   generic lane-head rule that existed to RETRACT the column rule above, and
   the capture is what settled the difference: dropping the retraction moved
   flex-shrink, min-width and border-radius on all four Fibery captions, so two
   of those three were the caption's own values wearing a retraction's clothes.
   A caption does not grow or shrink with its lane and may narrow freely. */
.m-lane-caption{flex:none;min-width:0;max-width:none}
/* A FOLDED column is VIEW STATE, so the host sets this attribute (as for a
   panel): the column folds to a rail carrying its name and count, its cards
   hidden in the plain board and in every lane's cell alike, and the toggle —
   revealed on hover or focus while open — stays visible once folded, which is
   what makes the fold recoverable. */
.m-group-toggle{margin-inline-start:.35rem;font:inherit;line-height:1;min-width:1.5rem;min-height:1.5rem;border-radius:6px;
  border:1px solid transparent;background:none;color:var(--text-muted);cursor:pointer;opacity:0}
.m-group-head:hover .m-group-toggle,.m-group-toggle:focus-visible,.m-group[data-morph-collapsed="true"] .m-group-toggle{opacity:1}
.m-group-toggle:hover{color:var(--text);border-color:var(--border-subtle)}
.m-group[data-morph-collapsed="true"]{flex:0 0 var(--lane-rail,2.5rem);min-width:0;max-width:none;padding:var(--gap) .25rem}
.m-group[data-morph-collapsed="true"]>*:not(.m-group-head){display:none}
.m-group[data-morph-collapsed="true"] .m-group-head{writing-mode:vertical-rl;justify-content:flex-start;gap:.6rem;margin:0}
.m-group[data-morph-collapsed="true"] .m-group-roll{display:none}
.m-group[data-morph-collapsed="true"] .m-group-toggle{writing-mode:horizontal-tb;margin:0}
.m-lane-cell[data-morph-collapsed="true"]>*{display:none}
.m-group-items{list-style:none;margin:0;padding:0;display:flex;
  flex-direction:column;gap:calc(var(--gap) * .5)}
.m-group .m-card-hit{background:var(--page);border:1px solid var(--border-subtle);
  border-radius:8px;padding:calc(var(--gap) * .6);
  box-shadow:0 1px 2px rgb(0 0 0/.04);transition:border-color .14s ease,transform .14s ease}
.m-group .m-card-hit:hover{border-color:var(--border-strong);transform:translateY(-1px)}

/* cards — the fallback */
.m-as-cards{display:grid;gap:var(--gap);grid-template-columns:repeat(auto-fill,minmax(11rem,1fr))}

.m-card{min-width:0}
.m-card-hit{display:block;width:100%;min-height:var(--target);text-align:left;
  background:none;border:0;padding:0;font:inherit;color:inherit;cursor:pointer}
.m-card-hit:focus-visible{outline:2px solid var(--focus-ring);outline-offset:3px;border-radius:10px}
/* ORDER, DELIBERATELY: the chip row is a BASE behaviour and sits ahead of
   every arrangement rule, so an arrangement that chooses its own display
   still wins on ordinary cascade rather than on a specificity game. It
   matters: the .m-as-list card-hit rule setting display:grid is two classes,
   and so is the row's :has() guard -- with the row written after it, a
   list card carrying two chips would silently have lost its grid: a
   regression in an arrangement this change has no business touching, and
   one no clause was watching for. Found by reading the specificity before
   than after. 0058 forbids source order deciding between two LOOKS; base
   two LOOKS; base before arrangement inside one generic sheet is a
   different question.

   AND ORDER WAS NOT ENOUGH, which is my specificity arithmetic wrong for
   the second time in one session -- @Codex's P2 on 4317f20. :has() takes
   the specificity of its own argument, so :has(> :is(A) ~ :is(B)) adds TWO
   classes and the guard was 0-3-0, not the 0-2-0 I had counted. It beat
   the list's 0-2-0 arrangement rule whatever the order, and the clause that
   pins the order could not see it because order is not what decides.
   :where() contributes ZERO specificity and selects identically, so the
   guard is 0-1-0 now and every arrangement rule outranks it. The order
   stays and is still asserted -- it is what makes the intent legible -- but
   the specificity is what makes it true. */
/* THE PROPERTY CHIP ROW -- task 0180, and it is a COMPONENT rather than a
   token, which is the answer 0173's standing question wants written down
   before the work: a wrapping row of property chips is what Notion's board
   card draws, what Fibery's draws, and what Coda's row detail and Fabric's
   hits want. Putting it in one app's block would be the seventh block.

   MEASURED, against fibery-board.png at its own 3x scale with our 1x capture
   as the method's control:

     card width      ~202 CSS reference  vs  200 ours     1%
     column pitch    ~224                vs  225          0.4%
     head row height  ~58                vs   55          5%
     CARD HEIGHT     ~132                vs  203        +54%
     chip rows          1                vs    2

   Three geometry facts inside 5% and the height half again too large: the
   grid was right and the card's INTERNAL flow was not. Every field took its
   own line because the hit is display:block, so an assignee sat under a
   number and a date that had happened to fit side by side.

   ONLY THE COMPACT KINDS FLOW. A title, a paragraph, a still and a document
   keep a full line -- a card whose body wrapped up beside its date would be
   a worse defect than the one this fixes -- so the default is 100% and the
   chip kinds opt in by kind, not by role. That also makes the rule silent
   for the one-field cards that fill Obsidian's tree and Fabric's hits: a
   single flex item wraps to one line and measures exactly as it did.

   The gaps are the LOOK's and the row is everyone's, which is the division
   0173 asks for.

   AND THREE LOOKS RESTATED display:block ON THEIR OWN CARD HIT -- Notion,
   Fibery and Fabric, at four or five classes each -- so the component's
   choice lost to a declaration that was only repeating the generic default.
   The restatement is removed rather than out-specified: decision 0058
   forbids specificity escalation as the answer, on the grounds that it
   merely replaces one accidental cascade winner with another. What is left
   in those three rules is the look's own -- its padding, its border, its
   background -- which is the division this whole task is about. */
.m-card-hit{flex-wrap:wrap;
  gap:var(--chip-gap-y,.3rem) var(--chip-gap-x,.35rem);align-items:center}
.m-card-hit>*{flex:0 0 100%;min-width:0}
/* AND THE GAP DOES THE SPACING, which is not a tidiness point: block layout
   COLLAPSES adjacent vertical margins and flex does not, so switching the
   flow made the card 16px TALLER before this line -- a fix that moved the
   measured defect in the wrong direction. Measured: 203 -> 217 with the
   fields' own margins left in, and the row steps reading 38-43px for 24-28px
   of content. Zeroed here rather than at the field, because the field's
   margin is right everywhere else it is drawn.

   SCOPED TO THE ROW, and the missing scope was a defect -- @Codex's P2 on
   660c7bd. This zeroed the margins on EVERY card hit while only the flex ones
   got a gap in exchange, so a card with fewer than two chips lost the spacing
   and got nothing back. Measured on the seeded Realtime cursors row, which has
   one chip: badge to title 10px against 19px on every card beside it. The
   comment three lines up already said one chip is left alone; this line was
   the reason it was not true. */
.m-card-hit:has(>:where(.m-kind-number,.m-kind-date,.m-kind-enum,.m-kind-score,.m-kind-ref) ~ :where(.m-kind-number,.m-kind-date,.m-kind-enum,.m-kind-score,.m-kind-ref))>*{margin-block:0}
.m-card-hit>:is(.m-kind-number,.m-kind-date,.m-kind-enum,.m-kind-score,.m-kind-ref){
  flex:0 1 auto}
/* A card with two or more chip-kind fields becomes the row. One is left
   alone, so nothing in the corpus that draws a single chip moves. The :has()
   selector is what lets the rule ask about the card's own contents rather than about the
   look it is in. */
.m-card-hit:has(>:where(.m-kind-number,.m-kind-date,.m-kind-enum,.m-kind-score,.m-kind-ref)
  ~ :where(.m-kind-number,.m-kind-date,.m-kind-enum,.m-kind-score,.m-kind-ref)){display:flex}

.m-media{background-color:var(--surface);background-size:cover;background-position:center}
/* A media node sitting DIRECTLY in a row region has no intrinsic content — a
   background-image div's content size is zero — so flex-basis auto collapses
   it to 0px and aspect-ratio derives height from nothing. Task 0162 defect 2,
   measured on the single-object sheet: the person's added photo was stored,
   bound, resolved to a blob URL, and painted at 0×0. Today only buildSingle's
   sheet emits media-in-row, so this is the hero's basis; written as the
   general rule because the next row-with-media would collapse identically.
   flex-SHRINK is 1, not 0 — @Codex, P2 on d958909: with shrink forbidden,
   two media children in one legal row (or one hero in a narrow embed) must
   overflow, because .m-row neither wraps nor has another responsive rule.
   The BASIS is what repairs 0162's zero-collapse; shrink only engages on
   overflow, which is exactly the case the fixed basis was breaking. */
.m-region.m-row>.m-media{flex:0 1 clamp(10rem,30%,18rem)}

/* Rollups. A total reads as a different KIND of thing from the values above it
   — heavier, and labelled with the fold that produced it, because "1152" and
   "19h 12m sum" answer different questions. */
.m-foot td{border-top:2px solid var(--border-strong);padding:.5rem .6rem;
  vertical-align:baseline}
.m-foot-v{font-weight:650;font-variant-numeric:tabular-nums}
.m-foot-fn{display:block;font-size:calc(var(--s0) * .7);letter-spacing:.08em;
  text-transform:uppercase;color:var(--text-muted)}
.m-foot-n td{padding:.2rem .6rem .5rem;font-size:calc(var(--s0) * .74);
  color:var(--text-muted);letter-spacing:.04em}
.m-group-roll{margin-inline-start:.5rem;font-size:calc(var(--s0) * .8);
  font-variant-numeric:tabular-nums;color:var(--text-muted)}
.m-roll-k{font-size:calc(var(--s0) * .7);letter-spacing:.06em;text-transform:uppercase}
/* Two folds on one column stack rather than run together. */
.m-foot-cell .m-foot-v + .m-foot-fn + .m-foot-v{margin-block-start:.35rem}

/* Time-based media. A recording is content, not chrome, so it gets a real box
   and a real label — and no aspect ratio on audio, because audio has no picture
   and a box shaped like one lies about what is in it. */
.m-av{display:block;inline-size:100%;background:var(--surface);border-radius:10px}
.m-video{aspect-ratio:16/9;object-fit:contain;background:#000}
.m-audio{margin:0;display:flex;flex-direction:column;gap:.35rem}
.m-audio figcaption{font-size:calc(var(--s0) * .78);font-weight:600;
  letter-spacing:.06em;text-transform:uppercase;color:var(--text-muted)}
.m-audio audio{inline-size:100%;min-height:var(--target)}
/* A DOCUMENT gets a page-shaped box and a real minimum. A PDF viewer inside a
   200px sliver is a scrollbar with a hint of a page behind it. No aspect-ratio
   rule: a document decides its own page shape and the viewer letterboxes, so
   imposing 16/9 or 2/3 would crop or pad somebody's contract. */
.m-doc{inline-size:100%;min-height:32rem;border:1px solid var(--border-subtle);
  border-radius:10px;background:var(--surface);display:block}
/* The fallback, which is what a browser with no viewer shows. It has to look
   deliberate rather than broken: a stated reason and a way through.

   This comment used to end "-- and what this repo's own Chromium always shows,
   since it reports no PDF viewer at all". Measured on 2026-08-31 and false: the
   Chromium at /opt/pw-browsers reports navigator.pdfViewerEnabled === true and
   embeds the document, so the fallback is correctly not painted there, while
   Playwright's own download reports no viewer and paints it. TWO Chromiums, two
   answers, and e2e/fabric-row.mjs was red about the product for it.

   An inherited claim of fact about an environment, never re-checked, is the
   shape CLAUDE.md lists as (e) -- and this one made a harness clause look safe
   to write unconditionally. */
.m-doc-fallback,.m-doc-linked{display:flex;flex-direction:column;gap:.5rem;
  align-items:flex-start;justify-content:center;block-size:100%;padding:1rem}
.m-doc-linked{min-height:0;border:1px dashed var(--border-subtle);border-radius:10px}
.m-doc-what{font-size:calc(var(--s0) * .85);color:var(--text-muted)}
.m-doc-open{font-size:var(--s0);color:var(--text);text-decoration:underline;
  text-underline-offset:3px;min-height:var(--target);display:inline-flex;align-items:center}
.m-doc-empty{display:grid;place-items:center;min-height:8rem;color:var(--text-muted);
  border:1px dashed var(--border-subtle);border-radius:10px}
/* A list is a scanning surface. A full-height document viewer in a row would
   make one row taller than the screen -- the same failure a 16:9 player had in
   a 3.25rem thumbnail slot, in the opposite direction. */
.m-as-list .m-doc{grid-column:1 / -1;min-height:14rem}
/* ---------------------------------------------------------------- calendar */
/* A month is TABULAR: seven weekday columns, one row per week. Drawn as a real
   table so the header cells and the reading order come from the element rather
   than from a role attribute bolted onto a grid of divs. */
.m-calendar{margin:0}
.m-cal-head{display:flex;align-items:baseline;gap:.75rem;margin-block-end:.75rem}
.m-cal-title{margin:0;font-size:var(--s2);font-weight:600;letter-spacing:-.01em}
.m-cal-grid{width:100%;border-collapse:collapse;table-layout:fixed}
.m-cal-grid th{padding:.35rem .5rem;text-align:start;font-size:calc(var(--s0) * .78);
  font-weight:550;letter-spacing:.04em;text-transform:uppercase;
  color:var(--text-muted);border-block-end:1px solid var(--border)}
.m-cal-grid th abbr{text-decoration:none;border:0}
/* A fixed minimum height so an empty week does not collapse and the grid keeps
   the shape of a month. Vertical-align top so a day with four events and one
   with none still line their numbers up. */
.m-cal-day,.m-cal-pad{border:1px solid var(--border-subtle);vertical-align:top;
  height:5.5rem;padding:.3rem .35rem}
.m-cal-pad{background:var(--surface);border-color:var(--border-subtle)}
.m-cal-num{display:block;font-size:calc(var(--s0) * .82);color:var(--text-muted);
  font-variant-numeric:tabular-nums;margin-block-end:.2rem}
.m-cal-has{background:var(--raised)}
.m-cal-events{list-style:none;margin:0;padding:0;display:flex;
  flex-direction:column;gap:.15rem}
/* The event is the only reachable thing on a calendar, so it carries the hit
   target rather than the cell — a whole day as one button would announce four
   events as one control. */
.m-cal-hit{font:inherit;font-size:calc(var(--s0) * .82);width:100%;text-align:start;
  padding:.12rem .3rem;border-radius:4px;border:1px solid transparent;
  background:var(--surface);color:var(--text);cursor:pointer;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
  min-height:calc(var(--target) * .5)}
.m-cal-hit:hover{background:var(--surface-hover)}
.m-cal-hit:focus-visible{outline:2px solid var(--focus-ring);outline-offset:1px}
.m-cal-undated{margin:.7rem 0 0;font-size:calc(var(--s0) * .85);color:var(--text-muted)}
/* A way out of the month on screen. Drawn only where there is somewhere to go,
   so a single-month collection gets no controls rather than two dead arrows. */
.m-cal-steps{display:flex;align-items:center;gap:.4rem;margin-inline-start:auto}
.m-cal-nav{font:inherit;font-size:var(--s0);line-height:1;min-inline-size:var(--target);
  min-block-size:var(--target);border-radius:6px;border:1px solid var(--border);
  background:var(--cal-nav-bg,var(--raised));color:var(--text);cursor:pointer}
/* AND THE SAME FOR THE MONTH NAV — task 0232. This rule is (0,3,0) and
   app.notion's resting .m-app-notion .m-cal-nav was (0,2,0) declaring the same
   property, so a generic rule OUT-SPECIFIED a look, which 0069 permits only as
   a named structural lock. It painted nothing wrong, because that look also
   declares its own hover at (0,4,0) — but a specificity a record cannot
   justify is what 0073 refuses to inventory, and the fix costs a token. */
.m-cal-nav:hover:not(:disabled){background:var(--cal-nav-bg-hover,var(--surface-hover))}
.m-cal-nav:disabled{opacity:.4;cursor:default}
.m-cal-nav:focus-visible{outline:2px solid var(--focus-ring);outline-offset:2px}
.m-cal-of{font-size:calc(var(--s0) * .8);color:var(--text-muted);
  font-variant-numeric:tabular-nums}
/* Under a narrow viewport a seven-column month is seven unreadable slivers.
   The cells keep their columns but lose their height, so it reads as a dense
   month rather than a broken one. */
@media (max-width:640px){
  .m-cal-day,.m-cal-pad{height:3.2rem;padding:.2rem}
  .m-cal-hit{font-size:calc(var(--s0) * .7)}
}
/* ---------------------------------------------------------------- timeline */
/* One lane per object against a shared axis. A table, because that is what it
   is — a row header naming the object, one track cell carrying the bar — and a
   real row header gives a screen reader the name against every bar without an
   ARIA attribute anywhere. */
.m-timeline{margin:0}
.m-tl-grid{width:100%;border-collapse:collapse;table-layout:fixed}
.m-tl-corner{inline-size:32%;text-align:start;padding:.3rem .5rem;
  font-size:calc(var(--s0) * .78);font-weight:550;letter-spacing:.04em;
  text-transform:uppercase;color:var(--text-muted);
  border-block-end:1px solid var(--border)}
/* The scale reads at both ends. Between them there is nothing to label without
   inventing tick marks the host never computed. */
.m-tl-scale{display:flex;justify-content:space-between;gap:1rem;
  padding:.3rem .5rem;font-size:calc(var(--s0) * .78);font-weight:550;
  color:var(--text-muted);border-block-end:1px solid var(--border);
  font-variant-numeric:tabular-nums}
.m-tl-name{text-align:start;font-weight:450;font-size:calc(var(--s0) * .9);
  padding:.3rem .5rem;color:var(--text);overflow:hidden;text-overflow:ellipsis;
  white-space:nowrap;border-block-end:1px solid var(--border-subtle)}
.m-tl-lane{margin-inline-start:.45rem;padding:.05em .45em;border-radius:999px;
  background:var(--surface);color:var(--text-muted);
  font-size:calc(var(--s0) * .72);letter-spacing:0}
/* The track is the positioning context, so a bar is a percentage of its own
   cell and survives the column being any width — no pixel arithmetic crosses
   the boundary between the host and the module. */
.m-tl-track{position:relative;height:var(--target);padding:0 .5rem;
  border-block-end:1px solid var(--border-subtle)}
/* THE BAR'S GROUND IS A TOKEN, resting and hovered — task 0232, and the
   contest was invisible until the sweep learned to switch views. app.notion
   declared background on .m-app-notion .m-tl-bar at (0,2,0), which TIES this
   rule's hover at (0,2,0) and wins on source order, so the hover background
   below has never painted in Notion's timeline. 0073 forbids a tie at a
   ceiling of zero and the resolution is the one this pass uses everywhere: a
   property declared by ONE rule, with the look setting the value.

   Both tokens, not just the resting one. A look that set only the ground would
   take this rule's --surface-hover on hover, which is a different product from
   the one it has — the hover-disappears trap facing the other way, and the
   reason app.notion sets its hovered ground to its own pill below. */
.m-tl-bar{position:absolute;inset-block:25%;min-inline-size:.5rem;
  border:1px solid var(--border-strong);border-radius:5px;
  background:var(--tl-bar-bg,var(--surface));cursor:pointer;padding:0}
.m-tl-bar:hover{background:var(--tl-bar-bg-hover,var(--surface-hover))}
.m-tl-bar:focus-visible{outline:2px solid var(--focus-ring);outline-offset:2px}
/* A MOMENT is an object with one date. A zero-width rectangle is invisible, so
   it is drawn as a mark of its own rather than left off the chart. */
.m-tl-moment{inline-size:.55rem;border-radius:50%;background:var(--solid);
  border-color:var(--solid);transform:translateX(-50%)}
/* An OPEN span: started, not finished. The right edge is ragged rather than
   square, so a bar that stops at "last touched" cannot be mistaken for one that
   stopped because the work was done. */
.m-tl-open{border-inline-end-style:dashed;border-start-end-radius:0;
  border-end-end-radius:0}
.m-tl-hidden{position:absolute;inline-size:1px;block-size:1px;padding:0;
  margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
.m-tl-undated{margin:.7rem 0 0;font-size:calc(var(--s0) * .85);color:var(--text-muted)}
@media (max-width:640px){
  .m-tl-corner{inline-size:40%}
  .m-tl-name{font-size:calc(var(--s0) * .8)}
}
/* ------------------------------------------------------------ document-flow */
/* A reading surface. The measure comes from the type system rather than from a
   number here -- editorial is 68ch, compact 76ch -- so a document in a dense
   lens is dense for the same reason everything else in it is. */
.m-doc-flow{display:block;max-inline-size:100%}
/* A QUOTED PASSAGE in the document canvas. Generic, and keyed on the ELEMENT
   rather than a class: the canvas paints authored meaning, so a blockquote is
   what a quote is, and there is no class for a stylesheet to miss.

   The rule takes var(--accent), which every look already defines, so a seventh
   app whose reference shows a quote gets one in its own colour with no rule of
   its own. Obsidian's reference measures its rule at #8a5cf5 against a #a97ef3
   call to action — deeper by a little, which a downscale cannot establish as a
   separate value (decision 0068), so the accent itself is what is asserted.

   The browser default is a 40px indent and no rule at all, which reads as an
   indented paragraph rather than a quotation. */
blockquote[data-morph-document-block]{margin:1.1rem 0;padding-inline-start:1rem;
  border-inline-start:3px solid var(--accent);color:var(--text-muted)}
.m-doc-body{column-gap:2.5rem;max-inline-size:var(--measure);
  margin-inline:auto;text-align:start}
.m-doc-body[style]{max-inline-size:none}
/* Running head and foot. position:fixed is what makes them repeat on every
   printed sheet in Chromium; on screen they are ordinary blocks at the ends of
   the article, which is why they are not fixed outside the print block. */
.m-doc-running{max-inline-size:var(--measure);margin-inline:auto;
  color:var(--text-muted);font-size:calc(var(--s0) * .82)}
.m-doc-head{border-block-end:1px solid var(--border-subtle);padding-block-end:.5rem;
  margin-block-end:1.5rem}
.m-doc-foot{border-block-start:1px solid var(--border-subtle);padding-block-start:.5rem;
  margin-block-start:1.5rem}
/* The apparatus. A rule above nothing is worse than no apparatus, so the region
   is emitted only when the spec asked for notes AND the host supplied some. */
.m-doc-notes{max-inline-size:var(--measure);margin-inline:auto;margin-block-start:2rem;
  padding-block-start:1rem;border-block-start:1px solid var(--border-subtle);
  font-size:calc(var(--s0) * .88)}
.m-doc-notes-title{font-size:calc(var(--s0) * .82);font-weight:600;margin:0 0 .5rem;
  color:var(--text-muted);text-transform:uppercase;letter-spacing:.06em}
/* Numbered, because a footnote without a number is an aside. The list numbers
   itself rather than the renderer counting -- a count computed in markup goes
   wrong the first time a note is filtered out. */
.m-doc-note-list{margin:0;padding-inline-start:1.4rem;display:flex;
  flex-direction:column;gap:.4rem}
.m-doc-note-list li{padding-inline-start:.2rem}
/* A derived note and its way back. The back-link is the return half of the
   round trip the marker starts; without it a reader who followed a marker to
   the end of the document has to find their place again by eye.

   LEFT AS list-item, deliberately. This rule was display:flex, and @Codex
   found what that costs: display:flex replaces display:list-item, so Chromium
   generates no ::marker and the ordered list stops numbering. The markers in
   the body said 1 to 7 and the notes they pointed at showed nothing at all --
   a numbered apparatus with the numbers missing from one end. The browser
   checks read ids, counts and textContent, none of which can see a ::marker,
   which is why fifteen assertions passed over it.

   The text and the back-link are inline, so they flow beside the number the
   list draws. There is nothing here flex was buying. */
.m-doc-note-text{overflow-wrap:anywhere}
.m-doc-note-back{color:var(--text-muted);text-decoration:none;
  margin-inline-start:.4rem;
  display:inline-flex;align-items:center;justify-content:center;
  min-inline-size:var(--target);min-block-size:var(--target)}
.m-doc-note-back:hover{color:var(--text)}
.m-doc-note-back:focus-visible{outline:2px solid var(--focus-ring);outline-offset:2px;
  border-radius:3px}
/* The marker: a reference in the margin, on the line of the section's heading.

   Three placements were tried and two of them are defects worth recording,
   because each looked right until it was measured.

   AFTER THE PROSE was the first. A section's children end in block-level
   .m-field divs, so
   an inline sup after them opens an anonymous block and the marker lands on a
   line of its own underneath -- a lone superscript reading as a control
   somebody forgot to remove. Found by @Codex; confirmed in a browser, where its
   box sat 15px below the bottom of the paragraph.

   INSIDE THE SUMMARY was the second, and it is worse. A summary owns the
   activation of everything inside it: clicking the marker FOLDED THE SECTION
   and never navigated -- measured, hash unchanged, details open true to false.
   A marker that cannot be followed is not an improvement on one that looks
   untidy.

   So the marker is a sibling of the disclosure, placed by the grid into the
   second column of the first row. Source order keeps the heading first, which
   is the reading order a screen reader needs; the grid puts the reference where
   a margin note goes. It is outside the summary, so it is an ordinary link.

   FULL --target size, and NEVER an em here. The size was min-inline-size:1.6em
   for one commit, written down as "24px" and measured by @Codex on the deployed
   page at 19.58 x 19.58 -- because an em inside a .72em superscript is .72 of
   the em anybody reading the rule would assume. The number in the comment was
   not the number the browser drew, which is the same defect as everything else
   this apparatus was fixed for, one layer down. The gate measures the rendered
   box now.

   It was cut in the first place while the marker lived inside the summary,
   where a full-size box would have sat on the control it was standing in. In
   its own grid column it overlaps nothing, so there is no trade left. */
/* .m-card as well as .m-card-noted: .m-foldable sets display:block and comes
   later in this sheet, so one class was the same specificity and lost. */
.m-card.m-card-noted{display:grid;grid-template-columns:1fr auto;
  align-items:baseline;column-gap:.4rem}
.m-card-noted > details,.m-card-noted > .m-card-hit{grid-column:1;grid-row:1}
.m-card-noted > .m-doc-marker{grid-column:2;grid-row:1}

/* A REPEATED action shares the card's first line instead of owning one.

   Task 0088, and the measurement it asked for: Tasks pool at 1500x1000,
   regular / balanced, a board card carrying an action was 88.7px against 44px
   for the same card without one. The button is min-height:var(--target) — 44px
   at every density — and it was emitted as a normal-flow sibling AFTER the
   contents, so it inflated the line it landed on by the whole target. Twelve
   cards on that board, and 544.9px of the 1081.3px they occupied was the
   action.

   THE TARGET DOES NOT MOVE. @Codex's patch for the sibling problem proposed
   min-height:2rem, and that was refused in #138 and stays refused: 32px clears
   the 24px WCAG 2.5.8 floor a11y.ts checks while undercutting the 44px
   tokens.ts holds, and checkFloor reads ir.tokens.minTargetPx so no gate could
   see it. The height comes off the LAYOUT instead — the button keeps a real
   44px box, and the box stops needing a line of its own because it sits in a
   column beside the contents rather than in a row under them.

   Column, not an absolute corner: an absolutely positioned action needs the
   contents to reserve width for it, and CSS cannot measure a button whose width
   is its label. A grid track sized to the button is the same reservation
   without the guess.

   Same shape as .m-card-noted above, which has been putting a marker in its own
   column on these same list-item cards since the note apparatus landed — so
   display:grid on a card <li> is not new here.

   AND THE COLUMN ALONE WAS NOT ENOUGH, which is the part worth keeping. It
   takes the action out of the flow, but it takes ~55px of width to do it, and
   at a 17rem board column that made the two enum fields stack — 22.3px — or the
   title wrap — 39.2px. Cost per card fell 44 to 33.6 and the board only 15%: a
   fix that moved the cost from height to width and then reported the height,
   which is exactly the failure task 0088 exists about. So a board column whose
   cards carry actions asks for the width one needs, and that is what took 11 of
   12 cards to zero. Measured in e2e/card-action.mjs, both ways.

   NO BACKTICKS IN HERE — BASE_CSS is a template literal. */
.m-group:has(.m-card-acting){flex-basis:clamp(18rem,24vw,21rem)}
.m-card.m-card-acting{display:grid;grid-template-columns:minmax(0,1fr) auto;
  align-items:start;column-gap:.4rem}
.m-card-acting > .m-card-body,.m-card-acting > .m-card-hit,
.m-card-acting > details{grid-column:1;grid-row:1;min-width:0}
.m-card-acting > .m-invoke{grid-column:2;grid-row:1}
/* Both at once needs three columns rather than two fighting over one. The
   marker keeps its baseline; align-items:start above is for the button, which
   is a box beside the text and not a mark within it. */
.m-card.m-card-noted.m-card-acting{grid-template-columns:minmax(0,1fr) auto auto}
.m-card-noted.m-card-acting > .m-doc-marker{grid-column:2;align-self:baseline}
.m-card-noted.m-card-acting > .m-invoke{grid-column:3}
.m-doc-marker{font-size:.72em;line-height:0;vertical-align:super}
.m-doc-marker a{color:var(--accent);text-decoration:none;font-weight:600;
  display:inline-flex;align-items:center;justify-content:center;
  min-inline-size:var(--target);min-block-size:var(--target)}
.m-doc-marker a:hover{text-decoration:underline;text-underline-offset:2px}
.m-doc-marker a:focus-visible{outline:2px solid var(--focus-ring);outline-offset:2px;
  border-radius:3px}
/* The section a marker or a contents entry just took the reader to. Without it
   a jump to an anchor moves the viewport and says nothing about where it
   landed, which in a stack of similar sections is no answer at all.

   SCREEN ONLY. :target survives as long as the URL does, so a reader who
   followed a marker and came back and then printed would have carried a
   transient navigation highlight into the PDF, around whichever section they
   happened to visit last. Found by @Codex, who also noted that the print
   assertions check that printing SUCCEEDS and never look at what it drew. */
@media screen{
  .m-card:target{outline:2px solid var(--focus-ring);outline-offset:4px;border-radius:4px}
  .m-doc-note:target{outline:2px solid var(--focus-ring);outline-offset:3px;border-radius:4px}
  /* The same treatment when focus arrives without the URL -- a fragment target
     is focusable now, so it can be reached by script or by restoring focus,
     and an outline that appears only for :target would leave those silent. */
  .m-card:focus,.m-doc-note:focus{outline:2px solid var(--focus-ring);outline-offset:4px;
    border-radius:4px}
}
/* Contents. Set as navigation rather than as part of the prose -- it is a way
   around the document, not a paragraph of it. */
.m-doc-toc{max-inline-size:var(--measure);margin-inline:auto;margin-block-end:2rem;
  padding-block-end:1rem;border-block-end:1px solid var(--border-subtle)}
.m-doc-toc-title{font-size:calc(var(--s0) * .82);font-weight:600;margin:0 0 .5rem;
  color:var(--text-muted);text-transform:uppercase;letter-spacing:.06em}
.m-doc-toc ol{margin:0;padding-inline-start:1.4rem;display:flex;flex-direction:column;
  gap:.3rem;font-size:calc(var(--s0) * .92)}
.m-doc-toc a{color:var(--text);text-decoration:none;min-height:var(--target);
  display:inline-flex;align-items:center}
.m-doc-toc a:hover{text-decoration:underline;text-underline-offset:3px}
.m-doc-toc a:focus-visible{outline:2px solid var(--focus-ring);outline-offset:2px;border-radius:3px}
/* A section that has just been jumped to. Scroll margin so the running head
   does not sit on top of it. */
.m-doc-body .m-card{scroll-margin-block-start:2rem}
@media print{ .m-doc-toc{break-after:page} }
/* ------------------------------------------------------------------ folding */
/* A disclosure, from the element that already is one. The marker is the
   browser's; restyling it per-engine is how a control ends up invisible in the
   one browser nobody tested. */
.m-fold-summary{cursor:pointer;font-size:var(--s1);font-weight:600;color:var(--text);
  padding-block:.35rem;min-height:var(--target);display:flex;align-items:center;gap:.5rem}
.m-fold-summary:focus-visible{outline:2px solid var(--focus-ring);outline-offset:2px;
  border-radius:4px}
.m-fold-summary:hover{color:var(--text)}
.m-fold-body{padding-block-start:.5rem}
/* A folded section still takes a line, so the document does not reflow into a
   wall of headings with no space between them. */
.m-fold:not([open]) .m-fold-summary{color:var(--text-muted)}
.m-foldable{display:block}
@media print{
  /* Print what is there, not what happens to be open. A folded section that
     vanishes from the printout is a document that lies about its own contents.

     It has to be the details-content pseudo-element. A closed disclosure hides
     its children from a UA shadow slot, so a display:block override on
     .m-fold-body -- the first version of this rule -- reached the wrong
     element and printed nothing but the summary. Measured, not assumed:
     printing that version produced one glyph run, this one produces two, the
     same as a disclosure forced open. An engine too old for the pseudo-element
     drops this rule and keeps the old behaviour; the rest of the sheet is
     unaffected. */
  .m-fold::details-content{content-visibility:visible !important}
}
/* A SHEET is page-shaped on screen. It is not pagination: the text flows down
   one sheet rather than breaking into numbered pages, for the reasons written
   beside the renderer. */
.m-doc-sheet{background:var(--page);padding:4rem 3.5rem;border-radius:2px;
  box-shadow:0 1px 3px rgb(0 0 0/.12),0 8px 28px -18px rgb(0 0 0/.3);
  max-inline-size:52rem;margin-inline:auto}
/* THE PAGE NUMBER, and a recorded fact that had gone stale.
   capability.ts said of this: "A CSS page counter lives in an @page margin box
   and Chromium does not implement those. So a number here would have to be
   counted by script and would be wrong the moment anything reflowed." That was
   true when it was written and is not true now.
   Measured on Chrome/151.0.7922.34, printing to PDF: adding this margin box
   grows the file, and the growth TRACKS the content — no box 6451 bytes, one
   glyph 6763, forty-eight glyphs 6777, counter(page) 6996, "Page N of 3" 8355.
   A rule that is parsed and discarded emits no bytes. On the product's own
   document view, beside the margin rule below, it adds 157.
   The second objection is now moot rather than answered: the browser counts
   the page, so nothing here can be wrong on reflow.
   WHAT THIS IS NOT EVIDENCE OF: that the number is legible, correctly placed,
   or says what it should. A PDF's text is font-encoded, so this sandbox can
   prove bytes were emitted for the box and cannot read them back. The bar
   asks for a human to drive it; this is the half a machine can establish. */
@page{margin:20mm;@bottom-center{content:counter(page);font:400 9pt system-ui,sans-serif;color:#666}}
@media print{
  /* Real pagination lives here and only here. */
  .m-doc-sheet{box-shadow:none;padding:0;max-inline-size:none;background:none}
  .m-doc-running{position:fixed;inset-inline:0}
  /* The HEAD does not run from here any more -- task 0165. position:fixed
     repeated it per sheet, but Chromium places print-fixed furniture at the
     top of the CONTENT area, exactly where each page's first line also
     starts: measured, page 5's opening line printed inside the head's band,
     and pages 3-4 cleared it by 1.3pt. Nothing flowing can pad every sheet's
     top, and a negative top does not lift the head into the margin -- it
     CLIPS: measured, top:-12mm erased the head from every page. The one
     mechanism the browser paints in the margin is the @page margin box the
     page counter above already uses, so the document-flow renderer emits a
     per-document @top-center rule carrying the head's text, and the in-flow
     header stays out of the way on paper. The foot keeps the fixed
     treatment; its band sits below the last baseline and has never been
     measured against content. */
  .m-doc-head{display:none}
  .m-doc-foot{bottom:0}
  /* A card sawn across a page boundary is the classic print defect. */
  .m-card,.m-doc-notes,figure,table{break-inside:avoid}
  /* A return link has no interaction on paper. Left visible it put a stray
     arrow after every note in the PDF -- a screen control printed as content,
     which is the same defect as the navigation outline one rule up. Found by
     @Codex. The MARKERS stay: a superscript number is the apparatus. */
  .m-doc-note-back{display:none}
  /* A placed panel is navigation and controls -- New, Open, a note list --
     and it printed as page-one content. Found by a blind driver (run 2):
     the app shell's own print rule hides everything outside #page, and the
     panel lives INSIDE the generated layout, so "chrome by construction"
     was green about the wrong container. Same reasoning as the note-back
     rule above: a screen control on paper is content that lies. */
  .m-place{display:none}
  /* And the surface grid's edge-zone gaps go with them — task 0165. The
     three-row template keeps a trailing row-gap (~var(--gap)) after the main
     area even when the edge zones are empty, and on paper they always are:
     the panels that live there are hidden one rule up. That empty 20px is
     the second member of the trailing-sheet class — measured, a 3,700-char
     append spilled it past the last note and minted a page holding only the
     running head and the page number. */
  .m-surface{gap:0}
  /* The text gets the page — task 0156. The sheet sheds its 52rem bound two
     rules up, and the field measure then kept every paragraph at card width
     on an A4 sheet: right on screen, where the measure is the reading bound,
     wrong on paper, where the page's own margins are. Scoped to the document
     body twice over: a table cell keeps its measure on paper, and the bare
     .m-field rule below this block would out-cascade an unscoped one — a
     media query raises no specificity, so the LATER unconditional rule wins
     the tie, which is exactly how the first version of this line changed
     nothing. */
  .m-doc-body .m-field{max-width:none}
  /* And so does a collection rendered inside it. The auto-fill card grid put
     two 11rem columns on an A4 sheet, so "body at card width" in the Word
     blind drive was literal — each note's prose was bound by its grid track,
     not by its measure. On paper the cards stack. */
  .m-doc-body .m-as-cards{grid-template-columns:1fr}
}
.m-av-empty{font-size:calc(var(--s0) * .9);color:var(--text-muted);
  border:1px dashed var(--border);border-radius:10px;padding:.75rem;text-align:center}
/* A transcript is long prose that belongs to a recording: measure-bound like
   any other body text, and scrollable rather than endless. */
.m-role-transcript{max-height:14rem;overflow-y:auto;white-space:pre-wrap;
  font-size:calc(var(--s0) * .94);line-height:1.6;
  border-inline-start:2px solid var(--border-subtle);padding-inline-start:.75rem}

.m-field{max-width:var(--measure);overflow-wrap:anywhere}
/* A SINGLE-OBJECT PAGE'S TITLE IS THE PAGE'S TITLE — task 0176. The head
   draws no heading there, so this field is the heading a person sees, and at
   the ordinary field size it read as a caption over its own properties.

   GENERIC and token-driven, which is what 0173 asks of any rule that is about
   a component rather than about one app. It is deliberately TWO classes: a
   look that wants its own title keeps it on specificity rather than on source
   order, and both that do — Obsidian and Fabric, each three classes — are
   untouched. A look re-points --type-page instead of restating the rule. */
/* The heading wrapper carries no box of its own: the h1 exists for the
   accessibility tree, and the field inside it keeps being the thing a person
   sees and types in. Without this the browser's default h1 margin and size
   would stack under the field's own. */
.m-ws-title{margin:0;font-size:inherit;font-weight:inherit;line-height:inherit}
.m-ws-title>.m-untitled{color:var(--text-muted)}
/* The editable title's accessible fallback leaves the tree the moment the
   title has text. See the note beside the h1: display:none is what takes a
   node out of the name computation, so this is the live half of that fix and
   the reason it is a node rather than an aria-label. It keys on the same
   :empty the visible prompt keys on, so the announced word and the drawn one
   are one condition. */
.m-ws-title:has(>.m-editable:not(:empty))>.m-hidden-label{display:none}
/* TWO classes, not three — @Codex's P2 on 49cc0e3, and my own specificity
   arithmetic was wrong. The wrapper selector was
   .m-ws-editor>.m-ws-title>.m-role-title, which is THREE classes and ties
   .m-app-obsidian .m-ws-editor .m-role-title; being later it won, and
   Obsidian's line-height, letter-spacing and margin were overridden by
   adding heading semantics. Dropping .m-ws-editor from the wrapped form
   takes it to two, so every look's own three-class rule wins again. */
/* margin-BLOCK, not the four-sided shorthand. An editable cancels its own hit
   padding with a negative inline margin -- m-editable is padding .3em with
   margin -.3em, so the GLYPH lands where the box would have -- and the
   shorthand here was resetting that to 0 while the padding stayed. At the page
   size that is .3em of 30px: the heading's text sat 9px right of every field
   under it, measured at glyph x=79 against 70.0 for the author, the date, the
   number and the body, all four exact. Setting only the block axis leaves the
   compensation to the rule that owns it, and a title that is NOT editable has
   no inline margin to preserve, so both cases land at the same edge.
   This is 0064's "a property already set by a reset has no neutral default"
   from the other side: a shorthand has no neutral value for the axis it was
   not written about. */
/* THE EDITOR PANE'S OWN RULE, which this component did not have -- 0173.
   Six looks each declared flex:1 and overflow:auto on .m-ws-editor and no
   generic rule said either, so the component's own behaviour was written six
   times and owned by nobody. Measured across all six doors before it moved:
   display flex, flex 1/1/0%, overflow auto/auto, border 0px/none in every one.

   AND display:flex CAME OFF FIVE OF THEM, which the restatement gate one file
   over cannot see. The element is class="m-region m-stack m-ws-pane
   m-ws-editor" and .m-region at (0,1,0) already declares display:flex, so the
   look rules were restating a SIBLING CLASS rather than this component. That
   is the exact blind spot that gate's own comment names -- a declaration
   identical to the generic rule for one of an element's classes is a
   candidate, and the rules reaching an element through its OTHER classes are
   a DOM fact no source walk has. Here the capture found five.

   gap is NOT here and must not be: .m-region gives gap:var(--gap), so the
   four looks declaring gap:0 are overriding it, and Fabric's gap:1rem is its
   own. Removing those would open every editor by a gap. */
.m-ws-editor{flex:1;overflow:auto}
/* THE TOPBAR FAMILY'S SHARED COMPONENT FACTS -- 0173, second attempt.
   All fourteen topbar classes were BARE: 117 app-scoped declarations and no
   generic rule between them, so five looks each re-implemented a strip, a tab,
   an action row and a more-button from nothing.

   THE FIRST ATTEMPT PROMOTED "WHAT FOUR OR MORE LOOKS AGREE ON" AND BROKE
   THREE THINGS, all caught by a computed-style capture and none visible in the
   source. What is here is the set that survives all three tests:

   1. EVERY LOOK THAT DRAWS IT DECLARES IT, not just four of them. Four looks
      declare align-items:center on .m-topbar and Notion does not, so promoting
      it ADDED centring Notion never had and its strip stopped stretching --
      width 1200px to 212px. .m-topbar is absent below for that reason.
   2. A LOOK CAN STYLE A COMPONENT BY HIDING IT. Fabric's rule for
      .m-topbar-tab-icon is display:none, so "four declare line-height:1" meant
      "all four that draw it visibly". That class is absent too.
   3. A GROUPED SELECTOR IS ONE RULE SERVING SEVERAL COMPONENTS. Notion's and
      Fibery's button reset is written
      .m-app-X .m-topbar-star,.m-app-X .m-topbar-more{...}, so a declaration is
      only removable when EVERY class in its group is promoted with the same
      value. Removing it for .m-topbar-more alone reverted both stars to raw
      browser buttons -- Arial, outset border, default cursor. That is why star
      and more appear together here, and why font-size and letter-spacing do
      NOT: they are unanimous for the star and not for the more.

   The drawer sets came from the DOM, per door, not from the stylesheet: which
   looks DRAW a component is not a fact a source walk has. */
/* THE BAR ITSELF, and until now it was written by each of the FIVE looks
   that draw one and by nothing else -- Obsidian has no topbar rule and no
   topbar element, so this is not every look. Task 0173.

   Those five draw the SAME component: a strip plus its end pieces, read out
   of the DOM rather than off the selectors, which is what settled that it is
   one component and not two sharing a name.

   display:flex and align-items:center are the bar's own answer. WHICH axis
   that centres is decided by the look, and Notion is the one that arranges
   the same children as a COLUMN -- so it says align-items:normal for itself
   rather than being carried by the absence of a declaration. Word keeps its
   grid at 0-2-0.

   flex-direction:row went out of Coda, Fibery and Fabric entirely. Not
   because a capture showed no difference: enumerated from the producer, only
   three rules in the whole sheet can set display, flex-direction or
   align-items on a topbar -- this one, Notion's and Word's -- and none of
   them sets flex-direction except Notion's, at any viewport and in any
   state. m-capability, the class these elements also carry, sets none of the
   three. */
.m-topbar{display:flex;align-items:center}
.m-topbar-strip{display:flex;align-items:center}
.m-topbar-tab{display:flex;align-items:center}
.m-topbar-actions{display:flex;position:relative;align-items:center}
.m-topbar-end{align-items:center;display:flex}
.m-topbar-star{font:inherit;justify-content:center;border:0;cursor:pointer;align-items:center;background:transparent;padding:0}
.m-topbar-more{justify-content:center;cursor:pointer;border:0;background:transparent;align-items:center;padding:0;display:inline-flex;font:inherit}
/* THE CHEVRON, which is ONE NAME OVER TWO COMPONENTS -- 0173.
   capability.ts emits m-chev twice: as a span holding the disclosure glyph in
   the nav workspace row, and as the topbar's back and forward buttons. A
   capture across the six doors is what said the two are not two presentations
   at all -- all three looks that draw the span were declaring the BUTTON rule
   unscoped, so the span already wears the button's box: 28x28 in Notion, 26x26
   in Coda and Fibery, radius 6px and a pointer cursor on both. The look blocks
   said the same thirteen declarations three times over.

   The size is the only thing they disagreed on, so it is the only thing that
   stays with a look. 26px is the component's default because Coda and Fibery
   both take it; Notion declares the token.

   Fabric draws the two buttons and styles them by HIDING them --
   .m-app-fabric .m-chev is display:none at (0,2,0), which beats this rule's
   (0,1,0), so promoting the box leaves Fabric where it was and does not need a
   declaration here. Obsidian and Word draw no chevron at all. What remains in
   the look blocks is the workspace glyph's colour and font-size, which is
   about the character in the span and not about the box around it.

   THE HOVER RULE IS WRAPPED IN :where(), and the sweep is what said so. As
   .m-chev:hover:not(:disabled) it is (0,3,0) -- one class and two
   pseudo-classes, :not() counting its argument -- which TIES
   .m-app-notion .m-nav-workspace .m-chev exactly, and a tie is settled by
   position in the stylesheet, which nobody declared. 0073 forbids that at a
   ceiling of zero. At (0,1,0) it still beats the base rule above it, on
   source order at equal specificity, and it loses to any look that declares
   the glyph's colour -- which is what was already happening, silently, for
   the same three looks.

   Found the first time e2e/specificity-sweep.mjs compared CO-ACTIVE states
   rather than skipping disjoint ones (@Codex's P2 on 48121a7). It had been
   invisible for two hours, in the commit whose own capture caught the
   RESTING colour: a computed-style capture reads the state the element is
   in, and hover is not one of them. */
.m-chev{border:0;background:transparent;color:var(--text-muted);border-radius:6px;
  display:inline-flex;align-items:center;justify-content:center;cursor:pointer;padding:0;
  width:var(--chev-size,26px);height:var(--chev-size,26px)}
.m-chev:where(:hover):where(:not(:disabled)){background:var(--surface);color:var(--text)}
.m-chev:disabled{color:var(--border-strong);cursor:default}
/* THE WORKSPACE GLYPH IS SMALLER THAN THE BOX, and three looks said so
   identically -- task 0173. Nothing else in the sheet sets a font-size on a
   chevron, and only Notion, Coda and Fibery draw one under a workspace row:
   Fabric hides its chevrons outright, Obsidian and Word draw none there.
   Measured per door with context-table --used=, not read off the selectors.

   ONLY the size moved. Those three also declare color:var(--text-muted) here,
   which the base rule above already says -- and it is NOT a restatement: at
   (0,3,0) it also beats the (0,1,0) hover rule, so it is what keeps the
   workspace glyph muted under the pointer. Deleting it would show 0
   differences in a resting capture and change the hover in three looks. */
.m-nav-workspace .m-chev{font-size:12px}
/* THE TAB STRIP'S PLUS -- 0173, and the capture moved the map again.
   Notion, Coda and Fibery each wrote the same eight declarations for the New
   page button in the tab strip, differing only in the type size and the
   horizontal padding. Word and Fabric draw it and style it by HIDING it,
   which the chevron above already establishes is safe: their rules are
   (0,2,0) and beat this one.

   AND FIBERY DOES NOT DRAW IT AT ALL. Its door renders zero of them, so its
   three rules reached nothing -- a look that had written down an intention
   rather than agreed with anything. Its values happened to be Coda's exactly,
   so nothing here rests on that agreement: the defaults are Coda's, Notion
   declares the two tokens, and Fibery's block goes because it was dead and
   not because it was redundant. Those are different reasons and the source
   scan that mapped this cluster could not tell them apart -- only opening
   each door could. */
.m-topbar-tab-add{border:0;background:transparent;font:inherit;color:var(--text-muted);
  cursor:pointer;border-radius:6px;line-height:1;
  font-size:var(--tabadd-size,18px);padding:var(--tabadd-pad,0 .5rem)}
.m-topbar-tab-add:hover{background:var(--surface)}
/* THE SEARCH FIELD -- 0173. Four looks draw it (Notion and Obsidian
   collapsed, Fibery and Fabric open) and all four declared these six, which
   is what makes them the component rather than a majority: a field that is
   100% of its box, sized in border-box, without the platform's own control
   chrome, in the host's type and ink, and refusing the UA focus ring.

   WHAT IS NOT HERE is the point. Type size, padding, border, radius and
   ground all DIVERGE, and not two against two but four ways -- Obsidian's
   dark ground and grey rule, Notion's page ground and border token, Fibery's
   and Fabric's borderless transparent field. There is no majority to make a
   default out of, so a token here would be an arbitrary value wearing a
   default's clothes. They stay with the looks.

   The placeholder colour stays too, for the same reason one step finer:
   three looks say var(--text-muted) and Obsidian says a literal. Three of
   four is not unanimous, and promoting it would give Obsidian a colour it
   never asked for. */
.m-ws-search-input{width:100%;box-sizing:border-box;appearance:none;font:inherit;color:var(--text)}
.m-ws-search-input:focus{outline:0}
/* THE VIEW SWITCH'S ITEM -- 0173. Two looks draw the view switch, Notion and
   Fibery, and both wrote the same eight declarations for the tab: a flex row,
   centred, no border, a 6px radius, transparent, the host's font, the muted
   ink and a pointer. They disagree only on gap, padding and type size, which
   stay with the looks.

   TWO is the whole drawer set here, not a majority of six -- Coda, Obsidian,
   Word and Fabric draw no view switch at all, so this is every look that draws
   it agreeing, which is the filter that matters rather than the count.

   The HOVER rule is generic too, and that is why the resting colour above it
   had to be: left in the look blocks it would sit at (0,2,0) and tie this
   rule exactly.

   AND THAT WAS NOT THE ONLY TIE, WHICH IS THE POINT OF RUNNING THE SWEEP
   RATHER THAN REASONING. This comment first said the generic colour settles
   it. It does not: the ACTIVE tab also carries .m-viewswitch-on, and each
   look declares that at (0,2,0) with its own lit ground and ink -- so at
   (0,2,0) the generic hover tied the look's on-state, and whether a hovered
   active tab kept its lit look was decided by position in the stylesheet.
   The sweep named both pairs; the reasoning above had not reached them.

   :where() puts the hover at (0,1,0). It still beats the base rule directly
   above on source order, and it now loses to the on-state, which is the
   behaviour the sheet already had by accident and now has by declaration. */
.m-viewswitch-item{display:flex;align-items:center;border:0;border-radius:6px;background:transparent;
  font:inherit;color:var(--text-muted);cursor:pointer}
.m-viewswitch-item:where(:hover){background:var(--surface);color:var(--text)}
/* THE VIEW SWITCH'S BOX, in both looks that draw one -- 0173. Notion and
   Fibery are the whole drawer set; their gaps and grounds differ and stay
   with them, and what is here is the arrangement they agreed on.

   .m-viewswitch-on SITS AFTER .m-viewswitch-item DELIBERATELY. The active
   item carries both classes, and the item rule declares font:inherit --
   a SHORTHAND, which covers font-weight. Two (0,1,0) rules, so the later
   one wins, and this is the later one. The promotion table did not raise
   it: its fourth filter compares property NAMES, and the shorthand font is
   not the longhand font-weight. A shorthand contesting its longhands is a gap in that
   filter rather than in this rule. */
.m-viewswitch{display:flex;align-items:center;justify-content:space-between;gap:1rem}
.m-viewswitch-list{display:flex;align-items:center}
.m-viewswitch-on{font-weight:500}
/* The view switch's NEW control, in both looks that draw one. Its border,
   ground and colour are each look's own and stay there; what is shared is
   that it is a button-shaped thing you can click.

   font:inherit BEFORE font-weight, because the shorthand resets the
   longhand and the looks wrote them in that order. Each look keeps its own
   font-size, which is a longhand the shorthand here does not carry away
   from it -- the look rule is (0,2,0) and wins. */
.m-viewswitch-new{border-radius:6px;font:inherit;font-weight:500;cursor:pointer}
/* THE TOPBAR MENU, which four looks each wrote out in full -- 0173.
   Deferred from the slice above because it is a dropdown CLOSED until clicked,
   and a capture of a closed menu is a specimen that cannot carry the property.
   Driven open on each door first: the more-button clicked, then measured.
   Notion, Coda, Fibery and Fabric draw it; Obsidian and Word have no
   more-button and no menu.

   The menu ITEM is a button reset written four times identically, and the menu
   a dropdown panel likewise. What is NOT here is what the looks actually
   disagree about, which is the token set this task is eventually after: top
   (40/38/36px), right, min-width (220/200px), the panel's radius (8/10px) and
   the item's font-size (14/13.5px). Those stay in the looks. */
/* THE NAV FAMILY'S SHARED COMPONENT FACTS -- 0173, the third cluster.
   Notion, Coda and Fibery draw a nav; Obsidian, Word and Fabric do not. The
   two largest entries are whole components written out three times: the nav
   ROW is a button reset -- display, align, full width, no border, a radius,
   transparent, inherited font, a pointer, a start-aligned label -- and the
   TOOL is the same reset with its own padding. The row LABEL is the truncation
   idiom written three times: flex 1, nowrap, hidden, ellipsis.

   Seventy-six pairs were REJECTED because the looks disagree on the value.
   Those are the token set this task is after and they stay in the looks.

   COLOUR NEEDED A FOURTH FILTER the topbar slices did not: a promoted generic
   rule must also WIN against the other generic rules matching the same
   element. The nav is class="m-capability m-nav", and .m-capability declares
   color:var(--text-muted) at the same (0,1,0) specificity and LATER in this
   sheet -- so the look's (0,2,0) rule was beating it and a generic (0,1,0) one
   cannot. Measured when first tried: the nav went muted on Coda and Fibery,
   and because colour INHERITS the capture counted 8 and 6 further elements
   changing with it.

   THIS PARAGRAPH USED TO END "and a generic one cannot", and that was one
   word too strong -- it ruled out generic rules rather than generic rules AT
   THAT SPECIFICITY. A .m-nav.m-capability rule is still generic, names no
   look, and is (0,2,0), so it beats the capability default by SPECIFICITY
   rather than by position. Re-measured across six doors and 103,124 (element,
   property) pairs: 0 differences, where the (0,1,0) attempt gave 133.

   The whole 133 traced to this one declaration, which is why it is worth
   saying how: withdrawing ONLY the nav colour and leaving the other six
   promotions in place gave 0. One mutation at a time is decision 0064, and
   here it turned "the slice broke something" into "one line of it did".

   A nav IS a capability whose text is not muted, so the sibling class is the
   truer statement as well as the working one -- and the same shape is
   available to any other capability-classed component this slice reaches. */
.m-nav{display:flex;flex-direction:column;gap:.1rem;overflow:auto}
.m-nav.m-capability{color:var(--text)}
.m-nav-section{display:flex;flex-direction:column;gap:1px}
.m-nav-workspace{display:flex;align-items:center;gap:.5rem;font-weight:600;font-size:14px;border-radius:6px}
.m-nav-workspace-name{flex:1}
.m-nav-row{display:flex;align-items:center;width:100%;border:0;border-radius:6px;background:transparent;font:inherit;cursor:pointer;text-align:start;color:var(--text)}
.m-nav-row-icon{display:inline-flex;justify-content:center}
.m-nav-row-detail{color:var(--text-muted);white-space:nowrap}
.m-nav-section-label{color:var(--text-muted)}
.m-nav-row-label{flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.m-nav-bottom{margin-top:auto;padding:.9rem .25rem 0}
.m-nav-newpage{display:flex;align-items:center;gap:.5rem;width:100%;font:inherit;cursor:pointer}
.m-tools{display:flex}
.m-tools-inert{display:flex;align-items:center;margin-left:auto}
.m-tool{display:flex;align-items:center;padding:.3rem .55rem;border:0;border-radius:6px;background:transparent;font:inherit;cursor:pointer;color:var(--text)}
.m-tool-label{font-weight:500}
.m-pagehead{display:flex}
.m-pagehead-titlerow{display:flex;align-items:center}
.m-pagehead-icon{display:inline-flex;align-items:center;justify-content:center;flex:none;line-height:1}
.m-pagehead-title{letter-spacing:-.01em;margin:0;color:var(--text)}
.m-ws-section{font-size:12px;color:var(--text-muted)}
/* AND THE CLOSED STATE IS PART OF THE COMPONENT -- @Codex's P2 on ce8d042,
   and it is the fifth mechanism this refactor has met.

   capability.ts emits the dropdown with the HIDDEN attribute on it, and hidden
   is a UA rule at display:none. The promoted rule below is AUTHOR-level, so it
   beats it: a look without its own [hidden] rule draws
   the dropdown OPEN before More is used, and toggling menu.hidden cannot
   close it.

   THE SIX-DOOR CAPTURE MASKED THIS, which is the part worth carrying. All four
   looks that draw a menu carry their own app-scoped
   [hidden]{display:none}, so every door measured identically before and after
   and reported 0 differences. The product that breaks is the SEVENTH look --
   which is the only product this task exists for, and the one no capture over
   today's doors can see.

   So a component's closed state travels with it. The four app-scoped copies
   are restatements now and are removed. */
.m-topbar-menu[hidden]{display:none}
.m-topbar-menu{position:absolute;background:var(--page);border:1px solid var(--border);box-shadow:0 8px 24px rgba(0,0,0,.12);padding:.3rem;z-index:5;display:flex;flex-direction:column}
.m-topbar-menu-item{border:0;background:transparent;text-align:left;font:inherit;color:var(--text);padding:.4rem .6rem;border-radius:6px;cursor:pointer}
/* A TOPBAR CONTROL LIGHTS TO --surface UNDER THE POINTER -- 0173.
   Three components said this in eight look rules, unanimously among the
   looks that draw each. One rule says it once.

   :where(:hover) AND NOT :hover, which is 0058 and not a style choice. Plain
   :hover here is (0,2,0) and would tie a look's own (0,2,0) rules on these
   elements -- a generic rule deciding against a look on source order, which
   0073 holds at zero. :where() contributes nothing, so this is (0,1,0): it
   beats each component's base rule, which sits above and declares
   background:transparent, and it loses to every look rule, which is the
   direction 0058 requires.

   ITS POSITION IS LOAD-BEARING, and generic-against-generic, which is
   ordinary cascade rather than the look-against-generic tie the sweep
   refuses. Above .m-topbar-menu-item this rule is inert.

   .m-nav-row AND .m-nav-newpage ARE NOT IN IT, and the hovered capture is
   why. Both looked identical in the resting capture -- 0 differences -- and
   under --hover they were four: a look gives .m-nav-newpage a resting
   background at (0,2,0), which beats this rule, so its hover VANISHED in
   three doors; and Coda's active row changed under the pointer, because its
   .m-nav-row-on (0,2,0) now wins where the look's own (0,3,0) hover used to.
   Both are correct cascade and neither is this slice's to change. */
:is(.m-topbar-star,.m-topbar-more,.m-topbar-menu-item):where(:hover){background:var(--surface)}
.m-ws-editor>.m-role-title,.m-ws-title>.m-role-title{
  font-size:var(--type-page,30px);font-weight:700;
  line-height:1.15;letter-spacing:-.02em;margin-block:0 .5rem}
/* A PROPERTY ROW IS A COMPONENT, AND IT HAD NO GENERIC PRESENTATION AT ALL
   — task 0176, found by looking at Coda's row page and then counting:

     grep -n "m-ws-prop" render-html.ts   every rule .m-app-obsidian scoped
                                          generic rules: NONE

   The renderer emits m-ws-prop with an m-ws-prop-name span for EVERY look,
   and only Obsidian ever said how one is laid out. So the other five drew a
   name and a value as two adjacent inline boxes with nothing between them:
   StatusDone and AreaStore read as one word each, and Owner wrapped away
   from Codex because the wrap point fell INSIDE a pair rather than between
   two of them. Three defects, one missing rule.

   This is 0173's own question answered the way 0173 asks: the seventh app
   needs no new component here, it needs tokens. The four a look would want
   are named and read with fallbacks and defined nowhere yet --
   --prop-gap-x and --prop-gap-y BETWEEN pairs, --prop-name-gap WITHIN one,
   and --prop-name-color -- so every look gets the default arrangement until
   one re-points them.

   --prop-name-gap is @Codex's P2 on 27a1a65 and it is the distinction that
   matters: the first version tokenised only the gap between pairs and left
   the name-to-value gap a literal .4rem, so a look whose property rows are
   tighter or looser INSIDE a pair would have needed a new selector and a new
   literal -- which is the thing this rule exists to stop.

   Specificity, not source order (0058). Counted rather than asserted: each
   of the three below is ONE class, and every Obsidian rule over the same
   elements is two to five, so its display:contents grid still wins and
   nothing about the vault moves. The one property that does reach Obsidian
   is white-space on the name, which it never set -- a property name that
   does not wrap, in a 270px column, which is what that column is for. */
.m-ws-properties{display:flex;flex-wrap:wrap;
  gap:var(--prop-gap-y,.35rem) var(--prop-gap-x,1.4rem);align-items:baseline}
.m-ws-prop{display:flex;gap:var(--prop-name-gap,.4rem);align-items:baseline;min-width:0}
.m-ws-prop-name{color:var(--prop-name-color,var(--text-muted));
  font-size:calc(var(--s0) * .88);white-space:nowrap}
/* A line break the user typed is a line break they see.
   white-space:normal collapses a newline to a space: measured in Chromium at
   18px for one line against 36px for the same string under pre-wrap. So the
   edit host reading the break correctly would still have rendered it away, and
   half a fix is not one.
   pre-wrap rather than pre, because the measure still has to wrap -- a
   paragraph is bound by --measure like every other line of prose, and only the
   breaks the person typed are theirs to keep. Scoped to text, since a date or
   a score has no breaks to preserve and no reason to keep leading spaces.
   (No backticks in here. A backtick in a comment inside BASE_CSS ends the
   template literal, and this is the FOURTH time that has cost a build -- the
   file says so twice already and I did it anyway.) */
.m-kind-text{white-space:pre-wrap}
/* An empty writable field has to LOOK writable.
   The renderer has drawn a cell for an absent value under a read-write binding
   for a while, and it drew an empty div -- zero height, no affordance, nothing
   to aim at. A person with a blank note saw blank space and correctly concluded
   there was nowhere to type. The label comes from data-morph-role so the
   stylesheet needs no vocabulary of its own. */
.m-editable:empty{min-height:1.5em}
.m-editable:empty::before{pointer-events:none}
.m-editable:empty:focus::before{opacity:.3}
.m-role-title{font-size:var(--s1);font-weight:600;line-height:1.22;letter-spacing:-.011em}
.m-kind-date,.m-kind-number,.m-kind-enum{color:var(--text-muted);
  font-size:calc(var(--s0) * .92)}
.m-kind-score{font-variant-numeric:tabular-nums;color:var(--text-muted)}
/* A virtualized table/board and its status are one grid/flex item. */
.m-virtualized{min-width:0}
.m-more{color:var(--text-muted);font-size:calc(var(--s0) * .9);padding:var(--gap) 0}
.m-capability{font-size:var(--s0);color:var(--text-muted)}
/* THE STATUS BAR IS A STRIP, IN BOTH LOOKS THAT DRAW ONE -- 0173.
   Obsidian and Word are the whole drawer set here, not a majority of six,
   and they agreed on four declarations. Colour is not among them and stays
   in the looks: Obsidian writes a literal #8a8a8a, Word var(--text-muted).

   SPLIT ACROSS TWO SPECIFICITIES, and the split is the whole point. The
   element is class="m-capability m-ws-status", and .m-capability above
   declares font-size at the same (0,1,0) LATER in this sheet -- so a
   font-size here at (0,1,0) would lose to it, which is the mistake this
   sheet already records against .m-nav. Only that one declaration needs
   the sibling class; putting the other three at (0,2,0) as well would
   raise the bar a look has to clear to override a box it has always
   owned at (0,2,0), and turn every future override into a TIE.

   The class leads its compound rather than trailing it, because the
   bare-component control in look-token-ratio.test.ts strips by prefix --
   task 0230. Same rule either way in CSS; only one of the two spellings
   is visible to the control. */
.m-ws-status{display:flex;align-items:center;background:var(--raised)}
.m-ws-status.m-capability{font-size:11.5px}
.m-capability-refused{min-height:12rem;display:grid;place-items:center;text-align:center;
  border:1px dashed var(--border);border-radius:8px;padding:.75rem}
/* The chart fits its column instead of hanging out of it.
   It was a fixed 1000px wide with max-width:none, copied from the graph -- so
   inside the default shell's 895px content column the right edge sat under the
   end panel, taking the endpoint tick that says what the domain IS and three
   controls with it. Found by @Codex, by opening the deployed preview; no
   assertion over markup could have seen it, and the e2e was measuring boxes
   without ever asking what was ON TOP of them.
   (No backticks in here. A backtick in a comment inside BASE_CSS ends the
   template literal, and this is the third time that has cost a build.) */
.m-plot{margin:0;min-width:0;overflow-x:auto}
/* The frame both the marks and the controls are positioned inside. It holds the
   chart's aspect ratio so a percentage means the same place at every width. */
/* min-width is MIN_FRAME_PX. Below it the figure scrolls instead of scaling
   further: the controls are a fixed 24px while the geometry shrinks, so
   without a floor the SPACING between them goes to nothing and the bar refusal
   has no worst case to be computed against. */
.m-plot-frame{position:relative;width:100%;max-width:1000px;min-width:600px}
.m-plot svg{display:block;width:100%;height:100%}
.m-plot-axis{stroke:var(--border-strong);stroke-width:1}
/* The zero line, when zero is not the bottom of the frame. Stronger than the
   frame axis because for a bar or an area it is the line every mark is
   measured from. */
.m-plot-zero{stroke:var(--text-muted);stroke-width:1.5}
.m-plot-label{fill:var(--text-muted);font-size:13px}
/* The domain, at the ends of each axis. Smaller than the role caption because
   it is the caption's evidence rather than a second heading. */
.m-plot-tick{fill:var(--text-muted);font-size:11px;font-variant-numeric:tabular-nums}
.m-plot-series{stroke-width:2}
/* Each series sets color, and stroke/fill derive from it. The first version set
   stroke and fill directly, so the legend swatch — which is
   background:currentColor — inherited the page colour for every series but the
   one that happened to set color, and five of six keys were wrong. @Codex.

   The five literal colours clear 3:1 against both a white and a near-black
   surface, which continuous-plot.test.ts computes rather than assumes. s5 was
   #b09b4a at 2.75:1 on white — a thin line in the sixth series was close to
   invisible on every light scheme. Also @Codex. */
.m-plot-s0{color:var(--solid)}
.m-plot-s1{color:#b4654a}
.m-plot-s2{color:#4a7fb4}
.m-plot-s3{color:#6a9b5c}
.m-plot-s4{color:#9b6ab0}
.m-plot-s5{color:#8a6f00}
.m-plot-series{stroke:currentColor;fill:currentColor}
/* AFTER the colour rules, and specific to the mark. A fill:none declared first
   was overridden by every per-series fill, so a line rendered as a filled
   polygon of its own area — the mark drawing something other than what it
   claims. Also @Codex. */
path.m-plot-series[data-mark=line]{fill:none}
path.m-plot-series[data-mark=area]{fill-opacity:.18}
circle.m-plot-series,rect.m-plot-series{stroke:none}
/* Controls do NOT scale with the chart, and that is the whole point of them
   being here rather than inside the SVG. A viewBox scales everything within it,
   so a 24-unit target becomes 21 CSS pixels in a 900px column and the floor
   a11y.ts exports quietly fails -- the third route by which this renderer has
   shipped an undersized target. Geometry scales because it should; a finger
   does not. */
.m-plot-hit{position:absolute;translate:-50% -50%;
  width:24px;height:24px;padding:0;border:0;border-radius:50%;
  background:transparent;cursor:pointer}
.m-plot-hit:hover{background:color-mix(in oklab,var(--solid) 22%,transparent)}
.m-plot-hit:focus-visible{outline:2px solid var(--focus-ring);outline-offset:1px}
.m-plot-legend{display:flex;flex-wrap:wrap;gap:.75em;margin-top:.5em;
  color:var(--text-muted);font-size:13px}
.m-plot-key::before{content:"";display:inline-block;width:.7em;height:.7em;
  border-radius:2px;margin-inline-end:.4em;background:currentColor}
.m-force-graph{margin:0;min-width:0;overflow-x:auto}
.m-force-graph svg{display:block;width:1000px;height:560px;min-height:0;max-width:none;
  border-radius:12px;background:radial-gradient(circle at 50% 48%,var(--surface),var(--raised))}
.m-graph-edges line{stroke:var(--border-strong);stroke-width:1.4;opacity:.72}
.m-graph-node{width:100%;height:36px;padding:0 .6rem;overflow:hidden;text-overflow:ellipsis;
  white-space:nowrap;border:1px solid var(--border-strong);border-radius:999px;
  background:var(--page);color:var(--text);font:600 12px/1 var(--ui);cursor:pointer;
  box-shadow:0 5px 16px -12px rgb(0 0 0/.55)}
.m-graph-node:hover{background:var(--surface-hover);transform:translateY(-1px)}
.m-graph-node:focus-visible{outline:3px solid var(--focus-ring);outline-offset:2px}
.m-graph-relations{position:absolute;inline-size:1px;block-size:1px;padding:0;margin:-1px;
  overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
.m-force-graph figcaption{padding:.45rem .25rem 0;color:var(--text-muted);
  font-size:calc(var(--s0) * .82);font-variant-numeric:tabular-nums}
.m-slot{border:1px solid var(--border-subtle);border-radius:10px;padding:var(--gap);
  display:flex;flex-direction:column;gap:calc(var(--gap) * .5);background:var(--raised)}
.m-slot-head{font-size:calc(var(--s0) * .78);font-weight:600;letter-spacing:.06em;
  text-transform:uppercase;color:var(--text-muted)}
/* Connections: a PROPERTY LIST, not a grid of cards.
   @Codex, inspecting the three-role surface: "Background expands into a large,
   full-width nested object card. Parent task and Subtasks flow side-by-side
   beneath it … reads as mixed card/grid layout rather than three consistent
   compact rows." Removing each nested slot's border was not a layout; the rows
   still inherited the region's flow and each one sized itself to its contents.
   A column of labelled rows is what the ruling asked for, and what every
   product this repo is measured against ships for the same thing. */
.m-connections{display:flex;flex-direction:column;
  border:1px solid var(--border-subtle);border-radius:10px;
  padding:var(--gap);gap:0;background:var(--raised)}
.m-connections-head{margin:0 0 calc(var(--gap) * .5);font-size:calc(var(--s0) * .78);
  font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--text-muted)}
/* Label left, targets and Add flowing beside it — one row per role, whatever
   the row holds. wrapping rather than a fixed height: a relation with four
   targets is allowed to be two lines, but it is still one row. */
.m-connections>.m-slot{display:flex;flex-flow:row wrap;align-items:baseline;
  gap:calc(var(--gap) * .5);border:0;border-radius:0;background:transparent;
  padding:calc(var(--gap) * .45) 0}
/* LOWERED — decision 0074 row 4 as amended. This tied
   .m-app-obsidian .m-ws-editor .m-slot at (0,3,0) on the border-top family,
   and a tie is settled by position in the sheet. Obsidian already paints no
   internal separator through a STRONGER later rule, so this is pixel-neutral:
   the amendment is explicit that a stronger declaration does not make the tie
   legal, it only conceals the tie's visual effect in today's cascade. The
   separator stays a live generic fallback at (0,2,0). */
:where(.m-connections)>.m-slot + .m-slot{border-top:1px solid var(--border-subtle)}
/* LOWERED — 0074 row 2. The 7.5rem minimum is a fallback for an ordinary
   Connections list, not a component invariant, and it was beating Obsidian's
   declared 132px label column: measured min-width 120px and flex-basis auto
   where the look asks for 0 and 132px. The generic keeps its fallback for
   every specimen outside a bounded look. */
:where(.m-connections)>.m-slot>.m-slot-head{flex:0 0 auto;min-width:7.5rem;margin:0}
/* And the target is a CHIP here, not a summary card.
   This is the half of the finding that flow direction alone does not fix: a
   populated relation rendered its nested object with a name and every detail
   row it had — Body, Owner, Updated — so one populated row measured 104px
   against 63px for its empty neighbours, and @Codex's inspection read the
   surface as "mixed card/grid".
   The ruling asks for "its current chips/summary" per row, which is what every
   product this repo is measured against shows in a relation row: the name, and
   a click to go and see the rest. The detail rows are HIDDEN rather than
   dropped, so the hover preview and the harnesses that read this subtree still
   see everything the projection resolved — only the stack stops drawing it. */
/* LOWERED — 0074 row 3 as amended. The live tie here is align-items and the
   gaps, NOT display: the rendered item computes to flex whatever these two say,
   because a later base rule wins and blockification applies. Alignment and a
   decorative gap are look choices, so Obsidian owns them and this stays the
   contextual default at (0,2,0). */
:where(.m-connections)>.m-slot .m-slot-item{display:inline-flex;align-items:baseline;
  gap:calc(var(--gap) * .35);max-width:100%;min-width:0}
/* The drawn card is hidden here and the OPEN control carries the name: one
   chip, which is the thing you click. Hidden rather than dropped, so the
   projection's detail is still in the subtree for the hover preview and for
   every harness that reads it — only the stack stops drawing it. */
.m-connections>.m-slot .m-ref-card{display:none}
/* In a Connections row the OPEN control is the chip, and the drawn name is
   decoration beside it — so the thing that looks clickable is the thing that
   is. Elsewhere it stays an ordinary small button. */
.m-slot-open{font:inherit;font-size:calc(var(--s0) * .82);cursor:pointer;
  border:1px solid var(--border-subtle);border-radius:999px;
  padding:calc(var(--gap) * .15) calc(var(--gap) * .45);background:var(--page)}
.m-slot-open:focus-visible{outline:2px solid var(--focus-ring);outline-offset:2px}
.m-connections>.m-slot .m-slot-open{max-width:22rem;overflow:hidden;
  text-overflow:ellipsis;white-space:nowrap;font-weight:600}

/* The two relation gestures. Both are ordinary buttons the same size as every
   other target on the page, because a control a finger cannot land on is a
   control that is not there.

   Detach sits beside its occurrence and stays quiet until the row is hovered or
   it is focused: a row of Remove buttons reads as a delete list rather than as
   a set of references. It is NOT hidden from a keyboard: focus-visible is
   what brings it back, so tabbing through reveals each one in turn. */
.m-slot-item{display:flex;align-items:flex-start;gap:calc(var(--gap) * .5);
  justify-content:space-between}
.m-detach{font:inherit;font-size:calc(var(--s0) * .78);
  min-height:var(--target);min-width:var(--target);padding:.15em .6em;border-radius:7px;
  border:1px solid transparent;background:none;color:var(--text-muted);cursor:pointer;
  opacity:0;transition:opacity .14s ease,border-color .14s ease,color .14s ease}
.m-slot-item:hover .m-detach,.m-detach:focus-visible{opacity:1}
.m-detach:hover{border-color:var(--border-strong);color:var(--text)}
.m-detach:focus-visible{outline:2px solid var(--focus-ring);outline-offset:2px}
/* A pointer-less device never hovers, so the quiet state would be permanent. */
@media (hover:none){.m-detach{opacity:1}}
.m-attach{font:inherit;font-size:calc(var(--s0) * .82);font-weight:600;
  min-height:var(--target);padding:.25em .8em;border-radius:8px;
  border:1px dashed var(--border-strong);background:none;color:var(--text-muted);
  cursor:pointer;align-self:flex-start;
  transition:border-color .14s ease,color .14s ease,background .14s ease}
.m-attach:hover{border-style:solid;color:var(--text);background:var(--surface-hover)}
.m-attach:focus-visible{outline:2px solid var(--focus-ring);outline-offset:2px}

/* A reference, and the object behind it. Hover or focus reveals it — no
   JavaScript, so it works before hydration and under a keyboard. */
.m-ref{position:relative;display:inline-block}
.m-ref-hit{font:inherit;color:inherit;background:none;border:0;padding:0;cursor:pointer;
  border-bottom:1px solid var(--border-strong);line-height:1.3}
.m-ref-hit:hover{border-bottom-color:var(--text)}
.m-ref-hit:focus-visible{outline:2px solid var(--focus-ring);outline-offset:2px}
.m-ref-pop{position:absolute;left:0;top:calc(100% + .45rem);z-index:20;
  width:max(15rem,22ch);padding:.7rem;border-radius:11px;
  background:var(--page);border:1px solid var(--border);
  box-shadow:0 12px 36px -14px rgb(0 0 0/.45);
  opacity:0;visibility:hidden;transform:translateY(-3px);
  transition:opacity .13s ease,transform .13s ease,visibility .13s}
.m-ref:hover .m-ref-pop,.m-ref:focus-within .m-ref-pop{
  opacity:1;visibility:visible;transform:none}
.m-ref-card{display:flex;gap:.6rem;align-items:flex-start;text-align:left}
.m-ref-media{flex:0 0 2.6rem;aspect-ratio:1;border-radius:6px;
  background-color:var(--surface);background-size:cover;background-position:center}
.m-ref-body{min-width:0;flex:1}
.m-ref-name{font-weight:600;font-size:calc(var(--s0) * .95);line-height:1.25;
  margin-bottom:.25rem}
.m-ref-row{display:flex;gap:.5rem;justify-content:space-between;
  font-size:calc(var(--s0) * .82);line-height:1.5}
.m-ref-key{color:var(--text-muted);flex-shrink:0}
.m-ref-val{text-align:right;overflow-wrap:anywhere}
.m-ref-deeper{font-size:calc(var(--s0) * .78);color:var(--text-muted);
  border-top:1px solid var(--border-subtle);margin-top:.4rem;padding-top:.4rem}
.m-kind-ref{display:flex;flex-wrap:wrap;gap:.4rem}
/* A relation CELL stays a cell: the flex above is the field's, and a <td>
   or <th> wearing the kind class would otherwise leave its row. */
.m-as-table td.m-kind-ref,.m-as-table th.m-kind-ref{display:table-cell}

/* An editable value. It reads as text until it is hovered or focused, because
   a lens full of input boxes is a form, and the thing being edited is the
   document — but it is still a target, so it consumes --edit-target rather than
   ending up whatever height its line box happened to be. Both numbers come from
   tokens.ts; a padding that lived only here is what put the box under the
   accessibility floor with nothing able to see it. */
.m-editable{border-radius:5px;padding:var(--edit-pad) .3em;margin:calc(-1 * var(--edit-pad)) -.3em;
  /* border-box, because --edit-target is the height of the whole painted box —
     line box plus both paddings — which is what a target size means. */
  min-height:var(--edit-target);box-sizing:border-box;cursor:text;
  transition:background .12s ease,box-shadow .12s ease}
.m-editable:hover{background:var(--surface)}
/* The empty-still note, matching the av and document ones rather than
   inventing a third look for the same idea. */
.m-still-empty{display:flex;align-items:center;justify-content:center;
  min-height:var(--edit-target);padding:.6em .8em;border-radius:6px;
  border:1px dashed var(--line);color:var(--text-muted);font-size:.9em}
/* A CHOOSER is not a text box, and inheriting cursor:text from .m-editable told
   every reader it was one. It also has to stop looking like a raw browser
   control sitting in the middle of a generated design: appearance:none plus the
   page's own type, with the caret drawn from two gradients rather than an SVG
   data URI. That is not decoration — BASE_CSS is a template literal, and a
   backtick inside it has broken the build five times, so a caret with no quotes
   and no escapes in it is the cheap way to never make that mistake again. */
.m-choose{cursor:pointer;font:inherit;color:inherit;background-color:transparent;
  border:0;appearance:none;-webkit-appearance:none;padding-right:1.4em;
  background-image:linear-gradient(45deg,transparent 50%,var(--text-muted) 50%),
    linear-gradient(135deg,var(--text-muted) 50%,transparent 50%);
  background-position:calc(100% - .7em) calc(50% - 1px),calc(100% - .4em) calc(50% - 1px);
  background-size:.3em .3em,.3em .3em;background-repeat:no-repeat}
/* An unset enum reads as a prompt, the same way an empty editable cell does.
   Chromium has :has(); a browser without it loses the grey and nothing else. */
/* AN UNSET CHOOSER READS AS PLACEHOLDER, IN EVERY LOOK -- decision 0069, and
   @Codex's P2 on e5ffb92 is what made it visible. This was
   .m-choose:has(...) at (0,3,1) and TIED
   .m-app-coda .m-as-table select.m-choose, so source order decided whether
   "nothing chosen yet" looked any different from a chosen value -- and Coda's
   rule is later in the sheet, so in Coda's table it did not.
   The .m-field is not weight for its own sake: there is ONE emission site for
   a chooser and it writes m-field m-editable m-choose m-kind-enum together,
   so the pair matches exactly the same elements and (0,4,1) is the least that
   exceeds a look. The look still owns the VALUE through --text-muted, which
   every look sets; what it may not own is whether unset is muted at all. */
.m-field.m-choose:has(option[value=""]:checked){color:var(--text-muted)}
.m-choose option{color:var(--text);background:var(--page)}
.m-editable:focus,.m-editable:focus-visible{outline:2px solid var(--focus-ring);
  outline-offset:1px;background:var(--page)}
/* An empty read-write cell is the one a user must click to FILL a property, so
   it has to occupy space and say what it is. Zero-height is indistinguishable
   from a property that cannot be added. */
.m-editable:empty{display:inline-block;min-width:6ch}
.m-editable:empty::before{content:attr(data-morph-empty);color:var(--text-muted);
  opacity:.55}
.m-as-table td .m-editable{display:block}

@media (prefers-reduced-motion:reduce){
  .m-root *{transition:none !important;animation:none !important}
}

/* ═══════════════════════════════════ INERT PRESENTATION — decision 0056
   Chrome an app shows that the host cannot back is imagery: a span with no
   hover, no cursor, no focus ring, its glyph and label in the app's type.
   Generic to every look; each look sizes its own.

   These eleven rules sat INSIDE the app.word block until task 0173 step 2,
   between .m-app-word>.m-region.m-stack and Word's title bar, with a
   comment correctly calling them generic. Five looks draw chrome that
   depends on them. A pass that "shrinks the Word block" — which is the whole
   direction 0173 points — would have removed the ribbon's boxes, Obsidian's
   vault glyphs, Notion's toolbar glyphs, Coda's and Fabric's, with nothing
   in pnpm run verify going red. That is a live hazard rather than untidy
   filing, and it is why this move is step 2 of the refactor rather than part
   of one.

   BEFORE the six app blocks, not after: every rule that overrides one of
   these is a descendant selector of .m-app-<name> and wins on specificity
   regardless of order, so the position is chosen to read as "generic first,
   looks after" rather than to settle a cascade. */
.m-inert{display:inline-flex;align-items:center;gap:.3rem;cursor:default;user-select:none;color:var(--text)}
.m-inert-glyph{display:inline-flex;align-items:center;justify-content:center;line-height:1}
/* THE WORKSPACE CHEVRON IS MUTED — task 0173. Notion, Coda and Fibery each
   wrote this rule with the identical declaration; the other three looks do not
   draw the element at all, measured across six doors, so promoting it reaches
   nobody who was inheriting. That is the check that rejected m-inert-glyph an
   hour earlier: a generic rule arriving at an element that had no rule is a
   change, and here there is no such element. */
.m-nav-workspace .m-chev{color:var(--text-muted)}
/* AND THE SEARCH PLACEHOLDER IS MUTED BY DEFAULT — task 0173. Notion, Fibery
   and Fabric each wrote var(--text-muted) and now share this one. Obsidian
   keeps its OWN rule and is the stated exception, because its literal is NOT
   its token: measured, --text-muted there is #9a9a9a while the placeholder is
   #6f6f6f, so promoting over it would have moved rgb(111,111,111) to
   rgb(154,154,154). Whether that literal is intended or is drift is a question
   for the reference, not for this refactor. Obsidian's rule is two classes and
   a pseudo-element against this one class and a pseudo-element, so it still
   wins. */
.m-ws-search-input::placeholder{color:var(--text-muted)}
.m-inert-big{flex-direction:column;justify-content:center;gap:.3rem}
.m-inert-box{border:1px solid var(--border);border-radius:4px;background:var(--page);padding:.15rem .5rem}
/* A swatch button: the glyph over its colour bar, the ▾ beside both. */
.m-inert-swatch{display:inline-grid;grid-template-columns:auto auto;grid-template-rows:auto auto;column-gap:.15rem;row-gap:1px;
  align-items:center;justify-items:center}
.m-inert-swatch .m-inert-glyph{grid-column:1;grid-row:1}
.m-inert-bar{display:block;width:16px;height:4px;border-radius:1px;grid-column:1;grid-row:2}
.m-inert-swatch .m-inert-menu{grid-column:2;grid-row:1/3}
.m-inert-menu{font-size:9px;color:var(--text-muted);margin-left:.1rem}
.m-inert-break{flex-basis:100%;height:0;display:block}
/* A WINDOW CONTROL: a filled circle of the colour the look declared, drawn
   instead of a glyph. Generic, because a window is not one app's idea — every
   look whose reference is a desktop window draws these, and the colours are
   the look's to say.

   The 12px circle on a 20px pitch is corroborated by the pinned capture rather
   than read off it. Decision 0068 forbids taking an absolute size from a
   downscale, and a run of colour is the worst thing to take one from: its edges
   are antialiased away, so obsidian-site.png measures the runs at 10px with 8px
   between them and understates both. What survives a rescale is the PITCH,
   start to start — 77, 97, 117 — and 20px is exactly macOS's own 12 plus 8. So
   the relation says the region is 1:1 and the platform supplies the number. */
.m-inert-dot{display:block;width:12px;height:12px;border-radius:50%}
/* The row those controls sit in. ONE rule for every container that holds
   them — Obsidian's tab strip and Notion's sidebar today — rather than a rule
   per look: a seventh app whose reference is a window declares the controls
   and gets the row, which is the arrangement the 2026-09-03 amendment asks
   for.

   align-self is STRETCH and that is the whole reason one rule serves both.
   The two containers run on different axes — Obsidian's strip is a row that
   aligns its tabs to flex-end, Notion's sidebar is a column — so align-self
   means "vertical" in one and "horizontal" in the other. Centre was tried and
   is wrong by construction: it centred the controls correctly in the strip and
   pushed them to the middle of the sidebar, 94px from a window edge the
   reference puts them 15px from. Stretch asks for neither axis, so the row
   fills the bar's height and the column's width, and align-items below centres
   the dots inside it either way. */
.m-winctl{display:flex;align-items:center;align-self:stretch;gap:8px;
  padding:4px 6px}

/* ═══════════════════════════════════ app.notion — decision 0055's second look
   Written from the pinned reference (the "Ramp HQ" page and its "Company
   tasks" board) and judged beside it at 1440×900. Light: white page, a warm
   near-white sidebar, near-black text, hairline warm-grey borders, colour
   only in the status pills and the New button. Every colour is a token the
   template declared, or one of the four status tints the reference shows. */
.m-app-notion{
/* THE CATEGORICAL PALETTE, as values -- task 0178, decision 0063. These four
   triples were four nth-child rules an hour ago, so a board column's colour
   was a fact about where the column sat. The slot now comes from the VALUE and
   the look supplies only the colours, which is what a look owns.

   Three roles per slot because a Notion lane paints three things: the column's
   ground, the name pill behind it, and the dot before the name. soft, bg, ink.

   The four slots the demo's statuses declare (Open 4, In progress 1,
   In review 3, Done 2) carry exactly the colours those four columns had, so
   the board is unchanged to look at and changed in what decides it. Slots 0
   and 5 are Notion's grey and orange, for a fifth and sixth value that
   declares a tone -- they were unreachable before, because a fifth column
   simply restarted the cycle at purple.

   Slot 0's ground is var(--raised) and not the literal it started as: the
   duplicate-literal guard caught that #f7f7f5 IS this look's --raised, which
   is exactly the check that exists so a look's private constant cannot drift
   away from the token it was copied from. */
--tone-0-soft:var(--raised);--tone-0-bg:#e3e2e0;--tone-0-ink:#9a9a97;
--tone-1-soft:#fbf9f1;--tone-1-bg:#f6ecc7;--tone-1-ink:#d9a441;
--tone-2-soft:#f3faf5;--tone-2-bg:#d8f0df;--tone-2-ink:#3f9c60;
--tone-3-soft:#f3f8fd;--tone-3-bg:#dcebfa;--tone-3-ink:#3d89dd;
--tone-4-soft:#f8f6fc;--tone-4-bg:#e9e2f5;--tone-4-ink:#9a6dd7;
--tone-5-soft:#fdf6f4;--tone-5-bg:#fadec9;--tone-5-ink:#d9730d;
/* The table, as VALUES. Every one of these was a declaration inside a
   .m-app-notion .m-as-table rule an hour ago; the rules they fed are now one
   generic component. What is absent is as much the point as what is here:
   Notion's header colour, its head rule and its row rule are the component's
   own defaults, so they are not restated. */
--table-collapse:collapse;--table-font:14px;
--table-head-weight:500;--table-head-align:start;--table-head-font:13px;
--table-head-pad:.45rem .6rem;--table-head-transform:none;--table-head-tracking:0;
--table-col-rule:1px solid var(--border-subtle);
--table-cell-pad:.55rem .6rem;--table-cell-align:middle;--table-muted-color:var(--text);
--table-name-weight:500;
--chev-size:28px;
--tabadd-size:20px;--tabadd-pad:0 .6rem;
  --font:-apple-system,BlinkMacSystemFont,'Segoe UI','Inter',Roboto,Helvetica,Arial,sans-serif;
  /* The view row's search is COLLAPSED (0187): the placement is the tool's,
     so the trigger and the open box land in the same place — the row, at the
     row's own height, ending 5.3rem short of the page's right edge with New
     beyond it. The trigger STAYS while the field is open, at the field's left
     as the app draws it, so the pair covers the inert endcap it grew out of
     and pressing the glyph again puts the row back;
     The m-ws-search-reserve token is the hole the endcap leaves for it, and
     it is now the TRIGGER's width rather than the box's, which is what put
     a 220px gap between the ··· and the glyph. */
  --m-ws-search-display:flex;
  --m-ws-search-align:flex-end;--m-ws-search-place:-50px 5.3rem 28px 0;
  --m-ws-search-row:34px;--m-ws-search-w:212px;--m-ws-search-gap:0px;
  --m-ws-search-open-bg:var(--page);
  --m-ws-search-edge:1px solid var(--border);--m-ws-search-radius:6px;
  --m-ws-search-edge-on:var(--focus-ring);
  --m-ws-search-reserve:1.8rem;--m-ws-search-btn:28px;--m-ws-search-btn-radius:4px;
  -webkit-font-smoothing:antialiased}
/* THE WINDOW IS THE VIEWPORT -- task 0217. minmax(100vh,1fr) is a height FLOOR
   and says nothing about a ceiling, which is the shape this look shared with
   app.fibery; app.coda grows for a different reason (an inert height:100%),
   and app.word and app.fabric already bind theirs with height:100vh. */
.m-app-notion>.m-region.m-row{--shell-tracks:240px minmax(0,1fr);
  grid-template-rows:minmax(0,1fr)}
@media (max-width: 48rem){.m-app-notion>.m-region.m-row{--shell-tracks-folded:minmax(0,1fr)}
  /* The nav's fold is GENERIC now — task 0232's promotion, at the top of this
     sheet beside the other shell rules. The finding that made it necessary was
     task 0218's: this look's fold read .m-app-notion .m-nav at (0,2,0) against
     the nav's own base rule, also (0,2,0), so it had never once applied and a
     person met the nav filling a 900px viewport with the page below it. */
}
/* The nav sidebar */
.m-app-notion .m-nav{background:var(--raised);padding:.5rem .5rem 1rem;font-size:14px}
.m-app-notion .m-nav-workspace{padding:.35rem .5rem}

.m-app-notion .m-tools{gap:.25rem;padding:.25rem .25rem .5rem}
.m-app-notion .m-tool{gap:.5rem;font-size:14px}
.m-app-notion .m-tool-on,.m-app-notion .m-tool:hover{background:var(--surfaceHover, #ebebea)}
.m-app-notion .m-tool-icon{padding:.3rem .45rem;color:var(--text-muted)}
/* The tools row's other icons — chat, calendar, inbox — inert (0056),
   in the row between Home and Search as the reference has them. */
.m-app-notion .m-tool-inert{padding:.3rem .45rem;color:var(--text-muted);cursor:default}
.m-app-notion .m-tool-inert .m-inert-glyph{color:var(--text-muted)}
/* The foot: the reference's compose button, pinned under the sections. */
.m-app-notion .m-nav-newpage{padding:.45rem .7rem;border:1px solid var(--border);border-radius:999px;background:var(--page);font-size:14px;font-weight:500;color:var(--text);box-shadow:0 1px 2px rgba(0,0,0,.04)}
.m-app-notion .m-nav-newpage:hover{background:var(--surface)}
.m-app-notion .m-nav-section{margin-top:.7rem}
/* The sidebar's TREE: a row that holds pages gets a twisty before it and
   its pages indented under it, both folded through the same view-state
   door as a panel, a column and the sidebar itself (decision 0014). The
   twisty is its own button beside the row, so disclosing and opening stay
   the two acts the app keeps them. */
.m-app-notion .m-nav-rowline{display:flex;align-items:center;gap:.1rem}
.m-app-notion .m-nav-rowline>.m-nav-row{flex:1;min-width:0}
.m-app-notion .m-nav-twisty{flex:none;width:18px;height:22px;display:inline-flex;align-items:center;justify-content:center;
  border:0;background:transparent;color:var(--text-muted);border-radius:4px;cursor:pointer;padding:0}
.m-app-notion .m-nav-twisty:hover{background:rgb(15 15 15 / .06);color:var(--text)}
.m-app-notion .m-nav-twisty>.m-glyph{transition:transform .12s ease}
.m-app-notion .m-nav-tree[data-morph-collapsed="true"] .m-nav-twisty>.m-glyph{transform:rotate(-90deg)}
.m-app-notion .m-nav-tree[data-morph-collapsed="true"] .m-nav-kids{display:none}
/* The indent is measured from the LABEL, not the marker: at 1.15rem the
   twisty's width cancelled it and a child's text began on the parent's own
   axis, so the nesting read only from the bullet (@Codex, the 37th
   verdict, named twice). The kids clear the twisty and then indent. */
.m-app-notion .m-nav-kids{display:flex;flex-direction:column;gap:1px;margin-left:2.5rem}
.m-app-notion .m-nav-kid .m-nav-row-icon{font-size:11px}
.m-app-notion .m-nav-kid .m-nav-row-label{color:var(--text-muted)}
.m-app-notion .m-nav-kid.m-nav-row-on .m-nav-row-label{color:var(--text)}
.m-app-notion .m-nav-section-label{font-size:12px;padding:.2rem .5rem .3rem;
  font-weight:500}
.m-app-notion .m-nav-row{gap:.55rem;padding:.28rem .5rem;font-size:14px;min-height:1.9rem}
.m-app-notion .m-nav-row:hover{background:var(--surface)}
.m-app-notion .m-nav-row-on{background:var(--surface)}
.m-app-notion .m-nav-row-icon{width:18px;color:var(--text-muted);font-size:14px}
.m-app-notion .m-nav-row-detail{font-size:13px}
/* The page column's topbar. The column itself is generic now -- the shell's
   inner column carries the ground and no gap for every look. */
.m-app-notion .m-topbar{flex-direction:column;align-items:normal;gap:0;padding:0;font-size:14px}
/* The tab strip: back and forward over the places visited under the look
   (greyed with nowhere to go, as the app greys them), the page's tab, a +. */
.m-app-notion .m-topbar-tablist{display:contents}
.m-app-notion .m-topbar-strip{gap:.25rem;height:44px;padding:0 .5rem 0 .35rem;border-bottom:1px solid var(--border-subtle)}
.m-app-notion .m-chev+.m-chev{margin-right:.5rem}
.m-app-notion .m-topbar-tab{gap:.5rem;padding:.4rem .9rem;height:100%;border-right:1px solid var(--border-subtle);border-left:1px solid var(--border-subtle);color:var(--text);font-weight:500}
.m-app-notion .m-topbar-tab-icon{display:inline-flex;color:var(--text);font-size:14px;line-height:1}
/* The page actions, right-aligned under the strip as the reference's Share,
   comment, star and ··· sit: only the two the host backs — the star keeps
   the page on the hub's shelf, ··· opens the actions the host has. */
.m-app-notion .m-topbar-actions{justify-content:flex-end;gap:.15rem;height:44px;padding:0 .75rem}
.m-app-notion .m-topbar-star,.m-app-notion .m-topbar-more{color:var(--text);width:32px;height:32px;border-radius:6px;display:inline-flex;font-size:18px;letter-spacing:.04em}
.m-app-notion .m-topbar-star[aria-pressed="true"]{color:#e9a825}
/* Share and the comment glyph among the page actions — inert (0056). */
.m-app-notion .m-topbar-action-inert{height:32px;padding:0 .45rem;font-size:14px;color:var(--text);gap:.3rem;cursor:default}
.m-app-notion .m-topbar-action-inert .m-inert-glyph{color:var(--text)}
/* The view row's endcap — Filter, Sort, the automations bolt, ··· —
   inert (0056), between the views and New. The search box is its own
   module, pulled up into this row by its margin (below) and 220px wide,
   so the endcap leaves it that room: seen with .35rem, the box sat ON
   the endcap and hid every tool under it. */
.m-app-notion .m-tools-inert{gap:.1rem;margin-right:var(--m-ws-search-reserve,calc(220px + 2.5rem))}
.m-app-notion .m-tool-inert.m-inert{padding:.35rem .5rem;border-radius:6px;font-size:14px;color:var(--text-muted);cursor:default}
.m-app-notion .m-viewswitch .m-tool-inert .m-inert-glyph{color:var(--text-muted)}
.m-app-notion .m-topbar-menu{top:40px;right:.75rem;min-width:220px;border-radius:8px}
.m-app-notion .m-topbar-menu-item{font-size:14px}
/* The page: wide, not centred — the reference's board runs to the right edge */
.m-app-notion .m-ws-editor{padding:1rem 3rem 3rem 5.5rem;gap:0;background:var(--page)}
.m-app-notion .m-pagehead{align-items:center;gap:1.1rem;margin:0 0 1.2rem}
.m-app-notion .m-pagehead-titlerow{gap:1.1rem}
.m-app-notion .m-pagehead-icon{width:88px;height:88px;border-radius:50%;background:var(--surface);color:var(--text);font-size:46px}
.m-app-notion .m-pagehead-title{font-size:38px;font-weight:700;line-height:1.2}
/* The view switcher */
.m-app-notion .m-viewswitch{margin:0 0 1rem;padding-bottom:0}
.m-app-notion .m-viewswitch-list{gap:.25rem}
.m-app-notion .m-viewswitch-item{gap:.45rem;padding:.35rem .7rem;font-size:15px}
.m-app-notion .m-viewswitch-on{background:var(--surface);color:var(--text)}
.m-app-notion .m-viewswitch-new{border:0;background:var(--solid);color:#fff;
  font-size:14px;padding:.4rem .9rem}
.m-app-notion .m-viewswitch-new:hover{filter:brightness(1.05)}
/* The board: the runtime's own presentation in the app's clothes. Tinted
   columns, a pill header of dot and label with a grey count, white cards
   with a hairline border, soft shadow and 16px padding. The four tints are
   the reference's (purple, amber, blue, green), by column. */
/* The board runs to the WINDOW's edge, as the reference's fourth column
   does — the page's right padding is given back to the scroll area, so a
   column is clipped by the window rather than by a margin. */
.m-app-notion .m-as-board{gap:1.25rem;padding:0 3rem 1rem 0;margin-right:-3rem;background:transparent}
/* The columns SHARE the workspace's width so a four-status board fits at
   1440×900 as the reference's does (the seventeenth verdict: the Done
   column was cut off) — flexible between a floor and the reference's
   width, scrolling only when more columns than fit. */
.m-app-notion .m-group{flex:1 1 230px;min-width:230px;max-width:320px;border:0;border-radius:8px;
  padding:.5rem .5rem .7rem;background:var(--tone-soft,#f6f6f4);box-shadow:none}
.m-app-notion .m-group-head{gap:.6rem;margin:0 0 .75rem;padding:0 .2rem;
  font-size:14px;font-weight:500;color:var(--text);border:0;background:transparent}
.m-app-notion .m-group-name{display:inline-flex;align-items:center;gap:.4rem;padding:.15rem .6rem .15rem .5rem;
  border-radius:999px;background:var(--tone-bg,#e3e2e0);font-size:14px}
.m-app-notion .m-group-name::before{content:"";width:8px;height:8px;border-radius:50%;background:var(--tone-ink,#9a9a97)}
.m-app-notion .m-group-head .m-count{color:var(--text-muted);font-weight:400;font-size:14px;
  background:transparent;padding:0;border:0}
.m-app-notion .m-group-items{gap:.5rem}
.m-app-notion .m-group .m-card{background:#fff;border:1px solid var(--border);border-radius:6px;
  box-shadow:0 1px 2px rgb(15 15 15 / .04);padding:0;margin:0}
.m-app-notion .m-group .m-card>.m-card-hit{width:100%;padding:.85rem 1rem;border:0;
  background:transparent;text-align:start;font:inherit;font-size:15px;color:var(--text);
  line-height:1.4;cursor:pointer;border-radius:6px}
.m-app-notion .m-group .m-card>.m-card-hit:hover{background:var(--raised)}
.m-app-notion .m-group .m-card .m-role-title{font-size:inherit;font-weight:inherit;margin:0;line-height:inherit}
.m-app-notion .m-group .m-card[aria-current="true"]>.m-card-hit,
.m-app-notion .m-group .m-card.m-current>.m-card-hit{background:var(--surface)}
/* The view row's Search: the search module's box, compact, IN the row at
   its right end beside New — where the reference's search icon expands
   into a field (the eighteenth verdict: the row as one compact strip, not
   a row with detached controls). The box follows the row in the doc, so
   it rides up onto it by its own height. */
.m-app-notion .m-ws-search-box{width:var(--m-ws-search-w,220px);margin:0;padding:0}
.m-app-notion .m-ws-search-input{font-size:14px;padding:.35rem .6rem;
  border:1px solid var(--border);border-radius:6px;background:var(--page)}
.m-app-notion .m-ws-search-input:focus{border-color:var(--focus-ring)}

/* The count sits UNDER the row, on the search tool's second line — the
   negative pull it had was measured against a tool whose only child was the
   box, and 0187 put the trigger on that line too. */
.m-app-notion .m-ws-section{text-align:end;margin:.35rem 0 0}
/* The TABLE view: grey small headers, hairline row separators, generous
   rows, the title bold-ish — the reference's database table. */
.m-app-notion .m-table-scroll{margin-right:-3rem;padding-right:3rem}
/* The table: Notion's database table — hairline rules between ROWS and
   between COLUMNS (the sixteenth verdict: the missing vertical rules
   flattened the grid), and a property-TYPE glyph before each header, by
   the column's kind, as the app draws one before every property name. */
/* THE GLYPH RULE NAMES EVERY KIND IT HAS A MASK FOR, and nothing else --
   the same allowlist Fibery's carries, and for the same reason. Unguarded,
   this matched th.m-kind-open, whose --nt-kind is undefined; an invalid mask
   falls back to none and the pseudo-element paints its whole
   background:currentColor box, so the Open gutter drew a SOLID GREY SQUARE
   where every other header draws its kind. Measured: mask none, background
   rgb(120,119,116), 14x14.

   Fibery's comment already recorded this exact defect -- @Codex's P2 on #227,
   the score header -- and its allowlist is why Fibery was unaffected when the
   gutter arrived. Notion's rule was the one still open to a new kind. */
.m-app-notion .m-as-table th:is(.m-kind-text,.m-kind-enum,.m-kind-date,.m-kind-number,.m-kind-score,.m-kind-ref,.m-kind-media,.m-kind-av,.m-kind-doc)::before{content:"";display:inline-block;width:14px;height:14px;
  margin-right:.4rem;vertical-align:-2px;background:currentColor;opacity:.75;
  -webkit-mask:var(--nt-kind) center/contain no-repeat;mask:var(--nt-kind) center/contain no-repeat}
.m-app-notion .m-as-table th.m-kind-text{--nt-kind:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round'%3E%3Cpath d='M4 7h16M4 12h16M4 17h10'/%3E%3C/svg%3E")}
.m-app-notion .m-as-table th.m-kind-enum{--nt-kind:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='9'/%3E%3Cpath d='M8.5 11l3.5 3.5 3.5-3.5'/%3E%3C/svg%3E")}
.m-app-notion .m-as-table th.m-kind-date{--nt-kind:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round'%3E%3Crect x='4' y='5' width='16' height='15' rx='2'/%3E%3Cpath d='M4 10h16M8 3v4M16 3v4'/%3E%3C/svg%3E")}
.m-app-notion .m-as-table th.m-kind-number,.m-app-notion .m-as-table th.m-kind-score{--nt-kind:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round'%3E%3Cpath d='M9 4L7 20M17 4l-2 16M4 9h17M3 15h17'/%3E%3C/svg%3E")}
.m-app-notion .m-as-table th.m-kind-ref{--nt-kind:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M7 17L17 7M9 7h8v8'/%3E%3C/svg%3E")}
.m-app-notion .m-as-table th.m-kind-media,.m-app-notion .m-as-table th.m-kind-av,.m-app-notion .m-as-table th.m-kind-doc{--nt-kind:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linejoin='round'%3E%3Cpath d='M6 3h9l5 5v13H6zM14 3v6h6'/%3E%3C/svg%3E")}
/* The calendar: Notion's month head carries ‹ Today › at the right, Today a
   small bordered button; the day itself is a red disc (the app's mark). */
.m-app-notion .m-cal-head{align-items:center;justify-content:space-between;margin:0 0 .5rem}
.m-app-notion .m-cal-title{font-size:16px;color:var(--text)}
.m-app-notion .m-cal-steps{gap:.15rem}
.m-app-notion .m-cal-nav{border:0;--cal-nav-bg:transparent;color:var(--text-muted);font-size:18px;
  width:28px;height:28px;display:inline-flex;align-items:center;justify-content:center}
.m-app-notion .m-cal-nav:hover:not(:disabled){--cal-nav-bg-hover:var(--surface);color:var(--text)}
.m-app-notion .m-cal-nav:disabled{color:var(--border-strong);cursor:default}
.m-app-notion .m-cal-today-btn{width:auto;font-size:13px;padding:0 .55rem;border:1px solid var(--border);
  border-radius:6px;color:var(--text);margin:0 .25rem}
.m-app-notion .m-cal-today-btn:disabled{color:var(--text-muted);border-color:var(--border-subtle)}
.m-app-notion .m-cal-today .m-cal-num{display:inline-flex;align-items:center;justify-content:center;
  min-width:22px;height:22px;border-radius:999px;background:#eb5757;color:#fff;font-weight:600;
  padding:0 .3rem;margin:-.15rem 0 0 -.25rem}
/* The timeline: Notion writes the title INSIDE each bar and colours the
   bar by its lane (the sixteenth verdict's third item: empty grey bars
   read as a generic Gantt skeleton). The lane index the module carries is
   first-seen, and the host hands the lanes over in the property's option
   order — the same order the board's columns take — so a status is one
   colour in both. */
.m-app-notion .m-timeline{margin-top:.25rem}
.m-app-notion .m-tl-corner,.m-app-notion .m-tl-scale{text-transform:none;letter-spacing:0;font-size:13px;
  font-weight:500;border-bottom:1px solid var(--border)}
.m-app-notion .m-tl-name{font-size:14px;font-weight:500;border-bottom:1px solid var(--border-subtle)}
.m-app-notion .m-tl-track{border-bottom:1px solid var(--border-subtle);
  background:repeating-linear-gradient(90deg,var(--border-subtle) 0 1px,transparent 1px 7.6923%)}
/* The ground as a TOKEN rather than a declaration — task 0232. Both are set
   to the same pill, which is what this look already painted: its own hover is
   a filter over the resting colour, never a different ground. */
.m-app-notion .m-tl-bar{inset-block:18%;border:0;border-radius:4px;text-align:start;
  --tl-bar-bg:var(--nt-pill,#ebebea);--tl-bar-bg-hover:var(--nt-pill,#ebebea);
  color:var(--text)}
.m-app-notion .m-tl-bar:hover{filter:brightness(.97)}
.m-app-notion .m-tl-bar[data-lane-index="0"]{--nt-pill:#e9e2f5}
.m-app-notion .m-tl-bar[data-lane-index="1"]{--nt-pill:#f6ecc7}
.m-app-notion .m-tl-bar[data-lane-index="2"]{--nt-pill:#dcebfa}
.m-app-notion .m-tl-bar[data-lane-index="3"]{--nt-pill:#d8f0df}
.m-app-notion .m-tl-bar .m-tl-hidden{position:static;inline-size:auto;block-size:auto;margin:0;clip:auto;
  display:block;padding:0 .55rem;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;
  font-size:13px;font-weight:500;line-height:1.9}
.m-app-notion .m-tl-moment .m-tl-hidden{display:none}
/* ═══════════════════════════════════ app.coda — decision 0055's third look
   A DOC with a table, read from the pinned reference: a light grey page
   tree at the left with the doc's name, a search field and the pages with
   emoji icons and "+ New page" at the foot; a white doc with a shallow
   cover band, a breadcrumb, an icon beside a bold title, a paragraph; red
   rounded BUTTONS as blocks; then the table — grey small headers, hairline
   rows of 40px, avatar cells and pill cells. The same registered modules
   Notion's look uses (nav, topbar, page head) wearing Coda's tokens, plus
   the buttons row. */
/* A module is a piece of the doc, never a boxed panel. It needs no rule
   saying so: the dashed capability box belongs to the refusal note now
   (decision 0071), so a module that declares no box has none. */
/* THE WINDOW IS THE VIEWPORT -- task 0217, and this look reached the same
   defect by a third route. height:100% and min-height:100% are both INERT
   here: percentage heights resolve against a parent with a definite height and
   this row's has none, so they computed to auto and the row was content-sized.
   Measured at 1440x900: shell 931 locally and 918.92 on the deployed preview,
   with the document 20px past the window -- less dramatic than app.fibery's
   twice-the-viewport, and the same defect.

   The cap engages rather than clips: the editor pane is overflow-y:auto at
   flex:1 1 0%, and the table has its own scroller beneath it.

   NOT the narrow window. This look has no breakpoint of any kind -- no media
   query anywhere names m-app-coda -- so its 232px sidebar holds at every width
   and at 600 the content column gets 368px. What that window should DO is a
   design question wanting the reference beside it, and 0217 keeps it open
   rather than guessing. */
.m-app-coda>.m-region.m-row{--shell-tracks:232px minmax(0,1fr);
  /* The one shell band that does NOT stretch its columns. It never said so
     -- it was taking the generic row default, which the band rule above now
     overrides -- so it says so here rather than depending on an absence. */
  align-items:flex-start;
  background:var(--page)}
@media (max-width: 48rem){.m-app-coda>.m-region.m-row{--shell-tracks-folded:minmax(0,1fr)}
  /* The nav's fold is GENERIC now — task 0232's promotion. Coda is the look
     that made it a consistency question rather than a design one: 0218 found
     the fold dead in Notion and in Fibery and repaired both without asking
     which other look has a nav. Coda does, and had no media block at all —
     a list of the instances somebody found is not the set.

     WHAT IT STILL DOES NOT DECIDE is what the table should BECOME at that
     width — a scrolling table, a stacked card per row, something else. That
     needs a reference and canon-looks.md has none below 1216px, so it stays
     open in 0217. What is settled without one is that a nav holding 39% of
     the window is wrong under every answer to it. */
}

/* AND THE ITEMS MUST BE ABLE TO SHRINK, which is the half a height cap alone
   does not buy. A grid item's automatic minimum size is its content, so
   binding the row to 100vh left .m-region.m-stack at 931 inside a 900 box and
   the DOCUMENT still scrolled -- shell bound, content escaping, which is the
   wrong product a cap can produce and is why the clause asserts
   documentElement.scrollHeight and not the shell alone. app.notion and
   app.fibery have carried this rule all along; app.coda never had it.

   ALL THREE DECLARATIONS ARE LOAD-BEARING, and the minimal-looking version was
   tried first and measured: min-width and min-height alone left the stack at
   930.92 in a 900px track and the document still at 931, with the item's
   computed min-height reading 0 and the track reading 900px. align-self is
   what actually stretches it. Written out rather than trimmed, because the
   trimmed version looked right and did nothing. */
.m-app-coda .m-nav{background:var(--raised);padding:.6rem .5rem 1rem;font-size:13.5px;border-right:1px solid var(--border-subtle)}
.m-app-coda .m-nav-workspace{padding:.35rem .5rem}

.m-app-coda .m-tools{gap:.25rem;padding:.25rem .25rem .5rem}
.m-app-coda .m-tool{gap:.5rem;font-size:13.5px}
.m-app-coda .m-tool-on,.m-app-coda .m-tool:hover{background:var(--surfaceHover,#e9e9ec)}
.m-app-coda .m-nav-section{margin-top:.6rem}
.m-app-coda .m-nav-section-label{font-size:11.5px;padding:.2rem .5rem .3rem;font-weight:600;
  text-transform:uppercase;letter-spacing:.04em}
.m-app-coda .m-nav-row{gap:.5rem;padding:.25rem .5rem;font-size:13.5px;min-height:1.8rem}
.m-app-coda .m-nav-row:hover{background:var(--surface)}
.m-app-coda .m-nav-row-on{background:#e6edfb;color:var(--selection-accent)}
.m-app-coda .m-nav-row-icon{width:18px;font-size:13px}
.m-app-coda .m-nav-row-detail{font-size:12px}
.m-app-coda .m-nav-newpage{padding:.4rem .6rem;border:0;border-radius:6px;background:transparent;font-size:13.5px;color:var(--text-muted)}
.m-app-coda .m-nav-newpage:hover{background:var(--surface);color:var(--text)}
/* The doc column's topbar: thin, with the strip's chevrons, the page's tab
   and the ···. The column itself is generic now. */
.m-app-coda .m-topbar{justify-content:space-between;gap:0;padding:0 .75rem 0 .35rem;height:44px;font-size:13.5px;border-bottom:1px solid var(--border-subtle)}
.m-app-coda .m-topbar-strip{gap:.25rem;height:44px}
.m-app-coda .m-topbar-tab{gap:.45rem;padding:.3rem .6rem;color:var(--text);font-weight:600}
.m-app-coda .m-topbar-tab-icon{display:inline-flex;font-size:13px;line-height:1}
.m-app-coda .m-topbar-actions{gap:.15rem;height:44px}
.m-app-coda .m-topbar-more{color:var(--text);width:30px;height:30px;border-radius:6px;font-size:18px}
.m-app-coda .m-topbar-menu{top:40px;right:0;min-width:200px;border-radius:8px}
.m-app-coda .m-topbar-menu-item{font-size:13.5px}
/* The doc: the cover runs the doc's full width; the text column is inset. */
.m-app-coda .m-ws-editor{padding:0 0 3rem;gap:0;background:var(--page)}
.m-app-coda .m-pagehead{flex-direction:column;align-items:stretch;gap:0;margin:0 0 .6rem}
.m-app-coda .m-cd-cover{height:128px;background:var(--cd-cover,var(--surface));background-size:cover;background-position:center}
.m-app-coda .m-cd-crumb{margin:1.1rem 3.5rem 0;font-size:13px;color:var(--text-muted);display:flex;align-items:center;gap:.35rem}
.m-app-coda .m-cd-crumb::before{content:"";width:12px;height:12px;border:1.5px solid var(--text-muted);border-radius:2px}
.m-app-coda .m-pagehead-titlerow{gap:.7rem;margin:.4rem 3.5rem 0}
.m-app-coda .m-pagehead-icon{width:36px;height:36px;border-radius:0;background:transparent;font-size:28px}
.m-app-coda .m-pagehead-title{font-size:30px;font-weight:700;line-height:1.2}
.m-app-coda .m-cd-words{margin:.6rem 3.5rem 0;font-size:14.5px;line-height:1.5;color:var(--text);max-width:64ch}
/* Buttons as blocks: Coda's red rounded rectangles with white text. */
.m-app-coda .m-cd-actions{display:flex;gap:.6rem;margin:.7rem 3.5rem .8rem}
.m-app-coda .m-cd-button{border:0;border-radius:6px;background:var(--solid);color:#fff;font:inherit;font-size:13px;
  font-weight:600;padding:.35rem .8rem;cursor:pointer;display:inline-flex;align-items:center;gap:.4rem;
  box-shadow:0 1px 2px rgba(0,0,0,.08)}
.m-app-coda .m-cd-button[data-ws-action="new"]::before{content:"+";font-size:14px;line-height:1}
.m-app-coda .m-cd-button:hover{filter:brightness(1.05)}
/* The heading ROW over the table — the reference's "View of Tasks" with
   the table's tools at its right, one tight block joined to the grid
   (the twentieth verdict: ours was a heading, a gap, a bare table). */
.m-app-coda .m-cd-headrow{display:flex;align-items:center;justify-content:space-between;margin:0 3.5rem;padding:.3rem 0 .25rem;
  border-bottom:1px solid var(--border-subtle)}
.m-app-coda .m-cd-heading{margin:0;font-size:15px;font-weight:600;color:var(--text)}
.m-app-coda .m-cd-tools{display:inline-flex;align-items:center;gap:.15rem}
.m-app-coda .m-cd-tool{font-size:12.5px;color:var(--text-muted);padding:.2rem .4rem;border-radius:5px;gap:.3rem}
.m-app-coda .m-cd-tool .m-inert-glyph{color:var(--text-muted)}
/* The table: grey small headers, hairline 34px rows, the title strong. */
/* Pill cells: an enum value drawn as Coda's select pill, whether it is a
   chooser (a writer's) or a plain cell (a reader's); the status column
   grey, the next enum column tinted; a text cell for an author role drawn
   with an avatar disc before the name. */
/* Coda's own six categorical colours. TOKENS, not rules: the slot mapping is
   generic, so a seventh app needs this block and nothing else. Two per-role
   literals stood here before -- one colour for every status and another for
   every area, which is a fact about the COLUMN and is why four statuses read
   as one colour. */
.m-app-coda{
--table-margin:0 3.5rem;--table-width:calc(100% - 7rem);
--table-collapse:collapse;--table-font:13.5px;
--table-head-weight:500;--table-head-align:start;--table-head-font:12px;
--table-head-pad:.4rem .5rem;--table-head-transform:none;--table-head-tracking:0;
--table-cell-pad:.3rem .5rem;--table-row-height:34px;--table-cell-align:middle;
--table-muted-color:var(--text);
--table-name-weight:500;--table-row-hover:var(--raised);
--tone-0-bg:#eceef1;--tone-0-fg:#4b5057;--tone-1-bg:#fdf0d9;--tone-1-fg:#8a5a12;
  --tone-2-bg:#e3f4e6;--tone-2-fg:#1f6b35;--tone-3-bg:#efe6fb;--tone-3-fg:#5b3a9e;
  --tone-4-bg:#e2ecfb;--tone-4-fg:#2452a8;--tone-5-bg:#fce7ef;--tone-5-fg:#9c2f5b}
.m-app-coda .m-as-table td.m-kind-enum .m-cell{display:inline-block;border-radius:999px;padding:.15rem .65rem;
  font-size:13px;font-weight:500;background:var(--surface);color:var(--text);line-height:1.5}
.m-app-coda .m-as-table td.m-kind-enum :is(.m-cell,select.m-choose)[data-m-tone]{background-color:var(--tone-bg);color:var(--tone-fg)}
/* BY ROLE, not by column number -- 0178's defect, and the file already used
   data-role one rule below for the author's avatar, so these three were the
   exception rather than the convention. They read :nth-of-type(2) and (3),
   which meant Status and Area only while the name was the first column; the
   Open gutter shifted both by one and the pills silently lost their colours.
   Mapped to the roles those positions held. */
/* BY VALUE, not by column -- decision 0063, and the measurement that made it
   concrete: every status painted #e2ecfb and every area #e3f4e6, so four
   statuses read as one colour and a person could not tell Done from Open by
   looking, which is most of what a status column is for. The two rules that
   did it keyed on data-role, which is one step better than the nth-of-type
   they replaced and still a fact about WHICH COLUMN rather than which value.

   The slots are the look's; the assignment is the pool's. Twelve tone slots
   fold onto six pairs the way the rail's do, so an undeclared value still
   lands on a real colour instead of on an attribute no rule matches. */
.m-app-coda .m-as-table select.m-choose{appearance:none;-webkit-appearance:none;border:0;border-radius:999px;
  padding:.15rem 1.4rem .15rem .6rem;font-size:13px;font-weight:500;background:var(--surface);color:var(--text);
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236f7075' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M7 10l5 5 5-5'/%3E%3C/svg%3E");
  background-repeat:no-repeat;background-position:right .35rem center;background-size:12px}
.m-app-coda .m-as-table td[data-role="author"] .m-cell{display:inline-flex;align-items:center;gap:.45rem}
.m-app-coda .m-as-table td[data-role="author"] .m-cell::before{content:"";width:22px;height:22px;border-radius:50%;flex:none;
  background:linear-gradient(135deg,#f0a56b,#c9663b)}
/* ═══════════════════════════════════ app.word — decision 0055's fourth look
   A DOCUMENT with the ribbon, read from the pinned reference: a light title
   bar naming the document; the ribbon — a tab row with the active tab
   underlined in Word's blue, then a white band of grouped controls with
   the group's label beneath each, a styles gallery of preview tiles among
   them; the white sheet on a pale blue-grey desk, US-letter proportion;
   a status bar with the word count. */
/* WORD'S HEADING BLUE is an ALIAS now, not a literal, and the difference is
   the whole finding. It read #185abd -- byte-identical to the value Word
   already publishes as its own solid, accent, focus ring and selection. Two
   copies of one colour that nothing kept in step: change the template's
   solid and Word's headings would silently stay behind.
   It survives as a NAME because its six remaining uses are one component
   concept -- the h1/h2 treatment, the fold summary, the note marks, the
   contents title -- and no look in the canon supplies a second value for a
   heading colour, so promoting it to a shared role would be the split by
   symmetry @Codex's ruling refuses. An alias records the grouping and costs
   nothing; a duplicate records the same grouping and can drift.
   The two uses that DID have evidence are gone: the active tab took
   --selection-accent, which Coda shows differing from its buttons, and the
   filled end button took --solid. */
.m-app-word{--wd-blue:var(--accent)}
/* The window is the VIEWPORT: title bar, ribbon and status bar take their
   own height and the desk takes the rest, scrolling inside it — as the
   vault's grid binds its rows to 100vh — so a caret placed in the
   document never scrolls the window's chrome away. */
.m-app-word>.m-region.m-stack{display:grid;grid-template-columns:minmax(0,1fr);grid-template-rows:auto auto minmax(0,1fr) auto;gap:0;height:100vh;
  background:var(--surface)}
/* The title bar, read from the reference: a light strip — AutoSave and
   its toggle, undo and redo, the document's name; a search box centred;
   Sign in and the window controls at the right. Only the name is real.

   overflow-x:auto and scrollbar-width:none are 0228's — see A ROW THAT RUNS
   OUT OF ROOM SCROLLS, at .m-wd-band below, which explains all three rows. */
.m-app-word .m-topbar{display:grid;grid-template-columns:1fr auto 1fr;height:32px;padding:0 .5rem 0 .75rem;background:var(--raised);border-bottom:1px solid var(--border-subtle);font-size:12px;overflow-x:auto;scrollbar-width:none}
.m-app-word .m-topbar-strip{gap:.5rem;height:32px}
.m-app-word .m-topbar-tab{gap:.4rem;padding:0;color:var(--text);font-weight:500;order:2}
.m-app-word .m-topbar-tab-icon{display:inline-flex;font-size:13px;line-height:1}
.m-app-word .m-topbar-tab-add{display:none}
.m-app-word .m-topbar-strip .m-inert{font-size:12px;color:var(--text)}
.m-app-word .m-topbar-strip .m-inert-start:nth-of-type(1){order:0}
.m-app-word .m-topbar-strip .m-inert-start:nth-of-type(2){order:1}
.m-app-word .m-topbar-strip .m-inert-start:nth-of-type(n+3){order:3}
.m-app-word .m-topbar-strip .m-inert-box{padding:.05rem .45rem;border-radius:999px;background:var(--surface);border-color:var(--border);
  font-size:10px;color:var(--text-muted);font-weight:600}
.m-app-word .m-topbar-centre{display:flex;justify-content:center}
.m-app-word .m-topbar-centre .m-inert-box{width:300px;height:22px;justify-content:flex-start;gap:.4rem;border-radius:5px;
  background:var(--page);color:var(--text-muted);font-size:12px}
.m-app-word .m-topbar-end{justify-content:flex-end;gap:.9rem;font-size:12px}
.m-app-word .m-topbar-end .m-inert-box{background:#1f1f1f;color:#fff;border-color:#1f1f1f;padding:.15rem .6rem;font-weight:600;
  font-size:11.5px;border-radius:3px}
/* The ribbon: the tab row — File, Home, Insert … Help, the active one in
   Word's blue, Comments / Editing / Share at the right — then the band.

   overflow-x:auto and scrollbar-width:none are 0228's — see A ROW THAT RUNS
   OUT OF ROOM SCROLLS, at .m-wd-band below, which explains all three rows. */
.m-app-word .m-wd-tabs{display:flex;align-items:flex-end;gap:0;height:26px;padding:0 .5rem;background:var(--raised);
  border-bottom:1px solid var(--border-subtle);overflow-x:auto;scrollbar-width:none}
.m-app-word .m-wd-tab{border:0;background:transparent;font:inherit;font-size:12.5px;color:var(--text);padding:.25rem .55rem;
  border-bottom:2px solid transparent;cursor:default;display:inline-flex;align-items:center}
.m-app-word .m-wd-tab-on{color:var(--selection-accent);font-weight:600;border-bottom-color:var(--selection-accent)}
.m-app-word .m-wd-tabs-end{margin-left:auto;display:flex;align-items:center;gap:.5rem;padding-bottom:.2rem;font-size:12px}
.m-app-word .m-wd-tabs-end .m-inert{border:1px solid var(--border);border-radius:4px;padding:.1rem .45rem;background:var(--page)}
.m-app-word .m-wd-tabs-end .m-inert:last-child{background:var(--solid);color:#fff;border-color:var(--solid)}
/* A ROW THAT RUNS OUT OF ROOM SCROLLS; IT DOES NOT PUSH THE PAGE -- 0228.
   Three rows of Word's chrome are wider than a narrow window and each was
   wrong in its own direction. Measured on the Word door at 600px:

     document.scrollWidth 851 against a 600px viewport
     .m-wd-tabs-end  +251px uncontained   .m-topbar-end  +71px uncontained
     .m-wd-band      contained by overflow:hidden -- and clipping SIX controls

   The band was the serious one and it did not look serious. Its groups are
   flex:0 0 auto (below, for a reason that still holds), so at 600px the
   Styles and File groups sit past the edge -- and overflow:hidden means a
   browser will not scroll the box for a PERSON, only for script. Normal,
   Heading, List, New note, Print and Close were mouse-unreachable, which is
   an unreachable-navigation defect rather than the cosmetic clip 0228 filed.

   The other two carry only inert imagery (0056: aria-hidden, cursor:default),
   so nothing there was unreachable -- but they had visible overflow, so the
   REGION grew and the whole door scrolled sideways, dragging the document
   with it. Whatever a look does when it runs out of room, that is not it.

   auto on all three, uniformly: nothing is hidden from anyone, and the next
   row added to this chrome gets the answer by pattern rather than by
   argument. The band pins overflow-y:hidden because its height is fixed at
   72px and a vertical bar in a horizontal strip is not a thing Word has.

   THE TWO IMAGERY ROWS ALSO SET scrollbar-width:none, AND THE BAND DOES NOT.
   "It costs no pixels" was measured in headless Chromium, which draws OVERLAY
   scrollbars -- the 600px render there is identical to the clipped one but
   for what it reaches. On a platform with classic scrollbars that claim is
   false, and a 15px gutter inside a 32px title bar or a 26px tab strip is
   disfiguring in a way no Word has ever looked. Those two rows hold only
   imagery, so a bar there is a cost with no reader: suppress it, and the
   wheel still scrolls. The band keeps its bar because its content is six real
   controls and the bar is the only thing that SAYS they are there.

   THE OTHER TWO DECLARATIONS ARE NOT HERE. They went into the rules those two
   components already have, above, because 0173's ratchet counts SELECTORS and
   two new ones would have raised a number whose whole point is to fall --
   correctly: this is not the scope repair the ceiling may be raised for. A
   declaration added to a rule that exists costs nothing, and one rule per
   component per look is the shape 0173 is heading for anyway.

   ON THE TOPBAR IT MUST STAY WORD-SCOPED. The generic .m-topbar cannot take
   overflow: the topbar menu is position:absolute at top:40px, so it hangs
   BELOW the bar, and a scroll container would clip it in the four looks that
   draw it. Word draws no menu in ANY view -- app.word sets
   topbarActions:false, main.ts leaves its actions argument undefined from
   that, and capability.ts gates the whole m-topbar-actions block, menu
   included, on it being defined. A producer proof, not a reading of one
   opening view. */
.m-app-word .m-wd-band{display:flex;align-items:stretch;gap:0;height:72px;padding:.25rem .4rem .1rem;
  background:var(--page);border-bottom:1px solid var(--border);overflow-x:auto;overflow-y:hidden}
/* A group takes its own width and never shrinks: a group that shrank
   wrapped its rows into a third the band cannot hold. */
.m-app-word .m-wd-group{display:flex;flex:0 0 auto;flex-direction:column;justify-content:space-between;padding:0 .3rem;
  border-right:1px solid var(--border-subtle)}
.m-app-word .m-wd-group:last-child{border-right:0}
.m-app-word .m-wd-items{display:flex;align-items:center;gap:.15rem;flex-wrap:wrap;max-width:230px}
.m-app-word .m-wd-group-label{font-size:10px;color:var(--text-muted);text-align:center;padding-bottom:0;line-height:1}
.m-app-word .m-wd-item{border:1px solid transparent;border-radius:3px;background:transparent;font:inherit;font-size:12px;
  color:var(--text);padding:.2rem .22rem;cursor:pointer;display:inline-flex;align-items:center;gap:.15rem;min-width:20px;
  justify-content:center;line-height:1.2}
/* Word's Font and Paragraph groups: two rows of 16px Fluent-style glyphs
   with a thin separator between clusters, the swatch bars in Word's
   colours, a ▾ on the split buttons. */

.m-app-word .m-wd-item .m-inert-glyph{font-size:13px;font-weight:600;color:var(--text)}
.m-app-word .m-wd-item .m-inert-glyph>svg{stroke:#3b3b3b}
.m-app-word .m-wd-item.m-wd-sep{margin-left:.35rem;position:relative}
.m-app-word .m-wd-item.m-wd-sep::before{content:"";position:absolute;left:-.25rem;top:15%;height:70%;width:1px;background:var(--border)}
/* THE DISCRIMINATOR IS m-inert, NOT a look-named copy of it — task 0216's
   first retirement, 2026-09-06. A rule reading m-app-word m-wd-inert with
   cursor:default stood here, and the class it keyed on said in Word's private
   vocabulary exactly what m-inert says in everyone's: this item is
   decorative. Measured in the Word door rather than reasoned from the
   emitter -- 51 elements wear m-wd-inert and all 51 wear m-inert -- so every
   selector that discriminated on the private name discriminates identically
   on the shared one. Of 44 m-wd-item, 38 are inert and all 38 wear both
   names, so the 6 real controls wear neither, and :not() takes its
   argument's specificity: one class for one class, nothing moves.

   THE CURSOR RULE WAS NOT DEAD, and a capture said so after this comment
   first claimed it was. The generic m-inert rule does declare cursor:default,
   so deleting Word's looked free -- and 131 elements went default to POINTER,
   because cursor INHERITS: the spans were taking pointer from an ancestor
   that the (0,2,0) Word rule had been overriding, and the (0,1,0) generic
   one cannot. So the declaration stays; only the name it keys on changes,
   at the same specificity it always had. It now reads as the claim it always
   was: in Word, an inert item is not clickable. */
.m-app-word .m-inert{cursor:default}
.m-app-word .m-wd-item:not(.m-inert):hover{background:var(--surface);border-color:var(--border)}
/* A BIG item is m-wd-big when backed and m-inert-big when inert — the
   inert Paste got neither this height nor the Clipboard grid below for a
   day, stood 62px tall, and pushed "Clipboard" under the band's edge
   (the twenty-seventh verdict's "cramped" label). Both spellings, once. */
.m-app-word .m-wd-big,.m-app-word .m-wd-item.m-inert-big{flex-direction:column;gap:.15rem;padding:.15rem .3rem;min-width:36px;height:50px;justify-content:center}
.m-app-word .m-wd-big .m-inert-label,.m-app-word .m-wd-item.m-inert-big .m-inert-label{font-size:11px}
.m-app-word .m-wd-item.m-inert-box{padding:.1rem .4rem;font-size:11.5px;color:var(--text);min-width:0;height:20px;
  border-color:var(--border);border-radius:3px;justify-content:flex-start}
/* The styles gallery: a tile per style, each drawn in its own style. */
.m-app-word .m-wd-tile{width:70px;height:40px;border:1px solid var(--border);border-radius:3px;background:var(--page);
  display:inline-flex;align-items:center;justify-content:center;padding:0}
.m-app-word .m-wd-tile:not(.m-inert):hover{border-color:var(--wd-blue)}
.m-app-word .m-wd-tile.m-inert .m-inert-label{font-size:12.5px}
/* The gallery is one row, whatever its width: a tile wrapped under
   another is cut off by the band. */
.m-app-word .m-wd-group:has(.m-wd-tile) .m-wd-items,.m-app-word .m-wd-group:has(.m-wd-big,.m-inert-big) .m-wd-items,
.m-app-word .m-wd-group:last-child .m-wd-items{flex-wrap:nowrap;max-width:none}
/* Clipboard: Paste big at the left, cut / copy / painter stacked beside it
   — a group with a big item AND small ones lays out as a three-row grid,
   the big item spanning the rows. */
.m-app-word .m-wd-group:has(.m-wd-big,.m-inert-big):has(.m-wd-item:nth-child(2)) .m-wd-items{display:grid;grid-auto-flow:column;
  grid-template-rows:repeat(3,auto);gap:0 .1rem;align-items:center}
.m-app-word .m-wd-group:has(.m-wd-big,.m-inert-big):has(.m-wd-item:nth-child(2)) .m-wd-big,
.m-app-word .m-wd-group:has(.m-wd-big,.m-inert-big):has(.m-wd-item:nth-child(2)) .m-wd-item.m-inert-big{grid-row:span 3}
/* Font and Paragraph wrap into TWO rows at the reference's widths: the
   host feeds the groups in Word's order, so the second and third groups
   are those two. */
/* Font and Paragraph carry their rows at Word's widths: a break inside a
   wrapping row does not bound a max-content width, so the width is said. */
.m-app-word .m-wd-group:nth-child(2) .m-wd-items{width:344px;max-width:none;flex-wrap:wrap}
.m-app-word .m-wd-group:nth-child(3) .m-wd-items{width:250px;max-width:none;flex-wrap:wrap}
.m-app-word .m-wd-group:has(.m-wd-big,.m-inert-big):has(.m-wd-item:nth-child(2)) .m-wd-item:not(.m-wd-big):not(.m-inert-big){padding:0 .3rem;height:17px}
.m-app-word .m-wd-item.m-inert-swatch{padding:.05rem .3rem}
.m-app-word .m-inert-bar{height:3px;width:15px}
.m-app-word .m-wd-big .m-inert-label,.m-app-word .m-wd-group:last-child .m-wd-item{white-space:nowrap}
.m-app-word .m-wd-tile-style-paragraph{font-size:13px}
.m-app-word .m-wd-tile-style-heading{font-size:17px;font-weight:600;color:var(--wd-blue)}
.m-app-word .m-wd-tile-style-list-item{font-size:13px}
.m-app-word .m-wd-tile-style-list-item::before{content:"•";margin-right:.4rem}
/* The desk and the sheet */
.m-app-word .m-ws-editor{padding:1.5rem 1rem 2rem;gap:0;background:var(--surface)}
.m-app-word .m-doc-sheet{max-inline-size:48rem;padding:4.5rem 4.5rem;border-radius:0;
  box-shadow:0 1px 2px rgb(0 0 0/.1),0 6px 20px -12px rgb(0 0 0/.35)}
/* The sections FLOW down the sheet as a document's do — one column, no
   card boxes: a section is its heading and its prose, in Word's type. */
.m-app-word .m-doc-body .m-as-cards{display:block}
.m-app-word .m-doc-body .m-card{border:0;box-shadow:none;background:transparent;padding:0;margin:0 0 1.6rem;
  border-radius:0}
.m-app-word .m-doc-body .m-card-hit{padding:0}
.m-app-word .m-doc-body .m-card .m-role-title{font-size:20px;font-weight:600;color:var(--wd-blue);margin:0 0 .45rem;
  line-height:1.3}
.m-app-word .m-doc-body .m-card .m-field,.m-app-word .m-doc-body .m-card .m-doc{font-size:14px;line-height:1.55;
  color:var(--text);max-width:none}
/* A section is a FOLD in the document view (a heading that discloses its
   prose); on Word's sheet the fold's box and marker go, and its summary is
   the heading in Word's blue heading style. */
.m-app-word .m-doc-body .m-fold{border:0;background:transparent;padding:0;border-radius:0;box-shadow:none}
.m-app-word .m-doc-body .m-fold-summary{font-size:20px;font-weight:600;color:var(--wd-blue);padding:0;margin:0 0 .45rem;
  border:0;background:transparent;list-style:none;line-height:1.3}
.m-app-word .m-doc-body .m-fold-summary::-webkit-details-marker{display:none}
.m-app-word .m-doc-body .m-fold-summary::marker{content:""}
.m-app-word .m-doc-body .m-fold-body{padding:0;border:0;background:transparent}
.m-app-word .m-doc-body .m-card-noted .m-doc-note-mark,.m-app-word .m-doc-body .m-note-ref{color:var(--wd-blue)}
.m-app-word .m-doc-body [data-morph-document-block]{outline:0}
.m-app-word .m-doc-body [data-morph-document-block]:focus-visible{outline:1px dotted var(--border-strong);outline-offset:2px}
/* The contents: Word's density, not a spaced list — the twenty-first
   verdict read the wide-spaced entries as the largest gap after the chrome. */
.m-app-word .m-doc-toc{margin:0 0 1.5rem}
.m-app-word .m-doc-toc ol,.m-app-word .m-doc-toc ul{margin:0;padding-left:1.4rem}
.m-app-word .m-doc-toc li{margin:0;padding:0;line-height:1.5;font-size:14px}
.m-app-word .m-doc-toc ol{gap:0}
.m-app-word .m-doc-toc a{min-height:0;padding:.1rem 0;display:inline-block}
.m-app-word .m-doc-toc-title{font-size:20px;color:var(--wd-blue);text-transform:none;letter-spacing:0}
/* The running head and foot are PRINT furniture — drawn once per printed
   page; on Word's screen the sheet begins with the document itself. */
@media screen{.m-app-word .m-doc-running{display:none}}
/* The status bar */
.m-app-word .m-ws-status{justify-content:flex-start;gap:16px;height:22px;padding:0 .75rem;
  border-top:1px solid var(--border-subtle);color:var(--text-muted)}
.m-app-word .m-ws-status .m-inert{font-size:11.5px;color:var(--text-muted);gap:.35rem}
.m-app-word .m-ws-status-end{margin-left:auto;display:inline-flex;align-items:center;gap:14px}
/* ═══════════════════════════════════ app.fibery — decision 0055's fifth look
   A DATABASE as a board, read from the pinned references: a white sidebar
   with the workspace, Home and Search, Favorites, then the SPACES as a
   tree of coloured square icons; a thin topbar; a compact view bar — the
   view's name beside its star; a toolbar of muted controls ending in a
   bordered "+ New" with the view's search beside it; the board as plain
   bold column headers with grey counts over white hairline cards of 4px
   radius, each opening with a TYPE BADGE, then its title, then its field
   chips; or the table as a hairline grid with pill states. Dense, 13–14px,
   a humanist sans; colour only in the badge, the chips, the space icons
   and the lit control. */
.m-app-fibery{
--table-collapse:collapse;--table-font:15px;--table-top-rule:1px solid var(--border);
--table-head-weight:500;--table-head-color:var(--text);--table-head-align:start;
--table-head-font:14px;--table-head-pad:.85rem .75rem;--table-head-bg:var(--page);
--table-head-transform:none;--table-head-tracking:0;
--table-col-rule:1px solid var(--border-subtle);
--table-cell-pad:.6rem .75rem;--table-row-height:52px;
--table-cell-align:middle;--table-name-weight:500;
--fb-badge-bg:#5a78a7;--fb-chip:#eef0f4;--fb-lit:#fff3ea;--fb-warm:#fffbf4;
  /* The chip row's gaps, which used to be a margin on every chip. Both
     are the reference's own, measured off fibery-board.png at 3x by the
     pass that wrote the geometry clause: 13 CSS px between the badge,
     the title and the chips. The row flows and the look says how far
     apart, which is the division 0173 asks for -- and it costs a TOKEN
     rather than a selector. */
  --chip-gap-y:.8rem;--chip-gap-x:.3rem;
  --font:-apple-system,BlinkMacSystemFont,'Segoe UI','Inter',Roboto,Helvetica,Arial,sans-serif;
  -webkit-font-smoothing:antialiased;font-size:13.5px}
/* THE WINDOW IS THE VIEWPORT -- task 0217, and this look was the worst of the
   three. minmax(100vh,1fr) is a height FLOOR: it made the board tall enough to
   be an app and then let it grow, so the shell was 1791px in a 900px window at
   1440x900 -- twice the window, at the very viewport canon-looks.md pins and
   every look screenshot has been judged at. Measured on main.eedf7fc21574.js,
   with document.documentElement.scrollHeight at 1792 corroborating that the
   PAGE really did scroll rather than a container being misread.

   Capping engages three overflow:auto regions that were inert without it: the
   nav, the editor pane and the board, the last two flex:1 1 0% so they take
   the remainder rather than dictating it. A cap with nothing to scroll would
   be the clipped-content wrong product, which is why that was measured before
   this was written and not after. */
.m-app-fibery>.m-region.m-row{--shell-tracks:232px minmax(0,1fr);
  grid-template-rows:minmax(0,1fr);background:var(--page)}
@media (max-width: 48rem){.m-app-fibery>.m-region.m-row{--shell-tracks-folded:minmax(0,1fr)}
  /* The nav's fold is GENERIC now — task 0232's promotion. This look was the
     second of 0218's two dead folds, found by scanning every display:none
     inside a media query for a later rule naming the same selector. */
}
/* The sidebar: white, a hairline at its right. */
.m-app-fibery .m-nav{background:var(--page);border-right:1px solid var(--border);padding:.5rem .5rem 1rem;font-size:13.5px}
.m-app-fibery .m-nav-workspace{padding:.4rem .5rem}
.m-app-fibery .m-nav-workspace>.m-glyph{color:var(--solid)}

/* The sidebar FOLDS (the thirty-third verdict: the reference's frame has it
   away and the workspace's name in the compact header). The toggle at the
   sidebar's head folds it; with it away the row is one column, and the
   topbar's start shows the expand toggle, the glyph and the name — the
   block that shares the fold's key and is hidden until then. */
.m-app-fibery .m-fold-toggle{border:0;background:transparent;color:var(--text-muted);width:26px;height:26px;border-radius:6px;
  display:inline-flex;align-items:center;justify-content:center;cursor:pointer;padding:0;flex:none}
.m-app-fibery .m-fold-toggle:hover{background:var(--surface);color:var(--text)}
.m-app-fibery .m-nav[data-morph-collapsed="true"]{display:none}
.m-app-fibery>.m-region.m-row:has(>.m-nav[data-morph-collapsed="true"]){--shell-tracks-folded:minmax(0,1fr)}
.m-app-fibery .m-fold-compact{display:none;align-items:center;gap:.4rem;font-weight:600;font-size:14px;color:var(--text);
  padding:0 .6rem 0 0;margin-right:.35rem;border-right:1px solid var(--border);height:26px}
.m-app-fibery .m-fold-compact[data-morph-collapsed="true"]{display:flex}
.m-app-fibery .m-fold-compact>.m-glyph{color:var(--solid)}
.m-app-fibery .m-tools{flex-direction:column;gap:1px;padding:.35rem 0 .25rem}
.m-app-fibery .m-tool{gap:.55rem;font-size:13.5px;text-align:start}
.m-app-fibery .m-tool .m-glyph{color:var(--text-muted)}
.m-app-fibery .m-tool-on,.m-app-fibery .m-tool:hover{background:var(--surface)}
/* The Search tool is a full row in Fibery's sidebar, not an icon. */
.m-app-fibery .m-tool-icon{padding:.3rem .55rem}
.m-app-fibery .m-tool-icon::after{content:"Search";font-weight:500}
.m-app-fibery .m-nav-section{margin-top:.8rem}
.m-app-fibery .m-nav-section-label{font-size:11px;padding:.2rem .55rem .35rem;font-weight:600;
  text-transform:uppercase;letter-spacing:.06em}
.m-app-fibery .m-nav-row{gap:.55rem;padding:.3rem .55rem;font-size:13.5px;min-height:1.9rem}
.m-app-fibery .m-nav-row:hover{background:var(--surface)}
/* The open space: the reference's pale yellow. */
.m-app-fibery .m-nav-row-on,.m-app-fibery .m-nav-row-on:hover{background:#fdf6dc}
/* A space's icon is a coloured SQUARE, by position among the spaces; the
   module's page glyph is not drawn under it. */
.m-app-fibery .m-nav-row-icon{width:16px;height:16px;border-radius:4px;align-items:center;font-size:12px;flex:none}
.m-app-fibery .m-nav-row[data-morph-go^="pool:"] .m-nav-row-icon{background:var(--tone-bg,#8b95a5)}
.m-app-fibery .m-nav-row[data-morph-go^="pool:"] .m-nav-row-icon>.m-glyph{display:none}
/* TASK 0178 — a space's colour is a fact about the SPACE, not about where
   its row sits. These read :nth-of-type(6n+1) through (6n+6) once: with six
   pools against a cycle of six every pool held a stable colour and nothing
   looked wrong, which is exactly why it survived. A seventh pool shifts
   every colour after it; a hidden or filtered row shifts them too, since
   nth- counts what is in the DOM; and under 0058 two looks may order one
   rail differently, so the same pool wears two colours.

   ONE DECLARATION NOW, not six rules — @Codex's P2 on 2e06944. Keying on
   data-m-tone was right and the MAPPING was still app-scoped, so a composed
   or seventh look with the same navigation row got no categorical paint
   until it wrote its own six. The generic mapping above reaches .m-nav-row,
   so this supplies only the six VALUES the mapping reads, and five
   app-scoped selectors leave 0173's count. A look with more or fewer
   colours re-points as many as it has; the runtime learns no palette's
   size. */
.m-app-fibery{--tone-0-bg:#f2762b;--tone-1-bg:#4a6bb0;--tone-2-bg:#3f9c60;
  --tone-3-bg:#9a6dd7;--tone-4-bg:#d9a441;--tone-5-bg:#3d89dd}
.m-app-fibery .m-nav-row[data-morph-go^="kept:"] .m-nav-row-icon{color:var(--text-muted)}
.m-app-fibery .m-nav-row-detail{font-size:12px}
.m-app-fibery .m-nav-newpage{padding:.35rem .6rem;border:0;border-radius:6px;background:transparent;font-size:13.5px;color:var(--text-muted)}
.m-app-fibery .m-nav-newpage:hover{background:var(--surface);color:var(--text)}
/* The view column's topbar: thin, over the view. The column itself is
   generic now. */
.m-app-fibery .m-topbar{justify-content:space-between;gap:0;padding:0 .75rem 0 .5rem;height:42px;font-size:13.5px;border-bottom:1px solid var(--border)}
/* The strip takes the bar's width so the end's chrome and the page actions
   sit at the RIGHT edge, as the reference's header has them: with no tab
   strip the bar's space-between had floated them to the middle (seen). */
.m-app-fibery .m-topbar-strip{gap:.15rem;height:42px;flex:1 1 auto}
/* Fibery declared .m-topbar-tab and .m-topbar-tab-icon and drew NEITHER --
   task 0229, and proved from the PRODUCER rather than from one view. Only
   Fibery declares topbarFold, main.ts turns that into strip:false, and
   capability.ts suppresses the whole tab block on strip === false. So those
   two rules could not apply under any state this look can reach.

   Removed rather than kept as harmless, which is capability.ts's own rule
   about a slotless fallback whose substitution could not fire: dead defence
   is what this repository files as measuring nothing. The same producer
   condition is why the tab strip's plus had no Fibery drawer an hour ago. */
.m-app-fibery .m-topbar-actions{gap:.15rem;height:42px}
.m-app-fibery .m-topbar-star,.m-app-fibery .m-topbar-more{color:var(--text-muted);width:30px;height:30px;border-radius:6px;display:inline-flex;font-size:18px;letter-spacing:.04em}
.m-app-fibery .m-topbar-star:hover,.m-app-fibery .m-topbar-more:hover{color:var(--text)}
.m-app-fibery .m-topbar-star[aria-pressed="true"]{color:#f0a020}
.m-app-fibery .m-topbar-menu{top:38px;right:0;min-width:200px;border-radius:8px}
.m-app-fibery .m-topbar-menu-item{font-size:13.5px}
/* The view: the bar, the toolbar, then the board running to the right edge. */
.m-app-fibery .m-ws-editor{padding:.5rem 0 3rem 1rem;gap:0;background:var(--page)}
/* The view bar: a small icon, the view's name at 20px, on one line. */
.m-app-fibery .m-pagehead{align-items:center;gap:.6rem;margin:0 1rem .35rem .25rem}
.m-app-fibery .m-pagehead-titlerow{gap:.6rem}
.m-app-fibery .m-pagehead-icon{width:22px;height:22px;border-radius:0;background:transparent;font-size:17px;color:var(--text-muted)}
.m-app-fibery .m-pagehead-title{font-size:20px;font-weight:600;line-height:1.3}
/* The toolbar: muted view pills at the left; "+ New", bordered, white,
   at the right — the search box rides up beside it. */
.m-app-fibery .m-viewswitch{margin:0 1rem .6rem .25rem;
  padding-bottom:.6rem;border-bottom:1px solid var(--border)}
.m-app-fibery .m-viewswitch-list{gap:.15rem}
.m-app-fibery .m-viewswitch-item{gap:.4rem;padding:.3rem .55rem;font-size:13.5px}
.m-app-fibery .m-viewswitch-on{background:var(--fb-lit);color:var(--solid)}
.m-app-fibery .m-viewswitch-new{border:1px solid var(--border);background:var(--page);color:var(--text);
  font-size:13.5px;padding:.3rem .7rem;display:inline-flex;align-items:center;gap:.35rem}
.m-app-fibery .m-viewswitch-new::before{content:"+";font-size:15px;line-height:1;color:var(--text-muted)}
.m-app-fibery .m-viewswitch-new:hover{background:var(--surface)}
/* The row's tools the host cannot back — Cards, Fields, Filter, Sort,
   Color — inert (0056), muted, between the search and New; the lit ones
   (Filter, Color, as the reference lights them) in the pale orange. The
   filter STRIP under the row: bordered chips, each true of the view. */
.m-app-fibery .m-tools-inert{gap:.15rem;margin-right:.35rem}
.m-app-fibery .m-tool-inert{padding:.3rem .5rem;border-radius:6px;font-size:13.5px;color:var(--text);gap:.35rem}
.m-app-fibery .m-tool-inert .m-inert-glyph{color:var(--text-muted)}
.m-app-fibery .m-tool-inert.m-inert-end{background:var(--fb-lit);color:var(--solid)}
.m-app-fibery .m-tool-inert.m-inert-end .m-inert-glyph{color:var(--solid)}
.m-app-fibery .m-viewswitch{flex-wrap:wrap}
.m-app-fibery .m-inert-chips{flex-basis:100%;display:flex;align-items:center;gap:.4rem;padding-top:.5rem}
.m-app-fibery .m-inert-chip{border:1px solid var(--border);border-radius:8px;padding:.25rem .6rem;font-size:13px;color:var(--text);gap:.4rem}
.m-app-fibery .m-inert-chip .m-inert-glyph{color:var(--text-muted);font-size:12px}
.m-app-fibery .m-inert-chip.m-inert-end{border-color:var(--solid);color:var(--solid);background:var(--fb-lit)}
/* The topbar's right end: the reference's ›› , search and close — inert. */
.m-app-fibery .m-topbar-end{gap:.6rem;color:var(--text-muted)}
.m-app-fibery .m-topbar-end .m-inert{color:var(--text-muted)}
/* The view's search sits IN the tool row, left of Cards, as the reference
   places it: the box follows the row in the document, so it is placed
   against the view column's top-right, past the five tools and New. */
.m-app-fibery .m-ws-editor{position:relative}
.m-app-fibery .m-ws-search-box{position:absolute;top:2.85rem;right:30.5rem;width:200px;margin:0;padding:0}
.m-app-fibery .m-ws-search-input{font-size:13.5px;padding:.3rem .5rem;border:0;
  border-radius:6px;background:transparent}
.m-app-fibery .m-ws-search-input:focus{background:var(--surface)}

.m-app-fibery .m-ws-section{text-align:end;margin:-.8rem 1rem .6rem 0}
/* The board: plain columns, no tint — a bold header and a grey count,
   hairline cards. */
/* The board at the reference's SCALE — the twenty-fourth verdict: the
   pinned cards have more physical presence (larger type, taller cards,
   stronger internal spacing) over a warm canvas that runs to the
   window's foot; ours compressed ten records into small cards over an
   empty white stage. */
.m-app-fibery .m-as-board{gap:1rem;padding:.5rem 1rem 1.5rem 0;background:transparent;align-items:stretch;flex:1}
/* The swimlane matrix in the reference's SHAPE (the fifth pass, judged by
   eye beside fibery-board.png): a white head row of the states with a
   hairline between them, the whole body one warm canvas, and each lane's
   label a BAND above its row — the orange corner glyph, then the name at
   body size — not a column at the left; the cells carry no box of their
   own. A folded column is the rail at the reference's right: the name
   read downward, the count in a pill, the strip a shade deeper than the
   canvas, the full height of the matrix. */
.m-app-fibery .m-as-lanes{gap:0;margin:.5rem 1rem 0 0;padding:0 0 1.5rem;
  background:var(--fb-warm);border-radius:8px;--lane-track:minmax(160px,200px);--lane-rail:2.6rem}
/* CAPPED, not just floored. The sixth pass lowered the floor from 220px to
   200px and called it the width-first change; at 1440 the surplus still
   went to 1fr and every open column got WIDER (@Codex, P1 on #224 — 362px
   against 358px, measured on the two verdict images). The reference's
   column is about 300 CSS px and its board scrolls when the window is
   narrower than its columns, so the track's ceiling is the reference's
   width and the canvas past the last column stays warm. */
/* DENSITY — the thirty-first verdict's order: the column and card width
   first, the lane's minimum height second, the band-to-card gap third.
   A lane sizes to its tallest occupied cell plus a gutter, not to a
   planning canvas; the band sits close over its cards; the cards keep
   the 17px title the twenty-fourth praised but lose the padding that
   made them wide and flat. */
/* THE COLUMN PITCH, off fibery-board.png at 3x (see the card's rhythm
   below for how the 3x was established). Its cards are 600 pixels wide on
   a 675 pitch — 200 and 225 CSS px — so the pitch is a 200px card and a
   25px gutter, and the track and this gap are the two halves of it. The
   cap was 300 before, from the same capture read as 2x, which put our
   columns 41% wider than the app's. */
.m-app-fibery .m-lane{grid-template-columns:var(--lane-cols,repeat(var(--cols,1),var(--lane-track)));gap:1.55rem;padding:0 .6rem}
.m-app-fibery .m-lane+.m-lane{border-top:0;padding-top:0}
/* THE HEAD ROW IS WARM, not white — sampled. Ten points inside it and ten
   on the lane canvas below it all read (255,251,244) in the reference: one
   ground, with a hairline between. This said #fff and a clause pinned
   rgb(255,255,255), which a verdict had passed on; the sample is what
   settles it, the same way the 3x ruler settled the scale. The canvas token
   itself was #fbf9f6 and is now the sampled #fffbf4. */
.m-app-fibery .m-lane-head{background:var(--fb-warm);border-bottom:1px solid var(--border);border-radius:8px 8px 0 0;padding:0 .75rem}
.m-app-fibery .m-lane.m-lane-head>.m-lane-label{display:none}
.m-app-fibery .m-lane-caption{padding:.5rem .25rem;background:transparent;position:relative;min-height:0;
  border-radius:8px}
/* Decision 0057, task 0172: an OPEN column head carries the state's name and
   nothing else, because the reference draws nothing else there. The FOLDED
   rail keeps its count — the reference draws one there and only there — and
   whether a column is folded right now is view state the host applies after
   paint (0014), which is why this is a stylesheet rule and not an emit-time
   decision. display:none rather than a colour, so the number leaves the
   accessibility tree with the pixels: a count nobody can see is not a count a
   screen reader should read.

   The per-column create door is gone from the emitter, not hidden here
   (TEMPLATE_BOARD_HEAD) — a control hidden by a stylesheet is still a control,
   and 0057 asks for the capability to MOVE, which it does: Fibery's toolbar
   draws the New the reference itself uses.

   This replaces a rule that made that door COMPACT rather than absent. The
   thirty-sixth verdict had measured a full-width "+ New page" under every head
   spending about eighty pixels at the top of the board, and the eleventh pass
   shrank it to a "+" beside the count. 0057 settles the question the shrink
   was working around. */
.m-app-fibery .m-lane-caption{display:flex;align-items:center;gap:.35rem}
.m-app-fibery .m-lane-head .m-group-head{flex:1;min-width:0}
.m-app-fibery .m-lane-head .m-group:not([data-morph-collapsed="true"]) .m-count{display:none}
/* And the first lane's band sits closer under the head row. */
.m-app-fibery .m-lane-head+.m-lane>.m-lane-label{padding-top:.75rem}
.m-app-fibery .m-lane-head .m-group:not(:last-child)::after{content:"";position:absolute;top:0;bottom:0;right:-.4rem;border-right:1px solid var(--border)}
.m-app-fibery .m-lane-head .m-group-head{margin:0;border-bottom:0;padding:.45rem .25rem}
.m-app-fibery .m-lane>.m-lane-label{grid-column:1/-1;display:flex;align-items:center;gap:.6rem;font-size:15px;font-weight:500;
  color:var(--text);padding:.7rem .4rem 0}
.m-app-fibery .m-lane>.m-lane-label::before{content:"";flex:none;width:10px;height:10px;background:#f7a41d;border-radius:0 0 100% 0}
.m-app-fibery .m-lane-cell{background:transparent;border-radius:0;padding:0;min-height:1.25rem}
.m-app-fibery .m-group[data-morph-collapsed="true"]{flex:0 0 var(--lane-rail);min-width:0;background:#f3eee7;border-radius:0;padding:0}
/* Above the cell's strip, which reaches up into the head row: without
   this the strip painted over the rail's expand toggle — seen in the
   sixth pass's first shot, with the harness green (opacity 1, focusable,
   and covered). */
.m-app-fibery .m-lane-head .m-group[data-morph-collapsed="true"]{padding:0;position:relative;z-index:1}
.m-app-fibery .m-group[data-morph-collapsed="true"] .m-group-head{margin:0;padding:.6rem .35rem;gap:.6rem;border-bottom:0;
  justify-content:flex-start;align-items:center}
.m-app-fibery .m-group[data-morph-collapsed="true"] .m-group-name{font-size:14px;color:var(--text-muted)}
.m-app-fibery .m-group[data-morph-collapsed="true"] .m-count{margin:0;writing-mode:horizontal-tb;background:#e6e0d7;color:var(--text-muted);
  border-radius:999px;padding:.1rem .45rem;font-size:12.5px}
.m-app-fibery .m-lane-cell[data-morph-collapsed="true"]{position:relative;align-self:stretch}
.m-app-fibery .m-lane-cell[data-morph-collapsed="true"]::before{content:"";position:absolute;left:0;right:0;top:-4.5rem;bottom:-1.5rem;background:#f3eee7}
.m-app-fibery .m-group:not(:where(.m-lane-caption)){flex:1 1 270px;min-width:270px;max-width:360px;border:0;border-radius:8px;padding:.25rem .5rem .75rem;
  background:var(--fb-warm);min-height:100%}
.m-app-fibery .m-group-head{gap:.5rem;margin:0 0 .9rem;padding:.55rem .35rem;
  border-bottom:1px solid var(--border-subtle)}
.m-app-fibery .m-group-name{font-size:16px;font-weight:500;color:var(--text);padding:0;background:transparent;border:0}
.m-app-fibery .m-group-head .m-count{color:var(--text-muted);font-weight:400;font-size:13.5px;margin-left:auto;background:transparent;
  padding:0;border:0;border-radius:0}
.m-app-fibery .m-group-items{gap:.55rem}
/* A card is a card in a column and in a lane's cell alike, one selector for
   both, so the matrix did not silently draw bare cards (seen: white gone,
   badge gone). */
/* 4px CORNERS, not 8 (@Codex P2 on #230, and canon-looks records 4 for
   this app). Measured off the reference: the top-left arc reaches the
   card's own left edge 8 device rows down and 10 columns in, so the radius
   is ~3 CSS px at 3x. 4 is the recorded value and within that reading. */
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card{background:#fff;border:1px solid var(--border);border-radius:4px;padding:0;
  box-shadow:0 1px 2px rgba(20,20,30,.05)}
/* THE CARD'S RHYTHM, off fibery-board.png — and the SCALE of that capture
   is the whole story, so it is measured rather than assumed. Its 1px
   structural lines (card border, head row's bottom, the four head column
   separators) are each 3 pixels wide, and its column pitch is 675, which
   is 225 exactly at 3x and 337.5 at 2x. A 1px border cannot be 3px at 2x
   and a layout pitch is an integer: the capture is 3x. Control: the same
   pixel scan over our own 1x screenshot measures our 1px borders at 1.

   Read at 3x the reference pads 16.3px and sets 13px between the badge,
   the title and the chips. An earlier pass read the capture as 2x, took
   those for 24 and 20, and shipped 22.4 and 18.4 — which is why the card
   stood at 159px against the reference's 158.7 while carrying one band
   fewer. The height was right and the reason was wrong: the reference
   reaches it with badge, a two-line title, chips AND a foot relation row,
   not with air. Denser, not bigger. */
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card>.m-card-hit{width:100%;padding:1rem;border:0;background:transparent;
  font:inherit;color:inherit;text-align:start;cursor:pointer;border-radius:4px}
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card>.m-card-hit:hover{background:var(--raised)}
/* The TYPE BADGE: the entity's type, from the host (--fb-badge), small
   caps on a blue tile, above the title. */
/* AND THE CONTENTS, at the same corrected scale (@Codex P1 on #230): the
   first correction moved the padding and left the badge, the chip and the
   title where they were, so a card could satisfy the new rhythm while
   keeping the very error the change existed to remove. Measured off the
   reference at 3x: the badge tile is 48 device px tall (16 CSS), the chip
   pill 72 (24), and a card title's capital inks 32 (10.7). The badge's
   fill is (90,120,167) — sampled, and it IS a fill: the blue/white
   alternation across it is the white letterforms of "FEATURE", not a ring.
   canon-looks.md called it "blue-outlined" and is corrected with this. */
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card>.m-card-hit::before{content:var(--fb-badge,"ENTITY");display:inline-block;
  font-size:10px;font-weight:600;letter-spacing:.12em;line-height:1;padding:.19rem .5rem;border-radius:4px;
  background:var(--fb-badge-bg);color:#fff;
  /* NO MARGIN UNDER THE ROW, where the gap is already the reference's 13px:
     its own .8rem became a SECOND 12.8px there, the same collapse-versus-gap
     difference that cost the chips theirs. The badge is a pseudo-element, so
     it is the one item the row's own reset cannot reach, which is why the
     withholding is written into this selector rather than beside them.

     But a card that is NOT a row has no gap to inherit, and zeroing it there
     took the badge-to-title separation with it -- @Codex's P2 on 660c7bd,
     measured at 10px against 19px. So the margin is the default and the row
     is what removes it.

     AND flex:0 0 auto, NOT 100%. A 100% basis made the badge fill the row,
     so its background painted the whole card width and it read as a coloured
     BAR where the reference draws a compact tag hugging the word. The badge
     still gets a line to itself, because the item after it is the title and
     the chip row gives every non-chip child a 100% basis -- an item that
     cannot fit beside anything takes the next line by itself. Found by
     looking at a screenshot beside fibery-board.png, which is the only way
     this class of defect surfaces: every clause about the badge was green. */
  flex:0 0 auto;margin-block-end:.8rem}
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card>.m-card-hit:has(>:where(.m-kind-number,.m-kind-date,.m-kind-enum,.m-kind-score,.m-kind-ref) ~ :where(.m-kind-number,.m-kind-date,.m-kind-enum,.m-kind-score,.m-kind-ref))::before{margin-block-end:0}
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card .m-role-title{font-size:15px;font-weight:400;margin:0;line-height:1.35;color:var(--text)}
/* The chips: the card's fields as pastel tags under the title. */
/* Fibery's CHIP -- its appearance only. It used to carry
   display:inline-flex and margin:.8rem .3rem 0 0 as well, which is a look
   hand-rolling the flow the chip-row component now owns; under block layout
   those margins collapsed into the spacing and under the component they
   ADDED to its gap, measuring 17.6px between rows where the gap asks for
   4.8. Handing the flow over is the same move as the three restated
   display:block declarations above, and it is 0173's division: what a chip
   LOOKS like is Fibery's, where it sits is everyone's. */
/* THE CHIP'S OWN MARGIN, for a card the row does not reach. Its margin was
   deleted outright when the chip row took over the flow, and the row's gap
   only exists once TWO chips make the card flex -- so a card with one chip
   lost the separation and got nothing back. Measured on the seeded Realtime
   cursors row, whose only chip is effort: title to chip 0px, against 13px on
   every card beside it.

   The same defect as the badge's two rules below, found in the same review
   and missed by the same fix: I scoped the reset and restored the badge, and
   the chip's margin had been deleted rather than reset, so there was nothing
   for the scope to give back. Withheld under the row, where the gap supplies
   it -- this rule outranks the row's own 0-1-0 reset, so the scope has to be
   written here rather than left to the cascade. */
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card>.m-card-hit:not(:has(>:where(.m-kind-number,.m-kind-date,.m-kind-enum,.m-kind-score,.m-kind-ref) ~ :where(.m-kind-number,.m-kind-date,.m-kind-enum,.m-kind-score,.m-kind-ref))) .m-field:not(.m-role-title){margin-block-start:.8rem}
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card .m-field:not(.m-role-title){display:inline-flex;align-items:center;
  padding:.22rem .5rem;border-radius:5px;background:var(--fb-chip);font-size:13px;line-height:1.3;color:var(--text)}
/* THE FOOT BAND: the card's parent relation, drawn as its own row at the
   card's foot rather than as another chip — the fourth information band
   the reference's cards carry. Full width, its own hairline, the relation's
   affordance kept: it is the same reference chip a person can follow, in a
   different presentation, not a decoration.

   @Codex ruled this on 2026-09-03 after their own two answers collided:
   the swimlane communicates spatial grouping, the footer communicates
   card-local relational context, and drawing both is truthful duplication
   of a ROLE, not of data.

   This rule keys on the ROLE. An earlier version of this comment claimed
   it keyed on the generator's fb-card-foot slot; @Codex checked and it
   does not — the slot name never reaches the markup, so the claim was a
   property the code did not have. The role is the right key anyway, and
   for a reason worth stating rather than settling for: under this look the
   parent relation IS the lane, so wherever a spec binds it on a card, the
   foot is where it belongs. What the role does NOT do is depend on
   position — a fifth chip cannot become the foot by being added last. */
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card .m-field.m-role-relation\\.parent{
  display:flex;align-items:center;gap:.4rem;width:100%;margin:0;box-sizing:border-box;
  padding:.3rem .45rem;border:1px solid var(--border);border-radius:5px;background:#fff;
  font-size:13px;color:var(--text);white-space:nowrap}
/* The flag is a PENNANT — a straight diagonal cut at the bottom right, not
   the lane label's rounded quarter-circle. Read at 6x off the reference's
   own foot row; the two shapes are easy to confuse at 1x and obvious side
   by side. Its colour is sampled: rgb(215,66,65), 426 pixels of it. */
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card .m-field.m-role-relation\\.parent::before{
  content:"";flex:none;width:10px;height:10px;background:#d74241;
  clip-path:polygon(0 0,100% 0,100% 52%,0 100%)}
/* AND A CHEVRON at the right, which is where the reference puts the
   "this leads somewhere" signal. Ours put it in an UNDERLINE under the
   label — a hyperlink's signal, not a row's — so the band read as a link
   where the reference's reads as a row. The underline goes and the chevron
   arrives: the same claim, in the reference's own place for it. The whole
   band stays followable, so nothing is lost but the wrong signal. */
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card .m-field.m-role-relation\\.parent::after{
  content:"";flex:none;margin-left:auto;width:12px;height:12px;background:var(--text-muted);
  -webkit-mask:url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" fill="none" stroke="black" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>') center/contain no-repeat;
  mask:url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" fill="none" stroke="black" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>') center/contain no-repeat}
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card .m-field.m-role-relation\\.parent .m-ref-in-hit{
  border-bottom:0;text-decoration:none;color:inherit;min-width:0;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card .m-field.m-kind-date{background:#e8f1fb}
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card .m-field.m-kind-number{background:#eaf5ec}
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card[aria-current="true"]>.m-card-hit,
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card.m-current>.m-card-hit{background:var(--fb-lit)}
/* Fibery declared .m-group-add and its hover and drew NEITHER -- task 0229,
   the third instance and again proved from the PRODUCER. The column's New
   page button is emitted only when TEMPLATE_BOARD_HEAD says the look wants
   it, and app.fibery says create:false, deliberately: 0057 records that
   Fibery's column heads carry the state's name alone and its TOOLBAR draws
   the New the reference uses, so the capability moved rather than went.

   Worth naming because of what these two rules were doing to the MAP. 0173
   picks its next cluster from which (class, property) pairs two or more looks
   both declare, and .m-group-add stood at the top of that table with eleven —
   a component two looks agree on. One of the two draws none of them. Removing
   dead rules is not only a smaller sheet: it stops the promotion map
   suggesting a component that does not exist. */
/* The TABLE: a hairline grid, small grey headers, 44px rows, pill states,
   relation chips. */
.m-app-fibery .m-table-scroll{margin-right:0;padding-right:1rem}
/* The table at the reference's SCALE (the ninth pass, judged by eye beside
   fibery-table.png): rows of 52px at 15px type under a header at 14px,
   each header opening with the FIELD'S TYPE glyph — text, select, number,
   date, relation — the way Fibery names a column's kind before its name;
   and every row numbered at its start, a position the way the reference
   numbers its rows. The kind is the column's class, so the glyph is a
   mask per kind and no markup; the number is a counter and no markup.
   The reference's hierarchy (goals, projects, tasks under disclosure
   triangles) is data the shape does not hold, so none is drawn. */
/* The glyph rule names EVERY kind the header can carry (ROLE_KINDS, the
   producer) and nothing else: a kind without a mask drew a solid square
   (@Codex's P2 on #227, the score header), so a kind this list does not
   name draws nothing, and the unit clause walks the producer's list. */
.m-app-fibery .m-as-table th:is(.m-kind-text,.m-kind-doc,.m-kind-enum,.m-kind-number,.m-kind-date,.m-kind-ref,.m-kind-score,.m-kind-media,.m-kind-av)::before{content:"";display:inline-block;width:15px;height:15px;margin:0 .45rem -3px 0;
  background:currentColor;mask-position:center;mask-size:contain;mask-repeat:no-repeat;-webkit-mask-position:center;-webkit-mask-size:contain;-webkit-mask-repeat:no-repeat}
.m-app-fibery .m-as-table th.m-kind-text::before,.m-app-fibery .m-as-table th.m-kind-doc::before{mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round'%3E%3Cpath d='M4 6h12M10 6v13M15 12h5M17.5 12v7'/%3E%3C/svg%3E");-webkit-mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round'%3E%3Cpath d='M4 6h12M10 6v13M15 12h5M17.5 12v7'/%3E%3C/svg%3E")}
.m-app-fibery .m-as-table th.m-kind-enum::before{mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round'%3E%3Cpath d='M4 12a8 8 0 0 1 14-5l2 2M20 12a8 8 0 0 1-14 5l-2-2M18 4v5h-5M6 20v-5h5'/%3E%3C/svg%3E");-webkit-mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round'%3E%3Cpath d='M4 12a8 8 0 0 1 14-5l2 2M20 12a8 8 0 0 1-14 5l-2-2M18 4v5h-5M6 20v-5h5'/%3E%3C/svg%3E")}
.m-app-fibery .m-as-table th.m-kind-number::before{mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round'%3E%3Cpath d='M10 4L8 20M16 4l-2 16M4 9h16M4 15h16'/%3E%3C/svg%3E");-webkit-mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round'%3E%3Cpath d='M10 4L8 20M16 4l-2 16M4 9h16M4 15h16'/%3E%3C/svg%3E")}
.m-app-fibery .m-as-table th.m-kind-date::before{mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round'%3E%3Cpath d='M4 5h16v15H4zM4 10h16M8 3v4M16 3v4'/%3E%3C/svg%3E");-webkit-mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round'%3E%3Cpath d='M4 5h16v15H4zM4 10h16M8 3v4M16 3v4'/%3E%3C/svg%3E")}
.m-app-fibery .m-as-table th.m-kind-ref::before{mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round'%3E%3Cpath d='M10 14a4 4 0 0 0 5.6 0l3-3a4 4 0 0 0-5.6-5.6l-1 1M14 10a4 4 0 0 0-5.6 0l-3 3a4 4 0 0 0 5.6 5.6l1-1'/%3E%3C/svg%3E");-webkit-mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round'%3E%3Cpath d='M10 14a4 4 0 0 0 5.6 0l3-3a4 4 0 0 0-5.6-5.6l-1 1M14 10a4 4 0 0 0-5.6 0l-3 3a4 4 0 0 0 5.6 5.6l1-1'/%3E%3C/svg%3E")}
.m-app-fibery .m-as-table th.m-kind-score::before{mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 3l2.8 5.8 6.2.9-4.5 4.4 1.1 6.2L12 17.4 6.4 20.3l1.1-6.2L3 9.7l6.2-.9z'/%3E%3C/svg%3E");-webkit-mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 3l2.8 5.8 6.2.9-4.5 4.4 1.1 6.2L12 17.4 6.4 20.3l1.1-6.2L3 9.7l6.2-.9z'/%3E%3C/svg%3E")}
.m-app-fibery .m-as-table th.m-kind-media::before{mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 5h16v14H4zM4 15l5-5 4 4 3-3 4 4M15.5 9.5h.01'/%3E%3C/svg%3E");-webkit-mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 5h16v14H4zM4 15l5-5 4 4 3-3 4 4M15.5 9.5h.01'/%3E%3C/svg%3E")}
.m-app-fibery .m-as-table th.m-kind-av::before{mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18zM10 8.5v7l5.5-3.5z'/%3E%3C/svg%3E");-webkit-mask-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18zM10 8.5v7l5.5-3.5z'/%3E%3C/svg%3E")}
.m-app-fibery .m-as-table tbody{counter-reset:m-row}
/* THE ROW NUMBER, and the NAME COLUMN'S WEIGHT above, key on the column's
   IDENTITY rather than on its position -- task 0178's defect under another
   name, exposed by 0176's Open gutter. A td:first-child selector, and a
   td.m-kind-text:first-child one, are :nth-of-type in a nicer suit: they
   were correct only while the name happened to be the leftmost column, and
   a leading gutter, a reordered table, or a hidden first column each break
   them. Three of these four rules had NO clause and would have shipped a
   silent regression; only Fibery's counter was measured.

   data-m-name marks the column that carries the ROW'S NAME, so this holds
   wherever the column sits AND whatever role names it -- @Codex's P2 on
   ed4f7a7. The first version of these rules matched data-role="title",
   which is one guess about the name further on than the position they had
   just stopped guessing: a card named by a subtitle binding sets
   nameFrom to that role, so the wrong column was bolded and this counter
   never incremented at all, taking Fibery's row numbers with it. The
   renderer knows which column names the row; the stylesheet cannot.

   NO BACKTICKS IN THIS FILE'S COMMENTS. The stylesheet is one template
   literal and the first backtick ends it: this very comment cost a build,
   the sixth time, written by a session that had re-read the rule. */
.m-app-fibery .m-as-table tbody tr>td[data-m-name] .m-cell::before{counter-increment:m-row;content:counter(m-row);display:inline-block;
  min-width:1.3rem;margin-right:.5rem;color:var(--text-muted);font-size:12.5px;font-weight:400;text-align:end;font-variant-numeric:tabular-nums}
.m-app-fibery .m-as-table td.m-kind-enum .m-cell,.m-app-fibery .m-as-table td.m-kind-enum select{display:inline-flex;
  align-items:center;gap:.4rem;padding:.2rem .6rem .2rem .5rem;border-radius:999px;
  border:1px solid var(--border);font-size:14px;appearance:none}
.m-app-fibery .m-as-table td.m-kind-enum .m-cell::before{content:"";width:10px;height:10px;border-radius:50%;
  border:1.5px solid var(--text-muted);display:inline-block}
.m-app-fibery .m-as-table .m-ref-hit{display:inline-flex;padding:.15rem .5rem;border-radius:5px;background:var(--fb-chip);
  border:0;font-size:13.5px;color:var(--text)}
.m-app-fibery .m-as-table .m-ref-hit:hover{background:var(--surfaceHover,#ececef);border-bottom-color:transparent}
/* PER-VALUE colour — the thirty-second verdict's next item: the reference's
   chips carry a colour per value (and an emoji the person chose, which our
   data does not hold, so none is drawn). A state's pill and its dot wear
   the value's hue in the table and on a card; a person's chip opens with
   an initial in a coloured circle, the way Fibery draws a person. Twelve
   hues from a hash of the text: the same value, the same colour,
   everywhere. */
.m-app-fibery .m-as-table td.m-kind-enum .m-cell,.m-app-fibery .m-as-table td.m-kind-enum select{background:hsl(var(--m-hue,220) 70% 94%);
  border-color:hsl(var(--m-hue,220) 45% 80%);color:hsl(var(--m-hue,220) 40% 28%)}
.m-app-fibery .m-as-table td.m-kind-enum .m-cell::before{background:hsl(var(--m-hue,220) 65% 55%);border-color:hsl(var(--m-hue,220) 65% 45%)}
.m-app-fibery :is(.m-group,.m-lane-cell) .m-card .m-field.m-kind-enum{background:hsl(var(--m-hue,220) 70% 93%);color:hsl(var(--m-hue,220) 40% 28%)}
.m-app-fibery :is(.m-group,.m-lane-cell,.m-as-table) .m-ref-hit{display:inline-flex;align-items:center;gap:.4rem}
/* The initial is a PERSON's: the assignee (relation.peer). A sprint is an
   entity too, and Fibery draws an entity chip plain with its type's icon,
   not as a face — the first table shot had an S in a circle before every
   sprint, which read as a person called S. */
.m-app-fibery :is(.m-group,.m-lane-cell,.m-as-table) .m-role-relation\\.peer .m-ref-hit::before{content:attr(data-m-initial);width:18px;height:18px;border-radius:50%;
  display:inline-grid;place-items:center;font-size:10.5px;font-weight:600;line-height:1;
  background:hsl(var(--m-hue,220) 55% 80%);color:hsl(var(--m-hue,220) 45% 26%)}
/* ═══════════════════════════════════ app.fabric — decision 0055's sixth look
   A NOTE in a floating window with the assistant panel, read from the
   pinned reference (the fabric.so hero, full resolution): a white window
   with an 18px radius and a soft shadow over a blurred photo; a thin
   title bar naming the note in muted grey, with the search, the lilac
   people button and ··· at its right; the note — a 34px light-grey
   title, then its media, then 16px prose with bold run-in headings; at
   the right a 300px panel: "AI assistant" with a chevron, a model pill,
   the thread, and a composer box at the foot with a chip, "Ask me
   anything…" and "+ Actions". The composer is the library's retrieval;
   the thread is what it found. */
.m-app-fabric{--fa-lilac-soft:#ebe8ff;--fa-title:#9a9a9f;
  --font:-apple-system,BlinkMacSystemFont,'Segoe UI','Inter',Roboto,Helvetica,Arial,sans-serif;
  -webkit-font-smoothing:antialiased;font-size:15px}
/* The desk: a blurred photo — soft warm and grey blobs over a pale ground. */
/* The panel is a SHARE of the window, not a fixed strip -- task 0170,
   finding 2 of the Fabric viewed pass. Measured off the reference by pixel
   scan: the window's white runs 123..1093 and the panel divider falls at
   777, so the panel is 315 of 970, or 32.5%. At a fixed 300px in a window
   that fills a 1440 viewport ours was 22.7%, and the assistant read as a
   margin beside the note rather than as half the product.

   A FLOOR and no ceiling, and the first attempt had both. clamp(300px,
   32.5%, 460px) put the share back at 25.6% on a 1920 viewport and 18.9%
   on 2560 -- reintroducing the exact defect at two of the commonest sizes,
   measured rather than reasoned about. The reference gives one fact, a
   proportion at its own scale; a ceiling is an invention about window
   widths it says nothing on, and this one contradicted the fact it sat
   beside. The floor is not: 300px is the width the panel already shipped
   at and the composer is known to fit in it.

   The percentage resolves against the grid container's content box, which
   is the window. */
.m-app-fabric>.m-region.m-row{--shell-tracks:minmax(0,1fr) max(300px,32.5%);padding:56px 60px;
  box-sizing:border-box;
  background:radial-gradient(60% 50% at 20% 30%,#d9cfc4 0,transparent 70%),radial-gradient(50% 40% at 80% 20%,#c9c3bd 0,transparent 70%),
    radial-gradient(45% 45% at 60% 85%,#e3d6c8 0,transparent 70%),#efeae5}
/* Fabric's shell does NOT stretch its children: its two columns are sized
   by their own content. Stated, because the generic rule above stretches. */
.m-app-fabric>.m-region>*{align-self:auto}
/* The window: the two columns share one white sheet with the radius. */
.m-app-fabric>.m-region>.m-region.m-stack:not(.m-ws-search){
  border-radius:18px 0 0 18px;box-shadow:0 30px 60px -20px rgb(0 0 0/.35),0 2px 6px rgb(0 0 0/.08);overflow:hidden}
.m-app-fabric>.m-region>.m-region[data-purpose="search"],.m-app-fabric>.m-region>.m-ws-search{background:var(--page);
  border-radius:0 18px 18px 0;border-left:1px solid var(--border-subtle);box-shadow:0 30px 60px -20px rgb(0 0 0/.35),0 2px 6px rgb(0 0 0/.08);
  display:flex;flex-direction:column;gap:0;overflow:hidden}
/* The title bar: the note's name, muted, at the left; no icon, no
   chevrons (Fabric keeps no tab strip); the search glyph, the lilac
   people button and the vertical ellipsis together at the right.

   TOGETHER is the load-bearing word and this rule used to say it while
   doing the opposite (task 0170, the Fabric viewed pass). The topbar has
   THREE flex children -- the strip, m-topbar-end holding the two glyphs, and
   m-topbar-actions holding the ellipsis -- so justify-content:space-between
   parked m-topbar-end at the CENTRE of the column and left the ellipsis alone
   380px away at the far right. Fabric groups all of them in one run ending
   at the editor column's right edge.

   ONE auto margin on the strip does it, and justify-content is left at its
   initial flex-start rather than restated. Both were written at first, and
   the mutation matrix caught the redundancy: restoring space-between with
   the auto margin in place changed NOTHING on screen, because an auto
   margin consumes the free space before justify-content can distribute it.
   Two mechanisms for one effect is a fix whose control cannot fail. */
.m-app-fabric .m-topbar{gap:.35rem;height:44px;padding:0 .9rem 0 1rem;border-bottom:1px solid var(--border-subtle);font-size:13.5px}
.m-app-fabric .m-topbar-strip{gap:.4rem;height:44px;margin-inline-end:auto}
/* And Fabric declared .m-topbar-star{display:none} for a button it never
   emits -- 0229. main.ts sets the kept flag only when a look declares
   topbarStar, Fabric's record says false, and capability.ts emits the star
   only when that flag is defined. Hiding what cannot exist is a rule whose
   control cannot fire. */
.m-app-fabric .m-chev,.m-app-fabric .m-topbar-tab-add,.m-app-fabric .m-topbar-tab-icon{display:none}
.m-app-fabric .m-topbar-tab{padding:0;color:var(--text-muted);font-weight:400;font-size:13.5px}
.m-app-fabric .m-topbar-end{gap:.35rem;color:var(--text)}
.m-app-fabric .m-topbar-end .m-inert{width:28px;height:28px;border-radius:8px;display:inline-flex;align-items:center;justify-content:center;
  color:var(--text)}
.m-app-fabric .m-topbar-end .m-inert-box{background:var(--fa-lilac-soft);color:var(--solid);border:0;padding:0}
.m-app-fabric .m-topbar-actions{gap:.1rem;height:44px}
/* Fabric's is a VERTICAL ellipsis. The glyph is shared markup, so it is
   turned rather than substituted -- a CSS content override would replace the
   text of a button whose accessible name comes from its aria-label, which is
   a way to be right by accident. Rotating the box leaves the name alone. */
.m-app-fabric .m-topbar-more{color:var(--text);width:28px;height:28px;border-radius:8px;font-size:16px;letter-spacing:.04em;transform:rotate(90deg)}
.m-app-fabric .m-topbar-menu{top:36px;right:0;min-width:200px;border-radius:10px}
.m-app-fabric .m-topbar-menu-item{font-size:13.5px}
/* The note: a wide measure, the title large and light grey, the prose 16px. */
/* NO flex-direction HERE — decision 0074 row 5. It restated column, which
   .m-region.m-stack already declares by definition (0070), and the two
   declarations tied at (0,2,0) so which one applied was decided by position in
   the sheet. 0073's fourth resolution: the redundancy goes, the generic keeps
   the topology, and everything else on this rule stays Fabric-owned. */
.m-app-fabric .m-ws-editor{padding:2.4rem 2.6rem 3rem;gap:1rem;background:var(--page)}
.m-app-fabric .m-ws-editor .m-field.m-role-title,.m-app-fabric .m-ws-editor .m-role-title{font-size:34px;font-weight:500;color:var(--fa-title);
  line-height:1.2;margin:0;letter-spacing:-.01em}
.m-app-fabric .m-ws-editor .m-field{font-size:16px;line-height:1.55;color:var(--text);max-width:64ch}
.m-app-fabric .m-ws-editor .m-field.m-kind-text:not(.m-role-title){white-space:pre-wrap}
.m-app-fabric .m-ws-editor .m-media{max-width:64ch;width:100%;border-radius:10px;overflow:hidden}
.m-app-fabric .m-ws-editor video,.m-app-fabric .m-ws-editor audio{width:100%;border-radius:10px}
/* The panel: the header, the thread, the composer at the foot. */
/* The module lays out as nothing, so its header, its count and its box are
   the panel's own children and take their places: the header at the top,
   the thread in the middle, the composer at the foot. That is the generic
   tool's DEFAULT now rather than a rule of this look's -- 0173 made the box a
   token and set its default to contents, which is what three of the four
   doors that draw it wanted. This look sets nothing and gets it. */
.m-app-fabric .m-ws-search{padding:0}
/* TWO ROWS, as the reference has: the chevron and "AI assistant" on the
   first, the model pill alone on the second, aligned under the heading
   rather than under the chevron. Fabric puts a thread glyph at the left of
   that second row; we draw no such control and do not invent one, so the
   row simply starts at the heading's own indent.

   A GRID, not a wrapping flex row. The first attempt gave the heading a
   flex-basis of calc(100% - 24px - .5rem) so the pill would wrap past it;
   that basis plus the chevron plus the gap comes to exactly 100%, sub-pixel
   rounding pushed it over, and the HEADING wrapped as well -- three rows,
   the chevron alone on the first. Two columns place both rows outright and
   no arithmetic has to come out even. */
.m-app-fabric .m-ws-search-head{display:grid;grid-template-columns:24px 1fr;column-gap:.5rem;row-gap:.35rem;
  align-items:center;padding:.75rem .9rem;font-size:13.5px;font-weight:500;
  border-bottom:1px solid var(--border-subtle);margin:0;order:0}
.m-app-fabric .m-ws-search-head::before{content:"›";width:24px;height:24px;border:1px solid var(--border);border-radius:6px;display:inline-flex;
  align-items:center;justify-content:center;color:var(--text);font-size:14px}
.m-app-fabric .m-ws-search-head .m-inert-box{font-weight:500;font-size:12.5px;background:var(--surface);border:0;border-radius:6px;padding:.2rem .5rem;
  grid-column:2;justify-self:start}
.m-app-fabric .m-ws-search-box{background:var(--raised);border:1px solid var(--border-subtle);border-radius:14px;padding:.7rem .8rem .6rem;
  display:flex;flex-direction:column;gap:.5rem;margin:auto .8rem .8rem;order:3}
.m-app-fabric .m-ws-search-input{order:2;font-size:13.5px;padding:.2rem 0;
  border:0;background:transparent}

.m-app-fabric .m-ws-search-inert{display:contents}
.m-app-fabric .m-ws-search-inert .m-inert-box{order:1;align-self:flex-start;background:var(--surface);border:0;border-radius:6px;font-size:12.5px;
  font-weight:500;padding:.25rem .55rem}
.m-app-fabric .m-ws-search-inert .m-inert:not(.m-inert-box){order:3;font-size:13px;color:var(--text);gap:.5rem;padding-top:.2rem}
.m-app-fabric .m-ws-search-inert .m-inert:not(.m-inert-box) .m-inert-glyph{font-size:16px;color:var(--text)}
.m-app-fabric .m-ws-section{padding:.5rem .9rem 0;order:1}
/* The thread: what the composer found, a card per hit. */
.m-app-fabric .m-ws-search .m-collection{order:2;overflow:auto;padding:.6rem .8rem;flex:0 1 auto}
.m-app-fabric .m-ws-search .m-as-cards{display:flex;flex-direction:column;gap:.45rem}
.m-app-fabric .m-ws-search .m-card{background:var(--raised);border:1px solid var(--border-subtle);border-radius:12px;padding:0}
.m-app-fabric .m-ws-search .m-card>.m-card-hit{width:100%;padding:.6rem .75rem;border:0;background:transparent;font:inherit;
  color:inherit;text-align:start;cursor:pointer;border-radius:12px}
.m-app-fabric .m-ws-search .m-card>.m-card-hit:hover{background:var(--surface)}
.m-app-fabric .m-ws-search .m-card .m-role-title{font-size:13.5px;font-weight:600;margin:0 0 .2rem;color:var(--text)}
.m-app-fabric .m-ws-search .m-card .m-field:not(.m-role-title){font-size:12.5px;color:var(--text-muted);line-height:1.4;
  display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
/* "+ New page" at the foot of a column: a muted row, lit on hover. */
.m-app-notion .m-group-add{display:flex;align-items:center;gap:.5rem;width:100%;margin-top:.6rem;
  padding:.5rem .75rem;border:0;border-radius:6px;background:transparent;font:inherit;font-size:14px;
  color:var(--text-muted);cursor:pointer;text-align:start}
.m-app-notion .m-group-add:hover{background:rgb(15 15 15 / .04);color:var(--text)}
.m-app-notion .m-group-add-plus{font-size:16px;line-height:1}
`;
